/**
 * 编辑器导入模型加载 composable。
 *
 * 职责：
 * 1. 建立 CAD 会话并读取项目中的 GLB 文件；
 * 2. 管理底部零件/装配标签的切换与缓存；
 * 3. 将导入模型挂载到场景，并同步构建 GLB 设计树；
 * 4. 保证设计树 hierarchy 的唯一权威来源始终来自 cad-core 的加载结果。
 *
 * Task 3 后 request ownership 收口到 `ImportedModelRequestRegistry`：
 *   - `preloadTabModel` / `handlePartTabSelect` 共享同一 scope，因此互相抢占；
 *   - `prepareInsertion` 独立 scope，但 tab 切换会主动 abort 当前 insertion handle，
 *     使其失去提交资格（"tab 切换或再次插入时，旧请求失去提交资格"）；
 *   - 只有仍 current 的 handle 才能 mount / switchTab / hideLoading / semantic commit；
 *   - 被抢占的 handle 晚到结果按资源所有权释放，避免 GPU 泄漏或误释放共享 cache。
 */
import { computed, ref } from 'vue'
import { resolveProjectModelNodeBySceneObjectIds } from './resolveProjectModelNodeBySceneObjectIds'
import * as THREE from 'three'
import type { Object3D as WebGpuObject3D } from 'three/webgpu'
import type { CadNode, FileInfo } from '@puppycad/proto'
import {
  AssemblyDraftDocumentStore,
  AssemblyComponentFixationService,
  AssemblyReferenceCopyKind,
  AssemblyReferenceCopyLineageMetadataService,
  AssemblyReferenceCopyLineageRole,
  AssemblyReferenceCopyMetadataService,
  AssemblyReferenceCopyNameService,
  assemblyTransformFromMatrix,
  applyAssemblyFixationStateWithHistory,
  applyImportedModelTabCacheState,
  buildProjectModelDesignTreeProjection,
  cloneImportedModelTopologyRuntime,
  cloneWithSharedGeometryRuntimeMap,
  collectAssemblyNodeIdentityLookupIds,
  collectResolvedPartTabTypesFromAssembly,
  composeProjectModelImportedResult,
  convertComponentInstanceSnapshotToAssemblyHistorySnapshot,
  createAssemblyMateDocument,
  createComponentInstanceSnapshotFromObject,
  createProjectModelLoadResultFromAssemblyStructureHistorySnapshot,
  DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
  createProjectModelLoadPlan,
  createImportedModelTopologyScopedRemovalPlan,
  createWorkerStateStepExportSource,
  detachImportedAssemblyFromScene,
  disposeCommittedImportedModelResult,
  disposeImportedModelTopologyHosts,
  disposePuppyPackageImportedModelResults,
  disposeImportedModel,
  disposeImportedModelInstance,
  disposeMaterialTextures,
  executeProjectModelLoad,
  filterGlbFiles,
  importPuppyPackage,
  GeometryRuntimeOwnershipIndex,
  ImportedModelRevisionSession,
  ImportedModelResourceRevision,
  ImportedModelObjectMetadataService,
  isImportedBrepGeometryObjectAssetScopeFromFile,
  mountImportedModelToScene,
  moveObject3DChildToIndex,
  normalizeAssemblyNodeId,
  normalizeImportedAssetFileId,
  rebindClonedImportedModelAssemblySelectionMetadata,
  rebindInsertedImportedModelAssemblySelectionMetadata,
  readComponentInstanceAssemblyNodeId,
  createProjectModelPuppyMateNodeAliasByNodeId as createCadCoreProjectModelPuppyMateNodeAliasByNodeId,
  createProjectModelPuppyNodeAliasByNodeId as createCadCoreProjectModelPuppyNodeAliasByNodeId,
  remapPuppyAssemblyMateDocumentNodeReferences,
  remapPuppyDatumExtensionNodeReferences,
  resolveImportedBrepGeometryObjectAssetScope,
  resolveImportedModelHierarchy,
  resolveImportedModelTabType,
  resolveProjectModelNodeAssemblyNodeId,
  resolvePuppyAssemblyPackageRootFile,
  splitAssemblyNodePath,
  AssemblyComponentFixationState,
  GlbHierarchyPersistenceMode,
  LinearComponentPatternPersistenceSchemaVersion,
  ProjectModelSubtreeSemanticFinalizer,
  ProjectModelLinearComponentPatternComponentRootRebinder,
  ProjectModelRuntimeStateResolver,
  PuppyDatumExtensionSchemaVersion,
} from '@puppycad/cad-core'
import type {
  AssemblyComponentHistorySnapshot,
  AssemblyDocument,
  AssemblyManifest,
  AssemblyMateCommittedSolveResult,
  AssemblyMateDocumentJSON,
  AssemblyMateFromJSONByAssemblyOptions,
  AssemblyMateLoadDocumentOptions,
  AssemblyReferenceCopyLineageMetadata,
  AssemblyStructureHistorySnapshot,
  AssemblyTransform,
  AssemblyTransformHistoryRecord,
  BRepRegistrar,
  ComponentTransformSnapshot,
  ComposedAssemblyResult,
  GeometryRuntimeOwnershipEntry,
  GlbHierarchyNode,
  GlbHierarchyNodeMeta,
  ImportedModelResult,
  ImportedModelTabDescriptor,
  ImportedModelTopologyScopedRemovalPlan,
  ImportedTopologySource,
  InsertComponentFixationMode,
  LinearComponentPatternCommittedRecord,
  LinearComponentPatternPersistenceDocument,
  Matrix4Tuple,
  ModelAssetFile,
  ModelAssetNode,
  ProjectModelLoadResult,
  ProjectModelPackageResult,
  ProjectModelSceneBinding,
  PuppyAssemblyDocument,
  PuppyDatumExtension,
  PuppyDatumExtensionInput,
  PuppyExportAssemblySemanticSource,
  PuppyFeatureExtension,
  PuppyImportedModelTabTemplate,
  PuppyPackageDocumentType,
  PuppySectionViewExtension,
  PuppySketchEntityReferenceExtension,
  PuppySketchExtension,
  SelectionTarget,
  StepExportSource,
  ViewerDocumentRuntimeSwitchHandle,
  ViewerDocumentRuntimeSwitchInput,
} from '@puppycad/cad-core'
import { useCadSessionStore, usePartTabsStore } from '@/stores'
import { useFeatureTreeStore } from '@/hooks/useFeatureTree'
import { useImportedModelCache } from '@/hooks/useImportedModelCache'
import { useImportedModelFeatureTree } from '@/hooks/useImportedModelFeatureTree'
import { ProjectModelStructureFeatureTreeProjector } from '@/core/feature-tree/presentation'
import {
  createOpenProjectSessionInput,
  normalizeOpenProjectSessionSource,
  type OpenProjectSessionInput,
  type OpenProjectSessionSource,
} from '@/services/api/open-project-input'
import {
  ImportedModelLoadAbortedError,
  isImportedModelLoadAbortedError,
} from '@/services/loaders/imported-model-load-guard'
import {
  loadCompleteGlbScene,
  loadImportedModel,
  type ImportedModelFileBytesReader,
  type LoadedImportedModel,
  type ProjectModelPuppyMateDocument,
} from '@/services/loaders'
import {
  loadInsertedCadModel,
  type InsertCadSourceDescriptor,
} from '@/services/loaders/insert-cad-loader'
import type {
  LoadedProjectModelPackageResult,
  OfflinePuppyPackageMountInput,
  ProjectModelLoadFailureInput,
  UseEditorGlbLoaderOptions,
} from '../../../types/editor'

export type {
  ProjectModelLoadFailureInput,
  UseEditorGlbLoaderOptions,
} from '../../../types/editor'
import { apiConfig } from '@/services/api/config'
import {
  createAuthorizedVersionFileReader,
  CompleteWorkAssemblySession,
  createAppendMissingFirstLevelNodeVersionLoadUnit as createAppendMissingFirstLevelNodeVersionLoadUnitFromService,
  createAssemblyDeletedSubtreeFileSnapshot,
  buildProjectAssetFileIdentityKey,
  buildProjectNodeFileBytesByReadKey,
  buildProjectNodeFileReadKey,
  collectMissingProjectModelFileReadTasksFromCache,
  collectProjectNodeFileReadTasks,
  completeProjectModelFileReadResultsFromEmbeddedContainers,
  createPreloadedProjectModelReader,
  createSwitchFirstLevelNodeVersionLoadUnit as createSwitchFirstLevelNodeVersionLoadUnitFromService,
  decodeAssemblyDeletedSubtreeFileSnapshotBytes,
  initializeAssemblyDraftDocumentStoreFromRoot,
  mapCadNodeToModelAssetNode,
  prepareProjectModelBackendLoad,
  prepareCompleteGlbPackageSnapshot,
  resolveLoadableOpenProjectRootSnapshot,
  readAssemblyDeletedSubtreePayload,
  restoreAssemblyDeletedSubtreeModelAssetFile,
  summarizeProjectModelSceneGraph,
  readProjectModelFileReadTasks,
  readProjectModelPlanFiles,
  writeAssemblyDeletedSubtreePayload,
  type AssemblyDeletedSubtreeFileSnapshot,
  type AssemblyDeletedSubtreePayload,
  type AssemblyDeletedSubtreePayloadRef,
  type AssemblyDeletedSubtreeHierarchySnapshot,
  type AssemblyDeletedSubtreeObjectSnapshot,
  type CreateAppendMissingFirstLevelNodeVersionLoadUnitInput,
  type CreateSwitchFirstLevelNodeVersionLoadUnitInput,
  type NodeReloadApiAdapter,
  type PrepareProjectModelBackendLoadInput,
  type PreparedProjectModelBackendLoadResult,
  type ProjectModelFileByteCacheAdapter,
  type ProjectNodeFileReadResult,
} from '@/services/project-model-loading'
import { resolveSceneObject } from '@/composables/useComponentPlacement'
import { extractFileName, logger } from '@/utils'
import {
  ImportedModelRequestRegistry,
  type ImportedModelRequestHandle,
} from '@/services/loaders/imported-model-request-registry'
import type { ProjectCommitSnapshot } from '@/services/version-workspace'
import {
  refreshVersionFileAuthorizationHeader,
  resolveVersionFileAuthorizationHeader,
} from '@/services/api/cad-session'
import { mapWithConcurrencyLimit } from '@/services/loaders/map-with-concurrency-limit'
import { ChildTabLoadingCoordinator } from './ChildTabLoadingCoordinator'
import {
  applyAssemblyTransformToManifestNode,
  resolveAssemblyHistorySceneObject,
  resolveAssemblyHistoryTarget,
} from './assemblyHistoryProjection'
import { useEditingFocusManager } from '@/hooks/useEditingFocusManager'
import {
  createOfflinePuppyTabDescriptors,
} from './offlinePuppyTabs'
import {
  resolveAssemblyBranchNodeRuntimeHoverTarget,
} from './resolveAssemblyBranchNodeRuntimeHoverTarget'
import {
  resolveAssemblyBranchNodeReferenceTarget,
  type AssemblyBranchNodeReferenceTarget,
} from './resolveAssemblyBranchNodeReferenceTarget'
import type { AssemblyBranchNodeHoverTarget } from './resolveAssemblyBranchNodeHoverTarget'
import {
  createProjectModelContextIdentity,
  isSameProjectModelContextIdentity,
  type ProjectModelContextIdentity,
} from './project-model-context-identity'

type ImportedBodyTopologyAssetWithGeometryObjectId =
  NonNullable<ImportedTopologySource['bodyTopologyAssets']>[string] & {
    readonly geometryObjectId: string
  }

/**
 * 判断导入拓扑资产是否已经绑定 Worker 几何对象。
 *
 * 设计说明：`geometryObjectId` 在 cad-core 的拓扑资产契约中允许为空，因为 hydration 可能分阶段完成；
 * 但运行时 ownership 索引只能消费已经注册到 Worker GeometryState 的资产，因此这里集中完成类型收窄。
 *
 * @param asset 待判断的导入拓扑资产。
 * @returns 已具备稳定几何对象 ID 时返回 true。
 */
function hasImportedBodyTopologyAssetGeometryObjectId(
  asset: NonNullable<ImportedTopologySource['bodyTopologyAssets']>[string],
): asset is ImportedBodyTopologyAssetWithGeometryObjectId {
  return typeof asset.geometryObjectId === 'string' && asset.geometryObjectId.trim().length > 0
}

//------ 线性零部件阵列撤销重做设计树同步 开始------
/**
 * 线性零部件阵列设计树同步原因。
 *
 * 设计说明：
 * 1. 提交命令后的同步需要沿用插入类刷新反馈，但历史恢复只需要重建树投影；
 * 2. 使用枚举区分固定同步原因，避免调用方传入散落字符串导致刷新语义分叉；
 * 3. 后续若新增版本预览或批量恢复场景，可继续扩展该枚举而不修改既有调用约定。
 */
export enum LinearComponentPatternFeatureTreeSyncReason {
  /** 用户确认创建、编辑或删除线性阵列后的常规同步。 */
  Commit = 'commit',
  /** 模型 Undo/Redo 应用历史快照后的同步。 */
  HistoryRestore = 'history-restore',
  /** 项目模型局部新增或版本替换完成后的同步。 */
  ProjectModelSubtreeApply = 'project-model-subtree-apply',
}
//------ 线性零部件阵列撤销重做设计树同步 结束------

/**
 * 判断导入模型结果是否来自 `.puppy` 离线包。
 *
 * @param result 导入模型结果。
 * @returns 是否为离线包结果。
 */
function isPuppyPackageImportedModelResult(result: ImportedModelResult): boolean {
  const sourceAssetKey = 'sourceAssetKey' in result ? result.sourceAssetKey : undefined
  return result.fileInfo?.kind === '.puppy' || sourceAssetKey?.startsWith('puppy:') === true
}

/**
 * 按对象引用去重导入模型结果，避免多个虚拟 tab 共享同一离线包结果时重复释放。
 *
 * @param results 待去重的结果列表。
 * @returns 去重后的结果列表。
 */
function collectUniqueImportedModelResults(
  results: Iterable<ImportedModelResult>,
): ImportedModelResult[] {
  return Array.from(new Set(results))
}

//------ 离线包基准扩展 开始------
/**
 * 创建空的 `.puppy` 基准扩展。
 *
 * 设计说明：
 * 1. 离线包全量导入会替换当前 CAD 文档，即使包内没有用户基准，也必须清空旧文档残留；
 * 2. 使用完整扩展对象而不是 undefined，避免和“非 `.puppy` 加载结果没有基准语义”混淆。
 *
 * @returns 空的文档级基准扩展。
 */
function createEmptyPuppyDatumExtension(): PuppyDatumExtension {
  return {
    schemaVersion: PuppyDatumExtensionSchemaVersion.V1,
    datumPointList: [],
    datumPointFeatureList: [],
    datumAxisList: [],
    datumAxisFeatureList: [],
    datumPlaneList: [],
  }
}
//------ 离线包基准扩展 结束------

//------ 线性零部件阵列离线包定义持久化 开始------
/**
 * 创建空的线性阵列定义文档。
 *
 * 全量 `.puppy` 加载必须用空文档清理旧场景阵列，不能把“扩展缺失”解释为保留旧状态。
 *
 * @returns 空的线性阵列定义文档。
 */
function createEmptyPuppyLinearComponentPatternDocument(): LinearComponentPatternPersistenceDocument {
  return {
    schemaVersion: LinearComponentPatternPersistenceSchemaVersion.V1,
    records: [],
  }
}
//------ 线性零部件阵列离线包定义持久化 结束------

/**
 * 释放已成功提交到共享 cache 的后台加载结果。
 *
 * 设计说明：
 * 1. `cached-part` / `composed-assembly` 的 geometry/material 已经由 Part/Assembly cache 接管；
 * 2. 后台 preload 或协作抢占分支不挂载 scene，只能清理实例父子关系，不能 full dispose 共享资源；
 * 3. `legacy-glb` 没有语义资产 cache owner，必须 full dispose，避免旧 GLB 回退路径泄漏 GPU 资源。
 *
 * @param result 后台加载结果。
 * @param errorContext 日志上下文。
 */
function disposeCommittedBackgroundImportedModelResult(
  result: ImportedModelResult,
  errorContext: string,
): void {
  try {
    disposeCommittedImportedModelResult(result)
  } catch (error) {
    logger.error(`[GlbLoader] ${errorContext} dispose committed result failed`, error)
  }
}

/**
 * 处理后台加载事务提交失败后的资源回滚。
 *
 * @param handle 当前请求句柄。
 * @param result 未挂载的加载结果。
 * @param errorContext 日志上下文。
 */
function rollbackBackgroundImportedModelCommitFailure(
  handle: ImportedModelRequestHandle,
  result: ImportedModelResult,
  errorContext: string,
): void {
  try {
    handle.abort()
  } catch (abortError) {
    logger.error(`[GlbLoader] ${errorContext} abort after commit failed`, abortError)
  }
  try {
    disposeImportedModel(result)
  } catch (disposeError) {
    logger.error(`[GlbLoader] ${errorContext} dispose after commit failed`, disposeError)
  }
}

interface VersionPreviewModelLoadInput {
  /** 本次历史预览要加载的目标文件。 */
  readonly targetFile: FileInfo
  /** 本次加载使用的项目文件清单。 */
  readonly projectFiles: FileInfo[]
  /** 本次加载使用的下载上下文 ID。 */
  readonly downloadContextId: string
  /** 本次加载使用的缓存版本。 */
  readonly version: string
}

/**
 * 整项目根快照切换上下文。
 */
interface ProjectModelRootSnapshotSwitchContext {
  /** 当前异步切换是否仍然属于最新请求。 */
  readonly isCurrent: () => boolean
}

/**
 * 整项目根快照切换输入。
 */
interface ProjectModelRootSnapshotSwitchInput {
  /** 历史提交快照。 */
  readonly snapshot: ProjectCommitSnapshot
  /** 请求有效性上下文；用于在真正提交模型前阻断陈旧请求。 */
  readonly context?: ProjectModelRootSnapshotSwitchContext
  /** 用户可见的加载提示。 */
  readonly loadingText?: string
  /** 本次加载遮罩归属；缺省时继续使用根模型标签 ID，保持既有调用语义。 */
  readonly loadingOwner?: string
  /** 模型应用后是否由调用方继续持有 loading，用于把模型与设计树刷新合并为一个事务。 */
  readonly preserveLoadingAfterApply?: boolean
  /**
   * 是否由本次项目根切换在模型和特征恢复后统一发布最终设计树。
   *
   * 保留既有选项名；它只控制刷新归属，不改变文档类型。零件仍合并新 Worker 特征与导入实体，
   * 装配体仍使用装配层级，Store 以全新节点表原子替换旧投影，不发布临时的不完整树。
   */
  readonly replaceDesignTreeAfterApply?: boolean
  /**
   * 根快照读取完成后、开启新 Viewer Worker runtime 前执行的宿主回调。
   *
   * 设计说明：根文档切换允许重建 Viewer 作为完整隔离边界，但必须等新根数据已经确认可用后再销毁
   * 旧 Viewer，避免 OpenProject 或包读取失败时把当前可见文档提前清空。
   */
  readonly beforeRuntimeSwitch?: () => Promise<void> | void
}

/**
 * 根 Viewer 重建时的 loader 清理选项。
 */
interface RootViewerRebuildResetOptions {
  /**
   * 是否保留正在执行的根快照切换取消控制器。
   *
   * 设计说明：`switchProjectModelRootSnapshot` 在新根数据读取成功后会回调宿主重建 Viewer，此时清理的是旧
   * 文档状态，不应把本次根快照请求自己 abort 掉。
   */
  readonly preserveActiveProjectRootSnapshotSwitch?: boolean
}

interface ProjectModelRootSnapshotResolveOptions {
  /** 调用方控制 OpenProject 与 root 补齐阶段生命周期的取消信号。 */
  readonly signal?: AbortSignal
}

/**
 * 编辑页项目会话初始化的生命周期选项。
 */
interface EditorProjectSessionLoadOptions {
  /** 调用方控制本次首屏 OpenProject 与模型恢复链路的取消信号。 */
  readonly signal?: AbortSignal
}

/**
 * 从 GLB 文件列表中解析历史版本快照对应的模型加载文件。
 *
 * @param glbFiles 可用 GLB 文件列表。
 * @param activeTabId 当前激活的底部标签 ID。
 * @returns 本次历史版本预览应加载的文件；没有可用文件时返回 null。
 */
function resolveVersionPreviewTargetFileFromGlbFiles(
  glbFiles: readonly FileInfo[],
  activeTabId: string | null,
): FileInfo | null {
  return glbFiles.find((file: FileInfo) => file.fileId === activeTabId)
    ?? glbFiles[0]
    ?? null
}

/**
 * 解析历史版本快照对应的模型加载输入。
 *
 * 设计说明：
 * 1. 这里显式消费 `modelSource`，避免历史预览隐式读取当前项目文件；
 * 2. 当前预览必须重新走模型加载链路，所以即使复用当前文件，也只复用文件描述，不复用已挂载场景对象；
 * 3. `checkout-commit-files` 只改变本次 load 的文件清单和下载上下文，不改写当前打开项目的 store。
 *
 * @param modelSource 历史提交快照中的模型来源声明。
 * @param currentProjectFiles 当前项目文件清单。
 * @param currentGlbFiles 当前项目可加载的 GLB 文件列表。
 * @param currentDownloadContextId 当前项目下载上下文 ID。
 * @param currentCacheVersion 当前项目缓存版本。
 * @param activeTabId 当前激活的底部标签 ID。
 * @returns 本次历史版本预览应使用的加载输入；没有可用文件时返回 null。
 */
function resolveVersionPreviewModelLoadInput(
  modelSource: ProjectCommitSnapshot['modelSource'],
  currentProjectFiles: readonly FileInfo[],
  currentGlbFiles: readonly FileInfo[],
  currentDownloadContextId: string,
  currentCacheVersion: string,
  activeTabId: string | null,
): VersionPreviewModelLoadInput | null {
  if (modelSource.kind === 'reuse-current-project-files') {
    const targetFile = resolveVersionPreviewTargetFileFromGlbFiles(currentGlbFiles, activeTabId)
    return targetFile
      ? {
        targetFile,
        projectFiles: [...currentProjectFiles],
        downloadContextId: currentDownloadContextId,
        version: currentCacheVersion,
      }
      : null
  }

  if (modelSource.kind === 'checkout-commit-files') {
    const projectFiles = [...modelSource.files]
    const targetFile = resolveVersionPreviewTargetFileFromGlbFiles(
      filterGlbFiles(projectFiles),
      activeTabId,
    )
    return targetFile
      ? {
        targetFile,
        projectFiles,
        downloadContextId: modelSource.downloadContextId,
        version: modelSource.commitId,
      }
      : null
  }

  throw new Error(`[GlbLoader] 不支持的历史版本模型来源：${JSON.stringify(modelSource)}`)
}

/**
 * 将可选 ID 归一化为 OpenProject 会话入参使用的字符串。
 *
 * @param value 后端 bigint 或服务层字符串 ID。
 * @returns 非空字符串；空白或缺失时返回 undefined。
 */
function normalizeRootSnapshotOpenProjectId(value: bigint | string | undefined): string | undefined {
  const normalizedValue = typeof value === 'bigint' ? value.toString() : value?.trim()

  return normalizedValue || undefined
}

/**
 * 从历史提交快照中解析整项目 OpenProject 会话入参。
 *
 * 设计说明：Step 5 起版本切换只关心“目标项目/仓库/分支/提交”，
 * 根节点树必须来自 OpenProject 返回值，避免模型已切换但会话 commit 仍停留在旧版本。
 *
 * @param snapshot 历史提交快照。
 * @param currentSession 当前 OpenProject 会话上下文，用于兼容复用当前文件来源的旧快照。
 * @returns 可直接传给 OpenProject 的会话上下文。
 */
function resolveProjectRootSnapshotOpenProjectInput(
  snapshot: ProjectCommitSnapshot,
  currentSession: {
    readonly repositoryId: bigint | null
    readonly branchId: bigint | null
  },
): OpenProjectSessionInput {
  const { modelSource } = snapshot
  const projectId = snapshot.projectId.trim()

  if (!projectId) {
    throw new Error(`[GlbLoader] 整项目历史版本切换缺少 projectId：commitId=${snapshot.commitId}`)
  }

  if (modelSource.kind === 'reuse-current-project-files') {
    return createOpenProjectSessionInput({
      projectId,
      repositoryId: currentSession.repositoryId?.toString(),
      branchId: currentSession.branchId?.toString(),
      commitId: snapshot.commitId,
    })
  }

  if (modelSource.kind === 'project-root-snapshot') {
    return createOpenProjectSessionInput({
      projectId,
      repositoryId: modelSource.repositoryId,
      branchId: normalizeRootSnapshotOpenProjectId(modelSource.branchId),
      commitId: normalizeRootSnapshotOpenProjectId(modelSource.commitId ?? snapshot.commitId),
    })
  }

  if (modelSource.kind === 'checkout-commit-files') {
    return createOpenProjectSessionInput({
      projectId,
      repositoryId: modelSource.repositoryId.toString(),
      branchId: normalizeRootSnapshotOpenProjectId(modelSource.branchId),
      commitId: normalizeRootSnapshotOpenProjectId(modelSource.commitId ?? snapshot.commitId),
    })
  }

  throw new Error(`[GlbLoader] 不支持的整项目历史版本来源：${JSON.stringify(modelSource)}`)
}

/**
 * 校验整项目根快照切换请求仍然有效。
 *
 * @param snapshot 历史提交快照。
 * @param context 请求上下文。
 */
function assertProjectRootSnapshotSwitchCurrent(
  snapshot: ProjectCommitSnapshot,
  context: ProjectModelRootSnapshotSwitchContext | undefined,
): void {
  if (context && !context.isCurrent()) {
    throw new Error(`[GlbLoader] 整项目版本切换请求已失效：commitId=${snapshot.commitId}`)
  }
}

/**
 * 校验当前模型文件读取尚未被上层请求中止。
 *
 * @param signal 本次模型读取的取消信号。
 */
function assertProjectModelLoadSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ImportedModelLoadAbortedError()
  }
}

/**
 * 后台加载成功后的事务提交与结果释放统一入口。
 *
 * @param handle 当前请求句柄。
 * @param loaded 加载结果。
 * @param errorContext 日志上下文。
 * @returns 是否提交成功。
 */
function commitAndDisposeBackgroundImportedModelResult(
  handle: ImportedModelRequestHandle,
  loaded: LoadedImportedModel,
  errorContext: string,
): boolean {
  try {
    handle.transaction.commit()
  } catch (error) {
    logger.error(`[GlbLoader] ${errorContext} commit failed`, error)
    rollbackBackgroundImportedModelCommitFailure(handle, loaded.result, errorContext)
    return false
  }

  disposeCommittedBackgroundImportedModelResult(loaded.result, errorContext)
  handle.release()
  return true
}

/**
 * 收集装配结果中需要前台提醒的外部引用 warning。
 *
 * @param result 导入模型加载结果。
 * @returns 需要通过 toast 告知用户的外部引用 warning。
 */
function collectExternalReferenceLoadWarnings(result: ImportedModelResult): string[] {
  if (result.kind !== 'composed-assembly' || !Array.isArray(result.warnings)) {
    return []
  }
  return result.warnings.filter((warning) => (
    warning.includes('外部引用加载失败')
    || warning.includes('缺少外部零件文件')
  ))
}

/**
 * 插入准备阶段返回的句柄。
 * 调用方在 prepareInsertion 返回后持有该对象，直到调用 commitInsertion 或
 * discardPreparedInsertion 为止。
 *
 * `requestId` 由 registry 颁发，仅表示句柄身份；loader 中断由 `transaction.isAborted`
 * 驱动，不把 `requestId` 当作 guard.isStale 数据源。
 */
export interface InsertPreparationHandle {
  loaded: LoadedImportedModel
  requestId: string
  file: FileInfo
  /** 本次插入使用的固定/浮动策略，提交阶段再结合当前装配体状态解析最终固定状态。 */
  fixationMode: InsertComponentFixationMode
}

/**
 * 队列插入准备选项。
 */
export interface PrepareQueuedInsertionOptions {
  /** 加载失败时是否由 loader 直接弹出错误；批量队列会在最终跳过时统一提示。 */
  showErrorOnFailure?: boolean
}

//------ 装配体引用复制 开始------
/**
 * 装配体引用复制预览句柄。
 */
export interface AssemblyReferenceCopyPreparationHandle {
  /** 复制发生的装配体标签 ID。 */
  readonly tabId: string
  /** 当前复制体父级 manifest 节点 ID。 */
  readonly parentNodeId: string
  /** 复制体新装配节点 ID。 */
  readonly copiedNodeId: string
  /** 被复制的源装配节点 ID。 */
  readonly sourceNodeId: string
  /** 本次 Ctrl+C 直接选中的源 occurrence ID。 */
  readonly directSourceOccurrenceId: string
  /** 被复制的源组件实例 ID。 */
  readonly sourceComponentInstanceId: string
  /** 被复制的源运行时对象。 */
  readonly sourceObject: THREE.Object3D
  /** 被复制的源零件或子装配文件 UUID。 */
  readonly sourcePartFileUuid?: string
  /** 引用复制族 ID。 */
  readonly referenceFamilyId: string
  /** 稳定定义 ID。 */
  readonly definitionId: string
  /** 最早参与复制的原始 occurrence ID。 */
  readonly originOccurrenceId: string
  /** 应写回源对象的复制族元数据。 */
  readonly sourceLineage: AssemblyReferenceCopyLineageMetadata
  /** 应写入复制体的复制族元数据。 */
  readonly copiedLineage: AssemblyReferenceCopyLineageMetadata
  /** 复制体显示名称。 */
  readonly displayName: string
  /** 用于鼠标跟随预览的 Object3D。 */
  readonly previewObject: THREE.Object3D
  /** 源 hierarchy 节点。 */
  readonly sourceHierarchy: GlbHierarchyNode
  /** 已按新 Object3D ID 重建的复制体 hierarchy 节点。 */
  readonly copiedHierarchy: GlbHierarchyNode
  /** 来源对象到复制体对象的运行时映射。 */
  readonly clonedObjectBySourceObject: WeakMap<THREE.Object3D, THREE.Object3D>
  /** 来源对象 UUID 到复制体对象 UUID 的映射。 */
  readonly clonedObjectIdBySourceObjectId: Map<string, string>
}

/**
 * 装配体引用复制提交结果。
 */
export interface AssemblyReferenceCopyCommitResult {
  /** 新复制体组件实例 ID。 */
  readonly componentInstanceId: string
  /** 可写入模型历史的复制体快照。 */
  readonly historySnapshot: AssemblyComponentHistorySnapshot
}
//------ 装配体引用复制 结束------

/**
 * Editor 对外暴露的项目模型后端加载单元预处理输入。
 *
 * 设计说明：调用方只提供业务生成的 load unit 与计划上下文，授权 reader、文件缓存、
 * 后端地址和节点重载适配器都由 glb loader 统一注入，避免各入口绕过同一条预读链路。
 */
export type PrepareProjectModelBackendLoadUnitInput = Omit<
  PrepareProjectModelBackendLoadInput,
  'reader' | 'fileCache' | 'backendBaseUrl' | 'nodeReloadAdapter'
>

/**
 * Editor 侧一级子节点版本替换 load unit 创建输入。
 *
 * 设计说明：Editor 只声明业务切换上下文，后端地址由 glb loader facade 统一注入，避免 UI 层感知下载 URL 生成细节。
 */
export type CreateEditorSwitchFirstLevelNodeVersionLoadUnitInput = Omit<
  CreateSwitchFirstLevelNodeVersionLoadUnitInput,
  'backendBaseUrl'
>

/**
 * Editor 侧缺失一级子节点补加载 load unit 创建输入。
 *
 * 设计说明：与普通版本替换保持同一 facade 边界，调用方不允许携带后端地址。
 */
export type CreateEditorAppendMissingFirstLevelNodeVersionLoadUnitInput = Omit<
  CreateAppendMissingFirstLevelNodeVersionLoadUnitInput,
  'backendBaseUrl'
>

/**
 * Editor 侧 runtime embedded 一级子树独立加载 load unit 创建输入。
 *
 * 设计说明：root `.puppy` 内嵌替换仍由 Editor 声明父子节点关系，后端地址由 loader 内部补齐。
 */
/** tab 相关 request（preload / tab-switch）共享的 registry scope。 */
const TAB_REQUEST_SCOPE = 'editor-tab'
/** prepare-insertion 使用独立 scope；tab 切换时主动 abort，达到 "tab 切换剥夺提交资格" 的效果。 */
const INSERTION_REQUEST_SCOPE = 'editor-insertion'
/** 装配删除 Undo 局部重建使用独立 scope，避免抢占当前 tab 加载 ownership。 */
const ASSEMBLY_DELETE_HISTORY_RESTORE_SCOPE = 'editor-assembly-delete-history-restore'
/**
 * OpenProject 节点包下载和解析的最大并发数。
 *
 * 设计说明：OpenProject 已经返回完整树，节点包之间没有下载依赖；这里并行启动独立包加载，
 * 最终仍按 OpenProject 层级在 compose 阶段递归挂接，避免串行下载拖慢大装配打开。
 */
const MAX_PROJECT_NODE_PACKAGE_LOAD_CONCURRENCY = 6
/** 项目根模型加载失败弹窗标题。 */
const PROJECT_MODEL_LOAD_FAILURE_TITLE = '模型加载失败'
/** 项目根模型加载失败时展示给用户的说明。 */
const PROJECT_MODEL_LOAD_FAILURE_DESCRIPTION = '当前模型没有加载完成，可能是网络波动或文件下载中断。是否重新加载模型？'
/** 错误详情最大展示长度，避免后端或运行时对象把弹窗撑得过长。 */
const PROJECT_MODEL_LOAD_FAILURE_ERROR_DETAIL_MAX_LENGTH = 500

/**
 * 提取适合展示在模型加载失败弹窗中的错误详情。
 *
 * @param error 原始异常。
 * @returns 用户可读的错误详情。
 */
function resolveProjectModelLoadFailureErrorDetail(error: unknown): string {
  let detail = ''
  if (error instanceof Error) {
    detail = error.message.trim() || error.name.trim()
  } else if (typeof error === 'string') {
    detail = error.trim()
  } else {
    try {
      detail = JSON.stringify(error) ?? ''
    } catch {
      detail = ''
    }
  }

  const normalizedDetail = detail.trim() || '未知错误'
  return normalizedDetail.length > PROJECT_MODEL_LOAD_FAILURE_ERROR_DETAIL_MAX_LENGTH
    ? `${normalizedDetail.slice(0, PROJECT_MODEL_LOAD_FAILURE_ERROR_DETAIL_MAX_LENGTH)}...`
    : normalizedDetail
}

/**
 * 创建项目根模型加载失败处理入参。
 *
 * @param error 原始异常。
 * @param retry 重新加载项目根模型的回调。
 * @returns 宿主 UI 可直接消费的失败提示数据。
 */
function createProjectModelLoadFailureInput(
  error: unknown,
  retry: () => Promise<void>,
): ProjectModelLoadFailureInput {
  return {
    title: PROJECT_MODEL_LOAD_FAILURE_TITLE,
    description: PROJECT_MODEL_LOAD_FAILURE_DESCRIPTION,
    errorDetail: resolveProjectModelLoadFailureErrorDetail(error),
    error,
    retry,
  }
}

/**
 * 收集本次实际加载包中的线性阵列文档，并绑定到各包最终 owner occurrence。
 *
 * @param packageResults 本次局部事务实际加载的包结果。
 * @param packageSemanticScopes Finalizer 已知的包级 owner 作用域。
 * @returns 可与 hierarchy、topology 和 Mate 一起交给 Finalizer 的阵列文档。
 */
function createProjectModelSubtreeLinearComponentPatternDocuments(
  packageResults: readonly LoadedProjectModelPackageResult[],
  packageSemanticScopes: readonly PuppyCAD.ProjectModelPackageSemanticScope[],
): readonly PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument[] {
  const scopeByOwnerNodeKey = new Map(
    packageSemanticScopes.map(scope => [scope.ownerNodeKey, scope]),
  )

  return packageResults.flatMap((packageResult) => {
    const document = packageResult.puppyLinearComponentPatternDocument
    if (!document) {
      return []
    }
    const scope = scopeByOwnerNodeKey.get(packageResult.nodeKey)
    if (!scope) {
      // scoped 下载可能为了组合完整根而携带作用域外包；这些包继续沿用当前 Manager 记录。
      return []
    }
    return [{
      ownerAssemblyNodeId: scope.ownerAssemblyNodeId,
      document,
    }]
  })
}

/**
 * 从组合后的模型结果中提取最新场景绑定。
 *
 * 设计说明：局部版本切换会重建目标子树的 Three.js 对象，旧的 sceneBindings
 * 只适合在下载事务中做未受影响节点保留；真正落场景后必须以组合结果为准。
 *
 * @param result 已经组合完成的导入模型结果。
 * @returns cad-core 设计树投影可消费的绑定列表。
 */
/**
 * 把几何组合结果回填到 ProjectModelLoadResult。
 *
 * @param loadResult cad-core 下载事务结果。
 * @param importedResult web-client 几何解析和组合结果。
 * @param completeContext complete 装配根的可选包内关联上下文。
 * @returns 带最新 sceneBindings/designTree 的加载结果。
 */
function createCommittedProjectModelLoadResult(
  loadResult: ProjectModelLoadResult,
  importedResult: ComposedAssemblyResult,
  completeContext?: PuppyCAD.ProjectModelCompleteRuntimeStateContext,
): ProjectModelLoadResult {
  const rootAssemblyDocument = loadResult.assemblyDocumentsByNodeKey.get(
    loadResult.root.nodeKey,
  )
  const resolution = new ProjectModelRuntimeStateResolver().resolve({
    rootNodeKey: loadResult.root.nodeKey,
    runtimeHierarchy: importedResult.hierarchy,
    sceneBindings: importedResult.sceneBindings,
    ...(rootAssemblyDocument
      ? {
          // 普通 Puppy 重载也会让 canonical occurrence key 与工作文档 key 分离；
          // Finalizer 必须在状态发布前建立稳定 instance_id 桥接，提交链路只消费解析结果。
          assemblyDocumentContext: {
            projectRoot: loadResult.root,
            assemblyDocument: rootAssemblyDocument,
          },
        }
      : {}),
    ...(completeContext ? { completeContext } : {}),
  })

  return {
    ...loadResult,
    persistenceStatus: importedResult.persistenceStatus,
    runtimeState: resolution.runtimeState,
    sceneBindings: resolution.sceneBindings,
    designTree: buildProjectModelDesignTreeProjection({
      root: loadResult.root,
      sceneBindings: resolution.sceneBindings,
    }),
  }
}

/**
 * Object3D.metadata 运行时扩展承载对象。
 */
type Object3DMetadataCarrier = THREE.Object3D & {
  metadata?: unknown
}

/**
 * Object3D 资源快照。
 */
interface Object3DResourceSnapshot {
  /** 仍被保留场景引用的几何体。 */
  readonly geometries: Set<THREE.BufferGeometry>
  /** 仍被保留场景引用的材质。 */
  readonly materials: Set<THREE.Material>
  /** 仍被保留场景引用的纹理。 */
  readonly textures: Set<THREE.Texture>
}

/**
 * 局部装配结构应用选项。
 */
interface ApplyProjectModelStructureLocallyOptions {
  /** 状态提交前的业务写入，例如 working_assembly.json。 */
  readonly beforeCommit?: () => void
  /** 状态提交失败后的业务回滚。 */
  readonly rollbackBeforeCommit?: () => void
}

/**
 * 局部删除项目模型节点输入。
 */
interface DeleteProjectModelNodeLocallyInput extends ApplyProjectModelStructureLocallyOptions {
  /** 要删除的项目模型节点 key。 */
  readonly nodeKey: string
  /** 已完成 working 文档覆盖后的删除后加载结果。 */
  readonly nextLoadResult: ProjectModelLoadResult
}

/**
 * 局部删除项目模型节点输出。
 */
interface DeleteProjectModelNodeLocallyResult {
  /** 删除后的项目模型加载结果。 */
  readonly loadResult: ProjectModelLoadResult
  /** 被移入冷存储的场景子树引用。 */
  readonly payloadRef: AssemblyDeletedSubtreePayloadRef
  /** 被删除节点及其后代 nodeKey。 */
  readonly deletedNodeKeys: readonly string[]
}

/**
 * 装配删除 Undo 局部恢复结果。
 */
interface RestoreAssemblyDeletedObjectsResult {
  /** 重新组合出的删除子树运行时结果。 */
  readonly composedResult: ComposedAssemblyResult
  /** 子树内每个节点重新解析出的节点包结果。 */
  readonly packageResults: readonly ProjectModelPackageResult[]
  /** 已经映射到新 Object3D UUID 的 hierarchy 快照。 */
  readonly hierarchySnapshots: readonly AssemblyDeletedSubtreeHierarchySnapshot[]
  /** 恢复子树最终应插入的父对象；准备阶段不提前改写场景。 */
  readonly parent: THREE.Object3D
  /** 恢复子树在父对象中的原始顺序。 */
  readonly parentIndex: number
}

/**
 * 克隆可序列化对象，避免 payload 与运行时对象共享引用。
 *
 * @param value 源值。
 * @returns 克隆值。
 */
function cloneSerializableValue<TValue>(value: TValue): TValue {
  if (value === undefined) {
    return value
  }
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value)
    } catch {
      // metadata 和 history payload 必须最终落到 plain data；遇到不可克隆值时继续交给 JSON 路径显式暴露问题。
    }
  }

  return JSON.parse(JSON.stringify(value)) as TValue
}

/**
 * 读取节点显示名称。
 *
 * @param node 项目模型节点。
 * @returns 展示名称。
 */
function resolveProjectModelNodeDisplayName(node: ModelAssetNode): string {
  const displayName = node.displayName?.trim()
  if (displayName) {
    return displayName
  }

  const segment = node.nodeKey.split('/').filter(Boolean).at(-1) ?? node.nodeKey
  try {
    const decoded = decodeURIComponent(segment)

    return decoded.trim() || node.nodeKey
  } catch {
    return segment.trim() || node.nodeKey
  }
}

/**
 * 把只读矩阵数组收口为 manifest 可保存的 16 元组。
 *
 * @param elements 节点局部矩阵。
 * @returns manifest 使用的矩阵元组。
 */
function toProjectModelManifestMatrixTuple(elements: readonly number[]): Matrix4Tuple {
  const source = elements.length === 16
    ? elements
    : [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1] as const

  return [
    source[0], source[1], source[2], source[3],
    source[4], source[5], source[6], source[7],
    source[8], source[9], source[10], source[11],
    source[12], source[13], source[14], source[15],
  ]
}

/**
 * 基于项目模型节点重建 imported-model manifest。
 *
 * @param root 当前项目模型根节点。
 * @returns 与组合装配结果同构的 manifest。
 */
function createProjectModelManifestFromRoot(root: ModelAssetNode): AssemblyManifest {
  const nodesById: AssemblyManifest['nodesById'] = {}
  const childIdsByNodeId: AssemblyManifest['childIdsByNodeId'] = {}
  const referencedPartUuids = new Set<string>()
  const rootFile = root.assemblyManifest ?? root.packageFiles[0]

  const visit = (node: ModelAssetNode, parentNodeId: string | null): void => {
    const packageFile = node.packageFiles[0]
    const partFileUuid = packageFile ? packageFile.uuid || packageFile.fileId : undefined

    if (partFileUuid) {
      referencedPartUuids.add(partFileUuid)
    }

    nodesById[node.nodeKey] = {
      nodeId: node.nodeKey,
      parentNodeId,
      name: resolveProjectModelNodeDisplayName(node),
      semanticKind: node.nodeType,
      transform: toProjectModelManifestMatrixTuple(node.localTransform),
      partFileUuid,
      partFileId: packageFile?.fileId,
    }
    childIdsByNodeId[node.nodeKey] = node.children.map((child) => child.nodeKey)
    node.children.forEach((child) => visit(child, node.nodeKey))
  }

  visit(root, null)

  return {
    assemblyFileId: rootFile?.fileId ?? root.nodeKey,
    assemblyFileUuid: rootFile?.uuid || root.nodeKey,
    version: root.versionKey,
    rootNodeId: root.nodeKey,
    nodesById,
    childIdsByNodeId,
    referencedPartUuids: Array.from(referencedPartUuids),
  }
}

/**
 * 收集模型资产子树 nodeKey。
 *
 * @param node 当前节点。
 * @param nodeKeys 输出集合。
 */
function collectModelAssetNodeKeys(node: ModelAssetNode, nodeKeys: Set<string>): void {
  nodeKeys.add(node.nodeKey)
  node.children.forEach((child) => collectModelAssetNodeKeys(child, nodeKeys))
}

/**
 * 按前序遍历收集项目模型节点。
 *
 * @param node 遍历根节点。
 * @returns 节点列表。
 */
function collectModelAssetNodesPreorder(node: ModelAssetNode): ModelAssetNode[] {
  return [
    node,
    ...node.children.flatMap((child) => collectModelAssetNodesPreorder(child)),
  ]
}

/**
 * 在项目模型树中查找节点。
 *
 * @param root 项目模型根节点。
 * @param nodeKey 目标节点 key。
 * @returns 命中的节点。
 */
function findModelAssetNodeByKey(root: ModelAssetNode, nodeKey: string): ModelAssetNode | null {
  if (root.nodeKey === nodeKey) {
    return root
  }

  for (const child of root.children) {
    const found = findModelAssetNodeByKey(child, nodeKey)

    if (found) {
      return found
    }
  }

  return null
}

/**
 * 计算从当前结果切换到下一结果时被删除的 nodeKey。
 *
 * @param currentRoot 当前模型根。
 * @param nextRoot 下一模型根。
 * @returns 被删除的 nodeKey 集合。
 */
function collectDeletedModelAssetNodeKeys(
  currentRoot: ModelAssetNode,
  nextRoot: ModelAssetNode,
): Set<string> {
  const currentNodeKeys = new Set<string>()
  const nextNodeKeys = new Set<string>()

  collectModelAssetNodeKeys(currentRoot, currentNodeKeys)
  collectModelAssetNodeKeys(nextRoot, nextNodeKeys)

  return new Set(Array.from(currentNodeKeys).filter((nodeKey) => !nextNodeKeys.has(nodeKey)))
}

/**
 * 计算从当前结果切换到下一结果时新增的 nodeKey。
 *
 * @param currentRoot 当前模型根。
 * @param nextRoot 下一模型根。
 * @returns 新增的 nodeKey 集合。
 */
function collectAddedModelAssetNodeKeys(
  currentRoot: ModelAssetNode,
  nextRoot: ModelAssetNode,
): Set<string> {
  const currentNodeKeys = new Set<string>()
  const nextNodeKeys = new Set<string>()

  collectModelAssetNodeKeys(currentRoot, currentNodeKeys)
  collectModelAssetNodeKeys(nextRoot, nextNodeKeys)

  return new Set(Array.from(nextNodeKeys).filter((nodeKey) => !currentNodeKeys.has(nodeKey)))
}

/**
 * 根据 nodeKey 集合过滤场景绑定。
 *
 * @param bindings 场景绑定列表。
 * @param nodeKeys nodeKey 集合。
 * @returns 命中的场景绑定。
 */
function filterSceneBindingsByNodeKeys(
  bindings: readonly ProjectModelSceneBinding[],
  nodeKeys: ReadonlySet<string>,
): ProjectModelSceneBinding[] {
  return bindings.filter((binding) => nodeKeys.has(binding.nodeKey))
}

/**
 * 从当前场景绑定中解析删除根对象。
 *
 * 设计说明：Undo 会重新生成 Object3D UUID，因此 Redo 删除不能继续依赖首次删除 payload 里的旧 objectId；
 * 当前 `sceneBindings` 才是此刻场景对象与装配节点关系的权威索引。
 *
 * @param scene 当前场景。
 * @param viewer 当前 Viewer。
 * @param bindings 当前项目模型场景绑定。
 * @param nodeKey 删除入口命中的节点 key。
 * @param deletedNodeKeys 被删除节点及其后代 key。
 * @returns 当前场景中应被整体移除的根对象列表。
 */
function resolveCurrentDeletionRootObjectsFromSceneBindings(
  scene: THREE.Scene,
  viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>,
  bindings: readonly ProjectModelSceneBinding[],
  nodeKey: string,
  deletedNodeKeys: ReadonlySet<string>,
): THREE.Object3D[] {
  const directSceneBindings = bindings.filter((binding) => binding.nodeKey === nodeKey)
  const deletedSceneBindings = filterSceneBindingsByNodeKeys(bindings, deletedNodeKeys)

  return resolveDeletionSceneRootObjects(
    scene,
    viewer,
    directSceneBindings.length > 0
      ? directSceneBindings.map((binding) => binding.objectId)
      : deletedSceneBindings.map((binding) => binding.objectId),
  )
}

/**
 * 从组合装配绑定索引中移除指定节点。
 *
 * @param sceneBindings 当前组合装配绑定索引。
 * @param removedNodeKeys 需要移除的节点 key。
 * @returns 移除后的绑定索引。
 */
function removeSceneBindingsByNodeKeys(
  sceneBindings: ComposedAssemblyResult['sceneBindings'],
  removedNodeKeys: ReadonlySet<string>,
): ComposedAssemblyResult['sceneBindings'] {
  const sourceProjectNodeBindings = resolveProjectNodeSceneBindingMap(sceneBindings)
  const removedObjectIds = new Set(
    [...removedNodeKeys].flatMap((nodeKey) => sourceProjectNodeBindings[nodeKey] ?? []),
  )
  const byAssemblyNodeId: ComposedAssemblyResult['sceneBindings']['byAssemblyNodeId'] = {}
  const byProjectNodeKey: NonNullable<ComposedAssemblyResult['sceneBindings']['byProjectNodeKey']> = {}
  const byObject3DUuid: ComposedAssemblyResult['sceneBindings']['byObject3DUuid'] = {}

  Object.entries(sceneBindings.byAssemblyNodeId).forEach(([assemblyNodeId, objectIds]) => {
    const retainedObjectIds = objectIds.filter((objectId) => !removedObjectIds.has(objectId))
    if (retainedObjectIds.length === 0) {
      return
    }

    byAssemblyNodeId[assemblyNodeId] = retainedObjectIds
  })
  Object.entries(sourceProjectNodeBindings).forEach(([nodeKey, objectIds]) => {
    if (removedNodeKeys.has(nodeKey)) {
      return
    }

    const retainedObjectIds = objectIds.filter((objectId) => !removedObjectIds.has(objectId))
    if (retainedObjectIds.length > 0) {
      byProjectNodeKey[nodeKey] = retainedObjectIds
    }
  })
  Object.entries(sceneBindings.byObject3DUuid).forEach(([objectId, object]) => {
    if (!removedObjectIds.has(objectId)) {
      byObject3DUuid[objectId] = object
    }
  })

  return { byAssemblyNodeId, byProjectNodeKey, byObject3DUuid }
}

/**
 * 合并组合装配绑定索引。
 *
 * @param base 基础绑定索引。
 * @param restored 需要补回的绑定索引。
 * @returns 合并后的绑定索引。
 */
function mergeSceneBindingIndexes(
  base: ComposedAssemblyResult['sceneBindings'],
  restored: ComposedAssemblyResult['sceneBindings'],
): ComposedAssemblyResult['sceneBindings'] {
  const byAssemblyNodeId: ComposedAssemblyResult['sceneBindings']['byAssemblyNodeId'] = {}
  const byProjectNodeKey: NonNullable<ComposedAssemblyResult['sceneBindings']['byProjectNodeKey']> = {}
  const byObject3DUuid: ComposedAssemblyResult['sceneBindings']['byObject3DUuid'] = {
    ...base.byObject3DUuid,
    ...restored.byObject3DUuid,
  }

  Object.entries(base.byAssemblyNodeId).forEach(([nodeKey, objectIds]) => {
    byAssemblyNodeId[nodeKey] = [...objectIds]
  })
  Object.entries(restored.byAssemblyNodeId).forEach(([nodeKey, objectIds]) => {
    const current = byAssemblyNodeId[nodeKey] ?? []
    byAssemblyNodeId[nodeKey] = Array.from(new Set([...current, ...objectIds]))
  })
  Object.entries(resolveProjectNodeSceneBindingMap(base)).forEach(([nodeKey, objectIds]) => {
    byProjectNodeKey[nodeKey] = [...objectIds]
  })
  Object.entries(resolveProjectNodeSceneBindingMap(restored)).forEach(([nodeKey, objectIds]) => {
    const current = byProjectNodeKey[nodeKey] ?? []
    byProjectNodeKey[nodeKey] = Array.from(new Set([...current, ...objectIds]))
  })

  return { byAssemblyNodeId, byProjectNodeKey, byObject3DUuid }
}

/**
 * 读取组合结果中的项目节点场景绑定。
 *
 * 设计说明：新组合结果使用独立 `byProjectNodeKey`；兼容测试夹具和历史内存结果时，
 * 才回退读取旧 `byAssemblyNodeId`。该回退只负责事务内迁移，不会写入持久化文档。
 *
 * @param sceneBindings 组合装配场景绑定索引。
 * @returns 项目稳定 nodeKey 到 Object3D UUID 的只读索引。
 */
function resolveProjectNodeSceneBindingMap(
  sceneBindings: ComposedAssemblyResult['sceneBindings'],
): Readonly<Record<string, readonly string[]>> {
  return sceneBindings.byProjectNodeKey ?? sceneBindings.byAssemblyNodeId
}

/**
 * 从组合装配绑定索引中移除指定对象树与节点绑定。
 *
 * 设计说明：root `.puppy` 内嵌节点不一定已经有 project-model nodeKey 绑定，
 * 因此清理时必须同时按 Object3D UUID 和目标 nodeKey 收口，避免旧选择代理残留。
 *
 * @param sceneBindings 当前组合装配绑定索引。
 * @param removedObjectIds 已从场景移除的对象 UUID。
 * @param removedNodeKeys 需要移除的项目模型节点 key。
 * @returns 清理后的绑定索引。
 */
function removeSceneBindingObjects(
  sceneBindings: ComposedAssemblyResult['sceneBindings'],
  removedObjectIds: ReadonlySet<string>,
  removedNodeKeys: ReadonlySet<string>,
): ComposedAssemblyResult['sceneBindings'] {
  const byAssemblyNodeId: ComposedAssemblyResult['sceneBindings']['byAssemblyNodeId'] = {}
  const byProjectNodeKey: NonNullable<ComposedAssemblyResult['sceneBindings']['byProjectNodeKey']> = {}
  const byObject3DUuid: ComposedAssemblyResult['sceneBindings']['byObject3DUuid'] = {}

  Object.entries(sceneBindings.byAssemblyNodeId).forEach(([assemblyNodeId, objectIds]) => {
    const retainedObjectIds = objectIds.filter((objectId) => !removedObjectIds.has(objectId))
    if (retainedObjectIds.length === 0) {
      return
    }

    byAssemblyNodeId[assemblyNodeId] = retainedObjectIds
  })
  Object.entries(resolveProjectNodeSceneBindingMap(sceneBindings)).forEach(([nodeKey, objectIds]) => {
    if (removedNodeKeys.has(nodeKey)) {
      return
    }

    const retainedObjectIds = objectIds.filter((objectId) => !removedObjectIds.has(objectId))
    if (retainedObjectIds.length > 0) {
      byProjectNodeKey[nodeKey] = retainedObjectIds
    }
  })
  Object.entries(sceneBindings.byObject3DUuid).forEach(([objectId, object]) => {
    if (!removedObjectIds.has(objectId)) {
      byObject3DUuid[objectId] = object
    }
  })

  return { byAssemblyNodeId, byProjectNodeKey, byObject3DUuid }
}

/**
 * 合并项目模型装配文档索引，并剪掉被替换子树的旧文档。
 *
 * 设计说明：同一个 direct child 可能在装配体和零件版本之间来回切换。
 * 旧子树的 `_assembly.json` 不能残留在根项目索引里，否则后续提交或 hover 识别可能读到过期结构。
 *
 * @param current 当前根项目模型文档索引。
 * @param next 新加载子树解析出的文档索引。
 * @param replacedNodeKey 被替换的项目模型节点 key。
 * @returns 合并后的文档索引。
 */
function mergeProjectModelAssemblyDocuments(
  current: ReadonlyMap<string, AssemblyDocument>,
  next: ReadonlyMap<string, AssemblyDocument>,
  replacedNodeKey: string,
): ReadonlyMap<string, AssemblyDocument> {
  const merged = new Map<string, AssemblyDocument>()
  const replacedNodeKeyPrefix = `${replacedNodeKey}/`

  current.forEach((document, nodeKey) => {
    if (nodeKey === replacedNodeKey || nodeKey.startsWith(replacedNodeKeyPrefix)) {
      return
    }

    merged.set(nodeKey, document)
  })

  next.forEach((document, nodeKey) => {
    merged.set(nodeKey, document)
  })

  return merged
}

/**
 * 解析场景对象。
 *
 * @param scene 当前场景。
 * @param viewer 当前 Viewer。
 * @param objectId 对象 UUID。
 * @returns 命中的场景对象。
 */
function resolveSceneObjectById(
  scene: THREE.Scene,
  viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>,
  objectId: string,
): THREE.Object3D | null {
  if (scene.uuid === objectId) {
    return scene
  }

  return viewer?.getSceneObjectById?.(objectId)
    ?? scene.getObjectByProperty('uuid', objectId)
    ?? null
}

/**
 * 判断候选对象是否位于某个根对象内部。
 *
 * @param object 候选对象。
 * @param root 根对象。
 * @returns 位于根对象内部时返回 true。
 */
function isObjectDescendantOf(object: THREE.Object3D, root: THREE.Object3D): boolean {
  let current = object.parent

  while (current) {
    if (current === root) {
      return true
    }
    current = current.parent
  }

  return false
}

/**
 * 根据对象 ID 收口为最小的可删除场景根对象。
 *
 * @param scene 当前场景。
 * @param viewer 当前 Viewer。
 * @param objectIds 候选对象 UUID。
 * @returns 删除根对象列表。
 */
function resolveDeletionSceneRootObjects(
  scene: THREE.Scene,
  viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>,
  objectIds: readonly string[],
): THREE.Object3D[] {
  const uniqueObjects = Array.from(new Set(objectIds))
    .map((objectId) => resolveSceneObjectById(scene, viewer, objectId))
    .filter((object): object is THREE.Object3D => Boolean(object && object !== scene))
  const objectSet = new Set(uniqueObjects)
  const rootObjects = uniqueObjects.filter((object) => {
    let current = object.parent

    while (current) {
      if (objectSet.has(current)) {
        return false
      }
      current = current.parent
    }

    return true
  })

  return rootObjects.sort((left, right) => {
    if (left.parent !== right.parent) {
      return 0
    }

    return (left.parent?.children.indexOf(left) ?? 0) - (right.parent?.children.indexOf(right) ?? 0)
  })
}

/**
 * 收集对象树内的全部 UUID。
 *
 * @param root 根对象。
 * @param objectIds 输出集合。
 */
function collectObjectTreeIds(root: THREE.Object3D, objectIds: Set<string>): void {
  root.traverse((object) => {
    objectIds.add(object.uuid)
  })
}

/**
 * 收集材质引用的纹理。
 *
 * @param material 材质。
 * @param textures 输出纹理集合。
 */
function collectMaterialTextures(material: THREE.Material, textures: Set<THREE.Texture>): void {
  Object.values(material as THREE.Material & Record<string, unknown>).forEach((value) => {
    if (value instanceof THREE.Texture) {
      textures.add(value)
    }
  })
}

/**
 * 读取对象上的可释放渲染资源。
 *
 * @param object 场景对象。
 * @returns 几何体和材质。
 */
function readRenderableResources(object: THREE.Object3D): {
  readonly geometry?: THREE.BufferGeometry
  readonly materials: readonly THREE.Material[]
} {
  const renderable = object as THREE.Object3D & {
    geometry?: unknown
    material?: unknown
  }
  const geometry = renderable.geometry instanceof THREE.BufferGeometry
    ? renderable.geometry
    : undefined
  const materials = Array.isArray(renderable.material)
    ? renderable.material.filter((material): material is THREE.Material => material instanceof THREE.Material)
    : renderable.material instanceof THREE.Material
      ? [renderable.material]
      : []

  return { geometry, materials }
}

/**
 * 收集对象树仍持有的资源。
 *
 * @param root 根对象。
 * @returns 资源快照。
 */
function collectObjectTreeResources(root: THREE.Object3D): Object3DResourceSnapshot {
  const snapshot: Object3DResourceSnapshot = {
    geometries: new Set(),
    materials: new Set(),
    textures: new Set(),
  }

  root.traverse((object) => {
    const resources = readRenderableResources(object)

    if (resources.geometry) {
      snapshot.geometries.add(resources.geometry)
    }
    resources.materials.forEach((material) => {
      snapshot.materials.add(material)
      collectMaterialTextures(material, snapshot.textures)
    })
  })

  return snapshot
}

/**
 * 释放对象树资源，但保留仍被当前场景引用的共享资源。
 *
 * @param root 要释放的对象树。
 * @param preservedResources 仍被场景保留的资源。
 * @param options topology 释放策略；scoped 切换由独立清理计划接管。
 */
function disposeDetachedObjectTreeResources(
  root: THREE.Object3D,
  preservedResources: Object3DResourceSnapshot,
  options: {
    /** 是否通过通用入口释放 topology；scoped 切换使用独立精确清理计划。 */
    readonly disposeTopology?: boolean
  } = {},
): void {
  if (options.disposeTopology !== false) {
    disposeImportedModelTopologyHosts(root)
  }
  root.traverse((object) => {
    const resources = readRenderableResources(object)

    if (resources.geometry && !preservedResources.geometries.has(resources.geometry)) {
      resources.geometry.dispose()
    }
    resources.materials.forEach((material) => {
      if (preservedResources.materials.has(material)) {
        return
      }

      disposeMaterialTextures(material, preservedResources.textures)
      material.dispose()
    })
  })
  root.clear()
}

/**
 * 捕获 Object3D.metadata。
 *
 * @param root 根对象。
 * @returns objectId 到 metadata 的映射。
 */
function captureObjectMetadataByObjectId(root: THREE.Object3D): Record<string, unknown> {
  const metadataByObjectId: Record<string, unknown> = {}

  root.traverse((object) => {
    const metadata = (object as Object3DMetadataCarrier).metadata

    if (metadata !== undefined) {
      metadataByObjectId[object.uuid] = cloneSerializableValue(metadata)
    }
  })

  return metadataByObjectId
}

/**
 * 插入对象到父节点指定位置。
 *
 * @param parent 父对象。
 * @param object 待插入对象。
 * @param index 目标位置。
 */
function insertObjectAtParentIndex(parent: THREE.Object3D, object: THREE.Object3D, index: number): void {
  parent.add(object)
  moveObject3DChildToIndex(parent, object, index)
}

/**
 * Object3D 局部变换快照。
 *
 * 设计说明：节点版本切换是“同一装配实例换模型”，不是重新插入一个默认位姿的新组件。
 * 因此替换时必须继承旧 Object3D 的局部空间状态，避免场景位置、旋转、缩放回到导入默认值。
 */
interface Object3DLocalTransformSnapshot {
  /** 旧对象在父级坐标系下的位置。 */
  readonly position: THREE.Vector3
  /** 旧对象在父级坐标系下的旋转。 */
  readonly quaternion: THREE.Quaternion
  /** 旧对象在父级坐标系下的缩放。 */
  readonly scale: THREE.Vector3
  /** 旧对象当前局部矩阵；matrixAutoUpdate=false 时这是权威姿态。 */
  readonly matrix: THREE.Matrix4
  /** 是否由 position/quaternion/scale 自动更新 matrix。 */
  readonly matrixAutoUpdate: boolean
  /** 保留组件可见性，避免切换版本后隐藏状态被重置。 */
  readonly visible: boolean
}

/**
 * 捕获 Object3D 局部变换快照。
 *
 * @param object 需要被替换的旧对象。
 * @returns 可应用到新对象的局部状态。
 */
function captureObject3DLocalTransformSnapshot(object: THREE.Object3D): Object3DLocalTransformSnapshot {
  return {
    position: object.position.clone(),
    quaternion: object.quaternion.clone(),
    scale: object.scale.clone(),
    matrix: object.matrix.clone(),
    matrixAutoUpdate: object.matrixAutoUpdate,
    visible: object.visible,
  }
}

/**
 * 将旧 Object3D 局部变换应用到新对象。
 *
 * @param object 新插入的替换对象。
 * @param snapshot 删除旧对象前捕获的局部状态。
 */
function applyObject3DLocalTransformSnapshot(
  object: THREE.Object3D,
  snapshot: Object3DLocalTransformSnapshot,
): void {
  object.position.copy(snapshot.position)
  object.quaternion.copy(snapshot.quaternion)
  object.scale.copy(snapshot.scale)
  object.matrixAutoUpdate = snapshot.matrixAutoUpdate
  object.matrix.copy(snapshot.matrix)
  object.visible = snapshot.visible
  object.updateMatrixWorld(true)
}

/**
 * 深克隆 hierarchy 节点。
 *
 * @param node 源节点。
 * @returns 克隆节点。
 */
function cloneGlbHierarchyNode(node: GlbHierarchyNode): GlbHierarchyNode {
  return cloneSerializableValue(node)
}

/**
 * 从 hierarchy 中移除指定对象根。
 *
 * @param root 当前 hierarchy 根。
 * @param rootObjectIds 要移除的对象 UUID。
 * @returns 移除后的 hierarchy 与被移除快照。
 */
function deleteHierarchySnapshotsByObjectIds(
  root: GlbHierarchyNode,
  rootObjectIds: ReadonlySet<string>,
): {
  readonly hierarchy: GlbHierarchyNode
  readonly snapshots: AssemblyDeletedSubtreeHierarchySnapshot[]
} {
  const snapshots: AssemblyDeletedSubtreeHierarchySnapshot[] = []

  const visit = (node: GlbHierarchyNode): GlbHierarchyNode => {
    const nextChildren: GlbHierarchyNode[] = []

    node.children.forEach((child, index) => {
      if (rootObjectIds.has(child.objectId)) {
        snapshots.push({
          objectId: child.objectId,
          parentObjectId: node.objectId,
          parentIndex: index,
          node: cloneGlbHierarchyNode(child),
        })
        return
      }

      nextChildren.push(visit(child))
    })

    return {
      ...node,
      children: nextChildren,
    }
  }

  return {
    hierarchy: visit(root),
    snapshots,
  }
}

/**
 * 在 hierarchy 中插入快照。
 *
 * @param root 当前 hierarchy 根。
 * @param snapshot 待恢复快照。
 * @returns 插入结果。
 */
function insertHierarchySnapshot(
  root: GlbHierarchyNode,
  snapshot: AssemblyDeletedSubtreeHierarchySnapshot,
): { hierarchy: GlbHierarchyNode; inserted: boolean } {
  if (root.objectId === snapshot.parentObjectId) {
    const nextChildren = [...root.children]
    const boundedIndex = Math.max(0, Math.min(snapshot.parentIndex, nextChildren.length))

    nextChildren.splice(boundedIndex, 0, cloneGlbHierarchyNode(snapshot.node))

    return {
      hierarchy: {
        ...root,
        children: nextChildren,
      },
      inserted: true,
    }
  }

  let inserted = false
  const nextChildren = root.children.map((child) => {
    const result = insertHierarchySnapshot(child, snapshot)

    if (result.inserted) {
      inserted = true
    }

    return result.hierarchy
  })

  return {
    hierarchy: inserted ? { ...root, children: nextChildren } : root,
    inserted,
  }
}

/**
 * 从 hierarchy 中移除指定对象 ID 的残留节点。
 *
 * 设计说明：runtime embedded 子节点替换是强替换语义。旧 root `.puppy` 数据或中间态可能残留
 * 与新替换根相同 objectId 的过期 hierarchy 节点；如果不先清掉，通用恢复逻辑会误以为新节点
 * 已存在并跳过插入，导致右侧设计树丢失被切换的装配节点。
 *
 * @param root 当前 hierarchy 根。
 * @param objectId 需要移除的对象 ID。
 * @returns 清理后的 hierarchy。
 */
function removeHierarchyDescendantByObjectId(
  root: GlbHierarchyNode,
  objectId: string,
): GlbHierarchyNode {
  return {
    ...root,
    children: root.children
      .filter((child) => child.objectId !== objectId)
      .map((child) => removeHierarchyDescendantByObjectId(child, objectId)),
  }
}

/**
 * 判断 hierarchy 中是否已存在目标对象。
 *
 * @param root 当前 hierarchy 根。
 * @param objectId 目标对象 ID。
 * @returns 是否已存在。
 */
function hasHierarchyObjectId(root: GlbHierarchyNode, objectId: string): boolean {
  if (root.objectId === objectId) {
    return true
  }

  return root.children.some((child) => hasHierarchyObjectId(child, objectId))
}

/**
 * 判断 hierarchy 节点自身是否就是目标装配节点身份。
 *
 * @param node 待检查 hierarchy 节点。
 * @param targetAssemblyNodeId 目标 occurrence 的运行时装配身份。
 * @returns 节点自身的 assemblyNodeId 是否与目标 occurrence 等价。
 */
function isHierarchyNodeAssemblyNodeIdEquivalent(
  node: GlbHierarchyNode,
  targetAssemblyNodeId: string,
): boolean {
  const assemblyNodeId = normalizeReplacementAssemblyNodeId(node.meta?.assemblyNodeId)
  const normalizedTargetAssemblyNodeId = normalizeReplacementAssemblyNodeId(targetAssemblyNodeId)
  return Boolean(assemblyNodeId && normalizedTargetAssemblyNodeId && (
    assemblyNodeId === normalizedTargetAssemblyNodeId
    || areReplacementAssemblyNodeIdsEquivalent(assemblyNodeId, normalizedTargetAssemblyNodeId)
  ))
}

/**
 * 判断 hierarchy 中是否存在指定装配节点身份。
 *
 * @param root 当前 hierarchy 根。
 * @param targetAssemblyNodeId 目标 occurrence 的运行时装配身份。
 * @returns hierarchy 中存在目标节点时返回 true。
 */
function hasHierarchyAssemblyNodeId(root: GlbHierarchyNode, targetAssemblyNodeId: string): boolean {
  if (isHierarchyNodeAssemblyNodeIdEquivalent(root, targetAssemblyNodeId)) {
    return true
  }

  return root.children.some((child) => hasHierarchyAssemblyNodeId(child, targetAssemblyNodeId))
}

/**
 * 严格插入项目模型替换子树 hierarchy。
 *
 * 设计说明：删除/撤销恢复可以容忍兜底插入，但版本切换不能。结构定位继续使用 targetNodeKey，
 * hierarchy、场景对象与 Worker runtime 则统一使用 targetAssemblyNodeId。两类身份必须在同一次事务中
 * 分别保持一致；若插入失败，应在提交前失败，避免出现“模型已换、设计树节点消失”的半成功状态。
 *
 * @param input 当前 hierarchy、替换快照与目标身份。
 * @returns 已插入替换子树的 hierarchy。
 */
function insertProjectModelReplacementHierarchySnapshot(input: {
  readonly root: GlbHierarchyNode
  readonly snapshot: AssemblyDeletedSubtreeHierarchySnapshot
  readonly targetNodeKey: string
  readonly targetAssemblyNodeId: string
  readonly context: string
}): GlbHierarchyNode {
  if (input.root.objectId === input.snapshot.node.objectId) {
    throw new Error(
      `[GlbLoader] ${input.context} replacement hierarchy objectId conflicts with root: `
      + `targetNodeKey=${input.targetNodeKey}, objectId=${input.snapshot.node.objectId}`,
    )
  }

  const hierarchyWithoutStaleObject = removeHierarchyDescendantByObjectId(
    input.root,
    input.snapshot.node.objectId,
  )

  if (!isHierarchyNodeAssemblyNodeIdEquivalent(input.snapshot.node, input.targetAssemblyNodeId)) {
    throw new Error(
      `[GlbLoader] ${input.context} replacement hierarchy root identity missing: `
      + `targetNodeKey=${input.targetNodeKey}, objectId=${input.snapshot.node.objectId}`,
    )
  }

  const result = insertHierarchySnapshot(hierarchyWithoutStaleObject, input.snapshot)

  if (!result.inserted) {
    throw new Error(
      `[GlbLoader] ${input.context} replacement hierarchy parent missing: `
      + `targetNodeKey=${input.targetNodeKey}, parentObjectId=${input.snapshot.parentObjectId ?? ''}`,
    )
  }

  if (!hasHierarchyAssemblyNodeId(result.hierarchy, input.targetAssemblyNodeId)) {
    throw new Error(
      `[GlbLoader] ${input.context} replacement hierarchy target missing after insert: `
      + `targetNodeKey=${input.targetNodeKey}, objectId=${input.snapshot.node.objectId}`,
    )
  }

  return result.hierarchy
}

/**
 * 将快照兜底恢复到根节点下。
 *
 * @param root 当前 hierarchy 根。
 * @param snapshot 待恢复快照。
 * @returns 恢复后的 hierarchy。
 */
function insertHierarchySnapshotIntoRoot(
  root: GlbHierarchyNode,
  snapshot: AssemblyDeletedSubtreeHierarchySnapshot,
): GlbHierarchyNode {
  const nextChildren = [...root.children]
  const boundedIndex = Math.max(0, Math.min(snapshot.parentIndex, nextChildren.length))

  nextChildren.splice(boundedIndex, 0, cloneGlbHierarchyNode(snapshot.node))

  return {
    ...root,
    children: nextChildren,
  }
}

/**
 * 恢复一组 hierarchy 快照。
 *
 * @param root 当前 hierarchy 根。
 * @param snapshots 待恢复快照。
 * @returns 恢复后的 hierarchy。
 */
function restoreHierarchySnapshots(
  root: GlbHierarchyNode,
  snapshots: readonly AssemblyDeletedSubtreeHierarchySnapshot[],
): GlbHierarchyNode {
  return [...snapshots]
    .sort((left, right) => left.parentIndex - right.parentIndex)
    .reduce((currentRoot, snapshot) => {
      if (hasHierarchyObjectId(currentRoot, snapshot.node.objectId)) {
        return currentRoot
      }

      const result = insertHierarchySnapshot(currentRoot, snapshot)

      if (result.inserted) {
        return result.hierarchy
      }

      logger.warn('[GlbLoader] restoreHierarchySnapshots: parent hierarchy missing, restore under root', {
        objectId: snapshot.node.objectId,
        parentObjectId: snapshot.parentObjectId,
      })

      return insertHierarchySnapshotIntoRoot(result.hierarchy, snapshot)
    }, root)
}

/**
 * 合并新生成 hierarchy 与旧快照 meta。
 *
 * @param generated 新 loader 生成的 meta。
 * @param snapshot 删除前快照中的 meta。
 * @returns 恢复后使用的 meta。
 */
function mergeRestoredHierarchyMeta(
  generated: GlbHierarchyNode['meta'],
  snapshot: GlbHierarchyNode['meta'],
): GlbHierarchyNode['meta'] {
  if (!generated && !snapshot) {
    return undefined
  }
  const baseMeta = generated ?? snapshot

  if (!baseMeta) {
    return undefined
  }

  return {
    semanticKind: generated?.semanticKind ?? snapshot?.semanticKind,
    assemblyNodeId: generated?.assemblyNodeId ?? snapshot?.assemblyNodeId,
    hasGeometry: generated?.hasGeometry ?? snapshot?.hasGeometry ?? baseMeta.hasGeometry,
    hasDirectGeometry: generated?.hasDirectGeometry ?? snapshot?.hasDirectGeometry ?? baseMeta.hasDirectGeometry,
    pmi: generated?.pmi ?? snapshot?.pmi,
    bodyCount: generated?.bodyCount ?? snapshot?.bodyCount,
    bodyEntries: generated?.bodyEntries ?? snapshot?.bodyEntries,
    bodyPrimitiveIndicesById: generated?.bodyPrimitiveIndicesById ?? snapshot?.bodyPrimitiveIndicesById,
    bodyHighlightTargetsById: generated?.bodyHighlightTargetsById ?? snapshot?.bodyHighlightTargetsById,
    materialNames: generated?.materialNames ?? snapshot?.materialNames ?? baseMeta.materialNames,
    materialName: generated?.materialName ?? snapshot?.materialName,
    isFixed: snapshot?.isFixed ?? generated?.isFixed,
    instanceIndex: generated?.instanceIndex ?? snapshot?.instanceIndex,
    configurationName: generated?.configurationName ?? snapshot?.configurationName,
    displayStateName: generated?.displayStateName ?? snapshot?.displayStateName,
    externalFileUuid: generated?.externalFileUuid ?? snapshot?.externalFileUuid,
    externalAssemblyName: generated?.externalAssemblyName ?? snapshot?.externalAssemblyName,
    selectionObjectId: generated?.selectionObjectId ?? snapshot?.selectionObjectId,
    runtimeWrapper: generated?.runtimeWrapper ?? snapshot?.runtimeWrapper,
    highlightTargets: generated?.highlightTargets ?? snapshot?.highlightTargets,
  }
}

/**
 * 用删除前 hierarchy 快照重定向新 loader 生成的 hierarchy。
 *
 * @param generated 新 loader 生成的 hierarchy。
 * @param snapshot 删除前的 hierarchy 快照。
 * @param parentObjectId 当前实际父对象 ID。
 * @returns 可插回当前设计树的 hierarchy。
 */
function rebaseRestoredHierarchyToSnapshot(
  generated: GlbHierarchyNode,
  snapshot: GlbHierarchyNode,
  parentObjectId: string | null,
): GlbHierarchyNode {
  const children = generated.children.map((child, index) => {
    const childSnapshot = snapshot.children[index] ?? child

    return rebaseRestoredHierarchyToSnapshot(child, childSnapshot, generated.objectId)
  })

  return {
    ...generated,
    parentObjectId,
    name: snapshot.name,
    path: snapshot.path,
    depth: snapshot.depth,
    nodeType: snapshot.nodeType,
    meta: mergeRestoredHierarchyMeta(generated.meta, snapshot.meta),
    children,
  }
}

/**
 * 判断两个替换阶段的装配节点身份是否等价。
 *
 * @param left 左侧节点 ID。
 * @param right 右侧节点 ID。
 * @returns 两侧是否代表同一个稳定装配节点。
 */
function areReplacementAssemblyNodeIdsEquivalent(left: string, right: string): boolean {
  const leftLookupIds = collectAssemblyNodeIdentityLookupIds(left)
  if (leftLookupIds.length === 0) {
    return false
  }

  const rightLookupIdSet = new Set(collectAssemblyNodeIdentityLookupIds(right))
  return leftLookupIds.some((lookupId) => rightLookupIdSet.has(lookupId))
}

/**
 * 规范化替换子树中的装配节点身份文本。
 *
 * @param value 待规范化的身份值。
 * @returns 可参与路径拼接的装配节点 ID。
 */
function normalizeReplacementAssemblyNodeId(value: unknown): string {
  return typeof value === 'string' ? normalizeAssemblyNodeId(value).trim() : ''
}

/**
 * 按 UUID 建立对象索引。
 *
 * @param root 场景对象根。
 * @returns UUID 到 Object3D 的索引。
 */
function buildObjectIndexByUuid(root: THREE.Object3D): Map<string, THREE.Object3D> {
  const objects = new Map<string, THREE.Object3D>()

  root.traverse((object) => {
    objects.set(object.uuid, object)
  })

  return objects
}

/**
 * 把恢复后的 hierarchy 写回新建对象 metadata。
 *
 * 设计说明：
 * 1. Undo 和节点版本切换都会重新解析节点包并生成新的 Object3D UUID；
 * 2. 旧 payload 中按 UUID 存储的 metadata 不能直接套回新对象；
 * 3. 因此这里以新 hierarchy 为权威补齐路径、父子关系和选择目标，并允许调用方覆盖运行时装配身份。
 *
 * @param input metadata 写回输入。
 */
function writeRestoredHierarchyMetadata(input: {
  readonly rootObject: THREE.Object3D
  readonly hierarchy: GlbHierarchyNode
  readonly fallbackFileId: string
  readonly fallbackAssemblyNodeId: string
  readonly assemblyNodeIdByObjectId?: ReadonlyMap<string, string>
  readonly selectionProxyObjectIdByObjectId?: ReadonlyMap<string, string>
}): void {
  const objectsByUuid = buildObjectIndexByUuid(input.rootObject)

  const visit = (node: GlbHierarchyNode): void => {
    const object = objectsByUuid.get(node.objectId)

    if (object) {
      const currentMetadata = ImportedModelObjectMetadataService.readOwnMetadata(object)
      const fileId = currentMetadata?.fileId ?? input.fallbackFileId
      const explicitAssemblyNodeId = input.assemblyNodeIdByObjectId?.get(node.objectId)
      const assemblyNodeId = explicitAssemblyNodeId
        ?? currentMetadata?.assemblyNodeId
        ?? input.fallbackAssemblyNodeId
      const explicitSelectionProxyObjectId = input.selectionProxyObjectIdByObjectId?.get(node.objectId)
      const selectionTarget = currentMetadata?.selectionTarget

      ImportedModelObjectMetadataService.writeMetadata(object, {
        fileId,
        hierarchy: {
          fileId,
          name: node.name,
          path: node.path,
          nodeType: node.nodeType,
          selectable: node.selectable,
          parentObjectId: node.parentObjectId,
          depth: node.depth,
          meta: node.meta ? cloneSerializableValue(node.meta) : undefined,
        },
        selectable: node.selectable,
        assemblyNodeId,
        ...(explicitSelectionProxyObjectId
          ? { selectionProxyObjectId: explicitSelectionProxyObjectId }
          : {}),
        selectionTarget: {
          ...(selectionTarget ?? {}),
          assemblyNodeId: explicitAssemblyNodeId ?? selectionTarget?.assemblyNodeId ?? assemblyNodeId,
        },
      })
    }

    node.children.forEach(visit)
  }

  visit(input.hierarchy)
}

/**
 * 以当前场景和 payload 重建选择索引。
 *
 * @param currentSelectionIndex 当前选择索引。
 * @param scene 当前场景。
 * @param deletedNodeKeys 已删除节点 key 集合。
 * @param restoredTargets Undo 恢复时需要补回的选择目标。
 * @returns 下一版选择索引。
 */
function createNextSelectionIndex(
  currentSelectionIndex: ComposedAssemblyResult['selectionIndex'],
  scene: THREE.Scene,
  deletedNodeKeys: ReadonlySet<string>,
  restoredTargets: Record<string, SelectionTarget> = {},
): ComposedAssemblyResult['selectionIndex'] {
  const byObject3DUuid: ComposedAssemblyResult['selectionIndex']['byObject3DUuid'] = {}

  Object.entries(currentSelectionIndex.byObject3DUuid).forEach(([objectId, target]) => {
    if (deletedNodeKeys.has(target.assemblyNodeId)) {
      return
    }
    if (!scene.getObjectByProperty('uuid', objectId)) {
      return
    }

    byObject3DUuid[objectId] = { ...target }
  })
  Object.entries(restoredTargets).forEach(([objectId, target]) => {
    if (!scene.getObjectByProperty('uuid', objectId)) {
      return
    }

    byObject3DUuid[objectId] = { ...target }
  })

  return { byObject3DUuid }
}

/**
 * 捕获删除子树的对象快照。
 *
 * @param rootObjects 删除根对象列表。
 * @returns 对象快照列表。
 */
function captureDeletedObjectSnapshots(
  rootObjects: readonly THREE.Object3D[],
): AssemblyDeletedSubtreeObjectSnapshot[] {
  return rootObjects.map((object) => ({
    objectId: object.uuid,
    parentObjectId: object.parent?.uuid ?? null,
    parentIndex: object.parent?.children.indexOf(object) ?? -1,
    metadataByObjectId: captureObjectMetadataByObjectId(object),
  }))
}

/**
 * 创建只服务于单个预读文件的内存 reader。
 *
 * 设计说明：项目模型入口已经通过 `ModelAssetFile.url` 完成精确下载；几何解析阶段继续复用旧
 * imported-model loader，但必须从这份已下载字节读取，不能回落到 session/fileId 的旧路由。
 *
 * @param sourceFile 已预读的文件描述。
 * @param sourceBytes 已预读字节。
 * @returns 供 imported-model loader 使用的内存读取器。
 */
function createSingleProjectAssetBytesReader(
  sourceFile: ModelAssetFile,
  sourceBytes: Uint8Array,
): ImportedModelFileBytesReader {
  return async (targetFile) => {
    const sourceFileId = normalizeImportedAssetFileId(sourceFile.fileId)
    const targetFileId = normalizeImportedAssetFileId(targetFile.fileId)
    const sourceUuid = sourceFile.uuid || sourceFile.fileId
    const targetUuid = targetFile.uuid || targetFile.fileId

    if (sourceFileId === targetFileId || sourceUuid === targetUuid) {
      return new Uint8Array(sourceBytes)
    }

    throw new Error(`项目模型节点包预读结果不匹配：source=${sourceFile.fileId}, target=${targetFile.fileId}`)
  }
}

/**
 * 项目根模型完整文档切换的活动会话。
 *
 * 设计说明：runtime switch handle 不能只存在于 `loadProjectRootTab` 的局部变量中；
 * begin、文件读取或解析都可能长时间挂起，dispose 或新的根加载抢占必须能覆盖
 * pending begin，并在 handle 晚返回后立刻关闭 staged Worker runtime。
 */
interface ActiveProjectRootRuntimeSwitchSession {
  /** loader 内部递增令牌；用于阻止旧 begin 晚返回后覆盖新活动会话。 */
  readonly token: number
  /** Viewer 返回的 staged runtime switch 句柄。 */
  handle: ViewerDocumentRuntimeSwitchHandle | null
  /** Viewer.beginDocumentRuntimeSwitch 尚未返回时的 pending promise。 */
  beginPromise: Promise<ViewerDocumentRuntimeSwitchHandle> | null
  /** 当前正在执行的 abort；并发关闭入口复用它，避免重复 abort 同一 handle。 */
  abortPromise: Promise<void> | null
  /** Worker runtime 是否已经成功提交；提交后禁止再 abort。 */
  committed: boolean
  /** 是否已有 dispose/preempt/catch 请求关闭；pending begin 返回后会据此立刻 abort。 */
  closing: boolean
  /** 会话是否已经关闭；用于让 catch、stale、dispose 多入口幂等。 */
  closed: boolean
}

/**
 * 局部子树 Worker runtime switch 会话。
 *
 * 设计说明：局部替换是短生命周期事务，不参与根模型抢占队列；但它仍必须记录提交状态，
 * 保证 Worker commit 成功后 catch/finally 不会再 abort 已经移交所有权的 staged runtime。
 */
interface ScopedSubtreeRuntimeSwitchSession {
  /** Viewer 返回的 staged runtime switch 句柄。 */
  readonly handle: ViewerDocumentRuntimeSwitchHandle
  /** 本次 scoped 子树加载期间实际注册到 staged runtime 的 bodyBindingId。 */
  readonly stagedBodyBindingIds: string[]
  /** Worker 成功提交后需要执行的旧 topology 精确清理计划。 */
  readonly topologyRemovalPlan: ImportedModelTopologyScopedRemovalPlan
  /** Worker runtime 是否已经成功提交。 */
  committed: boolean
  /** 会话是否已经关闭。 */
  closed: boolean
}

/**
 * scoped Worker begin 前冻结的旧场景替换边界。
 *
 * 旧 topology/BRep ownership 必须和后续真正摘除的 Object3D 使用同一组对象引用，
 * 避免异步下载完成后再次按同名节点解析到其它 occurrence。
 */
interface ScopedSubtreeSceneReplacementSnapshot {
  /** 当前已提交的完整组合模型。 */
  readonly currentComposedResult: ComposedAssemblyResult
  /** sceneBindings 首先解析出的目标对象。 */
  readonly primaryOldObject: THREE.Object3D
  /** 本次替换需要整体摘除的去重旧对象根。 */
  readonly oldRootObjects: readonly THREE.Object3D[]
}

export function useEditorGlbLoader(options: UseEditorGlbLoaderOptions) {
  const cadSessionStore = useCadSessionStore()
  const partTabsStore = usePartTabsStore()
  const featureTreeStore = useFeatureTreeStore()
  const { caches, dispose: disposeCaches } = useImportedModelCache()
  const glbFeatureTree = useImportedModelFeatureTree()
  /** 项目装配删除/恢复复用统一运行时子树协议，不在 Loader 内复制树修改算法。 */
  const projectModelStructureFeatureTreeProjector = new ProjectModelStructureFeatureTreeProjector({
    buildRuntimeSubtreeUpdate: glbFeatureTree.buildRuntimeSubtreeUpdate,
    applyRuntimeSubtreeUpdate: (tabId, update) => (
      typeof featureTreeStore.applyGlbRuntimeSubtreeUpdate === 'function'
        ? featureTreeStore.applyGlbRuntimeSubtreeUpdate(tabId, update)
        : false
    ),
  })
  /** Request ownership / artifact in-flight registry（Task 3）。 */
  const requestRegistry = new ImportedModelRequestRegistry({
    partAssetCache: caches.partAssetCache,
    assemblyModelCache: caches.assemblyModelCache,
    parsedGlbCache: caches.parsedGlbCache,
  })
  /**
   * 当前 Editor 会话的完整装配版本注册表。
   *
   * Worker 只维护一个活动 GeometryState，因此 Editor 只允许命中仍处于活动状态的
   * WorkAssembly；新版本成功提交后会立即淘汰旧快照，避免恢复只有画面、没有 BRep 的失效版本。
   */
  const completeWorkAssemblySession = new CompleteWorkAssemblySession({
    // 快照必须接收真实释放异常，才能保留失败 owner；后台清理入口仍可独立记录错误。
    disposeImportedResult: disposeCommittedImportedModelResult,
  })
  /** 局部 revision 的 topology 与 GPU scope 退役 owner。 */
  const importedModelRevisionSession = new ImportedModelRevisionSession()

  /** 当前已挂载到场景中的标签 ID。 */
  const mountedGlbTabId = ref<string | null>(null)
  /** 当前处于切换中的标签 ID。 */
  const pendingPartTabId = ref<string | null>(null)
  /** 设计树使用的权威 hierarchy 缓存。 */
  const glbHierarchyByTabId = new Map<string, GlbHierarchyNode>()
  /** 已挂载结果缓存，用于释放旧实例。 */
  const loadedResultByTabId = new Map<string, ImportedModelResult>()
  /**
   * 最近一次离线包特征重放的落地句柄。
   *
   * 设计树按 Worker 命令历史投影特征节点，而特征重放是异步的：刷新若早于重放落地，
   * 投影只能看到草图、看不到任何特征。非延迟路径已把刷新挂在重放之后，
   * `deferFeatureTreeRefresh` 的替换路径必须复用同一保证，否则提交后设计树会丢失全部特征节点。
   */
  let pendingPuppyFeatureRestore: Promise<void> = Promise.resolve()
  /** 当前项目模型加载事务结果；供子节点版本切换和后续插入零部件复用。 */
  let currentProjectModelLoadResult: ProjectModelLoadResult | null = null
  /** 当前已成功提交的 OpenProject 根模型上下文；只有完全提交后才允许作为 cache/mounted 复用依据。 */
  let currentProjectModelContextIdentity: ProjectModelContextIdentity | null = null
  /** 当前项目根文档类型；内部零件标签不得覆盖装配根文档语义。 */
  let currentRootDocumentType: PuppyPackageDocumentType | undefined
  /** 当前活动的项目根 staged runtime switch；dispose 或新 root load 必须能立即关闭它。 */
  let activeProjectRootRuntimeSwitchSession: ActiveProjectRootRuntimeSwitchSession | null = null
  /** 项目根 staged runtime switch 会话令牌；用于识别旧 begin 晚返回。 */
  let activeProjectRootRuntimeSwitchSessionToken = 0
  /** 当前 OpenProject root 经补齐器解析后的可加载 CadNode；为空时回退会话 root。 */
  let loadableOpenProjectRootNode: CadNode | null = null
  /** 与当前可加载 OpenProject root 同一响应返回的总归档包。 */
  let loadableOpenProjectArchiveFile: PuppyCADWeb.CadArchiveFileSnapshot | null = null
  /** OpenProject root 补齐请求序号；用于阻止旧异步结果晚返回覆盖新会话缓存。 */
  let loadableOpenProjectRootRequestId = 0
  /** OpenProject session 初始化请求序号；用于阻止旧 init 晚返回后再启动 root 补齐并污染新会话。 */
  let openProjectSessionInitRequestId = 0
  /** 当前新会话尚未成功解析 root 时，禁止回退消费 store 内可能残留的旧 root。 */
  let blockOpenProjectRootStoreFallback = false
  /** 当前根模型下每个节点已经解析完成的包结果；局部替换时复用未受影响节点。 */
  const projectModelPackageResultByNodeKey = new Map<string, LoadedProjectModelPackageResult[]>()
  /** 当前整项目历史快照切换的取消控制器；新切换开始时会中止旧模型文件读取。 */
  let activeProjectRootSnapshotAbortController: AbortController | null = null
  /** 当前项目模型已下载版本文件字节缓存，用于局部变更后无网络重建整棵场景对象。 */
  const projectModelFileBytesByIdentityKey = new Map<string, Uint8Array>()
  /** 当前编辑会话内存中的 `_assembly.json` 可修改副本仓库。 */
  const assemblyDraftDocumentStore = new AssemblyDraftDocumentStore()
  /** 已向用户提示过的外部引用 warning，避免缓存切回时重复弹出同一条消息。 */
  const reportedExternalReferenceWarningByTabId = new Map<string, string>()
  /** 当前 loader 生命周期是否已结束。用于释放 dispose 之后才返回的异步结果。 */
  let isDisposed = false
  /** 当前编辑器会话对应的项目 ID，用于外部引用 GetFile.file_id 拼接。 */
  let currentProjectId = ''
  /** 最近一次持有 insertion ownership 的 handle；用于兼容旧单项插入语义的调试定位。 */
  let currentInsertionHandle: ImportedModelRequestHandle | null = null
  /** 当前 loader 管理中的 insertion request handle；队列预加载允许同时存在当前项与下一项。 */
  const insertionRequestHandleById = new Map<string, ImportedModelRequestHandle>()
  /** 已完成准备、等待 commit/discard 的插入句柄。 */
  const preparedInsertionHandleById = new Map<string, InsertPreparationHandle>()
  /** 队列预加载 scope 序号；不同 scope 之间不会被 request registry 同步抢占。 */
  let queuedInsertionScopeSeq = 0
  /** 最近一次插入后设计树刷新的请求编号。用于阻止旧 refresh 晚返回时污染新 owner。 */
  let latestInsertionTreeRefreshRequestId = 0
  /** 模型挂载后的延迟聚焦帧；用于在布局稳定后再次适配相机。 */
  let scheduledSceneFocusFrameId: number | null = null
  let unsubscribeAssemblyModelCache: (() => void) | null = null
  let unsubscribePartAssetCache: (() => void) | null = null

  /**
   * 对 UI 暴露当前应显示的激活标签。
   * 切换中优先显示 pending 标签，避免底部栏闪回旧状态。
   */
  const displayActivePartTabId = computed(
    () => pendingPartTabId.value ?? partTabsStore.activeTabId,
  )

  // 编辑焦点管理器：负责格式化“编辑零部件”模式下的文档标题（零件名 ← 装配体名）
  const editingFocusManager = useEditingFocusManager();

  /**
   * 读取当前设计树逻辑上归属的标签。
   *
   * 设计说明：
   * 1. Pinia setup-store 会把 ref 自动解包，运行时这里拿到的是 `string | null`；
   * 2. 测试夹具不一定总是补齐 `activeTreeTabId`，因此回退到 `partTabsStore.activeTabId`；
   * 3. 统一收口后，所有 owner-guard 都基于同一份“当前树归属”判断，避免散落条件失配。
   */
  function resolveCurrentFeatureTreeTabId(): string | null {
    return featureTreeStore.activeTreeTabId ?? partTabsStore.activeTabId ?? null
  }

  /**
   * 取消尚未执行的模型聚焦帧。
   *
   * 设计说明：快速切换版本或标签时，旧模型的延迟聚焦不能晚于新模型执行，
   * 否则相机会被旧布局时机反向拉回，造成预览页看起来没有按当前模型自适应。
   */
  function cancelScheduledSceneContentFocus(): void {
    if (scheduledSceneFocusFrameId === null) {
      return
    }
    const scheduler = resolveSceneFocusFrameScheduler()
    if (typeof scheduler?.cancelAnimationFrame === 'function') {
      scheduler.cancelAnimationFrame(scheduledSceneFocusFrameId)
    }
    scheduledSceneFocusFrameId = null
  }

  /**
   * 解析当前可用的帧调度器。
   *
   * 设计说明：生产环境使用 window；单测或 SSR-like 环境可能只在 globalThis
   * 上挂载 requestAnimationFrame，因此这里集中做能力探测，避免业务流程依赖具体宿主对象。
   */
  function resolveSceneFocusFrameScheduler(): Pick<typeof globalThis, 'requestAnimationFrame' | 'cancelAnimationFrame'> | null {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      return window
    }
    if (typeof globalThis.requestAnimationFrame === 'function') {
      return globalThis
    }
    return null
  }

  /**
   * 按当前场景内容适配相机。
   *
   * 设计说明：
   * 1. 第一次立即聚焦，保证编辑页与标签切换的现有反馈不变；
   * 2. 再排入一帧聚焦，覆盖 Preview 抽屉、右侧设计树、底部标签完成布局后的容器尺寸；
   * 3. 只保留最后一次调度，避免快速切换时旧请求覆盖新请求。
   */
  function focusViewerOnSceneContent(): void {
    if (isDisposed) {
      return
    }
    cancelScheduledSceneContentFocus()
    const currentViewer = options.getViewer()
    currentViewer?.cameraManager?.fitVisibleItems?.()
    currentViewer?.requestImmediateRender?.()

    const scheduler = resolveSceneFocusFrameScheduler()
    if (scheduler === null) {
      return
    }
    scheduledSceneFocusFrameId = scheduler.requestAnimationFrame(() => {
      scheduledSceneFocusFrameId = null
      if (isDisposed) {
        return
      }
      const nextViewer = options.getViewer()
      nextViewer?.cameraManager?.fitVisibleItems?.()
      nextViewer?.requestImmediateRender?.()
    })
  }

  /**
   * 判断某次插入后设计树刷新是否仍拥有清理全局 loading / 自动选中的资格。
   *
   * 设计说明：
   * 1. 插入刷新可以清自己的 per-tab spinner，但全局 overlay / `isTreeLoading` / 自动选中
   *    只能由“当前仍归属于该 tab 的最新刷新”来做；
   * 2. 一旦用户切 tab、再次触发插入刷新，或设计树归属已切走，旧 refresh 只能静默退出；
   * 3. 这里同时检查 mounted tab、展示中的 tab、设计树归属与本地 requestId，确保四者一致。
   */
  function isCurrentInsertionTreeRefreshOwner(
    tabId: string,
    requestId: number,
  ): boolean {
    return latestInsertionTreeRefreshRequestId === requestId
      && mountedGlbTabId.value === tabId
      && displayActivePartTabId.value === tabId
      && resolveCurrentFeatureTreeTabId() === tabId
  }

  /**
   * 判断旧插入刷新是否需要保留同 tab 新刷新的 spinner。
   *
   * 设计说明：
   * 1. 当同一 tab 连续触发两次插入刷新时，旧请求的 finally 不应把新请求的 spinner 一并清掉；
   * 2. 只有“仍在同一 tab，且已有更新的插入刷新请求接管 ownership”时才保留；
   * 3. 若用户已切到别的 tab，则旧 tab 的 per-tab spinner 仍应由旧请求自己清理。
   */
  function shouldPreserveNewerInsertionRefreshSpinner(
    tabId: string,
    requestId: number,
  ): boolean {
    return latestInsertionTreeRefreshRequestId !== requestId
      && mountedGlbTabId.value === tabId
      && displayActivePartTabId.value === tabId
      && resolveCurrentFeatureTreeTabId() === tabId
  }

  /**
   * 生成外部引用 key -> fileId 映射。
   * 设计树中外链零件节点可能使用 uuid，也可能在 uuid 缺失时使用 fileId 作为引用 key。
   */
  const glbFileIdByReferenceKeyMap = computed(() => {
    const map = new Map<string, string>()
    cadSessionStore.getGlbFiles().forEach((file: FileInfo) => {
      const uuid = (file.uuid ?? '').trim().toLowerCase()
      if (uuid.length > 0 && !map.has(uuid)) {
        map.set(uuid, file.fileId)
      }
      const fileIdKey = normalizeImportedAssetFileId(file.fileId).toLowerCase()
      if (fileIdKey.length > 0 && !map.has(fileIdKey)) {
        map.set(fileIdKey, file.fileId)
      }
    })
    return map
  })

  /**
   * child tab spinner 协调器：
   * - start/complete 用 refcount 收口并发 child 解析；
   * - 对未知类型 child，把 `'staged'` 的 spinner 清零延后到 cache 投影完成之后；
   * - 对已知类型 child，不再让无关 subscription 误清 spinner。
   */
  const childTabLoadingCoordinator = new ChildTabLoadingCoordinator({
    resolveTabIdByUuid: (normalizedUuid: string) => glbFileIdByReferenceKeyMap.value.get(normalizedUuid),
    getTabSnapshotById: (tabId: string) => partTabsStore.tabs.find((tab) => tab.id === tabId),
    setTabLoading: (tabId: string, loading: boolean) => {
      partTabsStore.setTabLoading(tabId, loading)
    },
    reportError: (message: string, error: unknown) => {
      logger.error(`[GlbLoader] ${message}`, error)
    },
  })

  /**
   * 注册 GLB 设计树子树提供者。
   *
   * 两种模式：
   * 1. 装配体 / 分组标签：返回 `replaceTree: true`。
   *    GLB 层级就是整棵权威树，refreshTree() 会直接用它覆盖 `featureTreeData`，
   *    跳过原生投影，彻底避免任何 refreshTree 调用者把 GLB 树抹成"只剩原生"。
   * 2. 零件标签：走合并路径。
   *    原生投影已经覆盖 datum/feature/注释/材质 等结构节点；
   *    这里只把普通导入零件和 `.puppy` 离线包零件的 GLB 叶子实体合并进原生
   *    `solid-bodies-folder`，让「实体(N)」反映当前场景的全部实体数量。
   */
  featureTreeStore.registerGlbTreeRefreshProvider((context) => {
    const tabId = context.tabId
    if (!tabId) {
      return null
    }

    const hierarchy = glbHierarchyByTabId.get(tabId)
    if (!hierarchy) {
      return null
    }

    const tab = partTabsStore.tabs.find((item) => item.id === tabId)
    const tabType = tab?.type ?? undefined
    const merge = glbFeatureTree.buildSubtreeForMerge(
      tabId,
      hierarchy,
      context.partLabel ?? tab?.label,
      tabType as 'assembly' | 'part' | 'group' | undefined,
      glbFileIdByReferenceKeyMap.value,
      context.sceneSketches.map((sketch) => ({
        id: sketch.id,
        name: sketch.name,
      })),
    )

    if (tabType === 'part') {
      return {
        subtreeChildren: [],
        bodyChildren: merge.bodyChildren,
        nodeMapEntries: merge.nodeMapEntries,
      }
    }

    return {
      replaceTree: true,
      subtreeChildren: merge.subtreeChildren,
      bodyChildren: [],
      nodeMapEntries: merge.nodeMapEntries,
    }
  })

  /**
   * 解析导入模型缓存版本。
   * 优先使用后端版本号，缺失时退回前端下载上下文 ID，保证缓存随打开上下文变化失效。
   */
  function resolveImportedModelCacheVersion(): string {
    return cadSessionStore.lastKnownVersion || cadSessionStore.sessionId
  }

  /**
   * 解析当前模型加载链路应使用的完整文件清单。
   *
   * 设计说明：`projectFiles` 只表示根仓库并继续驱动 tab；`modelLoadFiles`
   * 额外包含 OpenProject 子模块节点文件，专供 loader 解析外部引用和 sidecar。
   *
   * @returns 可传入 loader 的文件清单。
   */
  function resolveCurrentModelLoadFiles(): readonly FileInfo[] {
    return cadSessionStore.modelLoadFiles.length > 0
      ? cadSessionStore.modelLoadFiles
      : cadSessionStore.projectFiles
  }

  /**
   * 对前台可见的外部引用加载失败做一次性提示。
   *
   * @param tabId 当前进入前台的模型标签 ID。
   * @param result 当前加载或缓存恢复得到的模型结果。
   */
  function reportExternalReferenceLoadWarnings(tabId: string, result: ImportedModelResult): void {
    const warnings = collectExternalReferenceLoadWarnings(result)
    if (warnings.length === 0) {
      reportedExternalReferenceWarningByTabId.delete(tabId)
      return
    }

    const warningKey = warnings.join('\n')
    if (reportedExternalReferenceWarningByTabId.get(tabId) === warningKey) {
      return
    }

    reportedExternalReferenceWarningByTabId.set(tabId, warningKey)
    logger.warn('[GlbLoader] external reference warnings while loading model', {
      tabId,
      warnings,
    })
    options.showError(`部分外部引用加载失败，已继续加载可用模型：${warnings[0]}`)
  }

  /**
   * 根据当前缓存状态回填所有标签页的类型与图标。
   * 深层子装配不会出现在当前顶层 manifest 的直接推导结果里，
   * 但递归加载后会写入 assemblyModelCache / partAssetCache，
   * 因此必须通过这条链路把缓存语义同步回标签状态。
   */
  /**
   * 记录会话中插入到装配体的组件。
   * 创建当前 STEP 导出源。
   *
   * 设计说明：
   * 1. STEP 导出必须消费 Worker 中的当前 Puppy CAD 内核 shape，保证拿到编辑后的最新几何；
   * 2. 导入时的 GLB/sidecar 只服务加载、显示和选择，不允许作为导出几何源；
   * 3. 如果 Worker 当前没有可导出的 shape，由 Worker 导出层抛出明确错误，禁止静默回退 sidecar。
   *
   * @returns STEP 导出源。
   */
  async function createCurrentStepExportSource(): Promise<StepExportSource> {
    const currentResult = mountedGlbTabId.value
      ? loadedResultByTabId.get(mountedGlbTabId.value)
      : null
    return createWorkerStateStepExportSource(currentResult, {
      sceneRoot: options.getViewer()?.getScene?.() ?? options.getScene?.() ?? null,
    })
  }

  /**
   * 创建当前 `.puppy` 离线包导出的装配语义源。
   *
   * 设计说明：
   * 1. `.puppy` 导出的显示几何来自 Viewer 场景，但装配树不能从扁平渲染树反推；
   * 2. 当前 mounted tab 的 ImportedModelResult 已经持有 cad-core 加载阶段生成的权威 hierarchy；
   * 3. 将该结果作为语义源传给 cad-core，可让导出端恢复真实装配/零件边界，同时保留未来扩展更多语义源的入口。
   *
   * @returns 当前活动 imported-model 结果语义源；不存在时返回 undefined。
   */
  function createCurrentPuppyExportAssemblySemanticSource(): PuppyExportAssemblySemanticSource | undefined {
    const currentResult = mountedGlbTabId.value
      ? loadedResultByTabId.get(mountedGlbTabId.value)
      : null
    return currentResult
      ? {
          kind: 'imported-model-result',
          result: currentResult,
        }
      : undefined
  }

  function syncPartTabsWithImportedModelCaches(): void {
    const previousTabs = partTabsStore.tabs
    const nextTabs = applyImportedModelTabCacheState(
      previousTabs,
      cadSessionStore.getGlbFiles(),
      resolveImportedModelCacheVersion(),
      caches.assemblyModelCache,
      caches.partAssetCache,
    )
    partTabsStore.tabs = nextTabs
    childTabLoadingCoordinator.reconcileAfterCacheProjection(previousTabs, nextTabs)
  }

  /**
   * 清空 OpenProject root 补齐缓存并使正在等待的补齐请求失效。
   *
   * 设计说明：新 session 或新历史切换开始时，旧 root 即使晚返回也不再属于当前加载语义；
   * 通过递增请求序号统一剥夺旧请求的写缓存资格，避免 stale root 被后续默认加载消费。
   */
  function clearLoadableOpenProjectRootNode(input: {
    /** 是否阻止 `resolveCurrentProjectModelRoot` fallback 到 store 旧 root。 */
    readonly blockStoreFallback?: boolean
  } = {}): void {
    loadableOpenProjectRootRequestId += 1
    loadableOpenProjectRootNode = null
    loadableOpenProjectArchiveFile = null
    blockOpenProjectRootStoreFallback = input.blockStoreFallback ?? false
  }

  /**
   * 登记一次新的 OpenProject session 初始化。
   *
   * 设计说明：`cadSessionStore.initSession` 本身可能晚于后续 session 返回。必须在 await 前
   * 取得请求归属，否则旧 init 返回后会把当前 store 中的新 root 当成自己的输入继续补齐。
   *
   * @param input 可选 root 缓存清理策略；首屏新 session 需要立即清理，历史切换需等 init 成功后再清。
   * @returns 本次 session 初始化请求序号。
   */
  function beginOpenProjectSessionInit(input: {
    readonly clearRootCache?: boolean
    readonly blockStoreFallback?: boolean
  } = {}): number {
    openProjectSessionInitRequestId += 1
    if (input.clearRootCache) {
      clearLoadableOpenProjectRootNode({ blockStoreFallback: input.blockStoreFallback })
      projectModelFileBytesByIdentityKey.clear()
    }

    return openProjectSessionInitRequestId
  }

  /**
   * 校验 OpenProject session 初始化结果仍属于当前请求。
   *
   * 设计说明：该 guard 覆盖“旧 init 晚返回后才开始 hydrate”的窗口，和 hydrate 内部
   * request id 分别保护 await 前后两个阶段。
   *
   * @param requestId beginOpenProjectSessionInit 返回的请求序号。
   */
  function assertOpenProjectSessionInitCurrent(requestId: number): void {
    if (isDisposed || requestId !== openProjectSessionInitRequestId) {
      throw new ImportedModelLoadAbortedError()
    }
  }

  /**
   * 解析当前会话可直接进入项目模型映射的 OpenProject root。
   *
   * 设计说明：OpenProject root 可能只携带装配元信息和 `_assembly.json`，没有 children 时
   * 无法还原项目装配结构；这里统一调用 project-model-loading 的 root 补齐器，并把结果
   * 缓存在 loader 生命周期内，避免后续 `resolveCurrentProjectModelRoot` 各自判断补齐策略。
   *
   * @param resolveOptions 可选 current guard，用于历史切换被抢占后丢弃旧补齐结果。
   * @returns 已补齐或无需补齐的 root CadNode；非 OpenProject 会话返回 null。
   */
  async function resolveCurrentLoadableProjectRootNode(resolveOptions: {
    readonly isCurrent?: () => boolean
  } = {}): Promise<CadNode | null> {
    // 每次补齐都颁发新序号，避免并发 OpenProject / 历史切换共享同一个写缓存资格。
    const requestId = loadableOpenProjectRootRequestId + 1
    loadableOpenProjectRootRequestId = requestId
    if (isDisposed || resolveOptions.isCurrent?.() === false) {
      throw new ImportedModelLoadAbortedError()
    }

    const nextRootSnapshot = await resolveLoadableOpenProjectRootSnapshot({
      rootNode: cadSessionStore.openProjectRootNode,
      archiveFile: cadSessionStore.openProjectArchiveFile,
      nodeReloadAdapter: options.projectRootNodeReloadAdapter,
    })

    if (
      isDisposed
      || requestId !== loadableOpenProjectRootRequestId
      || resolveOptions.isCurrent?.() === false
    ) {
      // 旧请求晚返回时必须中止整条调用链，不能把 stale root 暴露给后续默认加载。
      throw new ImportedModelLoadAbortedError()
    }

    loadableOpenProjectRootNode = nextRootSnapshot?.node ?? null
    loadableOpenProjectArchiveFile = nextRootSnapshot?.archiveFile ?? null
    blockOpenProjectRootStoreFallback = false
    return loadableOpenProjectRootNode
  }

  /**
   * 将 OpenProject/GetCadNodeTree 返回的根快照映射为统一 preparer 消费的加载单元。
   *
   * 设计说明：历史 root 切换与首屏 root 加载都应先把后端 CadNode 快照收口为 cad-core 的
   * ModelAssetNode，再交给 `prepareProjectModelBackendLoad` 统一完成文件预读、root `.puppy`
   * 一级子节点补齐与 cad-core 加载执行。
   *
   * @param rootNode 后端返回的完整项目根快照。
   * @param archiveFile 与根节点同一响应返回的总归档包。
   * @returns 统一项目模型后端加载准备器消费的根加载单元。
   */
  function mapProjectRootSnapshotToLoadUnit(
    rootNode: CadNode,
    archiveFile: PuppyCADWeb.CadArchiveFileSnapshot | null = loadableOpenProjectArchiveFile
      ?? cadSessionStore.openProjectArchiveFile,
  ): ModelAssetNode {
    return mapCadNodeToModelAssetNode({
      cadNode: rootNode,
      // 第三种加载方式只读取响应级 archiveFile，不再根据 CadNode.files 后缀推断。
      ...(archiveFile ? { archiveFile } : {}),
      backendBaseUrl: apiConfig.baseUrl,
      depth: 0,
      siblingIndex: 0,
    })
  }

  /**
   * 将 OpenProject 根节点映射为 cad-core 项目模型资产树。
   *
   * 设计说明：OpenProject 已经一次性返回完整 `children/files`，因此首版加载不需要再向后端查询父子层级；
   * web-client 只在 API 边界做 proto -> cad-core 资产树映射，后续组合交给 cad-core。
   *
   * @returns 当前会话的项目模型根资产；没有 OpenProject 根节点时返回 null。
   */
  function resolveCurrentProjectModelRoot(): ModelAssetNode | null {
    const rootNode = loadableOpenProjectRootNode
      ?? (blockOpenProjectRootStoreFallback ? null : cadSessionStore.openProjectRootNode)
    if (!rootNode) {
      return null
    }

    return mapProjectRootSnapshotToLoadUnit(rootNode)
  }

  /**
   * 解析项目根 tab 的显示名。
   *
   * @param root OpenProject 根资产节点。
   * @returns 根 tab 展示名。
   */
  function resolveProjectRootTabLabel(root: ModelAssetNode): string {
    return cadSessionStore.openProjectRootDisplayContext?.nodeName?.trim()
      || root.displayName?.trim()
      || root.nodeKey
  }

  /**
   * 解析 OpenProject 根节点对应的 tab 类型。
   *
   * 设计说明：根节点类型必须以后端 CadFileKind 映射结果为准；即使历史数据里 PART 带 children，
   * 也不能在 Editor 层自行升级成装配，否则 OpenProject 与后续 GetNodeTree 会出现类型语义分歧。
   *
   * @param root OpenProject 根资源节点。
   * @returns 底部 tab 与加载结果共同使用的显示类型。
   */
  function resolveProjectRootTabType(root: ModelAssetNode): ModelAssetNode['nodeType'] {
    return root.nodeType
  }

  /**
   * 解析根包稳定身份。
   *
   * 设计说明：同一个 root.nodeKey 可能指向不同提交或不同根包文件。这里只使用文件身份字段，
   * 并排序后拼接，避免后端返回顺序变化导致同一文档被误判为不同上下文。
   *
   * @param root OpenProject 根资产节点。
   * @returns 根包身份；没有根包时返回稳定空值。
   */
  function resolveProjectRootPackageIdentity(root: ModelAssetNode): string {
    const packageIdentity = root.packageFiles
      .map(buildProjectAssetFileIdentityKey)
      .sort((left, right) => left.localeCompare(right))
      .join('|')
    return packageIdentity || '<no-root-package>'
  }

  /**
   * 为 OpenProject 根模型创建完整上下文身份。
   *
   * 设计说明：mounted tab 和 loadedResult cache 的 key 仍是 root.nodeKey，但 Worker/topo 数据的
   * 安全复用条件必须同时包含提交、包身份和打开会话。只有该身份完全一致时，才允许走旧快速路径。
   *
   * @param root OpenProject 根资产节点。
   * @returns 可用于 mounted/cache 复用判断和 runtimeKey 派生的上下文身份。
   */
  function createProjectRootContextIdentity(root: ModelAssetNode): ProjectModelContextIdentity {
    return createProjectModelContextIdentity({
      projectId: currentProjectId || '<unknown-project>',
      repositoryId: root.repositoryId || '<unknown-repository>',
      branchId: root.branchId || '<unknown-branch>',
      commitId: root.commitId || cadSessionStore.currentCommitId || cadSessionStore.lastKnownVersion || '<unknown-commit>',
      rootNodeKey: root.nodeKey,
      rootPackageIdentity: resolveProjectRootPackageIdentity(root),
      openProjectSessionId: cadSessionStore.sessionId || '<unknown-session>',
    })
  }

  /**
   * 将 runtimeKey 字段规整为不会破坏分隔符的稳定片段。
   *
   * @param value 原始上下文字段。
   * @returns 可拼入 runtimeKey 的字段片段。
   */
  function normalizeProjectRuntimeKeySegment(value: string): string {
    return encodeURIComponent(value || '<empty>')
  }

  /**
   * 从项目根上下文身份派生 Worker runtimeKey。
   *
   * 设计说明：Worker runtime 是完整 CAD 文档的运行时边界，必须随项目、仓库、分支、提交、
   * 根节点、根包和打开会话变化而变化，避免不同文档上下文共享旧拓扑或 BRep 数据。
   *
   * @param identity 项目根上下文身份。
   * @returns 可传入 Viewer/Worker runtime switch 的稳定 key。
   */
  function createProjectRootRuntimeKey(identity: ProjectModelContextIdentity): string {
    return [
      'project-model',
      `project=${normalizeProjectRuntimeKeySegment(identity.projectId)}`,
      `repository=${normalizeProjectRuntimeKeySegment(identity.repositoryId)}`,
      `branch=${normalizeProjectRuntimeKeySegment(identity.branchId)}`,
      `commit=${normalizeProjectRuntimeKeySegment(identity.commitId)}`,
      `root=${normalizeProjectRuntimeKeySegment(identity.rootNodeKey)}`,
      `package=${normalizeProjectRuntimeKeySegment(identity.rootPackageIdentity)}`,
      `session=${normalizeProjectRuntimeKeySegment(identity.openProjectSessionId)}`,
    ].join('|')
  }

  /**
   * 创建项目根完整文档 runtime switch 输入。
   *
   * @param identity 项目根上下文身份。
   * @returns Viewer runtime switch 输入。
   */
  function createProjectRootRuntimeSwitchInput(
    identity: ProjectModelContextIdentity,
  ): ViewerDocumentRuntimeSwitchInput {
    const runtimeKey = createProjectRootRuntimeKey(identity)
    return {
      runtimeKey,
      reason: 'project-context-switch',
      scope: {
        kind: 'full-document',
        runtimeKey,
      },
    }
  }

  /**
   * 创建并登记项目根模型完整文档 runtime switch 会话。
   *
   * 设计说明：必须在调用 Viewer.beginDocumentRuntimeSwitch 之前登记会话，因为 cad-core
   * WorkerManager 在 begin pending 阶段已经占用 runtime switch 槽位；新的根加载需要先等待
   * 旧 begin 返回并 abort 成功，才能安全开启下一次 begin。
   *
   * @returns 本次活动会话，handle 会在 begin resolve 后补入。
   */
  function createActiveProjectRootRuntimeSwitchSession(): ActiveProjectRootRuntimeSwitchSession {
    const session: ActiveProjectRootRuntimeSwitchSession = {
      token: activeProjectRootRuntimeSwitchSessionToken + 1,
      handle: null,
      beginPromise: null,
      abortPromise: null,
      committed: false,
      closing: false,
      closed: false,
    }
    activeProjectRootRuntimeSwitchSessionToken = session.token
    activeProjectRootRuntimeSwitchSession = session

    return session
  }

  /**
   * 在确认会话仍是当前活动会话时清理 loader 级引用。
   *
   * @param session 需要清理的 runtime switch 会话。
   */
  function clearActiveProjectRootRuntimeSwitchSession(
    session: ActiveProjectRootRuntimeSwitchSession,
  ): void {
    if (activeProjectRootRuntimeSwitchSession === session) {
      activeProjectRootRuntimeSwitchSession = null
    }
  }

  /**
   * 读取已经完成 begin 的 runtime switch handle。
   *
   * 设计说明：活动会话会覆盖 pending begin 阶段，所以 handle 在类型上允许为空；
   * 但加载包与提交阶段必须只消费已经 resolve 的 staged runtime，缺失时应明确失败。
   *
   * @param session 项目根 staged runtime switch 会话。
   * @returns Viewer 返回的 staged runtime switch handle。
   */
  function getProjectRootRuntimeSwitchHandle(
    session: ActiveProjectRootRuntimeSwitchSession,
  ): ViewerDocumentRuntimeSwitchHandle {
    if (!session.handle) {
      throw new Error('项目根模型 runtime switch 尚未完成 begin，无法读取 staged Worker 句柄')
    }

    return session.handle
  }

  /**
   * 对后台触发的 runtime abort 做统一错误记录。
   *
   * 设计说明：dispose 与 snapshot 抢占不能 await 关闭过程，但也不能产生未处理 rejection；
   * 真正会开启下一次 begin 的路径仍直接 await abortActiveProjectRootRuntimeSwitch。
   *
   * @param errorContext 日志上下文。
   */
  function abortActiveProjectRootRuntimeSwitchInBackground(errorContext: string): void {
    void abortActiveProjectRootRuntimeSwitch(errorContext).catch((abortError) => {
      logger.error(`[GlbLoader] ${errorContext} background abort document runtime switch failed`, abortError)
    })
  }

  /**
   * 放弃项目根模型 staged runtime switch 会话。
   *
   * 设计说明：同一个 staged runtime 可能被 dispose、stale guard、catch 同时观察到；
   * abort 失败时 cad-core 仍可能保留 active handle，因此只有 abort 成功后才能 closed 并清理引用。
   *
   * @param session 当前 staged runtime switch 会话。
   * @param errorContext 日志上下文。
   */
  async function abortProjectRootRuntimeSwitchSession(
    session: ActiveProjectRootRuntimeSwitchSession | null,
    errorContext: string,
  ): Promise<void> {
    if (!session || session.closed || session.committed) {
      return
    }

    session.closing = true
    if (session.abortPromise) {
      await session.abortPromise
      return
    }

    let handle = session.handle
    if (!handle && session.beginPromise) {
      try {
        handle = await session.beginPromise
      } catch {
        session.beginPromise = null
        session.closed = true
        session.closing = false
        clearActiveProjectRootRuntimeSwitchSession(session)
        return
      }
      session.handle = handle
      session.beginPromise = null
    }

    if (session.closed || session.committed) {
      return
    }
    if (session.abortPromise) {
      await session.abortPromise
      return
    }
    if (!handle) {
      session.closed = true
      session.closing = false
      clearActiveProjectRootRuntimeSwitchSession(session)
      return
    }

    const abortPromise = (async () => {
      await handle.abort()
      session.closed = true
      session.closing = false
      clearActiveProjectRootRuntimeSwitchSession(session)
    })()
    session.abortPromise = abortPromise
    try {
      await abortPromise
    } catch (abortError) {
      session.abortPromise = null
      session.closing = false
      logger.error(`[GlbLoader] ${errorContext} abort document runtime switch failed`, abortError)
      throw abortError
    }
  }

  /**
   * 尝试关闭项目根模型 staged runtime switch，但不阻断本地清理。
   *
   * 设计说明：业务失败路径必须优先保证 web-client 本地缓存、场景对象和 loading 状态被清理；
   * abortProjectRootRuntimeSwitchSession 已经在失败时保留 active session 供后续抢占重试，
   * 这里吞掉 abort reject，避免 Worker 状态异常进一步覆盖原始模型错误。
   *
   * @param session 当前 staged runtime switch 会话。
   * @param errorContext 日志上下文。
   */
  async function tryAbortProjectRootRuntimeSwitchSession(
    session: ActiveProjectRootRuntimeSwitchSession | null,
    errorContext: string,
  ): Promise<void> {
    try {
      await abortProjectRootRuntimeSwitchSession(session, errorContext)
    } catch {
      // abortProjectRootRuntimeSwitchSession 已记录失败并保留 active session，后续 root preempt 会重试。
    }
  }

  /**
   * 放弃当前活动的项目根 staged runtime switch。
   *
   * @param errorContext 日志上下文。
   */
  async function abortActiveProjectRootRuntimeSwitch(errorContext: string): Promise<void> {
    const session = activeProjectRootRuntimeSwitchSession
    if (!session) {
      return
    }
    await abortProjectRootRuntimeSwitchSession(session, errorContext)
  }

  /**
   * 提交项目根模型 staged runtime switch。
   *
   * 设计说明：Worker runtime commit 必须早于 scene/cache/UI 提交；一旦 Worker commit 成功，
   * 后续错误只能走现有 UI 清理语义，不能再 abort 已提交 runtime。
   *
   * @param session 当前 staged runtime switch 会话。
   */
  async function commitProjectRootRuntimeSwitchSession(
    session: ActiveProjectRootRuntimeSwitchSession,
  ): Promise<void> {
    if (session.closed || !session.handle) {
      throw new Error('项目根模型 runtime switch 已关闭，无法提交')
    }
    await session.handle.commit()
    session.committed = true
    session.closed = true
    session.closing = false
    clearActiveProjectRootRuntimeSwitchSession(session)
  }

  /**
   * 开启项目根模型的完整文档 runtime switch。
   *
   * 设计说明：根模型 full-document reload 不能退回全局 WorkerManager，否则 `.puppy` 与
   * sidecar hydration 会把新文档拓扑写入旧 Worker runtime，造成跨提交数据混用。
   * 新 root load 开始前先关闭旧 active session，避免 WorkerManager 拒绝新 begin 或残留 staged 数据。
   *
   * @param identity 项目根上下文身份。
   * @returns 指向 staged Worker runtime 的活动会话。
   */
  async function beginProjectRootDocumentRuntimeSwitch(
    identity: ProjectModelContextIdentity,
  ): Promise<ActiveProjectRootRuntimeSwitchSession> {
    await abortActiveProjectRootRuntimeSwitch('project-root preempt before runtime switch begin')
    const viewer = options.getViewer()
    if (!viewer?.beginDocumentRuntimeSwitch) {
      throw new Error('项目根模型完整加载缺少 Viewer.beginDocumentRuntimeSwitch，无法隔离 Worker 文档运行时')
    }

    const session = createActiveProjectRootRuntimeSwitchSession()
    const ownerToken = session.token
    const beginPromise = viewer.beginDocumentRuntimeSwitch(createProjectRootRuntimeSwitchInput(identity))
    session.beginPromise = beginPromise

    let handle: ViewerDocumentRuntimeSwitchHandle
    try {
      handle = await beginPromise
    } catch (beginError) {
      session.beginPromise = null
      session.closed = true
      session.closing = false
      clearActiveProjectRootRuntimeSwitchSession(session)
      throw beginError
    }

    session.beginPromise = null
    session.handle = handle
    const activeSession = activeProjectRootRuntimeSwitchSession
    if (!activeSession || activeSession !== session || activeSession.token !== ownerToken) {
      await abortProjectRootRuntimeSwitchSession(
        session,
        'project-root pending begin resolved after owner changed',
      )
      throw new ImportedModelLoadAbortedError()
    }
    if (session.closing || session.closed) {
      throw new ImportedModelLoadAbortedError()
    }

    return session
  }

  /**
   * 确保底部 tab 栏存在 OpenProject 根节点 tab。
   *
   * @param root OpenProject 根资产节点。
   * @returns 根 tab 的 ID 和展示名。
   */
  function ensureProjectRootTab(root: ModelAssetNode): { tabId: string; label: string } {
    const tabId = root.nodeKey
    const label = resolveProjectRootTabLabel(root)
    const tabType = resolveProjectRootTabType(root)
    const existing = partTabsStore.tabs.find((tab) => tab.id === tabId)
    if (!existing) {
      partTabsStore.addTab({
        id: tabId,
        label,
        type: tabType,
        iconType: tabType,
        loading: false,
        sourceFileId: tabId,
        scopeObjectId: null,
      })
    } else {
      partTabsStore.updateTabType(tabId, tabType, tabType)
    }
    partTabsStore.switchTab(tabId)

    return { tabId, label }
  }

  /**
   * 覆盖当前项目模型节点包缓存。
   *
   * @param packageResults 根加载或局部应用成功后的节点包结果。
   */
  function replaceProjectModelPackageResultCache(packageResults: readonly LoadedProjectModelPackageResult[]): void {
    projectModelPackageResultByNodeKey.clear()
    packageResults.forEach((packageResult) => {
      const current = projectModelPackageResultByNodeKey.get(packageResult.nodeKey) ?? []
      current.push(packageResult)
      projectModelPackageResultByNodeKey.set(packageResult.nodeKey, current)
    })
  }

  /**
   * 判断节点 key 是否属于指定项目模型子树。
   *
   * @param nodeKey 待判断的节点 key。
   * @param subtreeRootNodeKey 子树根节点 key。
   * @returns 节点是否位于该子树中。
   */
  function isProjectModelNodeKeyInSubtree(nodeKey: string, subtreeRootNodeKey: string): boolean {
    // nodeKey 是稳定路径，子树边界只能按完整路径段判断，避免前缀同名节点误命中。
    return nodeKey === subtreeRootNodeKey || nodeKey.startsWith(`${subtreeRootNodeKey}/`)
  }

  /**
   * 收集项目模型文件可用于匹配 imported asset 的稳定身份。
   *
   * @param file 项目模型文件。
   * @returns 文件 UUID 与 fileId 去重后的身份集合。
   */
  function collectProjectModelFileIdentityKeys(file: ModelAssetFile): string[] {
    return uniqueStrings([
      file.uuid?.trim() ?? '',
      file.fileId.trim(),
    ])
  }

  /**
   * 收集指定节点范围内仍被项目模型引用的 package 文件身份。
   *
   * @param root 项目模型根节点。
   * @param predicate 判断节点是否需要纳入收集范围。
   * @returns 文件身份集合。
   */
  function collectProjectModelPackageFileIdentityKeys(
    root: ModelAssetNode,
    predicate: (node: ModelAssetNode) => boolean,
  ): Set<string> {
    const fileIdentityKeys = new Set<string>()

    collectModelAssetNodesPreorder(root)
      .filter(predicate)
      .forEach((node) => {
        node.packageFiles.forEach((file) => {
          collectProjectModelFileIdentityKeys(file)
            .forEach((identityKey) => fileIdentityKeys.add(identityKey))
        })
      })

    return fileIdentityKeys
  }

  /**
   * 判断 imported asset 是否来自指定文件集合。
   *
   * @param asset imported-model 聚合资产。
   * @param fileIdentityKeys 文件身份集合。
   * @returns asset 是否命中文件身份集合。
   */
  function isImportedAssetFromFileIdentityKeys(
    asset: Readonly<{ fileUuid: string, fileId: string }>,
    fileIdentityKeys: ReadonlySet<string>,
  ): boolean {
    return fileIdentityKeys.has(asset.fileUuid) || fileIdentityKeys.has(asset.fileId)
  }

  /**
   * 剪掉组合装配中已经被替换子树独占的旧 imported asset。
   *
   * 设计说明：局部切换成功后不能只把新 asset 追加进组合结果；否则旧子树的 topology 仍会参与
   * Worker ownership 扫描。这里按 OpenProject 文件身份裁剪，并保留 scope 外仍引用的共享文件，避免误删兄弟节点复用的资产。
   *
   * @param input 当前根、替换目标与新旧组合结果。
   */
  function mergeComposedAssemblyAssetsForSubtreeReplacement(input: {
    readonly currentRoot: ModelAssetNode
    readonly targetNodeKey: string
    readonly composedResult: ComposedAssemblyResult
    readonly nextComposedResult: ComposedAssemblyResult
  }): void {
    const replacedFileIdentityKeys = collectProjectModelPackageFileIdentityKeys(
      input.currentRoot,
      (node) => isProjectModelNodeKeyInSubtree(node.nodeKey, input.targetNodeKey),
    )
    const scopeOutFileIdentityKeys = collectProjectModelPackageFileIdentityKeys(
      input.currentRoot,
      (node) => !isProjectModelNodeKeyInSubtree(node.nodeKey, input.targetNodeKey),
    )
    const removableFileIdentityKeys = new Set(
      Array.from(replacedFileIdentityKeys)
        .filter((identityKey) => !scopeOutFileIdentityKeys.has(identityKey)),
    )

    if (removableFileIdentityKeys.size > 0) {
      Object.entries(input.composedResult.partAssetsByUuid).forEach(([assetKey, asset]) => {
        if (isImportedAssetFromFileIdentityKeys(asset, removableFileIdentityKeys)) {
          delete input.composedResult.partAssetsByUuid[assetKey]
        }
      })
      Object.entries(input.composedResult.assemblyAssetsByUuid).forEach(([assetKey, asset]) => {
        if (isImportedAssetFromFileIdentityKeys(asset, removableFileIdentityKeys)) {
          delete input.composedResult.assemblyAssetsByUuid[assetKey]
        }
      })
    }

    Object.assign(input.composedResult.partAssetsByUuid, input.nextComposedResult.partAssetsByUuid)
    Object.assign(input.composedResult.assemblyAssetsByUuid, input.nextComposedResult.assemblyAssetsByUuid)
  }

  /**
   * 判断两个项目模型包文件是否指向同一个版本资源。
   *
   * @param left 左侧文件描述。
   * @param right 右侧文件描述。
   * @returns 两个文件是否可视为同一个节点包。
   */
  function isSameProjectModelPackageFile(left: ModelAssetFile, right: ModelAssetFile): boolean {
    return buildProjectAssetFileIdentityKey(left) === buildProjectAssetFileIdentityKey(right)
      || (left.fileId === right.fileId && (left.uuid || left.fileId) === (right.uuid || right.fileId))
  }

  /**
   * 为局部子树应用构造成功后应保留的项目模型包结果。
   *
   * 设计说明：scoped 分支只允许把目标子树的新包交给 compose/Worker；scope 外节点继续由已提交 cache
   * 持有。这里仅为提交成功后的长期 cache 做合并，并按新 root 可达节点逐个断言旧包存在，避免静默漏掉
   * sibling，也避免把新 root 已删除的 stale package 继续留在 ownership 扫描范围内。
   *
   * @param root 本次局部应用后的新项目根。
   * @param targetNodeKey 本次替换的子树根节点 key。
   * @param nextPackageResults 本次真实下载并解析出的目标子树 package 结果。
   * @returns 成功提交后应写回的 package cache 结果。
   */
  function mergeProjectModelPackageResultsForSubtreeApply(
    root: ModelAssetNode,
    targetNodeKey: string,
    nextPackageResults: readonly LoadedProjectModelPackageResult[],
  ): LoadedProjectModelPackageResult[] {
    const mergedPackageResults: LoadedProjectModelPackageResult[] = []

    for (const node of collectModelAssetNodesPreorder(root)) {
      if (isProjectModelNodeKeyInSubtree(node.nodeKey, targetNodeKey)) {
        continue
      }

      // scope 外的每个 node-package 都仍属于当前组合结果；complete GLB 根包也必须继续由缓存持有，
      // 否则成功提交后的旧包释放会清空归档场景，只留下本次新加载的目标子树。
      const reusablePackageFiles = node.packageFiles
      if (reusablePackageFiles.length === 0) {
        continue
      }

      const cachedPackageResults = projectModelPackageResultByNodeKey.get(node.nodeKey) ?? []
      reusablePackageFiles.forEach((file) => {
        const cachedPackageResult = cachedPackageResults.find((packageResult) =>
          isSameProjectModelPackageFile(packageResult.file, file))

        if (!cachedPackageResult) {
          throw new Error(
            `[GlbLoader] scoped subtree scope-out package cache missing: nodeKey=${node.nodeKey}, fileId=${file.fileId}`,
          )
        }

        mergedPackageResults.push(cachedPackageResult)
      })
    }
    mergedPackageResults.push(...nextPackageResults)

    return mergedPackageResults
  }

  /**
   * 按项目模型稳定节点 key 查找 hierarchy 节点。
   *
   * @param root hierarchy 根节点。
   * @param nodeKey 项目模型稳定节点 key。
   * @returns 命中的 hierarchy 节点；不存在时返回 null。
   */
  function findHierarchyNodeByProjectModelNodeKey(root: GlbHierarchyNode, nodeKey: string): GlbHierarchyNode | null {
    const assemblyNodeId = typeof root.meta?.assemblyNodeId === 'string'
      ? root.meta.assemblyNodeId.trim()
      : ''
    if (assemblyNodeId && (
      assemblyNodeId === nodeKey
      || areProjectModelNodeKeysEquivalent(assemblyNodeId, nodeKey)
    )) {
      return root
    }

    for (const child of root.children) {
      const matched = findHierarchyNodeByProjectModelNodeKey(child, nodeKey)
      if (matched) {
        return matched
      }
    }

    return null
  }

  /**
   * 按对象 ID 收集 hierarchy 根到命中节点的路径。
   *
   * 设计说明：root `.puppy` 内嵌子节点切换时，上层可能因为只命中实体/Body 代理而传入内部
   * Object3D。切换提交必须回到根装配下的 direct child wrapper，否则右侧设计树会丢失该子节点层级。
   *
   * @param root hierarchy 根节点。
   * @param objectId 需要定位的对象 ID。
   * @returns 根到命中节点的路径；未命中时返回空数组。
   */
  function findHierarchyPathByObjectId(root: GlbHierarchyNode, objectId: string): GlbHierarchyNode[] {
    const path: GlbHierarchyNode[] = []
    const visit = (node: GlbHierarchyNode): boolean => {
      path.push(node)
      if (node.objectId === objectId) {
        return true
      }

      for (const child of node.children) {
        if (visit(child)) {
          return true
        }
      }

      path.pop()
      return false
    }

    return visit(root) ? [...path] : []
  }

  /**
   * 从当前 composed 结果中定位要被局部替换的场景对象。
   *
   * 设计说明：sceneBindings 是 project-model 与 Object3D 的权威映射；旧数据可能缺少绑定时再回退
   * hierarchy，保证 scoped 替换仍能在旧版本结果上定位目标，但绝不扫描或移动 sibling package 对象。
   *
   * @param input 当前场景、Viewer、composed 结果与目标节点 key。
   * @returns 目标子树根对象；不存在时返回 null。
   */
  function resolveCurrentProjectModelSubtreeSceneObject(input: {
    readonly scene: THREE.Scene
    readonly viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>
    readonly composedResult: ComposedAssemblyResult
    readonly targetNodeKey: string
    readonly targetObjectId?: string
  }): THREE.Object3D | null {
    const resolveObjectByStableId = (objectId: string): THREE.Object3D | null => {
      const sceneObject = resolveSceneObjectById(input.scene, input.viewer, objectId)

      if (sceneObject) {
        return sceneObject
      }

      // scoped 预提交时旧 root 可能尚未挂入当前 scene；此时 composedResult 仍是对象所有权来源。
      return input.composedResult.sceneBindings.byObject3DUuid[objectId]
        ?? input.composedResult.root.getObjectByProperty('uuid', objectId)
        ?? null
    }

    // complete finalization 已经确认组件根时，它比 composedResult 中不完整的兼容索引更权威。
    if (input.targetObjectId) {
      const targetObject = resolveObjectByStableId(input.targetObjectId)
      if (targetObject) {
        return targetObject
      }
    }

    // 局部切换入口接收的是 ModelAssetNode.nodeKey；物化节点的运行时 occurrence ID 允许与其不同。
    const bindingObjectIds = resolveProjectNodeSceneBindingMap(
      input.composedResult.sceneBindings,
    )[input.targetNodeKey] ?? []

    for (const objectId of bindingObjectIds) {
      const object = resolveObjectByStableId(objectId)
      if (object) {
        return object
      }
    }

    const hierarchyNode = findHierarchyNodeByProjectModelNodeKey(
      input.composedResult.hierarchy,
      input.targetNodeKey,
    )

    return hierarchyNode ? resolveObjectByStableId(hierarchyNode.objectId) : null
  }

  /**
   * 解析 root `.puppy` 内嵌子节点切换应替换的外层场景根。
   *
   * 设计说明：
   * 1. 优先使用 targetNodeKey 的权威 sceneBinding/hierarchy，保证切换根与项目节点一致；
   * 2. 旧离线包可能没有 target sceneBinding，且上层会传入内部实体 objectId，此时从 hierarchy 路径
   *    提升到根装配下的一级子节点；
   * 3. 只有以上都缺失时才退回传入 objectId，保持旧数据的兜底替换能力。
   *
   * @param input 当前场景、Viewer、composed 结果、目标节点和上层传入对象 ID。
   * @returns 应替换的外层场景对象；无法定位时返回 null。
   */
  function resolveRuntimeEmbeddedReplacementRootSceneObject(input: {
    readonly scene: THREE.Scene
    readonly viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>
    readonly composedResult: ComposedAssemblyResult
    readonly targetNodeKey: string
    readonly embeddedObjectId: string
  }): THREE.Object3D | null {
    const resolveObjectByStableId = (objectId: string): THREE.Object3D | null => (
      resolveSceneObjectById(input.scene, input.viewer, objectId)
      ?? input.composedResult.sceneBindings.byObject3DUuid[objectId]
      ?? input.composedResult.root.getObjectByProperty('uuid', objectId)
      ?? null
    )
    const resolveFirstLevelObjectByDescendantId = (objectId: string): THREE.Object3D | null => {
      const hierarchyPath = findHierarchyPathByObjectId(
        input.composedResult.hierarchy,
        objectId,
      )
      const firstLevelHierarchyNode = hierarchyPath[1] ?? null
      return firstLevelHierarchyNode
        ? resolveObjectByStableId(firstLevelHierarchyNode.objectId)
        : null
    }
    const targetObject = resolveCurrentProjectModelSubtreeSceneObject({
      scene: input.scene,
      viewer: input.viewer,
      composedResult: input.composedResult,
      targetNodeKey: input.targetNodeKey,
    })
    if (targetObject) {
      return resolveFirstLevelObjectByDescendantId(targetObject.uuid) ?? targetObject
    }

    const firstLevelObject = resolveFirstLevelObjectByDescendantId(input.embeddedObjectId)
    if (firstLevelObject) {
      return firstLevelObject
    }

    return resolveObjectByStableId(input.embeddedObjectId)
  }

  /**
   * 判断 hierarchy 节点是否属于本次要替换的项目模型节点子树。
   *
   * @param node hierarchy 节点。
   * @param targetAssemblyNodeId 目标 occurrence 的运行时装配身份。
   * @returns 节点身份是否等于目标节点，或落在目标节点后代路径下。
   */
  function isHierarchyNodeInProjectModelReplacementSubtree(
    node: GlbHierarchyNode,
    targetAssemblyNodeId: string,
  ): boolean {
    const assemblyNodeId = node.meta?.assemblyNodeId
    const assemblyNodeLookupIds = collectAssemblyNodeIdentityLookupIds(assemblyNodeId)
    const targetNodeLookupIds = collectAssemblyNodeIdentityLookupIds(targetAssemblyNodeId)

    if (assemblyNodeLookupIds.length === 0 || targetNodeLookupIds.length === 0) {
      return false
    }

    return assemblyNodeLookupIds.some((assemblyNodeLookupId) =>
      targetNodeLookupIds.some((targetNodeLookupId) => (
        assemblyNodeLookupId === targetNodeLookupId
        || assemblyNodeLookupId.startsWith(`${targetNodeLookupId}/`)
      )))
  }

  /**
   * 收集当前最终 hierarchy 中目标子树的 topology 清理边界。
   *
   * 设计树零件 hover 已经依靠 hierarchy/highlight objectId 精确命中旧 visible mesh；
   * scoped 清理复用同一事实来源，从而无需按包内局部名称猜测同模型实例。
   *
   * @param hierarchy 当前已提交项目模型 hierarchy。
   * @param targetAssemblyNodeId 本次替换目标子树根的运行时装配身份。
   * @param targetRootObjectIds 项目 nodeKey 已精确绑定的目标对象；存在时禁止跨包按身份扫描。
   * @returns 目标 bodyBindingId 与 hierarchy/highlight Object3D uuid。
   */
  function collectProjectModelReplacementSubtreeTopologyScope(
    hierarchy: GlbHierarchyNode,
    targetAssemblyNodeId: string,
    targetRootObjectIds?: ReadonlySet<string>,
  ): {
    readonly bodyBindingIds: string[]
    readonly objectIds: string[]
  } {
    const bodyBindingIds = new Set<string>()
    const objectIds = new Set<string>()
    const appendObjectId = (objectId: string | null | undefined): void => {
      const normalizedObjectId = objectId?.trim() ?? ''
      if (normalizedObjectId) {
        objectIds.add(normalizedObjectId)
      }
    }
    const visit = (node: GlbHierarchyNode, inheritedTargetScope: boolean): void => {
      const isTargetScope = inheritedTargetScope
        || (
          targetRootObjectIds && targetRootObjectIds.size > 0
            ? targetRootObjectIds.has(node.objectId)
            : isHierarchyNodeInProjectModelReplacementSubtree(node, targetAssemblyNodeId)
        )
      if (isTargetScope) {
        appendObjectId(node.objectId)
        node.meta?.highlightTargets?.forEach((target) => appendObjectId(target.objectId))
        Object.values(node.meta?.bodyHighlightTargetsById ?? {})
          .flat()
          .forEach((target) => appendObjectId(target.objectId))
        node.meta?.bodyEntries?.forEach((entry) => {
          const bodyBindingId = entry.bodyBindingId?.trim() ?? ''
          if (bodyBindingId) {
            bodyBindingIds.add(bodyBindingId)
          }
        })
      }
      node.children.forEach((child) => visit(child, isTargetScope))
    }
    visit(hierarchy, false)
    return {
      bodyBindingIds: [...bodyBindingIds],
      objectIds: [...objectIds],
    }
  }

  /**
   * 收集 root `.puppy` 中被展平到根下的旧目标对象。
   *
   * 设计说明：部分离线包不会保留 `aaa` 这一层包装 hierarchy，而是把 `aaa/子零件` 直接铺在根装配下。
   * 切换 `aaa` 版本时必须把这些旧后代一起移除，再插回新的 `aaa` 包装根，否则右侧设计树会继续显示
   * 一串旧子节点，看起来就像缺少 `aaa`。
   *
   * @param input 当前场景、composed 结果和目标节点。
   * @returns 应被同一次替换事务移除的旧 Object3D 根对象。
   */
  function collectRuntimeEmbeddedReplacementSceneObjects(input: {
    readonly scene: THREE.Scene
    readonly viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>
    readonly composedResult: ComposedAssemblyResult
    readonly targetRootObjectIds: ReadonlySet<string>
    readonly targetAssemblyNodeId: string
    readonly primaryObject: THREE.Object3D
  }): THREE.Object3D[] {
    const resolveObjectByStableId = (objectId: string): THREE.Object3D | null => (
      resolveSceneObjectById(input.scene, input.viewer, objectId)
      ?? input.composedResult.sceneBindings.byObject3DUuid[objectId]
      ?? input.composedResult.root.getObjectByProperty('uuid', objectId)
      ?? null
    )
    const objectsById = new Map<string, THREE.Object3D>([
      [input.primaryObject.uuid, input.primaryObject],
    ])
    // instance-id 只在所属归档包内唯一，项目级清理必须优先使用 finalization 确认的组件根边界。
    input.targetRootObjectIds.forEach((objectId) => {
      const object = resolveObjectByStableId(objectId)
      if (object && object !== input.scene) {
        objectsById.set(object.uuid, object)
      }
    })
    const visit = (node: GlbHierarchyNode, hasMatchedAncestor: boolean): void => {
      const isMatched = input.targetRootObjectIds.size > 0
        ? input.targetRootObjectIds.has(node.objectId)
        : isHierarchyNodeInProjectModelReplacementSubtree(node, input.targetAssemblyNodeId)
      if (isMatched && !hasMatchedAncestor) {
        const object = resolveObjectByStableId(node.objectId)
        if (object && object !== input.scene) {
          objectsById.set(object.uuid, object)
        }
        return
      }

      node.children.forEach((child) => visit(child, hasMatchedAncestor || isMatched))
    }

    input.composedResult.hierarchy.children.forEach((child) => visit(child, false))
    const topologyScope = collectProjectModelReplacementSubtreeTopologyScope(
      input.composedResult.hierarchy,
      input.targetAssemblyNodeId,
      input.targetRootObjectIds,
    )
    topologyScope.objectIds.forEach((objectId) => {
      const object = resolveObjectByStableId(objectId)
      if (object && object !== input.scene) {
        objectsById.set(object.uuid, object)
      }
    })

    return [...objectsById.values()]
  }

  /**
   * 收集当前项目模型包缓存持有的导入结果。
   *
   * @returns 去重后的包导入结果列表。
   */
  function collectProjectModelPackageImportedResults(): readonly ImportedModelResult[] {
    return collectUniqueImportedModelResults(
      Array.from(projectModelPackageResultByNodeKey.values())
        .flat()
        .map((packageResult) => packageResult.result),
    )
  }

  /**
   * 收集项目模型节点 key 的祖先路径。
   *
   * @param nodeKey 当前节点 key。
   * @returns 从根到当前节点的稳定路径 key。
   */
  function collectProjectModelNodeKeyAncestorPath(nodeKey: string): string[] {
    const segments = nodeKey.split('/').filter((segment) => segment.length > 0)
    const ancestorKeys: string[] = []

    segments.forEach((_segment, index) => {
      ancestorKeys.push(segments.slice(0, index + 1).join('/'))
    })

    return ancestorKeys.length > 0 ? ancestorKeys : [nodeKey]
  }

  /**
   * 按稳定 nodeKey 查找项目模型节点。
   *
   * @param root 查找起点。
   * @param nodeKey 目标节点 key。
   * @returns 找到的节点；不存在时返回 null。
   */
  function findProjectModelNodeByKey(root: ModelAssetNode, nodeKey: string): ModelAssetNode | null {
    return collectModelAssetNodesPreorder(root).find((node) => node.nodeKey === nodeKey) ?? null
  }

  /**
   * 从导入模型结果中收集可描述 Worker BRep runtime 的拓扑源。
   *
   * @param result 已加载的导入模型结果。
   * @returns 拓扑源列表。
   */
  function collectImportedTopologySourcesFromResult(result: ImportedModelResult): ImportedTopologySource[] {
    if (result.kind === 'cached-part') {
      return result.asset ? [result.asset] : []
    }

    if (result.kind !== 'composed-assembly') {
      return []
    }

    return [
      ...Object.values(result.partAssetsByUuid),
      ...Object.values(result.assemblyAssetsByUuid)
        .map((asset) => asset.localTopologySource)
        .filter((source): source is ImportedTopologySource => Boolean(source)),
    ]
  }

  /**
   * 从节点包缓存中收集 Worker BRep ownership 需要的拓扑源。
   *
   * 设计说明：part 类型 `.puppy` 保持 `legacy-glb` 模型结果以复用历史显示链路，
   * 但它的 BRep 拓扑源由 `importPuppyPackage()` 单独返回。scoped 子树切换在
   * begin 阶段必须读取这份包级 topologySource，否则旧 active imported-brep
   * 会缺席 releasable 集合，并在 commit 时被误判为不可释放冲突。
   *
   * @param packageResult 已提交的项目模型节点包结果。
   * @returns 去重后的拓扑源列表。
   */
  function collectImportedTopologySourcesFromPackageResult(
    packageResult: LoadedProjectModelPackageResult,
  ): ImportedTopologySource[] {
    return Array.from(new Set([
      ...(packageResult.topologySource ? [packageResult.topologySource] : []),
      ...collectImportedTopologySourcesFromResult(packageResult.result),
    ]))
  }

  /**
   * 判断两个装配节点 key 是否为精确等价身份。
   *
   * 设计说明：项目模型 nodeKey 通常 URL 编码，而离线包 assembly.json 可能保存未编码节点 ID。
   * 这里只使用 cad-core 的精确 lookup ID，不使用尾部路径模糊匹配，避免同名兄弟组件被误判为同一节点。
   *
   * @param left 左侧节点 key。
   * @param right 右侧节点 key。
   * @returns 两侧是否指向同一个装配节点身份。
   */
  function areProjectModelNodeKeysEquivalent(left: string, right: string): boolean {
    const leftLookupIds = collectAssemblyNodeIdentityLookupIds(left)
    if (leftLookupIds.length === 0) {
      return false
    }

    const rightLookupIdSet = new Set(collectAssemblyNodeIdentityLookupIds(right))
    return leftLookupIds.some((lookupId) => rightLookupIdSet.has(lookupId))
  }

  /**
   * 收集节点 key 自根到自身的装配路径。
   *
   * @param nodeKey 节点 key。
   * @returns 已规范化的祖先路径集合。
   */
  function collectAssemblyNodeKeyAncestorPath(nodeKey: string): string[] {
    const segments = splitAssemblyNodePath(nodeKey)
    return segments.map((_, index) => segments.slice(0, index + 1).join('/'))
  }

  /**
   * 判断节点 key 是否落在指定子树内。
   *
   * @param nodeKey 候选节点 key。
   * @param subtreeRootNodeKey 子树根节点 key。
   * @returns 候选节点是否位于子树根或其后代。
   */
  function isProjectModelNodeKeyInsideSubtreeByIdentity(
    nodeKey: string,
    subtreeRootNodeKey: string,
  ): boolean {
    const normalizedNodeKey = normalizeAssemblyNodeId(nodeKey)
    const normalizedSubtreeRootNodeKey = normalizeAssemblyNodeId(subtreeRootNodeKey)

    if (!normalizedNodeKey || !normalizedSubtreeRootNodeKey) {
      return normalizedNodeKey === normalizedSubtreeRootNodeKey
    }

    return normalizedNodeKey === normalizedSubtreeRootNodeKey
      || normalizedNodeKey.startsWith(`${normalizedSubtreeRootNodeKey}/`)
      || areProjectModelNodeKeysEquivalent(normalizedNodeKey, normalizedSubtreeRootNodeKey)
      || collectAssemblyNodeKeyAncestorPath(normalizedNodeKey)
        .some((ancestorNodeKey) => areProjectModelNodeKeysEquivalent(
          ancestorNodeKey,
          normalizedSubtreeRootNodeKey,
        ))
  }

  /**
   * 从 `.puppy/assembly.json` 中解析拓扑资产的装配 owner。
   *
   * 设计说明：root `.puppy` 会把 direct child 的 BRep sidecar 折叠在根包 topologySource 中；
   * 首次局部替换时旧场景 metadata 可能不足以反查 body owner，但 assembly.json.nodes 仍保存
   * bodyBindingId/geometryObjectId 与装配节点的结构关系。
   *
   * @param puppyAssemblyDocument 离线包完整装配文档。
   * @param asset 当前拓扑 body 资产。
   * @returns 持有该 body 的装配节点 ID。
   */
  function collectPuppyAssemblyTopologyOwnerNodeKeys(
    puppyAssemblyDocument: PuppyAssemblyDocument | undefined,
    asset: {
      readonly bodyBindingId: string
      readonly geometryObjectId: string
    },
  ): string[] {
    if (!puppyAssemblyDocument) {
      return []
    }

    const bodyBindingId = asset.bodyBindingId.trim()
    const geometryObjectId = asset.geometryObjectId.trim()
    if (!bodyBindingId && !geometryObjectId) {
      return []
    }

    return uniqueStrings(
      puppyAssemblyDocument.nodes
        .filter((node) => (
          node.bodyBindingIds.includes(bodyBindingId)
          || node.geometryObjectIds.includes(geometryObjectId)
        ))
        .map((node) => node.nodeId.trim()),
    )
  }

  /**
   * 遍历组合装配当前场景树。
   *
   * @param result 当前组合装配结果。
   * @returns 去重后的 Object3D 列表。
   */
  function collectComposedAssemblySceneObjects(
    result: Extract<ImportedModelResult, { kind: 'composed-assembly' }>,
  ): THREE.Object3D[] {
    const objects: THREE.Object3D[] = []
    const visited = new Set<string>()
    const pushObject = (object: THREE.Object3D | null | undefined): void => {
      if (!object || visited.has(object.uuid)) {
        return
      }
      visited.add(object.uuid)
      objects.push(object)
    }

    result.root.traverse((object) => pushObject(object))
    Object.values(result.sceneBindings.byObject3DUuid).forEach((object) => pushObject(object))

    return objects
  }

  /**
   * 按 bodyBindingId 从当前 composed 场景 metadata 中解析真实 owner 节点。
   *
   * 设计说明：root `.puppy` 包可能把一级子节点几何折叠在根包内，包结果 nodeKey 只能说明文件来源；
   * body 级 `bodyBindingSelectionTargetsById` 才能说明该 BRep body 在当前装配语义上属于哪个 child。
   *
   * @param bodyBindingId 导入 body 绑定 ID。
   * @returns 持有该 body 的项目模型节点 key 列表。
   */
  function resolveCurrentProjectModelBodyOwnerNodeKeys(bodyBindingId: string): string[] {
    const ownerNodeKeys = new Set<string>()

    Array.from(loadedResultByTabId.values()).forEach((result) => {
      if (result.kind !== 'composed-assembly') {
        return
      }

      collectComposedAssemblySceneObjects(result).forEach((object) => {
        const target = ImportedModelObjectMetadataService
          .resolveBodyBindingSelectionTargetFromSelfOrAncestors(object, bodyBindingId)
        if (target?.assemblyNodeId) {
          ownerNodeKeys.add(target.assemblyNodeId)
        }
      })
    })

    return Array.from(ownerNodeKeys)
  }

  /**
   * 按 imported-brep 几何对象 ID 的文件作用域解析项目模型 owner 节点。
   *
   * 设计说明：root `.puppy` 可能把一级子节点的 BRep sidecar 折叠在根包 topologySource 里，
   * 此时 bodyBinding metadata 不一定能反查到 OpenProject 的稳定 nodeKey；但 objectId 中的
   * `<packageUuid>::zip-entry::<entryPath>` 仍能指向对应的节点包。这里用该作用域补齐项目级
   * owner，保证切换一级节点时旧 active BRep 能进入 scoped release 集合。
   *
   * @param geometryObjectId Worker GeometryState 中的几何对象 ID。
   * @returns 与该几何对象来源文件匹配的项目模型节点 key。
   */
  function resolveCurrentProjectModelGeometryObjectFileOwnerNodeKeys(geometryObjectId: string): string[] {
    const currentRoot = currentProjectModelLoadResult?.root
    const assetScope = resolveImportedBrepGeometryObjectAssetScope(geometryObjectId)
    if (!currentRoot || !assetScope) {
      return []
    }

    return collectModelAssetNodesPreorder(currentRoot)
      .filter((node) => node.packageFiles.some((file) => (
        isImportedBrepGeometryObjectAssetScopeFromFile(assetScope, file)
      )))
      .map((node) => node.nodeKey)
  }

  /**
   * 按 geometryObjectId 追加项目模型 owner 节点。
   *
   * @param ownerNodeKeysByGeometryObjectId geometryObjectId 到项目节点 owner 的索引。
   * @param geometryObjectId Worker GeometryState 中的几何对象 ID。
   * @param ownerNodeKeys 候选项目节点 owner。
   */
  function appendProjectOwnerNodeKeysByGeometryObjectId(
    ownerNodeKeysByGeometryObjectId: Map<string, string[]>,
    geometryObjectId: string,
    ownerNodeKeys: readonly string[],
  ): void {
    const currentOwnerNodeKeys = ownerNodeKeysByGeometryObjectId.get(geometryObjectId) ?? []
    ownerNodeKeysByGeometryObjectId.set(
      geometryObjectId,
      uniqueStrings([
        ...currentOwnerNodeKeys,
        ...ownerNodeKeys.filter((ownerNodeKey) => isCurrentProjectModelScopedNodeKey(ownerNodeKey)),
      ]),
    )
  }

  /**
   * 为 root assembly 的孤儿 topology 解析更可信的项目模型 owner。
   *
   * 设计说明：root `.puppy` 会把 direct child 的 sidecar 折叠在根包里。局部切换提交后，
   * 旧 child 的场景对象已经移除，但根包历史 topology 仍可能留在包级缓存中；此时不能再把
   * 找不到 live metadata 的 body 兜底挂到 root，否则下一次切换会被误判为 target 与 root 共享同一 BRep。
   *
   * @param input topology 默认节点、live owner 与同 geometry 的文件 owner。
   * @returns root assembly orphan 的非 root owner；返回空数组表示应跳过该孤儿 topology；返回 null 表示走常规归一化。
   */
  function resolveRootAssemblyOrphanTopologyOwnerNodeKeys(input: {
    readonly defaultNodeKey: string
    readonly ownerNodeKeys: readonly string[]
    readonly preferredProjectOwnerNodeKeys: readonly string[]
  }): string[] | null {
    const currentRoot = currentProjectModelLoadResult?.root

    if (
      !currentRoot
      || currentRoot.nodeType !== 'assembly'
      || input.defaultNodeKey !== currentRoot.nodeKey
      || input.ownerNodeKeys.length > 0
    ) {
      return null
    }

    return uniqueStrings(
      input.preferredProjectOwnerNodeKeys
        .map((ownerNodeKey) => ownerNodeKey.trim())
        .filter((ownerNodeKey) => ownerNodeKey.length > 0 && ownerNodeKey !== currentRoot.nodeKey),
    )
  }

  /**
   * 判断运行时 owner key 是否已经落在当前项目模型稳定路径内。
   *
   * 设计说明：`.puppy` 的 topo/sidecar 可能保存包内局部装配 ID，例如 `SubAsm:1/InternalBody`。
   * 这些 ID 不是 OpenProject 的稳定 nodeKey，不能作为 scoped Worker release 的跨节点 owner；
   * 否则一级节点整体切换时，目标节点自己的旧 BRep 会被误判为 scope 外共享对象。
   *
   * @param nodeKey 待判断的运行时 owner key。
   * @returns owner key 是否属于当前项目模型根路径。
   */
  function isCurrentProjectModelScopedNodeKey(nodeKey: string): boolean {
    const currentRootNodeKey = currentProjectModelLoadResult?.root.nodeKey

    if (!currentRootNodeKey) {
      return false
    }

    return isProjectModelNodeKeyInsideSubtreeByIdentity(nodeKey, currentRootNodeKey)
  }

  /**
   * 把 body 级 owner key 归一到项目模型稳定 nodeKey。
   *
   * 设计说明：局部节点版本切换的语义是替换目标一级节点的完整数据，包括 package、topology sidecar 和 BRep sidecar。
   * 当 bodyBindingSelectionTargetsById 返回包内局部 key 时，应回落到产生该 topology source 的项目节点；
   * 只有已经位于当前项目根路径下的 key 才保留为显式项目 owner，从而继续保护真实的兄弟节点共享几何。
   *
   * @param defaultNodeKey 当前 topology source 所属的项目模型节点 key。
   * @param ownerNodeKeys 从场景 metadata 解析出的 body 级 owner key。
   * @returns 可用于 Worker scoped ownership 的项目模型 owner key。
   */
  function normalizeProjectModelRuntimeOwnerNodeKeys(
    defaultNodeKey: string,
    ownerNodeKeys: readonly string[],
    preferredProjectOwnerNodeKeys: readonly string[] = [],
  ): string[] {
    if (ownerNodeKeys.length === 0 && preferredProjectOwnerNodeKeys.length > 0) {
      return uniqueStrings(preferredProjectOwnerNodeKeys)
    }

    const sourceOwnerNodeKeys = ownerNodeKeys.length > 0 ? ownerNodeKeys : [defaultNodeKey]
    const normalizedOwnerNodeKeys = sourceOwnerNodeKeys
      .map((ownerNodeKey) => ownerNodeKey.trim())
      .filter((ownerNodeKey) => ownerNodeKey.length > 0)
      .flatMap((ownerNodeKey) => {
        if (isCurrentProjectModelScopedNodeKey(ownerNodeKey)) {
          return [ownerNodeKey]
        }

        return preferredProjectOwnerNodeKeys.length > 0
          ? preferredProjectOwnerNodeKeys
          : [defaultNodeKey]
      })

    return uniqueStrings(normalizedOwnerNodeKeys.length > 0
      ? normalizedOwnerNodeKeys
      : [defaultNodeKey])
  }

  /**
   * 收集当前已提交项目模型的 Worker 几何 ownership 快照。
   *
   * 设计说明：必须扫描全部已提交节点包，而不是只扫描目标子树；同一 geometryObjectId 若也被兄弟节点持有，
   * ownership index 会据此拒绝把它作为目标子树独占对象释放。
   *
   * @param runtimeKey 当前根文档 runtime key。
   * @returns 当前项目模型的结构化 ownership 条目。
   */
  function collectCurrentProjectModelRuntimeOwnershipEntries(runtimeKey: string): GeometryRuntimeOwnershipEntry[] {
    const entries: GeometryRuntimeOwnershipEntry[] = []
    const appendTopologySourceEntries = (
      defaultNodeKey: string,
      topologySource: ImportedTopologySource,
      puppyAssemblyDocument?: PuppyAssemblyDocument,
    ): void => {
      const assets = Object.values(topologySource.bodyTopologyAssets ?? {})
        .filter(hasImportedBodyTopologyAssetGeometryObjectId)
      const ownerNodeKeysByBodyBindingId = new Map<string, string[]>()
      const projectOwnerNodeKeysByGeometryObjectId = new Map<string, string[]>()

      assets.forEach((asset) => {
        const ownerNodeKeys = uniqueStrings([
          ...resolveCurrentProjectModelBodyOwnerNodeKeys(asset.bodyBindingId),
          ...collectPuppyAssemblyTopologyOwnerNodeKeys(puppyAssemblyDocument, asset),
        ])
        ownerNodeKeysByBodyBindingId.set(asset.bodyBindingId, ownerNodeKeys)
        appendProjectOwnerNodeKeysByGeometryObjectId(
          projectOwnerNodeKeysByGeometryObjectId,
          asset.geometryObjectId,
          ownerNodeKeys,
        )
        appendProjectOwnerNodeKeysByGeometryObjectId(
          projectOwnerNodeKeysByGeometryObjectId,
          asset.geometryObjectId,
          resolveCurrentProjectModelGeometryObjectFileOwnerNodeKeys(asset.geometryObjectId),
        )
      })

      assets.forEach((asset) => {
        if (!asset.geometryObjectId) {
          return
        }

        const binding = topologySource.bodyBrepBindings?.[asset.bodyBindingId]
        const sameGeometryProjectOwnerNodeKeys =
          projectOwnerNodeKeysByGeometryObjectId.get(asset.geometryObjectId) ?? []
        const ownerNodeKeys = ownerNodeKeysByBodyBindingId.get(asset.bodyBindingId) ?? []
        const rootAssemblyOrphanOwnerNodeKeys = resolveRootAssemblyOrphanTopologyOwnerNodeKeys({
          defaultNodeKey,
          ownerNodeKeys,
          preferredProjectOwnerNodeKeys: sameGeometryProjectOwnerNodeKeys,
        })

        if (rootAssemblyOrphanOwnerNodeKeys !== null && rootAssemblyOrphanOwnerNodeKeys.length === 0) {
          return
        }

        const resolvedOwnerNodeKeys = normalizeProjectModelRuntimeOwnerNodeKeys(
          defaultNodeKey,
          ownerNodeKeys,
          rootAssemblyOrphanOwnerNodeKeys ?? sameGeometryProjectOwnerNodeKeys,
        )

        resolvedOwnerNodeKeys.forEach((ownerNodeKey) => {
          entries.push({
            runtimeKey,
            assemblyNodeId: ownerNodeKey,
            componentInstanceId: ownerNodeKey,
            bodyBindingId: asset.bodyBindingId,
            geometryObjectId: asset.geometryObjectId,
            brepSha256: binding?.brepRef?.sha256,
            scopeNodeKeys: collectProjectModelNodeKeyAncestorPath(ownerNodeKey),
          })
        })
      })
    }

    projectModelPackageResultByNodeKey.forEach((packageResults, nodeKey) => {
      packageResults.forEach((packageResult) => {
        collectImportedTopologySourcesFromPackageResult(packageResult).forEach((topologySource) => {
          appendTopologySourceEntries(nodeKey, topologySource, packageResult.puppyAssemblyDocument)
        })
      })
    })
    Array.from(loadedResultByTabId.values()).forEach((result) => {
      const defaultNodeKey = currentProjectModelLoadResult?.root.nodeKey ?? result.fileInfo.fileId
      collectImportedTopologySourcesFromResult(result).forEach((topologySource) => {
        appendTopologySourceEntries(defaultNodeKey, topologySource)
      })
    })

    return entries
  }

  /**
   * 对字符串数组按出现顺序去重。
   *
   * @param values 原始字符串列表。
   * @returns 去重后的字符串列表。
   */
  function uniqueStrings(values: readonly string[]): string[] {
    return Array.from(new Set(values.filter((value) => value.length > 0)))
  }

  /**
   * 解析当前项目根 runtime key。
   *
   * @param root 当前项目模型根节点。
   * @returns 当前根文档 runtime key。
   */
  function resolveCurrentProjectRootRuntimeKey(root: ModelAssetNode): string {
    return createProjectRootRuntimeKey(
      currentProjectModelContextIdentity ?? createProjectRootContextIdentity(root),
    )
  }

  /**
   * 创建局部子树 runtime key。
   *
   * @param input 当前根、目标节点与下一子树根。
   * @returns 局部 staged runtime key。
   */
  function createScopedSubtreeRuntimeKey(input: {
    readonly currentRoot: ModelAssetNode
    readonly targetNodeKey: string
    readonly nextSubtreeRoot: ModelAssetNode
  }): string {
    const packageIdentity = collectModelAssetNodesPreorder(input.nextSubtreeRoot)
      .flatMap((node) => node.packageFiles.map((file) => [
        node.nodeKey,
        file.fileId,
        file.uuid,
        file.size?.toString() ?? '',
      ].join(':')))
      .join(',')
    const currentRootRuntimeKey = resolveCurrentProjectRootRuntimeKey(input.currentRoot)

    return [
      'project-model-scoped-subtree',
      `base=${normalizeProjectRuntimeKeySegment(currentRootRuntimeKey)}`,
      `target=${normalizeProjectRuntimeKeySegment(input.targetNodeKey)}`,
      `nextRoot=${normalizeProjectRuntimeKeySegment(input.nextSubtreeRoot.nodeKey)}`,
      `nextCommit=${normalizeProjectRuntimeKeySegment(input.nextSubtreeRoot.commitId)}`,
      `package=${normalizeProjectRuntimeKeySegment(packageIdentity || input.nextSubtreeRoot.nodeKey)}`,
    ].join('|')
  }

  /**
   * 创建 scoped-subtree runtime switch 输入。
   *
   * @param input 当前根、目标节点与下一子树根。
   * @returns Viewer runtime switch 输入。
   */
  function createScopedSubtreeRuntimeSwitchInput(input: {
    readonly currentRoot: ModelAssetNode
    readonly targetNodeKey: string
    readonly targetAssemblyNodeId: string
    readonly nextSubtreeRoot: ModelAssetNode
    readonly topologyRemovalPlan: ImportedModelTopologyScopedRemovalPlan
  }): ViewerDocumentRuntimeSwitchInput {
    const runtimeKey = createScopedSubtreeRuntimeKey(input)
    const rootRuntimeKey = resolveCurrentProjectRootRuntimeKey(input.currentRoot)
    const topologyRuntimeGeometryObjectIds = new Set(
      input.topologyRemovalPlan.bodyOwnerships
        .map((ownership) => ownership.geometryObjectId)
        .filter((geometryObjectId) => geometryObjectId.length > 0),
    )
    /**
     * 当前 live repository 是 Worker active runtime 的直接镜像。对于它已经覆盖的 geometryObjectId，
     * 必须替换包缓存推导条目，避免根包历史 sidecar 把已确认的目标 body 再误标为 scope 外 owner。
     */
    const cachedOwnershipEntries = collectCurrentProjectModelRuntimeOwnershipEntries(rootRuntimeKey)
      .filter((entry) => !topologyRuntimeGeometryObjectIds.has(entry.geometryObjectId))
    const topologyRuntimeOwnershipEntries: GeometryRuntimeOwnershipEntry[] = input.topologyRemovalPlan
      .bodyOwnerships
      .filter((ownership) => ownership.geometryObjectId.length > 0)
      .map((ownership) => {
        const ownerAssemblyNodeId = ownership.isTarget
          ? input.targetAssemblyNodeId
          : ownership.assemblyNodeId || input.currentRoot.nodeKey
        return {
          runtimeKey: rootRuntimeKey,
          assemblyNodeId: ownerAssemblyNodeId,
          componentInstanceId: ownerAssemblyNodeId,
          bodyBindingId: ownership.bodyBindingId,
          geometryObjectId: ownership.geometryObjectId,
          scopeNodeKeys: ownership.isTarget
            ? collectProjectModelNodeKeyAncestorPath(input.targetNodeKey)
            : collectProjectModelNodeKeyAncestorPath(ownerAssemblyNodeId),
        }
      })
    const ownershipEntries = [
      ...cachedOwnershipEntries,
      ...topologyRuntimeOwnershipEntries,
    ]
    const ownershipIndex = new GeometryRuntimeOwnershipIndex(ownershipEntries)
    const targetEntries = ownershipIndex.getEntriesBySubtree(input.targetNodeKey)
    const geometryObjectIds = uniqueStrings([
      ...targetEntries.map((entry) => entry.geometryObjectId),
      ...input.topologyRemovalPlan.targetGeometryObjectIds,
    ])
    const bodyBindingIds = uniqueStrings(
      [
        ...targetEntries.map((entry) => entry.bodyBindingId ?? ''),
        ...input.topologyRemovalPlan.targetBodyBindingIds,
      ].filter((bodyBindingId) => bodyBindingId.length > 0),
    )
    const releasableGeometryObjectIds = geometryObjectIds.filter((geometryObjectId) =>
      ownershipIndex.canReleaseGeometryObject({
        targetNodeKey: input.targetNodeKey,
        geometryObjectId,
      }))

    return {
      runtimeKey,
      reason: 'project-context-switch',
      scope: {
        kind: 'scoped-subtree',
        runtimeKey,
        rootNodeKey: input.currentRoot.nodeKey,
        targetNodeKey: input.targetNodeKey,
        ownerAssemblyNodeId: input.targetAssemblyNodeId,
        geometryObjectIds,
        bodyBindingIds,
        releasableGeometryObjectIds,
      },
    }
  }

  /**
   * 记录 scoped 子树加载期间真实注册出来的 bodyBindingId。
   *
   * 设计说明：新版包的 bodyBindingId 可能由 GLB/sidecar hydration 动态生成，begin 阶段
   * 无法从旧 ownership 或包 manifest 完整推导；因此必须以 staged BRep 注册事实作为
   * Worker commit 的补充边界。
   *
   * @param stagedBodyBindingIds 当前 scoped 会话记录容器。
   * @param bodyBindingId 候选 bodyBindingId。
   */
  function appendScopedStagedBodyBindingId(
    stagedBodyBindingIds: string[],
    bodyBindingId: unknown,
  ): void {
    if (typeof bodyBindingId !== 'string') {
      return
    }

    const normalizedBodyBindingId = bodyBindingId.trim()
    if (!normalizedBodyBindingId || stagedBodyBindingIds.includes(normalizedBodyBindingId)) {
      return
    }

    stagedBodyBindingIds.push(normalizedBodyBindingId)
  }

  /**
   * 创建带 bodyBindingId 记录能力的 scoped runtime switch 句柄。
   *
   * @param handle Viewer 返回的原始 runtime switch 句柄。
   * @param stagedBodyBindingIds 当前 scoped 会话记录容器。
   * @returns 包装后的 runtime switch 句柄。
   */
  function createScopedRuntimeSwitchRecordingHandle(
    handle: ViewerDocumentRuntimeSwitchHandle,
    stagedBodyBindingIds: string[],
  ): ViewerDocumentRuntimeSwitchHandle {
    const brepRegistrar: BRepRegistrar = {
      async registerImportedBrepShape(payload) {
        const result = await handle.brepRegistrar.registerImportedBrepShape(payload)

        // 只有真实写入 staged runtime 成功后，才把 binding 纳入本次提交边界。
        appendScopedStagedBodyBindingId(
          stagedBodyBindingIds,
          payload.registerGeometryState?.bodyBindingId,
        )

        return result
      },
      async registerImportedBrepShapesAtomically(payloads, registrationOptions) {
        const registerAtomically = handle.brepRegistrar.registerImportedBrepShapesAtomically
        if (!registerAtomically) {
          throw new Error('scoped runtime switch 缺少 BRep 原子注册能力')
        }

        const results = await registerAtomically.call(
          handle.brepRegistrar,
          payloads,
          registrationOptions,
        )

        // 原子批次只有整体成功后才能扩展 scoped commit 边界，失败批次不得留下部分 binding 所有权。
        payloads.forEach((payload) => {
          appendScopedStagedBodyBindingId(
            stagedBodyBindingIds,
            payload.registerGeometryState?.bodyBindingId,
          )
        })

        return results
      },
    }

    return {
      ...handle,
      brepRegistrar,
    }
  }

  /**
   * 开启局部子树 Worker runtime switch。
   *
   * @param input 当前根、目标节点与下一子树根。
   * @returns 局部 runtime switch 会话。
   */
  async function beginScopedSubtreeRuntimeSwitch(input: {
    readonly currentRoot: ModelAssetNode
    readonly targetNodeKey: string
    readonly targetAssemblyNodeId: string
    readonly nextSubtreeRoot: ModelAssetNode
    readonly topologyRemovalPlan: ImportedModelTopologyScopedRemovalPlan
  }): Promise<ScopedSubtreeRuntimeSwitchSession> {
    const viewer = options.getViewer()
    if (!viewer?.beginDocumentRuntimeSwitch) {
      throw new Error('项目模型局部替换缺少 Viewer.beginDocumentRuntimeSwitch，无法隔离 Worker 子树运行时')
    }

    const rawHandle = await viewer.beginDocumentRuntimeSwitch(createScopedSubtreeRuntimeSwitchInput(input))
    const stagedBodyBindingIds: string[] = []
    const handle = createScopedRuntimeSwitchRecordingHandle(rawHandle, stagedBodyBindingIds)

    return {
      handle,
      stagedBodyBindingIds,
      topologyRemovalPlan: input.topologyRemovalPlan,
      committed: false,
      closed: false,
    }
  }

  /**
   * 提交局部子树 Worker runtime switch。
   *
   * @param session 局部 runtime switch 会话。
   */
  async function commitScopedSubtreeRuntimeSwitchSession(
    session: ScopedSubtreeRuntimeSwitchSession,
  ): Promise<void> {
    if (session.closed) {
      throw new Error('项目模型局部 runtime switch 已关闭，无法提交')
    }

    const commitInput = session.stagedBodyBindingIds.length > 0
      ? { scopedBodyBindingIds: session.stagedBodyBindingIds }
      : undefined
    await session.handle.commit(commitInput)
    session.committed = true
    session.closed = true
  }

  /**
   * 尝试放弃局部子树 Worker runtime switch。
   *
   * @param session 局部 runtime switch 会话。
   * @param errorContext 日志上下文。
   */
  async function tryAbortScopedSubtreeRuntimeSwitchSession(
    session: ScopedSubtreeRuntimeSwitchSession | null,
    errorContext: string,
  ): Promise<void> {
    if (!session || session.closed || session.committed) {
      return
    }

    try {
      await session.handle.abort()
      session.closed = true
    } catch (abortError) {
      logger.error(`[GlbLoader] ${errorContext} abort scoped subtree runtime switch failed`, abortError)
    }
  }

  /**
   * 释放被新包缓存替换的旧项目模型包结果。
   *
   * @param previousResults 替换前缓存持有的包结果。
   * @param retainedPackageResults 新缓存继续持有的包结果。
   */
  function disposeReplacedProjectModelPackageResults(
    previousResults: readonly ImportedModelResult[],
    retainedPackageResults: readonly ProjectModelPackageResult[],
  ): void {
    const retainedResults = new Set(retainedPackageResults.map((packageResult) => packageResult.result))
    for (const previousResult of previousResults) {
      if (retainedResults.has(previousResult)) {
        continue
      }
      disposeCommittedBackgroundImportedModelResult(
        previousResult,
        'dispose replaced project model package result',
      )
    }
  }

  /**
   * 释放项目模型应用失败时已经解析但未进入长期缓存的节点包结果。
   *
   * 设计说明：scoped 分支会先用新 target package 组合前台场景，再等待 Worker commit。
   * 如果后续提交失败，新 package 不会写入 `projectModelPackageResultByNodeKey`，必须在失败路径
   * 单独释放；否则只释放 composed 结果会遗漏这些临时包对象。
   *
   * @param packageResults 本次请求已经解析完成的节点包结果。
   * @param transactionCommitted package loader 事务是否已经提交到共享语义 cache。
   * @param errorContext 日志上下文。
   */
  function disposeFailedProjectModelPackageResults(
    packageResults: readonly LoadedProjectModelPackageResult[],
    transactionCommitted: boolean,
    errorContext: string,
  ): void {
    const importedResults = collectUniqueImportedModelResults(
      packageResults.map((packageResult) => packageResult.result),
    )

    for (const result of importedResults) {
      if (transactionCommitted) {
        disposeCommittedBackgroundImportedModelResult(result, errorContext)
        continue
      }

      try {
        disposeImportedModel(result)
      } catch (disposeError) {
        logger.error(`[GlbLoader] ${errorContext} dispose package result failed`, disposeError)
      }
    }
  }

  /**
   * 替换指定子树的项目模型包缓存。
   *
   * 设计说明：root `.puppy` 内嵌子节点替换会让旧 target 后代包失效；如果只覆盖
   * target 自身，后续 scoped ownership 会继续扫描旧后代 BRep，误判共享与释放范围。
   *
   * @param targetNodeKey 被替换的子树根节点 key。
   * @param packageResults 新子树加载得到的包结果。
   */
  function replaceProjectModelPackageResultCacheForSubtree(
    targetNodeKey: string,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): void {
    const previousResults: ImportedModelResult[] = []
    const targetNodeKeyPrefix = `${targetNodeKey}/`

    Array.from(projectModelPackageResultByNodeKey.entries()).forEach(([nodeKey, cachedPackageResults]) => {
      if (nodeKey !== targetNodeKey && !nodeKey.startsWith(targetNodeKeyPrefix)) {
        return
      }

      previousResults.push(...cachedPackageResults.map((packageResult) => packageResult.result))
      projectModelPackageResultByNodeKey.delete(nodeKey)
    })

    packageResults.forEach((packageResult) => {
      const current = projectModelPackageResultByNodeKey.get(packageResult.nodeKey) ?? []
      projectModelPackageResultByNodeKey.set(packageResult.nodeKey, [...current, packageResult])
    })
    disposeReplacedProjectModelPackageResults(
      collectUniqueImportedModelResults(previousResults),
      packageResults,
    )
  }

  /**
   * 将项目模型资产文件转换为既有 imported-model loader 可消费的 FileInfo。
   *
   * @param file 项目模型节点包文件。
   * @returns imported-model loader 文件描述。
   */
  function toImportedLoaderFileInfo(file: ModelAssetFile): FileInfo {
    return {
      fileId: file.fileId,
      uuid: file.uuid || file.fileId,
      kind: file.kind,
      size: file.size ?? BigInt(0),
    } as FileInfo
  }

  /**
   * 判断节点包是否为 PuppyCAD 项目包。
   *
   * 设计说明：OpenProject 的 `FileInfo.kind` 当前可能只给出 `.puppy`，
   * 但真实文件名可能是 `xxx.step.puppy`；历史节点包也可能直接以 `.zip` 暴露。
   * 这里同时检查 kind 和 fileId，避免把压缩包误交给旧 GLB loader。
   *
   * @param file 项目模型节点包文件。
   * @returns 是否应该交给 `importPuppyPackage` 解析。
   */
  function isPuppyNodePackageFile(file: ModelAssetFile): boolean {
    const normalizedKind = file.kind.trim().toLowerCase()
    const normalizedFileId = file.fileId.trim().toLowerCase()
    return normalizedKind === '.puppy'
      || normalizedKind === 'application/zip'
      || normalizedFileId.endsWith('.puppy')
      || normalizedFileId.endsWith('.zip')
  }

  /**
   * 解压 PuppyCAD 节点包。
   *
   * @param bytes 已下载的节点包字节。
   * @returns zip entry 到字节的映射。
   */
  async function unzipPuppyNodePackage(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
    const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
    return unzipSync(bytes)
  }

  /**
   * 判断 zip 是否为完整离线包格式。
   *
   * 设计说明：完整离线包必须携带根目录 `manifest.json`；没有 manifest 的 `.step.puppy`
   * 是后端节点包轻量格式，需要按内部 GLB/sidecar 文件走旧导入模型 loader。
   *
   * @param files zip entry 到字节的映射。
   * @returns 是否包含完整离线包 manifest。
   */
  function hasPuppyPackageManifest(files: Record<string, Uint8Array>): boolean {
    return Object.keys(files).some((path) => normalizeImportedAssetFileId(path) === 'manifest.json')
  }

  /**
   * 解析 zip entry 的文件类型。
   *
   * @param path zip entry 路径。
   * @returns 供 imported-model loader 使用的 kind。
   */
  function resolveZipEntryKind(path: string): string {
    const normalized = normalizeImportedAssetFileId(path).toLowerCase()
    if (normalized.endsWith('.glb')) {
      return '.glb'
    }
    if (normalized.endsWith('.json')) {
      return '.json'
    }
    const lastDotIndex = normalized.lastIndexOf('.')
    return lastDotIndex >= 0 ? normalized.slice(lastDotIndex) : ''
  }

  /**
   * 解析节点包所在目录。
   *
   * @param fileId 节点包 fileId。
   * @returns 节点包目录；根目录文件返回空字符串。
   */
  function resolvePackageBaseDirectory(fileId: string): string {
    const segments = normalizeImportedAssetFileId(fileId).split('/').filter(Boolean)
    segments.pop()
    return segments.join('/')
  }

  /**
   * 把 zip entry 转换为 imported-model loader 可消费的虚拟文件。
   *
   * @param packageFile 当前 `.puppy` 节点包文件。
   * @param entryPath zip 内路径。
   * @param bytes entry 字节。
   * @returns 虚拟 FileInfo。
   */
  function createZipEntryFileInfo(
    packageFile: ModelAssetFile,
    entryPath: string,
    bytes: Uint8Array,
  ): FileInfo {
    const normalizedEntryPath = normalizeImportedAssetFileId(entryPath)
    const packageBaseDirectory = resolvePackageBaseDirectory(packageFile.fileId)
    const fileId = packageBaseDirectory
      ? `${packageBaseDirectory}/${normalizedEntryPath}`
      : normalizedEntryPath
    return {
      fileId,
      uuid: `${packageFile.uuid || packageFile.fileId}::zip-entry::${normalizedEntryPath}`,
      kind: resolveZipEntryKind(normalizedEntryPath),
      size: BigInt(bytes.byteLength),
    } as FileInfo
  }

  /**
   * 使用轻量 ZIP 节点包加载模型。
   *
   * 设计说明：轻量包没有 PuppyCAD 离线包 manifest，但包含 GLB、topo sidecar 和 BRep sidecar。
   * 这里只建立内存文件系统，后续解析仍复用 imported-model loader，保证拓扑恢复逻辑只有一个来源。
   *
   * @param handle 当前加载请求归属。
   * @param file 当前 `.puppy` 节点包文件。
   * @param files zip entry 到字节的映射。
   * @returns 已加载模型。
   */
  async function loadLightweightZipNodePackageWithHandle(
    handle: ImportedModelRequestHandle,
    file: ModelAssetFile,
    files: Record<string, Uint8Array>,
    brepRegistrar?: BRepRegistrar,
  ): Promise<LoadedImportedModel | null> {
    const entries = Object.entries(files)
      .map(([path, bytes]) => ({
        normalizedPath: normalizeImportedAssetFileId(path),
        bytes,
      }))
      .filter((entry) => entry.normalizedPath.length > 0 && !entry.normalizedPath.endsWith('/'))

    const glbEntries = entries.filter((entry) => entry.normalizedPath.toLowerCase().endsWith('.glb'))
    if (glbEntries.length !== 1) {
      throw new Error(`轻量 ZIP 节点包必须包含且仅包含一个 GLB 文件: ${file.fileId}`)
    }

    const virtualFiles = entries.map((entry) => createZipEntryFileInfo(file, entry.normalizedPath, entry.bytes))
    const bytesByFileId = new Map<string, Uint8Array>()
    virtualFiles.forEach((virtualFile, index) => {
      bytesByFileId.set(normalizeImportedAssetFileId(virtualFile.fileId), entries[index].bytes)
    })

    const readFileBytes: ImportedModelFileBytesReader = async (targetFile) => {
      const bytes = bytesByFileId.get(normalizeImportedAssetFileId(targetFile.fileId))
      if (!bytes) {
        throw new Error(`轻量 ZIP 节点包缺少文件: ${targetFile.fileId}`)
      }
      return new Uint8Array(bytes)
    }
    const sourceGlbFile = virtualFiles[entries.indexOf(glbEntries[0])]

    return loadImportedModelWithHandle(handle, sourceGlbFile, {
      downloadContextId: cadSessionStore.sessionId,
      projectFiles: virtualFiles,
      version: resolveImportedModelCacheVersion(),
      readFileBytes,
      brepRegistrar,
    })
  }

  /**
   * 解析导入链路注册 BRep Shape 所需的最小能力。
   *
   * 设计说明：默认预加载、项目包导入和 Insert CAD 都可能进入 cad-core hydration；
   * 因此调用真实 loader 前必须拿到稳定 registrar，避免缺依赖时继续下载或解析。
   *
   * @returns 可注册 BRep 运行时资产的 registrar。
   */
  function resolveBRepRegistrar(): BRepRegistrar {
    const brepRegistrar = options.getViewer()?.workerManager
    if (!brepRegistrar) {
      throw new Error('模型加载缺少 Viewer WorkerManager，无法注册 BRep 运行时资产')
    }
    return brepRegistrar
  }

  /**
   * 打印 `.puppy` 节点包内的 zip entry 列表。
   *
   * 设计说明：该日志只在解析失败时触发，用于判断后端返回的是现有 PuppyCAD 离线包 schema、
   * 带子目录的 zip，还是其它节点包格式；不在成功路径打印，避免污染正常加载控制台。
   *
   * @param file 触发解析失败的节点包文件。
   * @param bytes 已下载的节点包字节。
   * @param cause 原始解析错误。
   */
  async function logPuppyPackageEntriesForDebug(
    file: ModelAssetFile,
    bytes: Uint8Array,
    cause: unknown,
  ): Promise<void> {
    const errorMessage = cause instanceof Error ? cause.message : String(cause)
    try {
      const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
      const files = unzipSync(bytes)
      const entries = Object.entries(files)
        .map(([path, data]) => ({
          path,
          byteLength: data.byteLength,
        }))
        .sort((left, right) => left.path.localeCompare(right.path))

      logger.error('[GlbLoader] Puppy 节点包解析失败，zip entries 如下', {
        fileId: file.fileId,
        uuid: file.uuid,
        kind: file.kind,
        byteLength: bytes.byteLength,
        errorMessage,
        entryCount: entries.length,
        entries,
      })
    } catch (zipError) {
      logger.error('[GlbLoader] Puppy 节点包解析失败，且下载结果无法作为 zip 解压', {
        fileId: file.fileId,
        uuid: file.uuid,
        kind: file.kind,
        byteLength: bytes.byteLength,
        errorMessage,
        zipErrorMessage: zipError instanceof Error ? zipError.message : String(zipError),
      })
    }
  }

  /**
   * 在给定请求 handle 下加载 `.puppy` 项目节点包。
   *
   * 设计说明：OpenProject 主加载入口会先用版本文件 reader 按精确 URL 预读字节，再进入这里解析。
   * 这样可以避免 `_assembly.json` 与节点包共用 fileId/uuid 时重新走旧下载接口导致文件身份歧义。
   *
   * @param handle 当前加载请求归属。
   * @param file 项目模型节点包文件。
   * @param preloadedBytes 已通过版本文件 URL 读取的节点包字节。
   * @returns 已加载模型；请求被中止或组件销毁时返回 null。
   */
  async function loadPuppyNodePackageWithHandle(
    handle: ImportedModelRequestHandle,
    file: ModelAssetFile,
    preloadedBytes: Uint8Array,
    brepRegistrar?: BRepRegistrar,
  ): Promise<LoadedImportedModel | null> {
    let packageBytes: Uint8Array | null = null
    try {
      if (handle.transaction.isAborted) {
        return null
      }

      packageBytes = new Uint8Array(preloadedBytes)
      if (handle.transaction.isAborted) {
        return null
      }

      const zipFiles = await unzipPuppyNodePackage(packageBytes)
      if (!hasPuppyPackageManifest(zipFiles)) {
        return loadLightweightZipNodePackageWithHandle(handle, file, zipFiles, brepRegistrar)
      }

      const imported = await importPuppyPackage(packageBytes, {
        brepRegistrar: brepRegistrar ?? resolveBRepRegistrar(),
        packageName: file.fileId,
        fileInfo: toImportedLoaderFileInfo(file),
      })
      if (isDisposed) {
        disposeImportedModel(imported.modelResult)
        return null
      }

      return {
        displayType: imported.manifest.documentType,
        result: imported.modelResult,
        partAssetCache: caches.partAssetCache,
        importedBrepRuntimeCache: caches.importedBrepRuntimeCache,
        puppyMateDocument: imported.mates,
        puppyAssemblyDocument: imported.assemblyDocument,
        puppyDatumExtension: imported.datumExtension ?? createEmptyPuppyDatumExtension(),
        puppyLinearComponentPatternDocument: imported.linearComponentPatternExtension
          ?? createEmptyPuppyLinearComponentPatternDocument(),
        //------ 草图离线包定义持久化 开始------
        puppySketchExtension: imported.sketchExtension,
        puppySketchEntityReferenceExtension: imported.sketchEntityReferenceExtension,
        //------ 草图离线包定义持久化 结束------
        //------ 特征离线包定义持久化 开始------
        puppyFeatureExtension: imported.featureExtension,
        //------ 特征离线包定义持久化 结束------
        // 已确认当前文件是 `.puppy` 包；扩展缺失时保留 null，供完整根加载清空旧文档视图。
        puppySectionViewExtension: imported.sectionViewExtension ?? null,
        topologySource: imported.topologySource,
      }
    } catch (error) {
      if (isImportedModelLoadAbortedError(error)) {
        return null
      }
      if (packageBytes) {
        await logPuppyPackageEntriesForDebug(file, packageBytes, error)
      }
      throw error
    }
  }

  /**
   * 加载项目模型节点包。
   *
   * @param handle 当前加载请求归属。
   * @param node 当前包对应的项目模型节点。
   * @param file 项目模型节点包文件。
   * @param preloadedBytes 已通过版本文件 URL 读取的节点包字节。
   * @param brepRegistrar 可选的 BRep 注册器。
   * @param preparedCompleteGlbPackage complete 模式已完成解压与业务分析的快照。
   * @returns 已加载模型；失败由调用方统一收口。
   */
  async function loadProjectNodePackageWithHandle(
    handle: ImportedModelRequestHandle,
    node: ModelAssetNode,
    file: ModelAssetFile,
    preloadedBytes: Uint8Array,
    brepRegistrar?: BRepRegistrar,
    preparedCompleteGlbPackage?: PuppyCADWeb.PreparedCompleteGlbPackageSnapshot,
  ): Promise<LoadedImportedModel | null> {
    if (file.loadMode === 'complete-glb-scene') {
      const resolvedCompleteGlbPackage = preparedCompleteGlbPackage
        ?? await prepareCompleteGlbPackageSnapshot({
          node,
          packageFile: file,
          bytes: preloadedBytes,
          assertCurrent: () => {
            if (handle.transaction.isAborted || !handle.isCurrent()) {
              throw new ImportedModelLoadAbortedError()
            }
          },
        })
      return loadCompleteGlbSceneWithHandle(
        handle,
        node,
        file,
        resolvedCompleteGlbPackage,
        brepRegistrar,
      )
    }
    if (isPuppyNodePackageFile(file)) {
      return loadPuppyNodePackageWithHandle(handle, file, preloadedBytes, brepRegistrar)
    }
    return loadImportedModelWithHandle(handle, toImportedLoaderFileInfo(file), {
      downloadContextId: cadSessionStore.sessionId,
      projectFiles: [toImportedLoaderFileInfo(file)],
      version: resolveImportedModelCacheVersion(),
      readFileBytes: createSingleProjectAssetBytesReader(file, preloadedBytes),
      brepRegistrar,
    })
  }

  /**
   * 在现有 Editor request handle 下接管完整 GLB 场景。
   *
   * @param handle 当前加载请求归属。
   * @param node OpenProject 显式节点语义。
   * @param packageFile 触发 complete 模式的根包文件。
   * @param completePackage ZIP reader 返回的内存文件系统。
   * @param brepRegistrar 当前 staged Worker 的 BRep 注册能力。
   * @returns 已加载的完整 GLB 场景；取消或销毁时返回 null。
   */
  async function loadCompleteGlbSceneWithHandle(
    handle: ImportedModelRequestHandle,
    node: ModelAssetNode,
    packageFile: ModelAssetFile,
    completePackage: PuppyCADWeb.PreparedCompleteGlbPackageSnapshot,
    brepRegistrar?: BRepRegistrar,
  ): Promise<LoadedImportedModel | null> {
    try {
      const loaded = await loadCompleteGlbScene(
        cadSessionStore.sessionId,
        packageFile,
        completePackage.sourceGlbFile,
        {
          projectFiles: [...completePackage.projectFiles],
          version: resolveImportedModelCacheVersion(),
          expectedSemanticKind: node.nodeType,
          rootSelection: completePackage.rootSelection,
          occurrenceGraph: completePackage.occurrenceGraph,
          persistedNodeId: node.persistedNodeId,
          partAssetCache: caches.partAssetCache,
          assemblyModelCache: caches.assemblyModelCache,
          parsedGlbCache: caches.parsedGlbCache,
          importedBrepRuntimeCache: caches.importedBrepRuntimeCache,
          brepRegistrar: brepRegistrar ?? resolveBRepRegistrar(),
          readFileBytes: completePackage.readFileBytes,
          transaction: handle.transaction,
          requestRegistry,
          guard: { isStale: () => handle.transaction.isAborted },
          sourceAssetKey: `complete-glb:${packageFile.uuid || packageFile.fileId}`,
        },
      )
      if (isDisposed) {
        disposeImportedModel(loaded.result)
        return null
      }
      return loaded
    } catch (error) {
      if (isImportedModelLoadAbortedError(error)) {
        return null
      }
      throw error
    }
  }

  /**
   * 把项目模型版本文件读取结果写入本地字节缓存。
   *
   * 设计说明：局部版本切换只应重新请求变更子树；但当前组合器需要全新的 Object3D 实例才能安全
   * 替换场景，因此未变更节点使用已下载字节重新解析，不复用旧场景对象。
   *
   * @param fileReadResults 本次已经完成读取的版本文件。
   */
  function cacheProjectModelFileReadResults(fileReadResults: readonly ProjectNodeFileReadResult[]): void {
    for (const result of fileReadResults) {
      projectModelFileBytesByIdentityKey.set(
        buildProjectAssetFileIdentityKey(result.file),
        new Uint8Array(result.bytes),
      )
    }
  }

  /**
   * 创建供后端加载准备器使用的只读文件字节缓存适配器。
   *
   * 设计说明：preparer 只读缓存，不写入缓存；真实缓存提交/回滚仍由 glb loader 生命周期控制。
   *
   * @returns 项目模型文件字节只读缓存适配器。
   */
  function createProjectModelFileByteCacheAdapter(): ProjectModelFileByteCacheAdapter {
    return {
      has: (file: ModelAssetFile): boolean => (
        projectModelFileBytesByIdentityKey.has(buildProjectAssetFileIdentityKey(file))
      ),
      read: (file: ModelAssetFile): Uint8Array | null => {
        const bytes = projectModelFileBytesByIdentityKey.get(buildProjectAssetFileIdentityKey(file))

        return bytes ? new Uint8Array(bytes) : null
      },
      keys: (): Set<string> => new Set(projectModelFileBytesByIdentityKey.keys()),
    }
  }

  /**
   * 通过统一后端加载准备器预处理项目模型加载单元。
   *
   * 设计说明：节点版本切换、根版本切换等入口都必须复用同一套 version-file 预读、
   * root `.puppy` direct child patch 和 missing-only 补读能力；因此这里集中注入
   * reader/cache/后端地址，调用方只负责声明本次业务要加载的 load unit。
   *
   * @param input 外层业务生成的加载单元计划与装配文档覆盖。
   * @returns 已完成 cad-core 加载计划执行和文件预读的结果。
   */
  async function prepareProjectModelBackendLoadUnit(
    input: PrepareProjectModelBackendLoadUnitInput,
  ): Promise<PreparedProjectModelBackendLoadResult> {
    return prepareProjectModelBackendLoad({
      ...input,
      reader: createAuthorizedVersionFileReader({
        getAuthorizationHeader: resolveVersionFileAuthorizationHeader,
        refreshAuthorizationHeader: refreshVersionFileAuthorizationHeader,
      }),
      fileCache: createProjectModelFileByteCacheAdapter(),
      nodeReloadAdapter: options.projectRootNodeReloadAdapter,
      backendBaseUrl: apiConfig.baseUrl,
    })
  }

  /**
   * 创建一级子节点版本替换 load unit。
   *
   * 设计说明：该 facade 是 Editor 节点版本切换进入 service builder 的唯一入口，
   * 后端地址在这里统一注入，避免页面层直接参与版本文件 URL 生成。
   *
   * @param input 不含后端地址的业务切换输入。
   * @returns 可交给后端加载准备器消费的项目模型 load unit。
   */
  function createSwitchFirstLevelNodeVersionLoadUnit(
    input: CreateEditorSwitchFirstLevelNodeVersionLoadUnitInput,
  ): ModelAssetNode {
    return createSwitchFirstLevelNodeVersionLoadUnitFromService({
      ...input,
      backendBaseUrl: apiConfig.baseUrl,
    })
  }

  /**
   * 创建缺失一级子节点补加载 load unit。
   *
   * 设计说明：缺失节点补加载与普通替换共享同一后端地址注入边界，Editor 只负责提供当前模型与目标节点。
   *
   * @param input 不含后端地址的缺失节点补加载输入。
   * @returns 可交给后端加载准备器消费的项目模型 load unit。
   */
  function createAppendMissingFirstLevelNodeVersionLoadUnit(
    input: CreateEditorAppendMissingFirstLevelNodeVersionLoadUnitInput,
  ): ModelAssetNode {
    return createAppendMissingFirstLevelNodeVersionLoadUnitFromService({
      ...input,
      backendBaseUrl: apiConfig.baseUrl,
    })
  }

  /**
   * 为本次项目模型文件预读创建字节缓存回滚函数。
   *
   * 设计说明：文件字节缓存服务于当前已提交的项目模型树；快速切换、取消或解析失败时，
   * 本次预读写入的新 Uint8Array 不能继续挂在会话缓存中。这里在写入前记录旧值，
   * 失败路径恢复旧值或删除新 key，成功路径则丢弃该回滚函数并交给 prune 统一裁剪。
   *
   * @param fileReadResults 本次准备写入缓存的文件读取结果。
   * @returns 可在失败或取消路径恢复缓存状态的函数。
   */
  function createProjectModelFileByteCacheRollback(
    fileReadResults: readonly ProjectNodeFileReadResult[],
  ): () => void {
    const previousBytesByKey = new Map<string, Uint8Array | null>()

    for (const result of fileReadResults) {
      const key = buildProjectAssetFileIdentityKey(result.file)
      if (previousBytesByKey.has(key)) {
        continue
      }
      previousBytesByKey.set(key, projectModelFileBytesByIdentityKey.get(key) ?? null)
    }

    return () => {
      for (const [key, previousBytes] of previousBytesByKey) {
        if (previousBytes) {
          projectModelFileBytesByIdentityKey.set(key, previousBytes)
        } else {
          projectModelFileBytesByIdentityKey.delete(key)
        }
      }
    }
  }

  /**
   * 收集当前项目模型树仍需要保留的文件字节缓存 key。
   *
   * @param root 当前已经成功提交到编辑器的项目模型根节点。
   * @returns 当前 root 可达的版本文件身份 key 集合。
   */
  function collectProjectModelFileByteCacheKeys(root: ModelAssetNode): Set<string> {
    const retainedKeys = new Set<string>()

    for (const task of collectProjectNodeFileReadTasks(collectModelAssetNodesPreorder(root))) {
      retainedKeys.add(buildProjectAssetFileIdentityKey(task.file))
    }

    return retainedKeys
  }

  /**
   * 将项目模型文件字节缓存裁剪到当前已提交模型树。
   *
   * 设计说明：该缓存持有原始 Uint8Array，用于局部版本切换、删除快照与恢复时重建模型。
   * 根版本或节点版本切换成功后，旧提交/旧子树的文件不再属于当前编辑上下文，必须释放引用，
   * 否则连续切换大装配会让历史文件字节持续留在内存中。
   *
   * @param root 当前已经成功提交到编辑器的项目模型根节点。
   */
  function pruneProjectModelFileByteCache(root: ModelAssetNode): void {
    const retainedKeys = collectProjectModelFileByteCacheKeys(root)

    for (const key of projectModelFileBytesByIdentityKey.keys()) {
      if (!retainedKeys.has(key)) {
        projectModelFileBytesByIdentityKey.delete(key)
      }
    }
  }

  /**
   * 从项目模型字节缓存生成节点文件读取表。
   *
   * @param nodes 需要重新解析包对象的节点集合。
   * @returns nodeKey/role/file 组合 key 到字节的映射。
   */
  function buildProjectNodeFileBytesFromCacheByReadKey(
    nodes: readonly ModelAssetNode[],
  ): Map<string, Uint8Array> {
    const bytesByFileReadKey = new Map<string, Uint8Array>()

    for (const task of collectProjectNodeFileReadTasks(nodes)) {
      const bytes = projectModelFileBytesByIdentityKey.get(buildProjectAssetFileIdentityKey(task.file))
      if (!bytes) {
        throw new Error(`项目模型文件字节缓存缺失：nodeKey=${task.nodeKey}, fileId=${task.file.fileId}, role=${task.role}`)
      }

      bytesByFileReadKey.set(
        buildProjectNodeFileReadKey(task.nodeKey, task.file, task.role),
        new Uint8Array(bytes),
      )
    }

    return bytesByFileReadKey
  }

  /**
   * 从预读字节中解析当前计划涉及的节点几何包。
   *
   * @param handle 当前加载请求归属。
   * @param nodes 本次计划需要解析的节点列表。
   * @param bytesByFileReadKey 已下载文件字节索引。
   * @param completedPackageResults 调用方持有的完成列表，失败时用于回滚释放。
   * @param brepRegistrar 可选的 BRep 注册器。
   * @param completeGlbPackageSnapshotsByNodeKey complete loading unit 的预分析快照索引。
   * @returns 解析完成的节点包结果；请求被取消时返回 null。
   */
  async function loadProjectModelPackageResultsWithHandle(
    handle: ImportedModelRequestHandle,
    nodes: readonly ModelAssetNode[],
    bytesByFileReadKey: ReadonlyMap<string, Uint8Array>,
    completedPackageResults: LoadedProjectModelPackageResult[],
    brepRegistrar?: BRepRegistrar,
    completeGlbPackageSnapshotsByNodeKey: ReadonlyMap<
      string,
      PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
    > = new Map(),
  ): Promise<readonly LoadedProjectModelPackageResult[] | null> {
    const packageLoadTasks = collectProjectNodeFileReadTasks(nodes)
      .filter((task) => task.role === 'node-package')
      .map((task) => ({
        node: nodes.find((node) => node.nodeKey === task.nodeKey),
        nodeKey: task.nodeKey,
        packageFile: task.file,
        packageBytes: bytesByFileReadKey.get(buildProjectNodeFileReadKey(
          task.nodeKey,
          task.file,
          task.role,
        )),
      }))
    const loadedPackageResults = await mapWithConcurrencyLimit(
      packageLoadTasks,
      MAX_PROJECT_NODE_PACKAGE_LOAD_CONCURRENCY,
      async (task): Promise<LoadedProjectModelPackageResult | null> => {
        if (!task.node) {
          throw new Error(`项目模型节点包缺少节点语义：nodeKey=${task.nodeKey}`)
        }
        if (!task.packageBytes) {
          throw new Error(`项目模型节点包缺少预读字节：nodeKey=${task.nodeKey}, fileId=${task.packageFile.fileId}`)
        }
        const loadedPackage = await loadProjectNodePackageWithHandle(
          handle,
          task.node,
          task.packageFile,
          task.packageBytes,
          brepRegistrar,
          completeGlbPackageSnapshotsByNodeKey.get(task.nodeKey),
        )
        if (handle.transaction.isAborted) {
          if (loadedPackage?.result) {
            disposeImportedModel(loadedPackage.result)
          }
          return null
        }
        if (!loadedPackage) {
          throw new Error(`项目模型节点包加载失败：nodeKey=${task.nodeKey}, fileId=${task.packageFile.fileId}`)
        }
        const packageResult: LoadedProjectModelPackageResult = {
          nodeKey: task.nodeKey,
          file: task.packageFile,
          result: loadedPackage.result,
          puppyMateDocument: loadedPackage.puppyMateDocument,
          puppyAssemblyDocument: loadedPackage.puppyAssemblyDocument,
          puppyDatumExtension: loadedPackage.puppyDatumExtension,
          //------ 草图离线包定义持久化 开始------
          puppySketchExtension: loadedPackage.puppySketchExtension,
          puppySketchEntityReferenceExtension: loadedPackage.puppySketchEntityReferenceExtension,
          //------ 草图离线包定义持久化 结束------
          //------ 特征离线包定义持久化 开始------
          puppyFeatureExtension: loadedPackage.puppyFeatureExtension,
          //------ 特征离线包定义持久化 结束------
          puppySectionViewExtension: loadedPackage.puppySectionViewExtension,
          puppyLinearComponentPatternDocument: loadedPackage.puppyLinearComponentPatternDocument,
          topologySource: loadedPackage.topologySource,
        }
        completedPackageResults.push(packageResult)
        return packageResult
      },
    )

    if (handle.transaction.isAborted || loadedPackageResults.some((result) => !result)) {
      return null
    }

    return loadedPackageResults.filter((result): result is LoadedProjectModelPackageResult => Boolean(result))
  }

  /**
   * 恢复仍由当前 Worker GeometryState 支撑的 WorkAssembly。
   *
   * 这里故意同时校验版本注册表和活动 Worker 版本。注册表中即使未来保留了历史快照，
   * 只要 Worker 已切走就不能直接挂载，否则会出现可见但无法选择、拖动或提交的模型。
   *
   * @param root 本次 OpenProject 映射得到的根节点。
   * @param versionKey 本次请求的完整归档版本。
   * @param tabId 根模型 tab ID。
   * @param scene 当前 Viewer 场景。
   * @param nextContextIdentity 本次请求的项目上下文身份。
   * @returns 是否已经完成无归档读取的恢复。
   */
  function restoreActiveWorkAssemblySnapshot(
    root: ModelAssetNode,
    tabId: string,
    scene: THREE.Scene,
    nextContextIdentity: ProjectModelContextIdentity,
  ): boolean {
    const snapshot = completeWorkAssemblySession.getActiveSnapshot({
      root,
      archiveFile: loadableOpenProjectArchiveFile,
    })
    if (!snapshot) {
      return false
    }

    currentProjectModelLoadResult = snapshot.projectModelResult
    replaceProjectModelPackageResultCache(
      snapshot.packageResults as readonly LoadedProjectModelPackageResult[],
    )
    loadedResultByTabId.set(tabId, snapshot.importedResult)
    glbHierarchyByTabId.set(tabId, resolveImportedModelHierarchy(snapshot.importedResult))
    initializeAssemblyDraftDocumentStoreFromRoot({
      root: snapshot.projectModelResult.root,
      loadResult: snapshot.projectModelResult,
      store: assemblyDraftDocumentStore,
    })
    currentProjectModelContextIdentity = nextContextIdentity

    if (!restoreTabFromCache(tabId, scene)) {
      return false
    }

    logger.info('[GlbLoader] complete WorkAssembly 命中，跳过后端补树、归档读取与 GLB 实例化', {
      rootNodeKey: root.nodeKey,
      repositoryId: snapshot.versionKey.repositoryId,
      commitId: snapshot.versionKey.commitId,
    })
    return true
  }

  async function loadProjectRootTab(root: ModelAssetNode): Promise<void> {
    const { tabId, label } = ensureProjectRootTab(root)
    const nextProjectModelContextIdentity = createProjectRootContextIdentity(root)
    const supportsCompleteWorkAssembly = completeWorkAssemblySession.supportsRoot({
      root,
      archiveFile: loadableOpenProjectArchiveFile,
    })
    const sameProjectModelContext = isSameProjectModelContextIdentity(
      currentProjectModelContextIdentity,
      nextProjectModelContextIdentity,
    )
    if (sameProjectModelContext && mountedGlbTabId.value === tabId && displayActivePartTabId.value === tabId) {
      return
    }

    const scene = options.getScene()
    if (!scene) {
      logger.error('[GlbLoader] Scene is not ready, cannot load project root tab')
      return
    }

    // 当前编辑态可能已经包含插入、删除或版本替换；它必须优先于初始不可变版本快照。
    if (sameProjectModelContext && loadedResultByTabId.has(tabId) && restoreTabFromCache(tabId, scene)) {
      return
    }

    if (restoreActiveWorkAssemblySnapshot(
      root,
      tabId,
      scene,
      nextProjectModelContextIdentity,
    )) {
      return
    }

    // complete WorkAssembly 请求只有真正成为 Registry builder owner 后才获取前台句柄。
    // 同版本等待者因此不会抢占并中止共享的 prepare/download/parse/commit 事务。
    let handle!: ImportedModelRequestHandle

    const abortAndClearOwnedLoading = (): void => {
      if (!handle.isCurrent()) {
        return
      }
      handle.abort()
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(tabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(tabId)
    }

    const completedPackageResults: LoadedProjectModelPackageResult[] = []
    let packageResults: readonly LoadedProjectModelPackageResult[] = []
    let composedResultForCleanup: ImportedModelResult | null = null
    let transactionCommitted = false
    let fullDocumentRuntimeCommitted = false
    let runtimeSwitchSession: ActiveProjectRootRuntimeSwitchSession | null = null
    const previousResults = collectUniqueImportedModelResults(loadedResultByTabId.values())
    const previousPackageResults = collectProjectModelPackageImportedResults()
    const previousActiveWorkAssemblySnapshot = completeWorkAssemblySession.getActiveSnapshot()
    let restoreProjectModelFileByteCache: (() => void) | null = null

    const shouldAbortProjectRootRuntimeLoad = (): boolean =>
      handle.transaction.isAborted || !handle.isCurrent() || isDisposed

    const abortStagedRuntimeAndClearLoading = async (errorContext: string): Promise<void> => {
      await tryAbortProjectRootRuntimeSwitchSession(runtimeSwitchSession, errorContext)
      if (!handle.transaction.isAborted) {
        handle.abort()
      }
      partTabsStore.setTabLoading(tabId, false)
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
    }

    let builtCurrentWorkAssembly = false
    const buildAndCommitProjectRoot = async (
      workAssemblyBuildContext?: PuppyCADWeb.CompleteWorkAssemblyBuildContext,
    ): Promise<PuppyCADWeb.CompleteWorkAssemblyPublishInput> => {
      builtCurrentWorkAssembly = true
      clearImportedModelInteractionState()
      abortCurrentInsertionHandle()
      handle = requestRegistry.acquire('tab-switch', {
        scope: TAB_REQUEST_SCOPE,
        cooperative: true,
      })
      pendingPartTabId.value = tabId
      options.showLoading(`正在加载项目模型：${label}`, tabId)
      partTabsStore.setTabLoading(tabId, true)
      featureTreeStore.isTreeLoading = true
      try {
      runtimeSwitchSession = await beginProjectRootDocumentRuntimeSwitch(nextProjectModelContextIdentity)
      if (shouldAbortProjectRootRuntimeLoad()) {
        await abortStagedRuntimeAndClearLoading('project-root early abort after runtime switch begin')
        throw new ImportedModelLoadAbortedError()
      }

      const versionFileReader = createAuthorizedVersionFileReader({
        getAuthorizationHeader: resolveVersionFileAuthorizationHeader,
        refreshAuthorizationHeader: refreshVersionFileAuthorizationHeader,
      })
      const preparedLoad = await prepareProjectModelBackendLoad({
        planInput: { kind: 'root-load', root },
        reader: versionFileReader,
        fileCache: createProjectModelFileByteCacheAdapter(),
        nodeReloadAdapter: options.projectRootNodeReloadAdapter,
        backendBaseUrl: apiConfig.baseUrl,
      })
      const plan = preparedLoad.plan
      const projectModelLoadResult = preparedLoad.loadResult
      const restoredRoot = projectModelLoadResult.root
      const cacheFileReadResults = preparedLoad.fileReadResults

      if (shouldAbortProjectRootRuntimeLoad()) {
        await abortStagedRuntimeAndClearLoading('project-root early abort after load execution')
        throw new ImportedModelLoadAbortedError()
      }

      const rootPuppyPatch = preparedLoad.rootPuppyPatch
      if (rootPuppyPatch.insertedNodeKeys.length > 0) {
        logger.info('[GlbLoader] root .puppy 装配补齐一级采纳版本节点', {
          tabId,
          insertedNodeKeys: rootPuppyPatch.insertedNodeKeys,
          skippedUnversionedNodeKeys: rootPuppyPatch.skippedUnversionedNodeKeys,
          invalidNodeKeys: rootPuppyPatch.invalidNodeKeys,
        })
      } else if (
        rootPuppyPatch.skippedUnversionedNodeKeys.length > 0
        || rootPuppyPatch.invalidNodeKeys.length > 0
      ) {
        logger.warn('[GlbLoader] root .puppy 装配存在无法补齐的一级节点', {
          tabId,
          skippedUnversionedNodeKeys: rootPuppyPatch.skippedUnversionedNodeKeys,
          invalidNodeKeys: rootPuppyPatch.invalidNodeKeys,
        })
      } else if (
        !options.projectRootNodeReloadAdapter
        && projectModelLoadResult.root.nodeType === 'assembly'
        && projectModelLoadResult.root.packageFiles.some((file) => (
          file.kind.trim().toLowerCase() === '.puppy'
          || file.fileId.trim().toLowerCase().endsWith('.puppy')
        ))
        && projectModelLoadResult.assemblyDocumentsByNodeKey.has(projectModelLoadResult.root.nodeKey)
      ) {
        logger.warn('[GlbLoader] root .puppy 装配跳过一级采纳版本补齐：缺少节点重载适配器', {
          tabId,
          rootNodeKey: projectModelLoadResult.root.nodeKey,
        })
      }

      restoreProjectModelFileByteCache = createProjectModelFileByteCacheRollback(cacheFileReadResults)
      cacheProjectModelFileReadResults(cacheFileReadResults)
      if (shouldAbortProjectRootRuntimeLoad()) {
        restoreProjectModelFileByteCache?.()
        restoreProjectModelFileByteCache = null
        await abortStagedRuntimeAndClearLoading('project-root early abort after file byte cache write')
        throw new ImportedModelLoadAbortedError()
      }
      const loadedPackageResults = await loadProjectModelPackageResultsWithHandle(
        handle,
        plan.nodesToLoad,
        buildProjectNodeFileBytesFromCacheByReadKey(plan.nodesToLoad),
        completedPackageResults,
        getProjectRootRuntimeSwitchHandle(runtimeSwitchSession).brepRegistrar,
        preparedLoad.completeGlbPackageSnapshotsByNodeKey,
      )
      if (!loadedPackageResults) {
        restoreProjectModelFileByteCache?.()
        restoreProjectModelFileByteCache = null
        await abortStagedRuntimeAndClearLoading('project-root early abort after package load')
        for (const result of collectUniqueImportedModelResults(completedPackageResults.map((item) => item.result))) {
          disposeImportedModel(result)
        }
        throw new ImportedModelLoadAbortedError()
      }
      packageResults = loadedPackageResults
      if (shouldAbortProjectRootRuntimeLoad()) {
        restoreProjectModelFileByteCache?.()
        restoreProjectModelFileByteCache = null
        await abortStagedRuntimeAndClearLoading('project-root early abort before compose')
        for (const result of collectUniqueImportedModelResults(completedPackageResults.map((item) => item.result))) {
          disposeImportedModel(result)
        }
        throw new ImportedModelLoadAbortedError()
      }

      const composedResult = composeProjectModelImportedResult({
        // cad-core 已经在加载事务中应用 `_assembly.json` 与 children_transform_list，
        // 组合 Three.js 场景必须消费恢复后的 root，否则子装配内部零件位姿会被原始树覆盖掉。
        root: restoredRoot,
        partName: label,
        packageResults,
      })
      const completeRuntimeContext = workAssemblyBuildContext?.resolveRuntimeContext({
        root: restoredRoot,
        archiveFile: loadableOpenProjectArchiveFile,
        projectModelResult: projectModelLoadResult,
        packageResults,
        preparedSnapshotsByNodeKey: preparedLoad.completeGlbPackageSnapshotsByNodeKey,
      })
      const committedProjectModelLoadResult = createCommittedProjectModelLoadResult(
        projectModelLoadResult,
        composedResult,
        completeRuntimeContext,
      )
      const sceneGraphSummary = summarizeProjectModelSceneGraph(composedResult.root)
      if (sceneGraphSummary.meshCount === 0 || sceneGraphSummary.renderableMeshCount === 0) {
        logger.warn('[GlbLoader] 项目模型组合完成但未发现可渲染 mesh', {
          tabId,
          nodeCount: plan.nodesToLoad.length,
          packageCount: packageResults.length,
          ...sceneGraphSummary,
        })
      }
      composedResultForCleanup = composedResult
      const loadedModel: LoadedImportedModel = {
        displayType: resolveProjectRootTabType(restoredRoot),
        result: composedResult,
        partAssetCache: caches.partAssetCache,
        importedBrepRuntimeCache: caches.importedBrepRuntimeCache,
        puppyMateDocument: resolvePuppyMateDocumentForProjectModelRoot(restoredRoot, packageResults),
        puppyDatumExtension: resolvePuppyDatumExtensionForProjectModelRoot(restoredRoot, packageResults)
          ?? createEmptyPuppyDatumExtension(),
        //------ 草图离线包定义持久化 开始------
        puppySketchExtension: resolvePuppySketchExtensionForProjectModelRoot(restoredRoot, packageResults),
        puppySketchEntityReferenceExtension:
          resolvePuppySketchEntityReferenceExtensionForProjectModelRoot(restoredRoot, packageResults),
        //------ 草图离线包定义持久化 结束------
        //------ 特征离线包定义持久化 开始------
        puppyFeatureExtension: resolvePuppyFeatureExtensionForProjectModelRoot(restoredRoot, packageResults),
        //------ 特征离线包定义持久化 结束------
        puppySectionViewExtension: resolvePuppySectionViewExtensionForProjectModelRoot(
          restoredRoot,
          packageResults,
        ),
        puppyLinearComponentPatternDocument: packageResults.find((item) => (
          item.nodeKey === restoredRoot.nodeKey
        ))?.puppyLinearComponentPatternDocument
          ?? createEmptyPuppyLinearComponentPatternDocument(),
        projectModelPuppyMateDocuments: resolveProjectModelPuppyMateDocuments(restoredRoot, packageResults),
      }

      if (shouldAbortProjectRootRuntimeLoad()) {
        restoreProjectModelFileByteCache?.()
        restoreProjectModelFileByteCache = null
        await abortStagedRuntimeAndClearLoading('project-root stale after compose')
        disposeImportedModel(composedResult)
        composedResultForCleanup = null
        handle.release()
        throw new ImportedModelLoadAbortedError()
      }

      if (workAssemblyBuildContext) {
        workAssemblyBuildContext.validateDelivery({
          root: restoredRoot,
          archiveFile: loadableOpenProjectArchiveFile,
          projectModelResult: committedProjectModelLoadResult,
          packageResults,
          importedResult: composedResult,
        })
      }

      await commitProjectRootRuntimeSwitchSession(runtimeSwitchSession)
      fullDocumentRuntimeCommitted = true
      handle.transaction.commit()
      transactionCommitted = true
      commitPartTabSwitch(tabId, loadedModel, scene, {
        rootDocumentType: resolveProjectRootTabType(restoredRoot),
      })
      currentProjectModelLoadResult = committedProjectModelLoadResult
      replaceProjectModelPackageResultCache(packageResults)
      clearProjectModelRuntimeStateForRootSwitch(restoredRoot.nodeKey)
      pruneProjectModelFileByteCache(restoredRoot)
      restoreProjectModelFileByteCache = null
      initializeAssemblyDraftDocumentStoreFromRoot({
        root: restoredRoot,
        loadResult: committedProjectModelLoadResult,
        store: assemblyDraftDocumentStore,
      })
      currentProjectModelContextIdentity = nextProjectModelContextIdentity
      composedResultForCleanup = null
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(tabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(tabId)
      handle.release()
      return Object.freeze({
        root: restoredRoot,
        archiveFile: loadableOpenProjectArchiveFile,
        projectModelResult: committedProjectModelLoadResult,
        packageResults,
        importedResult: composedResult,
      })
      } catch (error) {
      if (fullDocumentRuntimeCommitted) {
        markFullDocumentRuntimeCommitRequiresReload(error)
      }
      await tryAbortProjectRootRuntimeSwitchSession(runtimeSwitchSession, 'project-root catch')
      restoreProjectModelFileByteCache?.()
      restoreProjectModelFileByteCache = null
      if (composedResultForCleanup) {
        if (transactionCommitted) {
          disposeCommittedBackgroundImportedModelResult(
            composedResultForCleanup,
            'project-root committed cleanup',
          )
          disposeFailedProjectModelPackageResults(
            completedPackageResults,
            transactionCommitted,
            'project root load package cleanup',
          )
        } else {
          // compose 会把节点包 Object3D 接到 composed root 下；未提交失败时只释放 composed root，避免同一资源被包清理再次 full dispose。
          const hasIndependentPackageScopes = composedResultForCleanup.kind === 'composed-assembly'
            && Boolean(composedResultForCleanup.resourceScope)
          disposeImportedModel(composedResultForCleanup)
          if (hasIndependentPackageScopes) {
            disposeFailedProjectModelPackageResults(
              completedPackageResults,
              transactionCommitted,
              'project root load scoped package cleanup',
            )
          }
        }
        composedResultForCleanup = null
      } else {
        disposeFailedProjectModelPackageResults(
          completedPackageResults,
          transactionCommitted,
          'project root load package cleanup',
        )
      }
      if (isImportedModelLoadAbortedError(error) || handle.transaction.isAborted || !handle.isCurrent()) {
        partTabsStore.setTabLoading(tabId, false)
        if (pendingPartTabId.value === tabId) {
          pendingPartTabId.value = null
        }
        if (handle.isCurrent()) {
          handle.abort()
        }
        throw error
      }
      logger.error(`[GlbLoader] Project root load failed: ${tabId}`, error)
      abortAndClearOwnedLoading()
      const failureInput = createProjectModelLoadFailureInput(error, () => loadProjectRootTab(root))
      if (options.handleProjectModelLoadFailure) {
        try {
          await options.handleProjectModelLoadFailure(failureInput)
        } catch (handlerError) {
          logger.error('[GlbLoader] Project root load failure handler failed', handlerError)
          options.showError(`${failureInput.title}：${failureInput.errorDetail}`)
        }
        throw error
      }
      options.showError(`${failureInput.title}：${failureInput.errorDetail}`)
      throw error
      }
    }

    try {
      let committedInput: PuppyCADWeb.CompleteWorkAssemblyPublishInput
      let replacedActiveWorkAssembly: boolean
      if (supportsCompleteWorkAssembly) {
        const resolved = await completeWorkAssemblySession.resolveRoot(
          {
            root,
            archiveFile: loadableOpenProjectArchiveFile,
          },
          async (buildContext) => buildAndCommitProjectRoot(buildContext),
        )
        committedInput = {
          root: resolved.snapshot.projectModelResult.root,
          archiveFile: loadableOpenProjectArchiveFile,
          projectModelResult: resolved.snapshot.projectModelResult,
          packageResults: resolved.snapshot.packageResults,
          importedResult: resolved.snapshot.importedResult,
        }
        replacedActiveWorkAssembly = resolved.replacedActiveSnapshot

        if (!builtCurrentWorkAssembly) {
          // 同版本 in-flight 或已完成快照命中时，本请求没有取得前台句柄或模型资源，只恢复已发布交付物。
          restoreActiveWorkAssemblySnapshot(
            root,
            tabId,
            scene,
            nextProjectModelContextIdentity,
          )
          return
        }
      } else {
        committedInput = await buildAndCommitProjectRoot()
        replacedActiveWorkAssembly = completeWorkAssemblySession.invalidate()
      }

      const replacedWorkAssemblyOwnedResults = replacedActiveWorkAssembly && previousActiveWorkAssemblySnapshot
        ? new Set<ImportedModelResult>([
          previousActiveWorkAssemblySnapshot.importedResult,
          ...previousActiveWorkAssemblySnapshot.packageResults.map((item) => item.result),
        ])
        : null
      disposeVersionPreviewReplacedResults(
        replacedWorkAssemblyOwnedResults
          ? previousResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
          : previousResults,
        committedInput.importedResult,
      )
      disposeReplacedProjectModelPackageResults(
        replacedWorkAssemblyOwnedResults
          ? previousPackageResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
          : previousPackageResults,
        committedInput.packageResults,
      )
    } catch (error) {
      // builder owner 已经完成运行时回滚、资源释放与用户可见错误处理。
      logger.error('[GlbLoader] switch active project root WorkAssembly failed', error)
      return
    }
  }

  /**
   * 释放已经提交 package cache、但尚未发布到当前装配 revision 的 Undo 恢复候选。
   *
   * complete 候选由聚合作用域释放渲染资源；legacy/cached-part 候选只清实例关系，
   * 再由 package disposer 按各自 cache owner 契约收尾，避免共享 GPU 资源被重复销毁。
   *
   * @param restored 已完成 package transaction 的恢复候选。
   * @param errorContext 清理日志上下文。
   * @returns 清理过程中收集到的全部异常。
   */
  function disposeCommittedAssemblyRestoreCandidate(
    restored: RestoreAssemblyDeletedObjectsResult,
    errorContext: string,
  ): readonly unknown[] {
    const cleanupErrors: unknown[] = []
    restored.composedResult.root.removeFromParent()
    try {
      if (restored.composedResult.resourceScope) {
        disposeImportedModel(restored.composedResult)
      } else {
        disposeImportedModelInstance(restored.composedResult)
      }
    } catch (cleanupError) {
      cleanupErrors.push(cleanupError)
    }
    try {
      disposeFailedProjectModelPackageResults(restored.packageResults, true, errorContext)
    } catch (cleanupError) {
      cleanupErrors.push(cleanupError)
    }
    return cleanupErrors
  }

  /**
   * 收集当前项目模型包缓存的完整交付记录。
   *
   * @returns 保持节点缓存顺序的包结果列表。
   */
  function collectProjectModelPackageResults(): readonly LoadedProjectModelPackageResult[] {
    return Array.from(projectModelPackageResultByNodeKey.values()).flat()
  }

  /**
   * 局部场景与 Worker 提交后，把可变工作树重新发布为当前 WorkAssembly revision。
   *
   * @param input 已完成局部提交的完整项目交付物。
   */
  function prepareScopedWorkAssemblyRevision(input: {
    readonly root: ModelAssetNode
    readonly projectModelResult: ProjectModelLoadResult
    readonly packageResults: readonly LoadedProjectModelPackageResult[]
    readonly importedResult: ImportedModelResult
  }): PuppyCADWeb.PreparedCompleteWorkAssemblyRevision | null {
    return completeWorkAssemblySession.prepareRevision({
      root: input.root,
      archiveFile: loadableOpenProjectArchiveFile,
      projectModelResult: input.projectModelResult,
      packageResults: input.packageResults,
      importedResult: input.importedResult,
    })
  }

  unsubscribeAssemblyModelCache = caches.assemblyModelCache.subscribe(() => {
    syncPartTabsWithImportedModelCaches()
  })

  unsubscribePartAssetCache = caches.partAssetCache.subscribe(() => {
    syncPartTabsWithImportedModelCaches()
  })

  /**
   * 初始化 CAD 会话并读取 GLB 文件列表。
   *
   * @param projectId OpenProject 会话上下文，或仅包含项目 ID 的旧字符串入参。
   * @returns 项目显示名称。
   */
  async function initSessionAndLoadFiles(
    projectId: OpenProjectSessionSource,
    initOptions: EditorProjectSessionLoadOptions = {},
  ): Promise<string> {
    const signal = initOptions.signal
    assertProjectModelLoadSignalActive(signal)
    const openProjectInput = normalizeOpenProjectSessionSource(projectId)
    currentProjectId = openProjectInput.projectIdText
    const sessionInitRequestId = beginOpenProjectSessionInit({
      clearRootCache: true,
      blockStoreFallback: true,
    })
    if (signal) {
      await cadSessionStore.initSession(openProjectInput, { signal })
    } else {
      await cadSessionStore.initSession(openProjectInput)
    }
    assertProjectModelLoadSignalActive(signal)
    assertOpenProjectSessionInitCurrent(sessionInitRequestId)

    if (cadSessionStore.error) {
      options.showError(`会话初始化失败: ${cadSessionStore.error}`)
      return '未命名项目'
    }

    await resolveCurrentLoadableProjectRootNode({
      isCurrent: () => sessionInitRequestId === openProjectSessionInitRequestId
        && !signal?.aborted,
    })
    assertProjectModelLoadSignalActive(signal)
    assertOpenProjectSessionInitCurrent(sessionInitRequestId)

    const projectModelRoot = resolveCurrentProjectModelRoot()
    if (projectModelRoot) {
      const rootTab = ensureProjectRootTab(projectModelRoot)
      editingFocusManager.setCurrentAssemblyName(rootTab.label)
      return rootTab.label
    }

    const glbFiles = cadSessionStore.getGlbFiles()
    if (glbFiles.length > 0) {
      partTabsStore.initFromGlbFiles(glbFiles, cadSessionStore.projectFiles)
      syncPartTabsWithImportedModelCaches()
    }

    if (cadSessionStore.projectFiles.length > 0) {
      const rootId = cadSessionStore.projectFiles[0].fileId
      const rootName = rootId.split('/').filter(Boolean).at(-1) ?? '未命名项目'
      editingFocusManager.setCurrentAssemblyName(rootName)
      return rootName
    }

    editingFocusManager.setCurrentAssemblyName('未命名项目')
    return '未命名项目'
  }

  /**
   * 依赖解析开始前 fire：把 uuid 翻译为 tabId 并点亮 per-tab spinner。
   * 具体 refcount / 已知类型 / 延后到 cache publish 的协调规则交给
   * `ChildTabLoadingCoordinator` 统一维护。
   */
  function handleChildResolveStart(uuid: string): void {
    childTabLoadingCoordinator.handleResolveStart(uuid)
  }

  /**
   * 与 `handleChildResolveStart` 成对。loader 在 try/finally 中调用，异常路径也保证 fire。
   * 具体规则：
   *   - `'staged'` 且 child 已有已知类型：立即清 spinner；
   *   - `'staged'` 且 child 仍未知类型：等待 cache subscription 把 type/icon 投影出来后再清，
   *     让 icon 回填与 spinner 归零保持同拍；
   *   - `'aborted'` / `'missing'`：立即清 spinner。
   */
  function handleChildResolveComplete(
    uuid: string,
    outcome: 'staged' | 'aborted' | 'missing',
  ): void {
    childTabLoadingCoordinator.handleResolveComplete(uuid, outcome)
  }

  /**
   * 在给定 request handle 下加载导入模型。
   *
   * 返回规则（协作式抢占契约）：
   *   - 成功：原样返回 `LoadedImportedModel`。调用方根据 `handle.isCurrent()` /
   *     `handle.transaction.isAborted` 自行决定 commit / dispose —— 这样被协作抢占的
   *     handle 仍可把 loader 成果 commit 到共享 cache 供下次命中。
   *   - loader 抛 abort-error：返回 null（transaction 已由 guard 推进到终态）。
   *   - 组件已 dispose：dispose result 并返回 null（已无挂载目标）。
   *   - 其它错误：原样抛出，由 caller 走错误分支。
   *
   * 注意：本函数**不再**因 `!handle.isCurrent() || handle.transaction.isAborted`
   * 自动 dispose result —— 那个决定由 caller 做（协作模式下想 commit，非协作模式下想
   * dispose）。
   */
  async function loadImportedModelWithHandle(
    handle: ImportedModelRequestHandle,
    file: FileInfo,
    loadInput?: {
      readonly downloadContextId: string
      readonly projectFiles: readonly FileInfo[]
      readonly version: string
      readonly readFileBytes?: ImportedModelFileBytesReader
      readonly brepRegistrar?: BRepRegistrar
    },
  ): Promise<LoadedImportedModel | null> {
    try {
      const brepRegistrar = loadInput?.brepRegistrar ?? resolveBRepRegistrar()
      const loaded = await loadImportedModel(
        loadInput?.downloadContextId ?? cadSessionStore.sessionId,
        file,
        {
          projectFiles: [...(loadInput?.projectFiles ?? resolveCurrentModelLoadFiles())],
          projectId: currentProjectId,
          version: loadInput?.version ?? resolveImportedModelCacheVersion(),
          assemblyModelCache: caches.assemblyModelCache,
          partAssetCache: caches.partAssetCache,
          assemblyManifestCache: caches.assemblyManifestCache,
          parsedGlbCache: caches.parsedGlbCache,
          importedBrepRuntimeCache: caches.importedBrepRuntimeCache,
          brepRegistrar,
          readFileBytes: loadInput?.readFileBytes,
          transaction: handle.transaction,
          requestRegistry,
          guard: { isStale: () => handle.transaction.isAborted },
          // 把依赖解析期间进入下载/解析阶段的子装配体以 tab spinner 可视化，
          // 让用户感知到"父 tab 加载的同时，其子 tab 也在下载"。
          onChildResolveStart: handleChildResolveStart,
          onChildResolveComplete: handleChildResolveComplete,
        },
      )
      // 组件已 dispose：无挂载目标，直接释放 result 资源。
      if (isDisposed) {
        if (loaded?.result) {
          try {
            disposeImportedModel(loaded.result)
          } catch (error) {
            logger.error('[GlbLoader] dispose stale load result failed', error)
          }
        }
        return null
      }
      return loaded
    } catch (error) {
      if (isImportedModelLoadAbortedError(error)) {
        return null
      }
      throw error
    }
  }

  /**
   * 预加载指定标签的导入模型（不挂载到场景）。
   *
   * 采用协作式抢占（`cooperative: true`）：
   *   - 如果本 preload 启动后被同 scope 的 tab-switch 抢占，preload 的 loader 会
   *     继续跑完，并在 await 回来时 commit 到共享 cache（`partAssetCache` /
   *     `assemblyModelCache`）——下次切回同一 tab 时可直接缓存命中；
   *   - 不挂载 scene，成功 commit 后按结果资源所有权释放，不能误释放共享 geometry/material。
   *
   * 依然 abort 当前 insertion handle —— insertion 不是协作语义，tab 上下文切换 =
   * 剥夺 insertion 提交资格（spec: "tab 切换或再次插入时，旧请求失去提交资格"）。
   */
  function preloadTabModel(tabId: string): Promise<LoadedImportedModel | null> {
    const file = cadSessionStore.getGlbFiles().find((item: FileInfo) => item.fileId === tabId)
    if (!file) {
      return Promise.resolve(null)
    }
    abortCurrentInsertionHandle()
    const handle = requestRegistry.acquire('tab-switch', {
      scope: TAB_REQUEST_SCOPE,
      cooperative: true,
    })
    return (async () => {
      try {
        const loaded = await loadImportedModelWithHandle(handle, file)
        if (loaded && !handle.transaction.isAborted) {
          // 成功路径（无论是否仍 current）：commit 把 staged assets 写入共享 cache。
          // 之后资源所有权通常已转移给 cache，preload 自身按结果类型释放未挂载结果。
          commitAndDisposeBackgroundImportedModelResult(handle, loaded, 'preload')
        } else if (loaded?.result && handle.transaction.isAborted) {
          // 显式 abort 路径：transaction 已回滚，result 尚未释放，这里补上。
          try {
            disposeImportedModel(loaded.result)
          } catch (error) {
            logger.error('[GlbLoader] preload dispose aborted result failed', error)
          }
        } else {
          // loader 抛 abort-error / null：transaction 已推进至终态，幂等 abort 兜底。
          handle.abort()
        }
        return loaded
      } catch (error) {
        handle.abort()
        logger.error(`[GlbLoader] preload failed: ${tabId}`, error)
        return null
      } finally {
        /**
         * preload 只能清理“自己独占”的 per-tab spinner。
         *
         * 关键场景：默认 tab 的 preload 尚未结束时，前台又发起了同 tab 的 slow switch。
         * 这时 `pendingPartTabId === tabId` 表示新的前台 handle 已接管该 tab 的 spinner；
         * 若 preload 的 finally 仍无条件 `setTabLoading(false)`，就会把前台转圈提前擦掉。
         */
        if (pendingPartTabId.value !== tabId) {
          partTabsStore.setTabLoading(tabId, false)
        }
      }
    })()
  }

  /**
   * 预加载默认标签的模型，并同步把设计树、标签栏和 viewport 都切到加载态。
   * 由 Editor.vue 在 Viewer 与 Worker registrar 就绪后调用；
   * 三处加载态会在 handlePartTabSelect 完成挂载后一起清除，保证视觉一致。
   */
  function preloadDefaultTabModel(): Promise<LoadedImportedModel | null> {
    if (resolveCurrentProjectModelRoot()) {
      return Promise.resolve(null)
    }
    const defaultTabId = partTabsStore.activeTabId
    if (!defaultTabId) {
      return Promise.resolve(null)
    }
    const tabLabel = partTabsStore.tabs.find((item) => item.id === defaultTabId)?.label ?? defaultTabId
    featureTreeStore.isTreeLoading = true
    partTabsStore.setTabLoading(defaultTabId, true)
    options.showLoading(`正在加载模型：${tabLabel}`, defaultTabId)
    return preloadTabModel(defaultTabId)
  }

  /**
   * 清理文档切换时的交互残留。
   *
   * 设计说明：
   * 1. 3D 交互态（选中、测量线、datum-slot、hover）由 Viewer 统一收口；
   * 2. 设计树选中投影在 web-client 侧显式清空，避免仅依赖 SelectionCleared 被动联动。
   */
  function clearImportedModelInteractionState(): void {
    const viewer = options.getViewer()
    viewer?.resetDocumentTransientInteraction?.()
    viewer?.setProgrammaticHoverTargets?.([])
    viewer?.clearImportedModelDatumSlotHighlight?.()
    viewer?.clearSelectionProgrammatically?.()
    featureTreeStore.clearSelection()
  }

  /**
   * 加载默认标签。
   * 默认使用 PartTabsStore 中当前激活的首个 GLB 标签。
   */
  async function loadDefaultTab(): Promise<void> {
    const projectModelRoot = resolveCurrentProjectModelRoot()
    if (projectModelRoot) {
      await loadProjectRootTab(projectModelRoot)
      return
    }
    const defaultTabId = partTabsStore.activeTabId
    if (defaultTabId) {
      await handlePartTabSelect(defaultTabId)
    }
  }

  /**
   * 局部应用项目模型加载结果的输入选项。
   */
  interface ApplyProjectModelLoadResultOptions {
    /** 需要局部替换的子树根节点 key；缺省时按整棵根模型应用。 */
    readonly targetNodeKey?: string
    /** 新增插入子树根节点 key；存在时按 insert-node 局部计划应用。 */
    readonly insertedNodeKey?: string
    /** 目标子树的父节点 key；局部替换计划需要它定位层级。 */
    readonly parentNodeKey?: string
    /** 用户可见的加载提示文案。 */
    readonly loadingText?: string
    /** 模型提交前必须一并完成的运行态写入；抛错时本次模型不进场景。 */
    readonly beforeCommit?: () => void
    /**
     * Worker 文档运行时提交钩子。
     *
     * 设计说明：仅 full-document 根切换入口会传入该钩子；局部 subtree apply 不传，
     * 避免把局部替换误升级为整文档 runtime switch。
     */
    readonly beforeModelCommit?: () => Promise<void> | void
    /** `beforeCommit` 已执行但后续提交失败时的回滚动作。 */
    readonly rollbackBeforeCommit?: () => void
    /** 项目包解析使用的 BRep registrar；full-document 切换时必须指向 staged runtime。 */
    readonly brepRegistrar?: BRepRegistrar
    /** 本次加载遮罩归属；缺省时继续使用根模型标签 ID，保持既有调用语义。 */
    readonly loadingOwner?: string
    /** 模型应用后是否由调用方继续持有 loading，用于把模型与设计树刷新合并为一个事务。 */
    readonly preserveLoadingAfterApply?: boolean
    /**
     * 是否延后设计树刷新给调用方统一执行。
     *
     * 设计说明：提交后项目根重载需要在一次性 replace 标记有效期内等待设计树刷新完成，
     * 因此不能让通用 tab 提交流程先发起普通的异步刷新。
     */
    readonly deferFeatureTreeRefresh?: boolean
    /** 本次项目模型读取的取消信号；用于快速切换历史版本时中止旧文件请求。 */
    readonly signal?: AbortSignal
    /** 后端加载准备阶段已经读取的 version-file 字节；apply 应复用它们，避免同一批 `_assembly.json` / `.puppy` 在场景应用前重复请求。 */
    readonly preparedFileReadResults?: readonly ProjectNodeFileReadResult[]
    /** 准备阶段已完成解包、选根与 occurrence 分析的完整 GLB 快照。 */
    readonly preparedCompleteGlbPackageSnapshotsByNodeKey?: ReadonlyMap<
      string,
      PuppyCADWeb.PreparedCompleteGlbPackageSnapshot
    >
    /**
     * 调用方在删除/切换旧子树前已经清理过悬挂 Mate 的根文档。
     *
     * 设计说明：子树版本切换不会重新下载 root `.puppy`，如果继续使用缓存包里的 root Mate 文档，
     * 会把切换前已经删除的旧子树 Mate 引用恢复回来。该字段让局部应用沿用 root reload 的“先恢复根文档”
     * 语义，同时以调用方刚刚确认并写入 controller 的清理结果作为 root baseline。
     */
    readonly cleanedRootMateDocument?: AssemblyMateDocumentJSON
    /** 确认阶段冻结、仅由 scoped 阵列事务消费的旧阵列 Group ID。 */
    readonly linearComponentPatternRemovalGroupObjectIds?: readonly string[]
    /** 内部递归调用已由 WorkAssembly Registry 取得当前版本 builder 所有权。 */
    readonly workAssemblyRegistryOwner?: boolean
    /** Registry builder 提供的 WorkAssembly 交付校验与 complete runtime context 能力。 */
    readonly workAssemblyBuildContext?: PuppyCADWeb.CompleteWorkAssemblyBuildContext
  }

  /**
   * 在项目子树完成场景、Worker、topology 与 Mate 提交后应用线性阵列差量。
   *
   * 设计说明：
   * 1. 旧阵列删除和新阵列恢复必须交给 cad-core Manager 一次性完成；
   * 2. 新阵列在 Mate 恢复求解后创建，才能读取新子树最终组件位姿；
   * 3. 设计树只在 Manager 成功后同步一次，避免中间态泄露给 UI。
   *
   * @param tabId 当前项目根标签 ID。
   * @param documents Finalizer 已整理到最终 occurrence/body 的新阵列文档。
   * @param removedGroupObjectIds 确认阶段冻结的旧阵列 Group ID。
   */
  function applyScopedProjectModelLinearComponentPatterns(
    tabId: string,
    documents: readonly PuppyCAD.ProjectModelSubtreeSemanticLinearComponentPatternDocument[],
    removedGroupObjectIds: readonly string[] = [],
  ): void {
    const persistenceDocuments = documents.map(item => item.document)
    const hasChanges = removedGroupObjectIds.length > 0
      || persistenceDocuments.some(document => document.records.length > 0)
    if (!hasChanges) {
      return
    }

    const viewer = options.getViewer()
    if (!viewer?.applyScopedPuppyLinearComponentPatternDocuments) {
      throw new Error('项目模型局部切换缺少线性零部件阵列事务入口。')
    }

    viewer.applyScopedPuppyLinearComponentPatternDocuments({
      removedGroupObjectIds,
      documents: persistenceDocuments,
    })
    syncLinearComponentPatternFeatureTree(
      viewer.linearComponentPatternManager?.getCommittedRecords() ?? [],
      {
        tabId,
        reason: LinearComponentPatternFeatureTreeSyncReason.ProjectModelSubtreeApply,
        deferRefresh: true,
      },
    )
  }

  /**
   * 在项目结构子树局部提交后重放当前 Mate 文件夹投影。
   *
   * 设计说明：Mate 文档恢复通知发生在模型子树提交之前，新装配节点当时可能尚未进入
   * FeatureTree；随后运行时子树替换又会清空 Mate 挂载索引。因此局部结构成功后必须以
   * Controller 的最终文档重放一次配合子树。完整刷新兜底会自行读取同一文档，不走本入口。
   *
   * @param tabId 当前项目根标签 ID。
   */
  function syncCurrentProjectModelMateFeatureTree(tabId: string): void {
    const mateDocument = options.getViewer()?.assemblyMateController?.getDocument?.()
    if (!mateDocument) {
      return
    }
    featureTreeStore.applyAssemblyMateDocument(tabId, mateDocument)
  }

  /**
   * 标签切换提交选项。
   */
  interface CommitPartTabSwitchOptions {
    /** 是否跳过默认设计树刷新，由上层业务在明确时机刷新。 */
    readonly deferFeatureTreeRefresh?: boolean
    /** 本次提交是否建立了新的项目根文档，以及该根文档的权威类型。 */
    readonly rootDocumentType?: PuppyPackageDocumentType
  }


  /**
   * 将项目模型加载结果提交到当前编辑器。
   *
   * 设计说明：该函数只负责几何包下载解析、模型组合和前台提交；调用方决定是否更新
   * `_assembly.json` 草稿。这样版本切换、插入零部件和整体快照切换可以共享同一条
   * 模型落地链路，但各自保留自己的业务校验。
   *
   * @param loadResult cad-core 已解析出的下一版项目模型结果。
   * @param applyOptions 局部替换或整棵替换选项。
   */
  async function applyProjectModelLoadResult(
    loadResult: ProjectModelLoadResult,
    applyOptions: ApplyProjectModelLoadResultOptions = {},
  ): Promise<ProjectModelLoadResult> {
    const root = loadResult.root
    const isFullDocumentRootApply = !applyOptions.targetNodeKey && !applyOptions.insertedNodeKey
    const { tabId, label } = ensureProjectRootTab(root)
    const scene = options.getScene()
    if (!scene) {
      throw new Error('场景尚未就绪，无法应用项目模型')
    }

    const supportsCompleteWorkAssembly = isFullDocumentRootApply
      ? completeWorkAssemblySession.supportsRoot({
        root,
        archiveFile: loadableOpenProjectArchiveFile,
      })
      : false
    if (supportsCompleteWorkAssembly && !applyOptions.workAssemblyRegistryOwner) {
      const previousResults = collectUniqueImportedModelResults(loadedResultByTabId.values())
      const previousPackageResults = collectProjectModelPackageImportedResults()
      const previousSnapshot = completeWorkAssemblySession.getActiveSnapshot()
      let builtCurrentVersion = false
      const resolved = await completeWorkAssemblySession.resolveRoot(
        {
          root,
          archiveFile: loadableOpenProjectArchiveFile,
        },
        async (buildContext) => {
          builtCurrentVersion = true
          const committedProjectModelLoadResult = await applyProjectModelLoadResult(loadResult, {
            ...applyOptions,
            workAssemblyRegistryOwner: true,
            workAssemblyBuildContext: buildContext,
          })
          const importedResult = loadedResultByTabId.get(tabId)
          if (!importedResult) {
            throw new Error(`complete WorkAssembly 提交后缺少根模型结果：rootNodeKey=${root.nodeKey}`)
          }
          return {
            root: committedProjectModelLoadResult.root,
            archiveFile: loadableOpenProjectArchiveFile,
            projectModelResult: committedProjectModelLoadResult,
            packageResults: collectProjectModelPackageResults(),
            importedResult,
          }
        },
      )

      if (!builtCurrentVersion) {
        restoreActiveWorkAssemblySnapshot(
          root,
          tabId,
          scene,
          createProjectRootContextIdentity(root),
        )
        return resolved.snapshot.projectModelResult
      }

      const replacedWorkAssemblyOwnedResults = resolved.replacedActiveSnapshot && previousSnapshot
        ? new Set<ImportedModelResult>([
          previousSnapshot.importedResult,
          ...previousSnapshot.packageResults.map((item) => item.result),
        ])
        : null
      disposeVersionPreviewReplacedResults(
        replacedWorkAssemblyOwnedResults
          ? previousResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
          : previousResults,
        resolved.snapshot.importedResult,
      )
      disposeReplacedProjectModelPackageResults(
        replacedWorkAssemblyOwnedResults
          ? previousPackageResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
          : previousPackageResults,
        resolved.snapshot.packageResults,
      )
      return resolved.snapshot.projectModelResult
    }

    if ((applyOptions.targetNodeKey || applyOptions.insertedNodeKey) && !currentProjectModelLoadResult) {
      throw new Error('当前项目模型尚未完成加载，无法局部应用模型变更')
    }

    clearImportedModelInteractionState()
    abortCurrentInsertionHandle()

    const handle = requestRegistry.acquire('tab-switch', {
      scope: TAB_REQUEST_SCOPE,
    })
    const previousResults = collectUniqueImportedModelResults(loadedResultByTabId.values())
    const previousPackageResults = collectProjectModelPackageImportedResults()
    const previousActiveWorkAssemblySnapshot = isFullDocumentRootApply
      ? completeWorkAssemblySession.getActiveSnapshot()
      : null
    const completedPackageResults: LoadedProjectModelPackageResult[] = []
    let composedResultForCleanup: ImportedModelResult | null = null
    let beforeCommitApplied = false
    let transactionCommitted = false
    let pendingResourceRevision: ImportedModelResourceRevision | null = null
    let restoreProjectModelFileByteCache: (() => void) | null = null
    let runtimeSwitchSession: ActiveProjectRootRuntimeSwitchSession | null = null
    let scopedRuntimeSwitchSession: ScopedSubtreeRuntimeSwitchSession | null = null
    let scopedSceneReplacementSnapshot: ScopedSubtreeSceneReplacementSnapshot | null = null
    let scopedNextSubtreeRoot: ModelAssetNode | null = null
    let scopedTargetAssemblyNodeId: string | null = null
    let scopedAssemblyDocumentNodeKey: string | undefined
    let scopedInsertionParentObject: THREE.Object3D | null = null
    let scopedInsertionParentHierarchy: GlbHierarchyNode | null = null
    let scopedInsertionSiblingIndex = 0
    let nextProjectModelContextIdentity: ProjectModelContextIdentity | null = null
    let brepRegistrar = applyOptions.brepRegistrar
    let beforeModelCommit = applyOptions.beforeModelCommit
    let fullDocumentRuntimeCommitted = false
    const loadingOwner = applyOptions.loadingOwner ?? tabId

    pendingPartTabId.value = tabId
    options.showLoading(applyOptions.loadingText ?? `正在加载项目模型：${label}`, loadingOwner)
    partTabsStore.setTabLoading(tabId, true)
    featureTreeStore.isTreeLoading = true

    const createApplyPlan = (): ReturnType<typeof createProjectModelLoadPlan> => {
      if (applyOptions.insertedNodeKey) {
        if (!applyOptions.parentNodeKey) {
          throw new Error(`插入项目模型缺少父节点 key：insertedNodeKey=${applyOptions.insertedNodeKey}`)
        }

        return createProjectModelLoadPlan({
          kind: 'insert-node',
          root,
          insertedNodeKey: applyOptions.insertedNodeKey,
          parentNodeKey: applyOptions.parentNodeKey,
        })
      }

      if (applyOptions.targetNodeKey) {
        if (!applyOptions.parentNodeKey) {
          throw new Error(`替换项目模型子树缺少父节点 key：targetNodeKey=${applyOptions.targetNodeKey}`)
        }

        return createProjectModelLoadPlan({
          kind: 'replace-subtree',
          root,
          targetNodeKey: applyOptions.targetNodeKey,
          parentNodeKey: applyOptions.parentNodeKey,
        })
      }

      return createProjectModelLoadPlan({ kind: 'root-load', root })
    }

    const releaseLoading = (): void => {
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(tabId, false)
      if (!applyOptions.preserveLoadingAfterApply) {
        featureTreeStore.isTreeLoading = false
        options.hideLoading(loadingOwner)
      }
      handle.release()
    }

    try {
      assertProjectModelLoadSignalActive(applyOptions.signal)
      let downloadPlanSnapshot: ReturnType<typeof createProjectModelLoadPlan> | null = null
      const getDownloadPlan = (): ReturnType<typeof createProjectModelLoadPlan> => {
        if (!downloadPlanSnapshot) {
          downloadPlanSnapshot = createApplyPlan()
        }

        return downloadPlanSnapshot
      }
      const versionFileReader = createAuthorizedVersionFileReader({
        getAuthorizationHeader: resolveVersionFileAuthorizationHeader,
        refreshAuthorizationHeader: refreshVersionFileAuthorizationHeader,
      })
      let preloadedFileReadResultsPromise: Promise<readonly ProjectNodeFileReadResult[]> | null = null
      const ensurePreloadedFileReadResults = (): Promise<readonly ProjectNodeFileReadResult[]> => {
        if (!preloadedFileReadResultsPromise) {
          preloadedFileReadResultsPromise = (async () => {
            const currentDownloadPlan = getDownloadPlan()
            const fileReadResults = applyOptions.preparedFileReadResults
              ?? await readProjectModelPlanFiles(
                currentDownloadPlan.nodesToLoad,
                versionFileReader,
                applyOptions.signal,
              )

            assertProjectModelLoadSignalActive(applyOptions.signal)
            const completedFileReadResults = await completeProjectModelFileReadResultsFromEmbeddedContainers(
              root,
              fileReadResults,
              new Set(projectModelFileBytesByIdentityKey.keys()),
            )
            assertProjectModelLoadSignalActive(applyOptions.signal)

            return completedFileReadResults
          })()
        }

        return preloadedFileReadResultsPromise
      }

      if (isFullDocumentRootApply) {
        nextProjectModelContextIdentity = createProjectRootContextIdentity(root)
        const hasExternalRuntimeOption = Boolean(applyOptions.brepRegistrar || applyOptions.beforeModelCommit)
        if (hasExternalRuntimeOption && (!applyOptions.brepRegistrar || !applyOptions.beforeModelCommit)) {
          throw new Error('项目根模型完整应用必须同时提供 staged brepRegistrar 与 runtime commit 钩子')
        }
        if (!hasExternalRuntimeOption) {
          runtimeSwitchSession = await beginProjectRootDocumentRuntimeSwitch(nextProjectModelContextIdentity)
          brepRegistrar = getProjectRootRuntimeSwitchHandle(runtimeSwitchSession).brepRegistrar
          beforeModelCommit = async () => {
            if (!runtimeSwitchSession) {
              throw new Error('项目根模型完整应用缺少 runtime switch 会话，无法提交 Worker 文档运行时')
            }
            await commitProjectRootRuntimeSwitchSession(runtimeSwitchSession)
          }
        }
        assertProjectModelLoadSignalActive(applyOptions.signal)
      }
      if (!isFullDocumentRootApply && applyOptions.targetNodeKey && !applyOptions.insertedNodeKey) {
        const currentLoadResult = currentProjectModelLoadResult
        if (!currentLoadResult) {
          throw new Error('当前项目模型尚未完成加载，无法开启局部子树 runtime switch')
        }
        const currentTargetNode = findProjectModelNodeByKey(
          currentLoadResult.root,
          applyOptions.targetNodeKey,
        )
        const currentTargetRuntimeBinding = resolveProjectModelRuntimeBinding(
          currentLoadResult.runtimeState,
          applyOptions.targetNodeKey,
        )
        scopedAssemblyDocumentNodeKey = currentTargetRuntimeBinding?.assemblyDocumentNodeKey
        /**
         * 旧 root `.puppy` 可能把一级装配展平为若干 hierarchy 后代，尚未形成显式 ModelAssetNode。
         * complete finalization 已建立运行时绑定时必须优先使用其中的项目作用域 occurrence；
         * 只有旧结果没有 binding 时才按显式模型节点或 targetNodeKey 兼容推导。
         */
        const currentTargetAssemblyNodeId = currentTargetRuntimeBinding?.runtimeAssemblyNodeId
          ?? (currentTargetNode
            ? resolveProjectModelNodeAssemblyNodeId(currentTargetNode)
            : normalizeAssemblyNodeId(applyOptions.targetNodeKey))
        const currentComposedResult = resolveCurrentComposedAssemblyResult(currentLoadResult.root.nodeKey)
        const targetRootObjectIds = new Set(
          currentTargetRuntimeBinding
            ? [currentTargetRuntimeBinding.componentRootObjectId]
            : resolveProjectNodeSceneBindingMap(currentComposedResult.sceneBindings)[
                applyOptions.targetNodeKey
              ] ?? [],
        )
        const primaryOldObject = resolveCurrentProjectModelSubtreeSceneObject({
          scene,
          viewer: options.getViewer(),
          composedResult: currentComposedResult,
          targetNodeKey: applyOptions.targetNodeKey,
          ...(currentTargetRuntimeBinding
            ? { targetObjectId: currentTargetRuntimeBinding.componentRootObjectId }
            : {}),
        })
        if (!primaryOldObject || primaryOldObject === scene) {
          throw new Error(`项目模型局部替换缺少可替换场景对象：targetNodeKey=${applyOptions.targetNodeKey}`)
        }
        const collectedOldObjects = collectRuntimeEmbeddedReplacementSceneObjects({
          scene,
          viewer: options.getViewer(),
          composedResult: currentComposedResult,
          targetRootObjectIds,
          targetAssemblyNodeId: currentTargetAssemblyNodeId,
          primaryObject: primaryOldObject,
        })
        const oldRootObjects = collectedOldObjects.filter((object, index, objects) => (
          !objects.some((candidate, candidateIndex) => (
            candidateIndex !== index && isObjectDescendantOf(object, candidate)
          ))
        ))
        if (oldRootObjects.length === 0) {
          throw new Error(`项目模型局部替换缺少旧子树对象边界：targetNodeKey=${applyOptions.targetNodeKey}`)
        }
        const oldTopologyScope = collectProjectModelReplacementSubtreeTopologyScope(
          currentComposedResult.hierarchy,
          currentTargetAssemblyNodeId,
          targetRootObjectIds,
        )
        const topologyRemovalPlan = createImportedModelTopologyScopedRemovalPlan({
          root: currentComposedResult.root,
          targetAssemblyNodeId: currentTargetAssemblyNodeId,
          removedObjectRoots: oldRootObjects,
          targetBodyBindingIds: oldTopologyScope.bodyBindingIds,
        })
        const nextSubtreeRoot = findProjectModelNodeByKey(root, applyOptions.targetNodeKey) ?? root
        const nextTargetAssemblyNodeId = resolveProjectModelNodeAssemblyNodeId(nextSubtreeRoot)
        if (nextTargetAssemblyNodeId !== currentTargetAssemblyNodeId) {
          throw new Error(
            '项目模型局部替换 occurrence 身份发生漂移：'
            + `targetNodeKey=${applyOptions.targetNodeKey}, currentAssemblyNodeId=${currentTargetAssemblyNodeId}, `
            + `nextAssemblyNodeId=${nextTargetAssemblyNodeId}`,
          )
        }
        scopedSceneReplacementSnapshot = {
          currentComposedResult,
          primaryOldObject,
          oldRootObjects,
        }
        scopedNextSubtreeRoot = nextSubtreeRoot
        scopedTargetAssemblyNodeId = nextTargetAssemblyNodeId
        scopedRuntimeSwitchSession = await beginScopedSubtreeRuntimeSwitch({
          currentRoot: currentLoadResult.root,
          targetNodeKey: applyOptions.targetNodeKey,
          targetAssemblyNodeId: nextTargetAssemblyNodeId,
          nextSubtreeRoot,
          topologyRemovalPlan,
        })
        brepRegistrar = scopedRuntimeSwitchSession.handle.brepRegistrar
        assertProjectModelLoadSignalActive(applyOptions.signal)
      }
      if (!isFullDocumentRootApply && applyOptions.insertedNodeKey && !applyOptions.targetNodeKey) {
        const currentLoadResult = currentProjectModelLoadResult
        if (!currentLoadResult) {
          throw new Error('当前项目模型尚未完成加载，无法开启新增子树 runtime switch')
        }
        if (!applyOptions.parentNodeKey) {
          throw new Error(`插入项目模型缺少父节点 key：insertedNodeKey=${applyOptions.insertedNodeKey}`)
        }

        const insertedNode = findProjectModelNodeByKey(root, applyOptions.insertedNodeKey)
        const nextParentNode = findProjectModelNodeByKey(root, applyOptions.parentNodeKey)
        if (!insertedNode || !nextParentNode) {
          throw new Error(
            '插入项目模型缺少新节点或父节点：'
            + `insertedNodeKey=${applyOptions.insertedNodeKey}, parentNodeKey=${applyOptions.parentNodeKey}`,
          )
        }
        if (resolveProjectModelRuntimeBinding(currentLoadResult.runtimeState, applyOptions.insertedNodeKey)) {
          throw new Error(
            `插入项目模型目标已存在运行时绑定，应使用 replace：insertedNodeKey=${applyOptions.insertedNodeKey}`,
          )
        }

        const currentComposedResult = resolveCurrentComposedAssemblyResult(currentLoadResult.root.nodeKey)
        const parentRuntimeBinding = resolveProjectModelRuntimeBinding(
          currentLoadResult.runtimeState,
          applyOptions.parentNodeKey,
        )
        const parentObject = resolveCurrentProjectModelSubtreeSceneObject({
          scene,
          viewer: options.getViewer(),
          composedResult: currentComposedResult,
          targetNodeKey: applyOptions.parentNodeKey,
          ...(parentRuntimeBinding
            ? { targetObjectId: parentRuntimeBinding.componentRootObjectId }
            : {}),
        })
        if (!parentObject) {
          throw new Error(
            `插入项目模型缺少父场景对象：parentNodeKey=${applyOptions.parentNodeKey}`,
          )
        }
        const parentHierarchy = findHierarchyPathByObjectId(
          currentComposedResult.hierarchy,
          parentObject.uuid,
        ).at(-1) ?? null
        if (!parentHierarchy) {
          throw new Error(
            `插入项目模型缺少父 hierarchy：parentNodeKey=${applyOptions.parentNodeKey}, objectId=${parentObject.uuid}`,
          )
        }

        const insertedAssemblyNodeId = resolveProjectModelNodeAssemblyNodeId(insertedNode)
        const topologyRemovalPlan = createImportedModelTopologyScopedRemovalPlan({
          root: currentComposedResult.root,
          targetAssemblyNodeId: insertedAssemblyNodeId,
          removedObjectRoots: [],
          targetBodyBindingIds: [],
        })
        scopedInsertionParentObject = parentObject
        scopedInsertionParentHierarchy = parentHierarchy
        scopedInsertionSiblingIndex = Math.max(
          0,
          nextParentNode.children.findIndex((child) => child.nodeKey === insertedNode.nodeKey),
        )
        scopedNextSubtreeRoot = insertedNode
        scopedTargetAssemblyNodeId = insertedAssemblyNodeId
        scopedRuntimeSwitchSession = await beginScopedSubtreeRuntimeSwitch({
          currentRoot: currentLoadResult.root,
          targetNodeKey: insertedNode.nodeKey,
          targetAssemblyNodeId: insertedAssemblyNodeId,
          nextSubtreeRoot: insertedNode,
          topologyRemovalPlan,
        })
        brepRegistrar = scopedRuntimeSwitchSession.handle.brepRegistrar
        assertProjectModelLoadSignalActive(applyOptions.signal)
      }
      const downloadPlan = getDownloadPlan()
      const preloadedFileReadResults = await ensurePreloadedFileReadResults()
      const composePlan = createProjectModelLoadPlan({ kind: 'root-load', root })
      // scoped 替换的兄弟包必须来自已提交 cache；这里禁止为 compose 再扩大下载/解析范围。
      const shouldMergeScopedPackageCache = Boolean(
        scopedRuntimeSwitchSession
        && (applyOptions.targetNodeKey || applyOptions.insertedNodeKey),
      )
      const packageLoadNodes = shouldMergeScopedPackageCache ? downloadPlan.nodesToLoad : composePlan.nodesToLoad
      const missingFileReadTasks = shouldMergeScopedPackageCache
        ? []
        : collectMissingProjectModelFileReadTasksFromCache(
          composePlan.nodesToLoad,
          preloadedFileReadResults,
          new Set(projectModelFileBytesByIdentityKey.keys()),
        )
      const missingFileReadResults = missingFileReadTasks.length > 0
        ? await readProjectModelFileReadTasks(
          missingFileReadTasks,
          versionFileReader,
          applyOptions.signal,
        )
        : []
      assertProjectModelLoadSignalActive(applyOptions.signal)
      const cacheFileReadResults = await completeProjectModelFileReadResultsFromEmbeddedContainers(
        root,
        [
          ...preloadedFileReadResults,
          ...missingFileReadResults,
        ],
        new Set(projectModelFileBytesByIdentityKey.keys()),
      )
      assertProjectModelLoadSignalActive(applyOptions.signal)
      restoreProjectModelFileByteCache = createProjectModelFileByteCacheRollback(cacheFileReadResults)
      cacheProjectModelFileReadResults(cacheFileReadResults)
      const loadedPackageResults = await loadProjectModelPackageResultsWithHandle(
        handle,
        packageLoadNodes,
        buildProjectNodeFileBytesFromCacheByReadKey(packageLoadNodes),
        completedPackageResults,
        brepRegistrar,
        applyOptions.preparedCompleteGlbPackageSnapshotsByNodeKey,
      )
      if (!loadedPackageResults || handle.transaction.isAborted || !handle.isCurrent()
        || applyOptions.signal?.aborted) {
        throw new ImportedModelLoadAbortedError()
      }

      if (shouldMergeScopedPackageCache && applyOptions.insertedNodeKey && scopedRuntimeSwitchSession) {
        const currentLoadResult = currentProjectModelLoadResult
        const insertedSubtreeRoot = scopedNextSubtreeRoot
        const insertedAssemblyNodeId = scopedTargetAssemblyNodeId
        const parentObject = scopedInsertionParentObject
        const parentHierarchy = scopedInsertionParentHierarchy
        if (
          !currentLoadResult
          || !insertedSubtreeRoot
          || !insertedAssemblyNodeId
          || !parentObject
          || !parentHierarchy
        ) {
          throw new Error(
            `项目模型新增子树缺少事务快照：insertedNodeKey=${applyOptions.insertedNodeKey}`,
          )
        }

        const currentComposedResult = resolveCurrentComposedAssemblyResult(
          currentLoadResult.root.nodeKey,
        )
        /**
         * 插入提交会原位替换 currentComposedResult.hierarchy；局部设计树协议需要保留
         * 插入前的父 hierarchy，用于验证父节点在前后版本中保持稳定。
         */
        const previousHierarchy = currentComposedResult.hierarchy
        if (findHierarchyNodeByProjectModelNodeKey(
          currentComposedResult.hierarchy,
          applyOptions.insertedNodeKey,
        )) {
          throw new Error(
            `项目模型新增子树 hierarchy 已存在，应使用 replace：insertedNodeKey=${applyOptions.insertedNodeKey}`,
          )
        }

        const packageResults = mergeProjectModelPackageResultsForSubtreeApply(
          root,
          applyOptions.insertedNodeKey,
          loadedPackageResults,
        )
        const resolvedNextMateDocuments = resolveProjectModelPuppyMateDocuments(
          root,
          packageResults,
          applyOptions.cleanedRootMateDocument,
        )
        let insertedComposedResult = composeProjectModelImportedResult({
          root: insertedSubtreeRoot,
          partName: insertedSubtreeRoot.displayName,
          packageResults: loadedPackageResults,
        })
        composedResultForCleanup = insertedComposedResult

        const loadedMateNodeKeys = new Set(
          loadedPackageResults
            .filter((packageResult) => Boolean(packageResult.puppyMateDocument))
            .map((packageResult) => packageResult.nodeKey),
        )
        const semanticMateDocuments = resolvedNextMateDocuments.flatMap((mateDocument) => {
          if (!mateDocument.restoreAsRootDocument && !loadedMateNodeKeys.has(mateDocument.assemblyNodeId)) {
            return []
          }
          const ownerAssemblyNodeId = mateDocument.restoreAsRootDocument
            ? resolveProjectModelNodeAssemblyNodeId(root)
            : (() => {
                const ownerNode = findProjectModelNodeByKey(
                  insertedSubtreeRoot,
                  mateDocument.assemblyNodeId,
                )
                if (!ownerNode) {
                  throw new Error(
                    `项目模型新增子树 Mate 文档缺少 owner 节点：nodeKey=${mateDocument.assemblyNodeId}`,
                  )
                }
                return resolveProjectModelNodeAssemblyNodeId(ownerNode)
              })()
          return [{
            ...mateDocument,
            assemblyNodeId: ownerAssemblyNodeId,
          }]
        })
        const packageSemanticScopes = loadedPackageResults.flatMap((packageResult) => {
          const ownerNode = findProjectModelNodeByKey(insertedSubtreeRoot, packageResult.nodeKey)
          if (!ownerNode) {
            throw new Error(
              `项目模型新增子树归档包缺少 occurrence owner：nodeKey=${packageResult.nodeKey}`,
            )
          }
          return [{
            ownerNodeKey: packageResult.nodeKey,
            ownerAssemblyNodeId: packageResult.nodeKey === insertedSubtreeRoot.nodeKey
              ? insertedAssemblyNodeId
              : resolveProjectModelNodeAssemblyNodeId(ownerNode),
            sourceHierarchy: packageResult.result.hierarchy,
          }]
        })
        const semanticLinearComponentPatternDocuments =
          createProjectModelSubtreeLinearComponentPatternDocuments(
            loadedPackageResults,
            packageSemanticScopes,
          )
        const fallbackFileId = insertedSubtreeRoot.packageFiles[0]?.fileId
          ?? insertedSubtreeRoot.assemblyManifest?.fileId
          ?? applyOptions.insertedNodeKey
        const finalizedSubtree = new ProjectModelSubtreeSemanticFinalizer().finalize({
          operation: {
            kind: 'insert',
            parentObjectId: parentHierarchy.objectId,
            parentPath: parentHierarchy.path,
            depth: parentHierarchy.depth + 1,
            siblingIndex: scopedInsertionSiblingIndex,
            localTransform: insertedSubtreeRoot.localTransform,
          },
          target: {
            projectNodeKey: applyOptions.insertedNodeKey,
            assemblyNodeId: insertedAssemblyNodeId,
            displayName: insertedSubtreeRoot.displayName,
            nodeType: insertedSubtreeRoot.nodeType,
          },
          subtreeImportedResult: insertedComposedResult,
          packageSemanticScopes,
          mateDocuments: semanticMateDocuments,
          linearComponentPatternDocuments: semanticLinearComponentPatternDocuments,
          fallbackFileId,
        })
        insertedComposedResult = finalizedSubtree.importedResult
        const writableComponentInstanceIds = Object.keys(
          insertedComposedResult.sceneBindings.byAssemblyNodeId,
        )
        /**
         * 仅恢复本次插入事务经过 Finalizer 重绑的 Mate 文档。范围外包仍保留在原始缓存中，
         * 但它们不是本次事务结果；再次回放会用旧 occurrence 覆盖当前根装配中的有效 Mate。
         */
        const nextMateDocuments = finalizedSubtree.mateDocuments
        resolveImportedModelHierarchy(insertedComposedResult)
        assertProjectModelLoadSignalActive(applyOptions.signal)

        const hierarchyInsertResult = insertHierarchySnapshot(
          currentComposedResult.hierarchy,
          {
            objectId: insertedComposedResult.hierarchy.objectId,
            parentObjectId: parentHierarchy.objectId,
            parentIndex: scopedInsertionSiblingIndex,
            node: insertedComposedResult.hierarchy,
          },
        )
        if (!hierarchyInsertResult.inserted) {
          throw new Error(
            '项目模型新增子树 hierarchy 父节点不存在：'
            + `insertedNodeKey=${applyOptions.insertedNodeKey}, parentObjectId=${parentHierarchy.objectId}`,
          )
        }
        if (!hasHierarchyAssemblyNodeId(hierarchyInsertResult.hierarchy, insertedAssemblyNodeId)) {
          throw new Error(
            `项目模型新增子树 hierarchy 身份未闭合：insertedNodeKey=${applyOptions.insertedNodeKey}`,
          )
        }

        const nextSceneBindings = mergeSceneBindingIndexes(
          currentComposedResult.sceneBindings,
          insertedComposedResult.sceneBindings,
        )
        const nextPersistenceStatus = packageResults.some((packageResult) => (
          packageResult.result.persistenceStatus === 'runtime-only'
        ))
          ? 'runtime-only'
          : undefined
        const nextSelectionIndex: ComposedAssemblyResult['selectionIndex'] = {
          byObject3DUuid: {
            ...currentComposedResult.selectionIndex.byObject3DUuid,
            ...insertedComposedResult.selectionIndex.byObject3DUuid,
          },
        }
        const finalizedProjectModelLoadResult = createCommittedProjectModelLoadResult(
          loadResult,
          {
            ...currentComposedResult,
            hierarchy: hierarchyInsertResult.hierarchy,
            persistenceStatus: nextPersistenceStatus,
            sceneBindings: nextSceneBindings,
            selectionIndex: nextSelectionIndex,
            partAssetsByUuid: {
              ...currentComposedResult.partAssetsByUuid,
              ...insertedComposedResult.partAssetsByUuid,
            },
            assemblyAssetsByUuid: {
              ...currentComposedResult.assemblyAssetsByUuid,
              ...insertedComposedResult.assemblyAssetsByUuid,
            },
          },
        )
        const runtimeState = new ProjectModelRuntimeStateResolver().append(
          currentLoadResult.runtimeState,
          finalizedProjectModelLoadResult.runtimeState,
        )
        const sceneBindings = [
          ...currentLoadResult.sceneBindings.filter((binding) => (
            !isProjectModelNodeKeyInSubtree(binding.nodeKey, applyOptions.insertedNodeKey!)
          )),
          ...finalizedProjectModelLoadResult.sceneBindings.filter((binding) => (
            isProjectModelNodeKeyInSubtree(binding.nodeKey, applyOptions.insertedNodeKey!)
          )),
        ]
        const committedProjectModelLoadResult: ProjectModelLoadResult = {
          ...finalizedProjectModelLoadResult,
          runtimeState,
          sceneBindings,
          designTree: buildProjectModelDesignTreeProjection({
            root: finalizedProjectModelLoadResult.root,
            sceneBindings,
          }),
        }
        const preparedWorkAssemblyRevision = prepareScopedWorkAssemblyRevision({
          root,
          projectModelResult: committedProjectModelLoadResult,
          packageResults,
          importedResult: currentComposedResult,
        })
        const insertedResourceScope = insertedComposedResult.resourceScope
        const resourceRevision = insertedResourceScope
          ? ImportedModelResourceRevision.prepare({
            currentOwner: currentComposedResult,
            replacementOwner: insertedComposedResult,
            replacedRoots: [],
          })
          : null
        pendingResourceRevision = resourceRevision

        applyOptions.beforeCommit?.()
        beforeCommitApplied = true
        assertProjectModelLoadSignalActive(applyOptions.signal)
        handle.transaction.commit()
        transactionCommitted = true

        insertObjectAtParentIndex(
          parentObject,
          insertedComposedResult.root,
          scopedInsertionSiblingIndex,
        )
        try {
          await commitScopedSubtreeRuntimeSwitchSession(scopedRuntimeSwitchSession)
        } catch (runtimeCommitError) {
          insertedComposedResult.root.removeFromParent()
          try {
            resourceRevision?.rollback()
          } catch (rollbackError) {
            logger.error('[GlbLoader] rollback inserted resource revision failed', rollbackError)
          }
          pendingResourceRevision = null
          throw runtimeCommitError
        }
        try {
          options.getViewer()?.syncModelDisplayMode?.()
        } catch (displayModeError) {
          logger.error(
            `[GlbLoader] sync inserted project model subtree display mode failed: insertedNodeKey=${applyOptions.insertedNodeKey}`,
            displayModeError,
          )
        }

        currentProjectModelLoadResult = committedProjectModelLoadResult
        currentComposedResult.hierarchy = hierarchyInsertResult.hierarchy
        currentComposedResult.manifest = createProjectModelManifestFromRoot(root)
        currentComposedResult.persistenceStatus = nextPersistenceStatus
        currentComposedResult.sceneBindings = nextSceneBindings
        currentComposedResult.selectionIndex = nextSelectionIndex
        mergeComposedAssemblyAssetsForSubtreeReplacement({
          currentRoot: currentLoadResult.root,
          targetNodeKey: applyOptions.insertedNodeKey,
          composedResult: currentComposedResult,
          nextComposedResult: insertedComposedResult,
        })
        glbHierarchyByTabId.set(tabId, currentComposedResult.hierarchy)
        loadedResultByTabId.set(tabId, currentComposedResult)
        replaceProjectModelPackageResultCache(packageResults)
        const publishInsertedRevision = (): void => {
          preparedWorkAssemblyRevision?.commit()
        }
        const retiredScopeErrors = importedModelRevisionSession.commit({
          ...(resourceRevision ? { resourceRevision } : {}),
          publish: publishInsertedRevision,
          retireTopology: () => {
            scopedRuntimeSwitchSession.topologyRemovalPlan.dispose(currentComposedResult.root)
          },
        })
        pendingResourceRevision = null
        invalidateAssemblyMateComponentTransformRootIndex()
        if (retiredScopeErrors.length > 0) {
          logger.error(
            '[GlbLoader] release inserted resource revision owners failed',
            new AggregateError(retiredScopeErrors),
          )
        }
        pruneProjectModelFileByteCache(committedProjectModelLoadResult.root)
        restoreProjectModelFileByteCache = null
        disposeVersionPreviewReplacedResults(previousResults, currentComposedResult)
        disposeReplacedProjectModelPackageResults(previousPackageResults, packageResults)
        clearImportedModelInteractionState()
        restoreProjectModelPuppyMateDocuments(
          nextMateDocuments,
          'scoped-subtree',
          writableComponentInstanceIds,
        )
        applyScopedProjectModelLinearComponentPatterns(
          tabId,
          finalizedSubtree.linearComponentPatternDocuments,
          applyOptions.linearComponentPatternRemovalGroupObjectIds,
        )
        try {
          options.getViewer()?.requestImmediateRender?.()
        } catch (renderError) {
          logger.error('[GlbLoader] request inserted project model subtree render failed', renderError)
        }
        if (!applyOptions.deferFeatureTreeRefresh) {
          let runtimeSubtreeApplied = false
          try {
            /**
             * 新节点只追加到一个已存在的稳定父节点。使用精确 sibling index 发布新增子树，
             * 避免完整刷新破坏当前根、兄弟节点与 UI 展开状态的对象引用。
             */
            runtimeSubtreeApplied = projectModelStructureFeatureTreeProjector.apply({
              tabId,
              activeTreeTabId: featureTreeStore.activeTreeTabId,
              previousHierarchy,
              nextHierarchy: hierarchyInsertResult.hierarchy,
              removedSnapshots: [],
              addedSnapshots: [{
                objectId: insertedComposedResult.hierarchy.objectId,
                parentObjectId: parentHierarchy.objectId,
                parentIndex: scopedInsertionSiblingIndex,
                node: insertedComposedResult.hierarchy,
              }],
              fileIdByUuidMap: glbFileIdByReferenceKeyMap.value,
            })
          } catch (treeProjectionError) {
            /** 主状态已经提交，UI 局部投影异常时继续由完整刷新恢复权威树。 */
            logger.error(
              '[GlbLoader] apply inserted project model tree subtree failed',
              treeProjectionError,
            )
          }
          if (runtimeSubtreeApplied) {
            syncCurrentProjectModelMateFeatureTree(tabId)
          } else {
            await featureTreeStore.refreshTree(tabId)
          }
        }
        composedResultForCleanup = null
        logger.info('[GlbLoader] project model inserted subtree apply complete', {
          tabId,
          insertedNodeKey: applyOptions.insertedNodeKey,
          packageCount: packageResults.length,
        })

        return committedProjectModelLoadResult
      }

      if (shouldMergeScopedPackageCache && applyOptions.targetNodeKey && scopedRuntimeSwitchSession) {
        const currentLoadResult = currentProjectModelLoadResult
        const nextSubtreeRoot = scopedNextSubtreeRoot
        const targetAssemblyNodeId = scopedTargetAssemblyNodeId

        if (!currentLoadResult) {
          throw new Error('当前项目模型尚未完成加载，无法局部替换项目模型子树')
        }
        if (!nextSubtreeRoot) {
          throw new Error(`项目模型局部替换缺少目标子树：targetNodeKey=${applyOptions.targetNodeKey}`)
        }
        if (!targetAssemblyNodeId) {
          throw new Error(`项目模型局部替换缺少 occurrence 身份：targetNodeKey=${applyOptions.targetNodeKey}`)
        }

        const packageResults = mergeProjectModelPackageResultsForSubtreeApply(
          root,
          applyOptions.targetNodeKey,
          loadedPackageResults,
        )
        const resolvedNextMateDocuments = resolveProjectModelPuppyMateDocuments(
          root,
          packageResults,
          applyOptions.cleanedRootMateDocument,
        )
        const replacementSnapshot = scopedSceneReplacementSnapshot
        if (!replacementSnapshot) {
          throw new Error(`项目模型局部替换缺少旧场景快照：targetNodeKey=${applyOptions.targetNodeKey}`)
        }
        const currentComposedResult = replacementSnapshot.currentComposedResult
        /**
         * 局部树投影必须同时持有替换前后的权威 hierarchy。后续场景提交会原位更新
         * currentComposedResult.hierarchy，因此这里在任何主状态写入前保留旧引用。
         */
        const previousHierarchy = currentComposedResult.hierarchy
        const oldObject = replacementSnapshot.primaryOldObject
        const oldRootObjects = [...replacementSnapshot.oldRootObjects]
        const primaryOldRootObject = oldRootObjects.find((object) => (
          object === oldObject || isObjectDescendantOf(oldObject, object)
        )) ?? null
        const oldObjectPlacements = oldRootObjects.map((object) => ({
          object,
          parent: object.parent,
          parentIndex: object.parent?.children.indexOf(object) ?? -1,
        }))
        const primaryOldObjectPlacement = oldObjectPlacements.find(({ object }) => (
          object === primaryOldRootObject
        ))

        if (
          !primaryOldRootObject
          || !primaryOldObjectPlacement?.parent
          || primaryOldObjectPlacement.parentIndex < 0
          || oldObjectPlacements.some(({ parent, parentIndex: oldParentIndex }) => !parent || oldParentIndex < 0)
        ) {
          throw new Error(`项目模型局部替换缺少完整旧子树场景快照：targetNodeKey=${applyOptions.targetNodeKey}`)
        }

        const sceneParentObject = primaryOldObjectPlacement.parent
        const sceneParentIndex = primaryOldObjectPlacement.parentIndex
        const deletedObjectIds = new Set<string>()
        oldRootObjects.forEach((object) => collectObjectTreeIds(object, deletedObjectIds))
        const hierarchyDeleteResult = deleteHierarchySnapshotsByObjectIds(
          currentComposedResult.hierarchy,
          new Set(oldRootObjects.map((object) => object.uuid)),
        )
        const hierarchySnapshot = hierarchyDeleteResult.snapshots.find((snapshot) => (
          snapshot.objectId === primaryOldRootObject.uuid
        )) ?? null

        if (!hierarchySnapshot) {
          throw new Error(`项目模型局部替换缺少目标 hierarchy 快照：targetNodeKey=${applyOptions.targetNodeKey}`)
        }
        /**
         * root `.puppy` 内嵌节点可能物理挂在一个不参与设计树投影的运行时包装对象下。
         * 场景回插必须沿用物理父级，而 hierarchy 重建必须沿用删除快照记录的语义父级；
         * 两者的 objectId 和 sibling index 不能互换。
         */
        const hierarchyParentObjectId = hierarchySnapshot.parentObjectId
        const hierarchyParentIndex = hierarchySnapshot.parentIndex
        const shouldPreserveOldRootTransform = oldRootObjects.length === 1
        /**
         * 单旧根继续沿用原有局部变换继承语义；扁平化子装配的多个旧根分别代表零件，任取一个
         * 零件变换覆盖新子装配根会导致整棵新子树偏移。
         */
        const oldObjectLocalTransformSnapshot = shouldPreserveOldRootTransform
          ? captureObject3DLocalTransformSnapshot(primaryOldRootObject)
          : null

        let nextComposedResult = composeProjectModelImportedResult({
          root: nextSubtreeRoot,
          partName: nextSubtreeRoot.displayName,
          packageResults: loadedPackageResults,
        })
        composedResultForCleanup = nextComposedResult

        const loadedMateNodeKeys = new Set(
          loadedPackageResults
            .filter((packageResult) => Boolean(packageResult.puppyMateDocument))
            .map((packageResult) => packageResult.nodeKey),
        )
        const semanticMateDocuments = resolvedNextMateDocuments.flatMap((mateDocument) => {
          if (!mateDocument.restoreAsRootDocument && !loadedMateNodeKeys.has(mateDocument.assemblyNodeId)) {
            return []
          }
          const ownerAssemblyNodeId = mateDocument.restoreAsRootDocument
            ? resolveProjectModelNodeAssemblyNodeId(root)
            : (() => {
                const ownerNode = findProjectModelNodeByKey(
                  nextSubtreeRoot,
                  mateDocument.assemblyNodeId,
                )
                if (!ownerNode) {
                  throw new Error(
                    `项目模型局部替换 Mate 文档缺少 owner 节点：nodeKey=${mateDocument.assemblyNodeId}`,
                  )
                }
                return resolveProjectModelNodeAssemblyNodeId(ownerNode)
              })()
          return [{
            ...mateDocument,
            assemblyNodeId: ownerAssemblyNodeId,
          }]
        })
        const packageSemanticScopes = loadedPackageResults.flatMap((packageResult) => {
          const ownerNode = findProjectModelNodeByKey(nextSubtreeRoot, packageResult.nodeKey)
          if (!ownerNode) {
            const belongsToReplacementSubtree = packageResult.nodeKey === nextSubtreeRoot.nodeKey
              || packageResult.nodeKey.startsWith(`${nextSubtreeRoot.nodeKey}/`)
            if (belongsToReplacementSubtree) {
              throw new Error(
                `项目模型局部替换归档包缺少 occurrence owner：nodeKey=${packageResult.nodeKey}`,
              )
            }
            return []
          }
          return [{
            ownerNodeKey: packageResult.nodeKey,
            ownerAssemblyNodeId: packageResult.nodeKey === nextSubtreeRoot.nodeKey
              ? targetAssemblyNodeId
              : resolveProjectModelNodeAssemblyNodeId(ownerNode),
            sourceHierarchy: packageResult.result.hierarchy,
          }]
        })
        const semanticLinearComponentPatternDocuments =
          createProjectModelSubtreeLinearComponentPatternDocuments(
            loadedPackageResults,
            packageSemanticScopes,
          )
        const fallbackFileId = nextSubtreeRoot.packageFiles[0]?.fileId
          ?? nextSubtreeRoot.assemblyManifest?.fileId
          ?? applyOptions.targetNodeKey
        const finalizedSubtree = new ProjectModelSubtreeSemanticFinalizer().finalize({
          operation: {
            kind: 'replace',
            previousHierarchySnapshot: {
              node: hierarchySnapshot.node,
              parentObjectId: hierarchyParentObjectId,
            },
          },
          target: {
            projectNodeKey: applyOptions.targetNodeKey,
            ...(scopedAssemblyDocumentNodeKey
              ? { assemblyDocumentNodeKey: scopedAssemblyDocumentNodeKey }
              : {}),
            assemblyNodeId: targetAssemblyNodeId,
            displayName: nextSubtreeRoot.displayName,
            nodeType: nextSubtreeRoot.nodeType,
          },
          subtreeImportedResult: nextComposedResult,
          packageSemanticScopes,
          mateDocuments: semanticMateDocuments,
          linearComponentPatternDocuments: semanticLinearComponentPatternDocuments,
          fallbackFileId,
        })
        nextComposedResult = finalizedSubtree.importedResult
        /**
         * Finalizer 输出的是尚未入场的完整目标子树；其 assembly binding key 正好构成
         * 本次局部事务唯一允许接收 Mate 求解位姿的 occurrence 集合。禁止从 instance-id、
         * 显示名或已合并的整场景反推，否则同名归档根会把范围外新增节点纳入写回范围。
         */
        const writableComponentInstanceIds = Object.keys(
          nextComposedResult.sceneBindings.byAssemblyNodeId,
        )
        /**
         * 仅恢复本次替换事务经过 Finalizer 重绑的 Mate 文档。范围外包仍保留在原始缓存中，
         * 但它们不是本次事务结果；再次回放会用旧 occurrence 覆盖当前根装配中的有效 Mate。
         */
        const nextMateDocuments = finalizedSubtree.mateDocuments
        const rebasedHierarchy = nextComposedResult.hierarchy
        resolveImportedModelHierarchy(nextComposedResult)
        assertProjectModelLoadSignalActive(applyOptions.signal)

        const previousTargetSubtreeRoot = findProjectModelNodeByKey(
          currentLoadResult.root,
          applyOptions.targetNodeKey,
        )
        const removedNodeKeys = new Set(
          collectModelAssetNodesPreorder(previousTargetSubtreeRoot ?? nextSubtreeRoot)
            .map((node) => node.nodeKey),
        )
        const nextSceneBindings = mergeSceneBindingIndexes(
          removeSceneBindingObjects(currentComposedResult.sceneBindings, deletedObjectIds, removedNodeKeys),
          nextComposedResult.sceneBindings,
        )
        /**
         * 局部替换后必须按完整包集合重新计算保存能力；不能沿用旧子树或仅使用新子树的状态，
         * 否则替换掉唯一 runtime-only 包后仍会错误禁用保存，或兄弟 runtime-only 包会被漏掉。
         */
        const nextPersistenceStatus = packageResults.some((packageResult) => (
          packageResult.result.persistenceStatus === 'runtime-only'
        ))
          ? 'runtime-only'
          : undefined
        const nextHierarchy = insertProjectModelReplacementHierarchySnapshot({
          root: hierarchyDeleteResult.hierarchy,
          snapshot: {
            objectId: rebasedHierarchy.objectId,
            parentObjectId: hierarchyParentObjectId,
            parentIndex: hierarchyParentIndex,
            node: rebasedHierarchy,
          },
          targetNodeKey: applyOptions.targetNodeKey,
          targetAssemblyNodeId,
          context: 'project model scoped subtree apply',
        })
        /**
         * 局部替换同样必须经过项目运行时 finalization。这里使用替换后的完整 hierarchy 与
         * 完整 sceneBindings 构建状态，禁止仅更新兼容绑定而遗留旧 Object3D 身份。
         */
        const finalizedProjectModelLoadResult = createCommittedProjectModelLoadResult(
          loadResult,
          {
            ...currentComposedResult,
            hierarchy: nextHierarchy,
            persistenceStatus: nextPersistenceStatus,
            sceneBindings: nextSceneBindings,
          },
        )
        const scopedTargetNodeKey = applyOptions.targetNodeKey
        const runtimeState = new ProjectModelRuntimeStateResolver().replaceSubtree(
          currentLoadResult.runtimeState,
          finalizedProjectModelLoadResult.runtimeState,
          scopedTargetNodeKey,
        )
        const sceneBindings = [
          ...currentLoadResult.sceneBindings.filter((binding) => (
            !isProjectModelNodeKeyInSubtree(binding.nodeKey, scopedTargetNodeKey)
          )),
          ...finalizedProjectModelLoadResult.sceneBindings.filter((binding) => (
            isProjectModelNodeKeyInSubtree(binding.nodeKey, scopedTargetNodeKey)
          )),
        ]
        /**
         * scoped finalization 没有 complete 包上下文，只能发布目标子树的新 Object3D 绑定；
         * 未切换兄弟的 complete occurrence 与工作文档反向索引必须沿用已提交状态。
         */
        const committedProjectModelLoadResult: ProjectModelLoadResult = {
          ...finalizedProjectModelLoadResult,
          runtimeState,
          sceneBindings,
          designTree: buildProjectModelDesignTreeProjection({
            root: finalizedProjectModelLoadResult.root,
            sceneBindings,
          }),
        }
        const preparedWorkAssemblyRevision = prepareScopedWorkAssemblyRevision({
          root,
          projectModelResult: committedProjectModelLoadResult,
          packageResults,
          importedResult: currentComposedResult,
        })
        const retainedPackageResults = new Set(packageResults.map((packageResult) => packageResult.result))
        const retainedScopeOwners = previousPackageResults.filter((previousPackageResult): previousPackageResult is (
          Extract<ImportedModelResult, { kind: 'composed-assembly' }>
        ) => (
          previousPackageResult.kind === 'composed-assembly'
          && Boolean(previousPackageResult.resourceScope)
          && retainedPackageResults.has(previousPackageResult)
        ))
        const resourceRevision = currentComposedResult.resourceScope || nextComposedResult.resourceScope
          ? ImportedModelResourceRevision.prepare({
            currentOwner: currentComposedResult,
            replacementOwner: nextComposedResult,
            retainedOwners: retainedScopeOwners,
            replacedRoots: oldRootObjects,
          })
          : null
        pendingResourceRevision = resourceRevision

        applyOptions.beforeCommit?.()
        beforeCommitApplied = true
        assertProjectModelLoadSignalActive(applyOptions.signal)
        handle.transaction.commit()
        transactionCommitted = true

        if (oldObjectLocalTransformSnapshot) {
          applyObject3DLocalTransformSnapshot(nextComposedResult.root, oldObjectLocalTransformSnapshot)
        }
        oldRootObjects.forEach((object) => object.removeFromParent())
        insertObjectAtParentIndex(sceneParentObject, nextComposedResult.root, sceneParentIndex)
        try {
          await commitScopedSubtreeRuntimeSwitchSession(scopedRuntimeSwitchSession)
        } catch (runtimeCommitError) {
          nextComposedResult.root.removeFromParent()
          let resourceRevisionRollbackError: unknown = null
          try {
            resourceRevision?.rollback()
          } catch (rollbackError) {
            resourceRevisionRollbackError = rollbackError
          }
          pendingResourceRevision = null
          oldObjectPlacements
            .slice()
            .sort((left, right) => (
              left.parent === right.parent ? left.parentIndex - right.parentIndex : 0
            ))
            .forEach(({ object, parent, parentIndex: oldParentIndex }) => {
              if (parent) {
                insertObjectAtParentIndex(parent, object, oldParentIndex)
              }
            })
          try {
            await featureTreeStore.refreshTree(tabId)
          } catch (refreshError) {
            logger.error('[GlbLoader] refresh scoped project model rollback tree failed', refreshError)
          }
          if (resourceRevisionRollbackError) {
            logger.error(
              '[GlbLoader] rollback scoped resource revisions failed',
              resourceRevisionRollbackError,
            )
          }
          throw runtimeCommitError
        }
        try {
          /**
           * scoped 子树替换不会经过 commitPartTabSwitch，必须在新 topology 稳定后主动同步当前显示模式。
           * 这里仅同步、不立即渲染：一方面保证显示边只绑定新仓库，另一方面让不着色控制器在旧对象
           * 通用资源释放前归还其材质快照；最终画面仍在配合恢复完成后统一渲染。
           */
          options.getViewer()?.syncModelDisplayMode?.()
        } catch (displayModeError) {
          logger.error(
            `[GlbLoader] sync scoped project model subtree display mode failed: targetNodeKey=${applyOptions.targetNodeKey}`,
            displayModeError,
          )
        }

        currentProjectModelLoadResult = committedProjectModelLoadResult
        currentComposedResult.hierarchy = nextHierarchy
        currentComposedResult.manifest = createProjectModelManifestFromRoot(root)
        currentComposedResult.persistenceStatus = nextPersistenceStatus
        currentComposedResult.sceneBindings = nextSceneBindings
        currentComposedResult.selectionIndex = createNextSelectionIndex(
          currentComposedResult.selectionIndex,
          scene,
          removedNodeKeys,
          nextComposedResult.selectionIndex.byObject3DUuid,
        )
        mergeComposedAssemblyAssetsForSubtreeReplacement({
          currentRoot: currentLoadResult.root,
          targetNodeKey: applyOptions.targetNodeKey,
          composedResult: currentComposedResult,
          nextComposedResult,
        })
        glbHierarchyByTabId.set(tabId, currentComposedResult.hierarchy)
        loadedResultByTabId.set(tabId, currentComposedResult)
        replaceProjectModelPackageResultCache(packageResults)
        const publishReplacementRevision = (): void => {
          preparedWorkAssemblyRevision?.commit()
        }
        const retiredScopeErrors = importedModelRevisionSession.commit({
          ...(resourceRevision ? { resourceRevision } : {}),
          publish: publishReplacementRevision,
          retireTopology: () => {
            scopedRuntimeSwitchSession.topologyRemovalPlan.dispose(nextComposedResult.root)
          },
        })
        pendingResourceRevision = null
        invalidateAssemblyMateComponentTransformRootIndex()
        // package owner 必须在旧容器 clear 前按 cache 契约收尾，否则 legacy 子树会先失去可遍历资源根。
        disposeReplacedProjectModelPackageResults(previousPackageResults, packageResults)
        try {
          if (resourceRevision) {
            // Registry 已发布新 revision 后才能断开旧子树引用，旧 scope 也已由 revision 统一淘汰。
            oldRootObjects.forEach((object) => object.clear())
          } else {
            // 非 complete 历史结果仍沿用对象树差集释放，避免改变旧 loader 的共享缓存契约。
            const preservedResources = collectObjectTreeResources(scene)
            oldRootObjects.forEach((object) => {
              disposeDetachedObjectTreeResources(object, preservedResources, {
                disposeTopology: false,
              })
            })
          }
        } catch (disposeError) {
          logger.error('[GlbLoader] dispose replaced scoped project model subtree failed', disposeError)
        }
        if (retiredScopeErrors.length > 0) {
          logger.error(
            '[GlbLoader] release replaced scoped resource revision owners failed',
            new AggregateError(retiredScopeErrors),
          )
        }
        pruneProjectModelFileByteCache(committedProjectModelLoadResult.root)
        restoreProjectModelFileByteCache = null
        disposeVersionPreviewReplacedResults(previousResults, currentComposedResult)
        clearImportedModelInteractionState()
        restoreProjectModelPuppyMateDocuments(
          nextMateDocuments,
          'scoped-subtree',
          writableComponentInstanceIds,
          targetAssemblyNodeId,
        )
        applyScopedProjectModelLinearComponentPatterns(
          tabId,
          finalizedSubtree.linearComponentPatternDocuments,
          applyOptions.linearComponentPatternRemovalGroupObjectIds,
        )
        try {
          options.getViewer()?.requestImmediateRender?.()
        } catch (renderError) {
          logger.error('[GlbLoader] request scoped project model subtree render failed', renderError)
        }
        if (!applyOptions.deferFeatureTreeRefresh) {
          let runtimeSubtreeApplied = false
          try {
            /**
             * 版本替换只改变稳定父节点下的一棵直接子树。复用项目结构局部投影协议，
             * 既保持父节点与兄弟节点引用稳定，也避免重新向 Worker 请求整棵设计树。
             */
            runtimeSubtreeApplied = projectModelStructureFeatureTreeProjector.apply({
              tabId,
              activeTreeTabId: featureTreeStore.activeTreeTabId,
              previousHierarchy,
              nextHierarchy,
              removedSnapshots: hierarchyDeleteResult.snapshots,
              addedSnapshots: [{
                objectId: rebasedHierarchy.objectId,
                parentObjectId: hierarchyParentObjectId,
                parentIndex: hierarchyParentIndex,
                node: rebasedHierarchy,
              }],
              fileIdByUuidMap: glbFileIdByReferenceKeyMap.value,
            })
          } catch (treeProjectionError) {
            /** 主状态已经提交，UI 局部投影异常时由完整刷新恢复权威设计树。 */
            logger.error(
              '[GlbLoader] apply scoped project model replacement tree subtree failed',
              treeProjectionError,
            )
          }
          if (runtimeSubtreeApplied) {
            syncCurrentProjectModelMateFeatureTree(tabId)
          } else {
            await featureTreeStore.refreshTree(tabId)
          }
        }
        composedResultForCleanup = null
        logger.info('[GlbLoader] project model scoped subtree apply complete', {
          tabId,
          downloadedNodeCount: downloadPlan.nodesToLoad.length,
          composedNodeCount: downloadPlan.nodesToLoad.length,
          packageCount: packageResults.length,
          targetNodeKey: applyOptions.targetNodeKey,
        })

        return committedProjectModelLoadResult
      }

      const packageResults = loadedPackageResults
      const composedResult = composeProjectModelImportedResult({
        root,
        partName: label,
        packageResults,
      })
      composedResultForCleanup = composedResult

      const completeRuntimeContext = isFullDocumentRootApply && applyOptions.workAssemblyBuildContext
        ? applyOptions.workAssemblyBuildContext.resolveRuntimeContext({
          root,
          archiveFile: loadableOpenProjectArchiveFile,
          projectModelResult: loadResult,
          packageResults,
          preparedSnapshotsByNodeKey:
            applyOptions.preparedCompleteGlbPackageSnapshotsByNodeKey ?? new Map(),
        })
        : undefined

      const committedProjectModelLoadResult = createCommittedProjectModelLoadResult(
        loadResult,
        composedResult,
        completeRuntimeContext,
      )
      // 在事务提交和场景替换前先解析一次 hierarchy，避免坏结果进入场景后才失败。
      resolveImportedModelHierarchy(composedResult)
      assertProjectModelLoadSignalActive(applyOptions.signal)
      const loadedModel: LoadedImportedModel = {
        displayType: resolveProjectRootTabType(root),
        result: composedResult,
        partAssetCache: caches.partAssetCache,
        importedBrepRuntimeCache: caches.importedBrepRuntimeCache,
        puppyMateDocument: resolvePuppyMateDocumentForProjectModelRoot(root, packageResults),
        puppyDatumExtension: resolvePuppyDatumExtensionForProjectModelRoot(root, packageResults)
          ?? (isFullDocumentRootApply ? createEmptyPuppyDatumExtension() : undefined),
        //------ 草图离线包定义持久化 开始------
        puppySketchExtension: resolvePuppySketchExtensionForProjectModelRoot(root, packageResults),
        puppySketchEntityReferenceExtension:
          resolvePuppySketchEntityReferenceExtensionForProjectModelRoot(root, packageResults),
        //------ 草图离线包定义持久化 结束------
        //------ 特征离线包定义持久化 开始------
        puppyFeatureExtension: resolvePuppyFeatureExtensionForProjectModelRoot(root, packageResults),
        //------ 特征离线包定义持久化 结束------
        // 剖面属于 Viewer 根文档显示状态，作用域子树替换不能用子包覆盖当前根视图。
        puppySectionViewExtension: isFullDocumentRootApply
          ? resolvePuppySectionViewExtensionForProjectModelRoot(root, packageResults)
          : undefined,
        puppyLinearComponentPatternDocument: isFullDocumentRootApply
          ? packageResults.find((item) => item.nodeKey === root.nodeKey)
            ?.puppyLinearComponentPatternDocument
            ?? createEmptyPuppyLinearComponentPatternDocument()
          : undefined,
        projectModelPuppyMateDocuments: resolveProjectModelPuppyMateDocuments(
          root,
          packageResults,
          applyOptions.cleanedRootMateDocument,
        ),
      }

      applyOptions.beforeCommit?.()
      beforeCommitApplied = true
      assertProjectModelLoadSignalActive(applyOptions.signal)
      if (applyOptions.workAssemblyBuildContext) {
        applyOptions.workAssemblyBuildContext.validateDelivery({
            root,
            archiveFile: loadableOpenProjectArchiveFile,
            projectModelResult: committedProjectModelLoadResult,
            packageResults,
            importedResult: composedResult,
        })
      }
      await beforeModelCommit?.()
      if (isFullDocumentRootApply) {
        fullDocumentRuntimeCommitted = true
      }
      const scopedRollbackSnapshot = scopedRuntimeSwitchSession
        ? {
            tabId,
            scene,
            previousMountedGlbTabId: mountedGlbTabId.value,
            previousActiveTabId: partTabsStore.activeTabId,
            previousLoadedResult: loadedResultByTabId.get(tabId),
            previousHierarchy: glbHierarchyByTabId.get(tabId),
            previousProjectModelLoadResult: currentProjectModelLoadResult,
            previousPackageResultsByNodeKey: new Map(
              Array.from(projectModelPackageResultByNodeKey.entries())
                .map(([nodeKey, cachedPackageResults]) => [nodeKey, [...cachedPackageResults]]),
            ),
          }
        : null
      handle.transaction.commit()
      transactionCommitted = true
      try {
        const shouldDeferFeatureTreeRefresh = applyOptions.deferFeatureTreeRefresh
          || scopedRuntimeSwitchSession !== null
        commitPartTabSwitch(tabId, loadedModel, scene, {
          deferFeatureTreeRefresh: shouldDeferFeatureTreeRefresh,
          rootDocumentType: isFullDocumentRootApply
            ? resolveProjectRootTabType(root)
            : undefined,
        })
      } catch (webCommitError) {
        if (scopedRollbackSnapshot) {
          rollbackScopedProjectModelTabSwitchWebCommit(scopedRollbackSnapshot)
        }
        throw webCommitError
      }
      currentProjectModelLoadResult = committedProjectModelLoadResult
      if (!scopedRuntimeSwitchSession) {
        replaceProjectModelPackageResultCache(packageResults)
        pruneProjectModelFileByteCache(committedProjectModelLoadResult.root)
        restoreProjectModelFileByteCache = null
      }
      if (scopedRuntimeSwitchSession) {
        try {
          await commitScopedSubtreeRuntimeSwitchSession(scopedRuntimeSwitchSession)
        } catch (runtimeCommitError) {
          if (scopedRollbackSnapshot) {
            rollbackScopedProjectModelTabSwitchWebCommit(scopedRollbackSnapshot)
          }
          throw runtimeCommitError
        }
        invalidateAssemblyMateComponentTransformRootIndex()
        replaceProjectModelPackageResultCache(packageResults)
        pruneProjectModelFileByteCache(committedProjectModelLoadResult.root)
        restoreProjectModelFileByteCache = null
        if (!applyOptions.deferFeatureTreeRefresh) {
          await featureTreeStore.refreshTree(tabId)
        }
      }
      const replacedActiveWorkAssembly = isFullDocumentRootApply
        ? supportsCompleteWorkAssembly
          ? false
          : completeWorkAssemblySession.invalidate()
        : false
      const replacedWorkAssemblyOwnedResults = replacedActiveWorkAssembly
        && previousActiveWorkAssemblySnapshot
        ? new Set<ImportedModelResult>([
          previousActiveWorkAssemblySnapshot.importedResult,
          ...previousActiveWorkAssemblySnapshot.packageResults.map((item) => item.result),
        ])
        : null
      if (!applyOptions.workAssemblyRegistryOwner) {
        disposeVersionPreviewReplacedResults(
          replacedWorkAssemblyOwnedResults
            ? previousResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
            : previousResults,
          composedResult,
        )
        disposeReplacedProjectModelPackageResults(
          replacedWorkAssemblyOwnedResults
            ? previousPackageResults.filter((result) => !replacedWorkAssemblyOwnedResults.has(result))
            : previousPackageResults,
          packageResults,
        )
      }
      if (isFullDocumentRootApply && nextProjectModelContextIdentity) {
        currentProjectModelContextIdentity = nextProjectModelContextIdentity
      }
      composedResultForCleanup = null
      logger.info('[GlbLoader] project model apply complete', {
        tabId,
        downloadedNodeCount: downloadPlan.nodesToLoad.length,
        composedNodeCount: composePlan.nodesToLoad.length,
        packageCount: packageResults.length,
        targetNodeKey: applyOptions.targetNodeKey ?? null,
        insertedNodeKey: applyOptions.insertedNodeKey ?? null,
      })
      return committedProjectModelLoadResult
    } catch (error) {
      const scopedRuntimeCommitted = scopedRuntimeSwitchSession?.committed === true
      if (pendingResourceRevision) {
        try {
          pendingResourceRevision.rollback()
        } catch (rollbackError) {
          logger.error(
            '[GlbLoader] rollback pending project model resource revision failed',
            rollbackError,
          )
        }
        pendingResourceRevision = null
      }
      if (isFullDocumentRootApply && fullDocumentRuntimeCommitted) {
        markFullDocumentRuntimeCommitRequiresReload(error)
      }
      if (scopedRuntimeCommitted) {
        markScopedSubtreeRuntimeCommitRequiresReload(
          error,
          applyOptions.targetNodeKey ?? scopedNextSubtreeRoot?.nodeKey ?? null,
        )
      }
      await tryAbortProjectRootRuntimeSwitchSession(runtimeSwitchSession, 'project model apply catch')
      await tryAbortScopedSubtreeRuntimeSwitchSession(scopedRuntimeSwitchSession, 'project model scoped apply catch')
      if (!scopedRuntimeCommitted) {
        restoreProjectModelFileByteCache?.()
      }
      restoreProjectModelFileByteCache = null
      if (beforeCommitApplied && !scopedRuntimeCommitted) {
        try {
          applyOptions.rollbackBeforeCommit?.()
        } catch (rollbackError) {
          logger.error('[GlbLoader] rollback project model beforeCommit failed', rollbackError)
        }
      }
      if (scopedRuntimeCommitted) {
        composedResultForCleanup = null
      } else if (composedResultForCleanup) {
        if (transactionCommitted) {
          disposeCommittedBackgroundImportedModelResult(
            composedResultForCleanup,
            'project model apply committed cleanup',
          )
          disposeFailedProjectModelPackageResults(
            completedPackageResults,
            transactionCommitted,
            'project model apply package cleanup',
          )
        } else {
          // compose 已接管本次加载的 package object，未提交失败时由 composed root 一次性释放，避免 geometry/material 重复 dispose。
          const hasIndependentPackageScopes = composedResultForCleanup.kind === 'composed-assembly'
            && Boolean(composedResultForCleanup.resourceScope)
          disposeImportedModel(composedResultForCleanup)
          if (hasIndependentPackageScopes) {
            disposeFailedProjectModelPackageResults(
              completedPackageResults,
              transactionCommitted,
              'project model apply scoped package cleanup',
            )
          }
        }
        composedResultForCleanup = null
      } else {
        disposeFailedProjectModelPackageResults(
          completedPackageResults,
          transactionCommitted,
          'project model apply package cleanup',
        )
      }
      if (!isImportedModelLoadAbortedError(error)) {
        logger.error(`[GlbLoader] Project model apply failed: ${tabId}`, error)
        throw error
      }
      throw error
    } finally {
      releaseLoading()
    }
  }

  /**
   * 读取当前已提交的项目模型加载结果。
   *
   * @returns 当前项目模型结果；尚未通过 OpenProject 新入口加载时返回 null。
   */
  function getCurrentProjectModelLoadResult(): ProjectModelLoadResult | null {
    return currentProjectModelLoadResult
  }

  /**
   * 从正式项目身份图解析编辑零件的 Worker 基线范围。
   * @param objectId 场景选择身份。 @param stableNodeKey 设计树稳定节点身份。
   * @returns 可持久化范围；不以场景 UUID 代替 Worker 几何身份。
   */
  function resolveFeatureDocumentScope(objectId: string, stableNodeKey?: string): FeatureDocumentScope | null {
    const loadResult = currentProjectModelLoadResult
    if (!loadResult) return null
    const node = resolveProjectModelNodeBySceneObjectIds({
      loadResult, objectIds: [objectId], stableNodeKey,
      getSceneObjectById: (id) => options.getViewer()?.getSceneObjectById(id) ?? null,
    })
    if (!node) return null
    const entries = collectCurrentProjectModelRuntimeOwnershipEntries(resolveCurrentProjectRootRuntimeKey(loadResult.root))
    const ownership = new GeometryRuntimeOwnershipIndex(entries)
    return {
      documentScopeNodeKey: node.nodeKey,
      documentObjectIds: [...new Set(ownership.getEntriesBySubtree(node.nodeKey).map((entry) => entry.geometryObjectId))],
    }
  }

  /**
   * 解析当前根装配的 composed result。
   *
   * @param rootNodeKey 根装配节点 key。
   * @returns composed result。
   */
  function resolveCurrentComposedAssemblyResult(rootNodeKey: string): ComposedAssemblyResult {
    const currentResult = loadedResultByTabId.get(rootNodeKey)

    if (!currentResult || currentResult.kind !== 'composed-assembly') {
      throw new Error(`当前根装配缺少可局部编辑的组合结果：${rootNodeKey}`)
    }

    return currentResult
  }

  /**
   * 捕获被删除子树的本地文件字节快照。
   *
   * @param root 当前项目模型根。
   * @param deletedNodeKeys 被删除节点 key 集合。
   * @returns 可写入 payload 的文件快照列表。
   */
  function captureDeletedSubtreeFileSnapshots(
    root: ModelAssetNode,
    deletedNodeKeys: ReadonlySet<string>,
  ): AssemblyDeletedSubtreeFileSnapshot[] {
    const deletedNodes = collectModelAssetNodesPreorder(root)
      .filter((node) => deletedNodeKeys.has(node.nodeKey))

    return collectProjectNodeFileReadTasks(deletedNodes).map((task) => {
      const bytes = projectModelFileBytesByIdentityKey.get(buildProjectAssetFileIdentityKey(task.file))

      if (!bytes) {
        throw new Error(`装配删除缺少被删节点文件字节缓存：nodeKey=${task.nodeKey}, fileId=${task.file.fileId}, role=${task.role}`)
      }

      return createAssemblyDeletedSubtreeFileSnapshot({
        nodeKey: task.nodeKey,
        role: task.role,
        file: task.file,
        bytes,
      })
    })
  }

  /**
   * 从删除 payload 恢复文件读取结果。
   *
   * @param payload 删除子树 payload。
   * @returns 可同时供 cad-core 项目加载事务与节点包 loader 消费的文件读取结果。
   */
  function restoreDeletedSubtreeFileReadResults(
    payload: AssemblyDeletedSubtreePayload,
  ): ProjectNodeFileReadResult[] {
    return (payload.fileSnapshots ?? []).map((snapshot) => {
      const file = restoreAssemblyDeletedSubtreeModelAssetFile(snapshot.file)
      const bytes = decodeAssemblyDeletedSubtreeFileSnapshotBytes(snapshot)

      projectModelFileBytesByIdentityKey.set(
        buildProjectAssetFileIdentityKey(file),
        new Uint8Array(bytes),
      )

      return {
        nodeKey: snapshot.nodeKey,
        file,
        role: snapshot.role,
        bytes,
      }
    })
  }

  /**
   * 捕获装配删除子树 payload。
   *
   * @param input 删除目标与差异。
   * @returns 冷存储 payload 与引用。
   */
  async function captureAssemblyDeletedSubtreePayload(input: {
    readonly nodeKey: string
    readonly deletedNodeKeys: ReadonlySet<string>
  }): Promise<{
    readonly payload: AssemblyDeletedSubtreePayload
    readonly payloadRef: AssemblyDeletedSubtreePayloadRef
  }> {
    const loadResult = currentProjectModelLoadResult
    const scene = options.getScene()
    const viewer = options.getViewer()

    if (!loadResult) {
      throw new Error('当前项目模型尚未加载，无法捕获删除子树')
    }
    if (!scene) {
      throw new Error('当前场景尚未就绪，无法捕获删除子树')
    }

    const composedResult = resolveCurrentComposedAssemblyResult(loadResult.root.nodeKey)
    const deletedSceneBindings = filterSceneBindingsByNodeKeys(loadResult.sceneBindings, input.deletedNodeKeys)
    const deletionRootObjects = resolveCurrentDeletionRootObjectsFromSceneBindings(
      scene,
      viewer,
      loadResult.sceneBindings,
      input.nodeKey,
      input.deletedNodeKeys,
    )

    if (deletionRootObjects.length === 0) {
      throw new Error(`装配删除未找到可移除的场景对象：nodeKey=${input.nodeKey}`)
    }

    const rootObjectIds = new Set(deletionRootObjects.map((object) => object.uuid))
    const deletedObjectIds = new Set<string>()

    deletionRootObjects.forEach((object) => collectObjectTreeIds(object, deletedObjectIds))

    const hierarchyDeleteResult = deleteHierarchySnapshotsByObjectIds(
      composedResult.hierarchy,
      rootObjectIds,
    )
    if (hierarchyDeleteResult.snapshots.length === 0) {
      throw new Error(`装配删除未找到可移除的设计树 hierarchy：nodeKey=${input.nodeKey}`)
    }

    const selectionTargetsByObjectId: Record<string, SelectionTarget> = {}
    Object.entries(composedResult.selectionIndex.byObject3DUuid).forEach(([objectId, target]) => {
      if (deletedObjectIds.has(objectId) || input.deletedNodeKeys.has(target.assemblyNodeId)) {
        selectionTargetsByObjectId[objectId] = { ...target }
      }
    })

    const payloadWithoutId = {
      rootNodeKey: loadResult.root.nodeKey,
      deletedNodeKey: input.nodeKey,
      deletedNodeKeys: Array.from(input.deletedNodeKeys),
      sceneBindings: deletedSceneBindings.map((binding) => ({ ...binding })),
      selectionTargetsByObjectId,
      objectSnapshots: captureDeletedObjectSnapshots(deletionRootObjects),
      hierarchySnapshots: hierarchyDeleteResult.snapshots,
      fileSnapshots: captureDeletedSubtreeFileSnapshots(loadResult.root, input.deletedNodeKeys),
    }
    const payloadRef = await writeAssemblyDeletedSubtreePayload(payloadWithoutId)
    const payload: AssemblyDeletedSubtreePayload = {
      ...payloadWithoutId,
      payloadId: payloadRef.payloadId,
      createdAt: Date.now(),
    }

    return { payload, payloadRef }
  }

  /**
   * 从场景摘除删除对象树，资源释放由后续 owner revision 发布阶段统一完成。
   *
   * @param scene 当前场景。
   * @param rootObjects 需要移除的对象根。
   */
  function detachAssemblyDeletedObjects(
    scene: THREE.Scene,
    rootObjects: readonly THREE.Object3D[],
  ): readonly THREE.Object3D[] {
    const normalizedRootObjects = rootObjects
      .filter((object) => object !== scene)
      .filter((object, index, objects) => !objects.some((candidate, candidateIndex) => (
        candidateIndex !== index && isObjectDescendantOf(object, candidate)
      )))

    normalizedRootObjects.forEach((object) => object.removeFromParent())
    return normalizedRootObjects
  }

  /**
   * 从 payload 恢复被删除的对象树。
   *
   * @param scene 当前场景。
   * @param viewer 当前 Viewer。
   * @param composedResult 当前组合装配结果。
   * @param payload 删除子树 payload。
   */
  async function restoreAssemblyDeletedObjects(
    scene: THREE.Scene,
    viewer: ReturnType<UseEditorGlbLoaderOptions['getViewer']>,
    composedResult: ComposedAssemblyResult,
    payload: AssemblyDeletedSubtreePayload,
    nextLoadResult: ProjectModelLoadResult,
  ): Promise<RestoreAssemblyDeletedObjectsResult> {
    const deletedRootNode = findModelAssetNodeByKey(nextLoadResult.root, payload.deletedNodeKey)
    if (!deletedRootNode) {
      throw new Error(`装配删除 Undo 未在目标结构中找到恢复节点：nodeKey=${payload.deletedNodeKey}`)
    }

    const rootHierarchySnapshot = payload.hierarchySnapshots
      .slice()
      .sort((left, right) => left.parentIndex - right.parentIndex)[0]
    const rootObjectSnapshot = rootHierarchySnapshot
      ? payload.objectSnapshots.find((snapshot) => snapshot.objectId === rootHierarchySnapshot.objectId)
        ?? payload.objectSnapshots[0]
      : payload.objectSnapshots[0]

    if (!rootHierarchySnapshot || !rootObjectSnapshot) {
      throw new Error(`装配删除 Undo 缺少恢复子树快照：payload=${payload.payloadId}`)
    }

    const parentFromSnapshot = rootObjectSnapshot.parentObjectId
      ? resolveSceneObjectById(scene, viewer, rootObjectSnapshot.parentObjectId)
      : scene
    const parentFromAssemblyNode = deletedRootNode.parentNodeKey
      ? resolveSceneObjectById(
        scene,
        viewer,
        composedResult.sceneBindings.byAssemblyNodeId[deletedRootNode.parentNodeKey]?.[0] ?? '',
      )
      : null
    const parent = parentFromSnapshot ?? parentFromAssemblyNode ?? (rootObjectSnapshot.parentObjectId ? null : scene)

    if (!parent) {
      throw new Error(`装配删除 Undo 缺少恢复父对象：payload=${payload.payloadId}, parent=${rootObjectSnapshot.parentObjectId}`)
    }

    const plan = createProjectModelLoadPlan({ kind: 'root-load', root: deletedRootNode })
    const fileReadResults = restoreDeletedSubtreeFileReadResults(payload)
    const fileReadKeys = new Set(
      fileReadResults.map((result) => buildProjectNodeFileReadKey(result.nodeKey, result.file, result.role)),
    )
    const missingReadTask = collectProjectNodeFileReadTasks(plan.nodesToLoad)
      .find((task) => !fileReadKeys.has(buildProjectNodeFileReadKey(task.nodeKey, task.file, task.role)))

    if (missingReadTask) {
      throw new Error(
        `装配删除 Undo 缺少被删节点文件快照：nodeKey=${missingReadTask.nodeKey}, fileId=${missingReadTask.file.fileId}, role=${missingReadTask.role}`,
      )
    }

    const restoreHandle = requestRegistry.acquire('tab-switch', {
      scope: ASSEMBLY_DELETE_HISTORY_RESTORE_SCOPE,
    })
    const completedPackageResults: LoadedProjectModelPackageResult[] = []
    let restoredComposedResult: ComposedAssemblyResult | null = null
    let transactionCommitted = false
    let handleReleased = false

    try {
      const restoredLoadResult = await executeProjectModelLoad({
        root: deletedRootNode,
        reader: createPreloadedProjectModelReader(fileReadResults),
        plan,
        assemblyDocumentOverridesByNodeKey: nextLoadResult.assemblyDocumentsByNodeKey,
      })
      const packageResults = await loadProjectModelPackageResultsWithHandle(
        restoreHandle,
        plan.nodesToLoad,
        buildProjectNodeFileBytesByReadKey(fileReadResults),
        completedPackageResults,
      )

      if (!packageResults) {
        throw new ImportedModelLoadAbortedError()
      }

      restoredComposedResult = composeProjectModelImportedResult({
        root: restoredLoadResult.root,
        partName: rootHierarchySnapshot.node.name,
        packageResults,
      })
      restoredComposedResult.root.name = rootHierarchySnapshot.node.name

      const parentObjectId = parent === scene ? null : parent.uuid
      const rebasedHierarchy = rebaseRestoredHierarchyToSnapshot(
        restoredComposedResult.hierarchy,
        rootHierarchySnapshot.node,
        parentObjectId,
      )
      const fallbackFileId = deletedRootNode.packageFiles[0]?.fileId
        ?? deletedRootNode.assemblyManifest?.fileId
        ?? deletedRootNode.nodeKey

      restoredComposedResult.hierarchy = rebasedHierarchy
      writeRestoredHierarchyMetadata({
        rootObject: restoredComposedResult.root,
        hierarchy: rebasedHierarchy,
        fallbackFileId,
        fallbackAssemblyNodeId: deletedRootNode.nodeKey,
      })
      restoreHandle.transaction.commit()
      transactionCommitted = true
      restoreHandle.release()
      handleReleased = true

      return {
        composedResult: restoredComposedResult,
        packageResults,
        hierarchySnapshots: [{
          objectId: rebasedHierarchy.objectId,
          parentObjectId,
          parentIndex: rootHierarchySnapshot.parentIndex,
          node: rebasedHierarchy,
        }],
        parent,
        parentIndex: rootObjectSnapshot.parentIndex,
      }
    } catch (error) {
      const cleanupErrors: unknown[] = []
      if (restoredComposedResult) {
        restoredComposedResult.root.removeFromParent()
        const hasIndependentPackageScopes = Boolean(restoredComposedResult.resourceScope)
        try {
          if (transactionCommitted && !hasIndependentPackageScopes) {
            disposeImportedModelInstance(restoredComposedResult)
          } else {
            disposeImportedModel(restoredComposedResult)
          }
        } catch (cleanupError) {
          cleanupErrors.push(cleanupError)
        }
        if (transactionCommitted || hasIndependentPackageScopes) {
          try {
            disposeFailedProjectModelPackageResults(
              completedPackageResults,
              transactionCommitted,
              'assembly deleted subtree restore package cleanup',
            )
          } catch (cleanupError) {
            cleanupErrors.push(cleanupError)
          }
        }
      } else {
        for (const result of collectUniqueImportedModelResults(completedPackageResults.map((item) => item.result))) {
          try {
            disposeImportedModel(result)
          } catch (cleanupError) {
            cleanupErrors.push(cleanupError)
          }
        }
      }

      try {
        if (!transactionCommitted) {
          restoreHandle.abort()
        } else if (!handleReleased) {
          restoreHandle.release()
        }
      } catch (cleanupError) {
        cleanupErrors.push(cleanupError)
      }

      if (cleanupErrors.length > 0) {
        throw new AggregateError([error, ...cleanupErrors], '装配删除子树恢复失败且资源回滚不完整')
      }
      throw error
    }
  }

  /**
   * 应用项目模型结构的局部切换。
   *
   * @param input 切换输入。
   */
  async function applyProjectModelStructureLocally(input: {
    readonly nextLoadResult: ProjectModelLoadResult
    readonly removedNodeKeys: ReadonlySet<string>
    readonly addedNodeKeys: ReadonlySet<string>
    readonly payload?: AssemblyDeletedSubtreePayload | null
  } & ApplyProjectModelStructureLocallyOptions): Promise<void> {
    const currentLoadResult = currentProjectModelLoadResult
    const scene = options.getScene()
    const viewer = options.getViewer()

    if (!currentLoadResult) {
      throw new Error('当前项目模型尚未加载，无法局部应用装配结构')
    }
    if (!scene) {
      throw new Error('当前场景尚未就绪，无法局部应用装配结构')
    }
    if (currentLoadResult.root.nodeKey !== input.nextLoadResult.root.nodeKey) {
      throw new Error(`局部装配结构只能应用到当前根装配：current=${currentLoadResult.root.nodeKey}, next=${input.nextLoadResult.root.nodeKey}`)
    }

    const composedResult = resolveCurrentComposedAssemblyResult(currentLoadResult.root.nodeKey)
    const payload = input.payload ?? null
    const needsPayload = input.removedNodeKeys.size > 0 || input.addedNodeKeys.size > 0

    if (needsPayload && !payload) {
      throw new Error('装配结构局部切换缺少删除子树 payload')
    }

    input.beforeCommit?.()
    clearImportedModelInteractionState()

    const previousHierarchy = composedResult.hierarchy
    let nextHierarchy = previousHierarchy
    let nextSceneBindings = composedResult.sceneBindings
    let restoredSelectionTargetsByObjectId: Record<string, SelectionTarget> = {}
    const previousPackageResults = collectProjectModelPackageResults()
    const previousComposedScope = composedResult.resourceScope
    let deletionRootObjects: readonly THREE.Object3D[] = []
    let removedPackageResults: readonly LoadedProjectModelPackageResult[] = []
    let removedHierarchySnapshots: readonly AssemblyDeletedSubtreeHierarchySnapshot[] = []
    let restored: RestoreAssemblyDeletedObjectsResult | null = null

    if (payload && input.removedNodeKeys.size > 0) {
      deletionRootObjects = resolveCurrentDeletionRootObjectsFromSceneBindings(
        scene,
        viewer,
        currentLoadResult.sceneBindings,
        payload.deletedNodeKey,
        input.removedNodeKeys,
      )

      if (deletionRootObjects.length === 0) {
        throw new Error(`装配删除未找到当前可移除的场景对象：nodeKey=${payload.deletedNodeKey}`)
      }

      const removedNodeKeys = new Set(payload.deletedNodeKeys)
      removedPackageResults = previousPackageResults.filter((packageResult) => (
        removedNodeKeys.has(packageResult.nodeKey)
      ))
      const rootObjectIds = new Set(deletionRootObjects.map((object) => object.uuid))
      const hierarchyDeletion = deleteHierarchySnapshotsByObjectIds(nextHierarchy, rootObjectIds)
      nextHierarchy = hierarchyDeletion.hierarchy
      removedHierarchySnapshots = hierarchyDeletion.snapshots
      nextSceneBindings = removeSceneBindingsByNodeKeys(nextSceneBindings, input.removedNodeKeys)
    }

    if (payload && input.addedNodeKeys.size > 0) {
      restored = await restoreAssemblyDeletedObjects(
        scene,
        viewer,
        composedResult,
        payload,
        input.nextLoadResult,
      )

      nextHierarchy = restoreHierarchySnapshots(nextHierarchy, restored.hierarchySnapshots)
      nextSceneBindings = mergeSceneBindingIndexes(nextSceneBindings, restored.composedResult.sceneBindings)
      restoredSelectionTargetsByObjectId = restored.composedResult.selectionIndex.byObject3DUuid
    }

    const restoredComposedScope = restored?.composedResult.resourceScope
    const removedNodeKeys = new Set(payload?.deletedNodeKeys ?? [])
    const retainedScopeOwners = Array.from(new Set(
      previousPackageResults
        .filter((packageResult) => !removedNodeKeys.has(packageResult.nodeKey))
        .map((packageResult) => packageResult.result),
    )).filter((result): result is Extract<ImportedModelResult, { kind: 'composed-assembly' }> => (
      result.kind === 'composed-assembly' && Boolean(result.resourceScope)
    ))
    let resourceRevision: ImportedModelResourceRevision | null = null
    try {
      if (previousComposedScope || restoredComposedScope) {
        resourceRevision = ImportedModelResourceRevision.prepare({
          currentOwner: composedResult,
          replacementOwner: restored?.composedResult,
          retainedOwners: retainedScopeOwners,
          replacedRoots: deletionRootObjects,
        })
      }
    } catch (revisionError) {
      const rollbackErrors = restored
        ? disposeCommittedAssemblyRestoreCandidate(
          restored,
          'assembly structure revision prepare rollback',
        )
        : []
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [revisionError, ...rollbackErrors],
          '装配结构资源 revision 准备失败且回滚不完整',
        )
      }
      throw revisionError
    }

    /**
     * 删除与恢复会改变 hierarchy、对象 UUID 和节点绑定。必须在发布新加载结果前，以最终场景
     * 快照重新构建统一运行时状态，保证 WorkAssembly、高亮和提交读取同一份身份关系。
     */
    const currentPackageResults = [
      ...previousPackageResults.filter((packageResult) => !removedNodeKeys.has(packageResult.nodeKey)),
      ...(restored?.packageResults ?? []),
    ]
    let committedProjectModelLoadResult: ProjectModelLoadResult
    let preparedWorkAssemblyRevision: PuppyCADWeb.PreparedCompleteWorkAssemblyRevision | null = null
    try {
      committedProjectModelLoadResult = createCommittedProjectModelLoadResult(
        input.nextLoadResult,
        {
          ...composedResult,
          hierarchy: nextHierarchy,
          persistenceStatus: input.nextLoadResult.persistenceStatus,
          sceneBindings: nextSceneBindings,
        },
      )
      preparedWorkAssemblyRevision = prepareScopedWorkAssemblyRevision({
        root: input.nextLoadResult.root,
        projectModelResult: committedProjectModelLoadResult,
        packageResults: currentPackageResults,
        importedResult: composedResult,
      })
    } catch (preparationError) {
      const rollbackErrors: unknown[] = []
      try {
        resourceRevision?.rollback()
      } catch (rollbackError) {
        rollbackErrors.push(rollbackError)
      }
      if (restored) {
        rollbackErrors.push(...disposeCommittedAssemblyRestoreCandidate(
          restored,
          'assembly structure revision validation rollback',
        ))
      }
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [preparationError, ...rollbackErrors],
          '装配结构 revision 校验失败且资源回滚不完整',
        )
      }
      throw preparationError
    }

    // 以上均属于可失败准备阶段；从这里开始只执行同步、不可中断的场景/缓存/Registry 发布。
    if (deletionRootObjects.length > 0) {
      deletionRootObjects = detachAssemblyDeletedObjects(scene, deletionRootObjects)
      removedNodeKeys.forEach((nodeKey) => {
        projectModelPackageResultByNodeKey.delete(nodeKey)
      })
    }
    if (restored) {
      insertObjectAtParentIndex(restored.parent, restored.composedResult.root, restored.parentIndex)
      Object.assign(composedResult.partAssetsByUuid, restored.composedResult.partAssetsByUuid)
      Object.assign(composedResult.assemblyAssetsByUuid, restored.composedResult.assemblyAssetsByUuid)
      restored.packageResults.forEach((packageResult) => {
        const current = projectModelPackageResultByNodeKey.get(packageResult.nodeKey) ?? []
        projectModelPackageResultByNodeKey.set(packageResult.nodeKey, [...current, packageResult])
      })
    }
    currentProjectModelLoadResult = committedProjectModelLoadResult
    composedResult.hierarchy = nextHierarchy
    composedResult.manifest = createProjectModelManifestFromRoot(input.nextLoadResult.root)
    composedResult.sceneBindings = nextSceneBindings
    composedResult.selectionIndex = createNextSelectionIndex(
      composedResult.selectionIndex,
      scene,
      input.removedNodeKeys,
      restoredSelectionTargetsByObjectId,
    )
    glbHierarchyByTabId.set(input.nextLoadResult.root.nodeKey, nextHierarchy)
    loadedResultByTabId.set(input.nextLoadResult.root.nodeKey, composedResult)

    const publishStructureRevision = (): void => {
      preparedWorkAssemblyRevision?.commit()
    }
    const retiredScopeReleaseErrors = importedModelRevisionSession.commit({
      ...(resourceRevision ? { resourceRevision } : {}),
      publish: publishStructureRevision,
      retireTopology: () => {
        if (previousComposedScope) {
          deletionRootObjects.forEach((object) => {
            disposeImportedModelTopologyHosts(object)
            object.clear()
          })
        }
      },
    })

    if (deletionRootObjects.length > 0) {
      // package owner 先按语义 cache 契约清理；cached-part 只释放实例关系，不能销毁 cache-owned GPU 资源。
      disposeReplacedProjectModelPackageResults(
        removedPackageResults.map((packageResult) => packageResult.result),
        currentPackageResults,
      )
      if (!previousComposedScope && removedPackageResults.length === 0) {
        // 只有无法定位 package owner 的 legacy 对象才允许使用场景差集兜底释放。
        const preservedResources = collectObjectTreeResources(scene)
        deletionRootObjects.forEach((object) => {
          disposeDetachedObjectTreeResources(object, preservedResources)
        })
      } else if (!previousComposedScope) {
        deletionRootObjects.forEach((object) => object.clear())
      }
    }
    if (retiredScopeReleaseErrors.length > 0) {
      logger.error(
        '[GlbLoader] release assembly structure resource revision owners failed',
        new AggregateError(retiredScopeReleaseErrors),
      )
    }

    clearImportedModelInteractionState()
    viewer?.requestImmediateRender?.()
    let runtimeSubtreeApplied = false
    try {
      runtimeSubtreeApplied = projectModelStructureFeatureTreeProjector.apply({
        tabId: input.nextLoadResult.root.nodeKey,
        activeTreeTabId: featureTreeStore.activeTreeTabId,
        previousHierarchy,
        nextHierarchy,
        removedSnapshots: removedHierarchySnapshots,
        addedSnapshots: restored?.hierarchySnapshots ?? [],
        fileIdByUuidMap: glbFileIdByReferenceKeyMap.value,
      })
    } catch (error) {
      /** 局部 UI 投影失败不能回滚已经提交的模型结构；完整刷新会重新建立权威树。 */
      logger.error('[GlbLoader] apply local assembly structure tree subtree failed', error)
    }
    if (runtimeSubtreeApplied) {
      return
    }
    try {
      await featureTreeStore.refreshTree(input.nextLoadResult.root.nodeKey)
    } catch (error) {
      logger.error('[GlbLoader] refresh tree after local assembly structure apply failed', error)
      throw new Error(
        `[GlbLoader] 局部装配结构已应用，但设计树刷新失败：rootNodeKey=${input.nextLoadResult.root.nodeKey}`,
        { cause: error },
      )
    }
  }

  /**
   * 局部删除项目模型节点。
   *
   * @param input 删除输入。
   * @returns 删除结果。
   */
  async function deleteProjectModelNodeLocally(
    input: DeleteProjectModelNodeLocallyInput,
  ): Promise<DeleteProjectModelNodeLocallyResult> {
    const currentLoadResult = currentProjectModelLoadResult

    if (!currentLoadResult) {
      throw new Error('当前项目模型尚未加载，无法删除装配组件')
    }

    const deletedNodeKeys = collectDeletedModelAssetNodeKeys(
      currentLoadResult.root,
      input.nextLoadResult.root,
    )

    if (!deletedNodeKeys.has(input.nodeKey)) {
      throw new Error(`删除后的项目模型结果未移除目标节点：${input.nodeKey}`)
    }

    // 删除入口通常来自当前选中对象；必须先清理选中/hover/datum 高亮，再序列化 Object3D。
    clearImportedModelInteractionState()
    options.getViewer()?.requestImmediateRender?.()
    const { payload, payloadRef } = await captureAssemblyDeletedSubtreePayload({
      nodeKey: input.nodeKey,
      deletedNodeKeys,
    })

    try {
      await applyProjectModelStructureLocally({
        nextLoadResult: input.nextLoadResult,
        removedNodeKeys: deletedNodeKeys,
        addedNodeKeys: new Set(),
        payload,
        beforeCommit: input.beforeCommit,
      })
    } catch (error) {
      input.rollbackBeforeCommit?.()
      throw error
    }

    return {
      loadResult: currentProjectModelLoadResult ?? input.nextLoadResult,
      payloadRef,
      deletedNodeKeys: Array.from(deletedNodeKeys),
    }
  }

  /**
   * 局部应用装配结构历史快照。
   *
   * @param snapshot 历史目标快照。
   * @param applyOptions 应用选项。
   */
  async function applyAssemblyStructureHistorySnapshotLocally(
    snapshot: AssemblyStructureHistorySnapshot,
    applyOptions: ApplyProjectModelStructureLocallyOptions = {},
  ): Promise<void> {
    const currentLoadResult = currentProjectModelLoadResult

    if (!currentLoadResult) {
      throw new Error('当前项目模型尚未加载，无法恢复装配结构历史')
    }

    const nextLoadResult = createProjectModelLoadResultFromAssemblyStructureHistorySnapshot(snapshot)
    const removedNodeKeys = collectDeletedModelAssetNodeKeys(currentLoadResult.root, nextLoadResult.root)
    const addedNodeKeys = collectAddedModelAssetNodeKeys(currentLoadResult.root, nextLoadResult.root)
    const payload = snapshot.deletedSubtreePayloadRef
      ? await readAssemblyDeletedSubtreePayload(snapshot.deletedSubtreePayloadRef)
      : null

    try {
      await applyProjectModelStructureLocally({
        nextLoadResult,
        removedNodeKeys,
        addedNodeKeys,
        payload,
        beforeCommit: applyOptions.beforeCommit,
      })
    } catch (error) {
      applyOptions.rollbackBeforeCommit?.()
      throw error
    }
  }

  /**
   * 创建项目模型世界矩阵读取器。
   *
   * 设计说明：该读取器只表达当前场景对象位姿，不再用于把移动结果写回
   * `workingAssemblyDocument`。装配工作草稿只记录协同节点数据，移动姿态由场景与离线包保存链路负责。
   *
   * @returns 根据装配节点 key 读取当前场景世界变换的函数；场景或绑定缺失时返回 null。
   */
  function createProjectModelWorldTransformReader(): (nodeKey: string) => AssemblyTransform | null {
    const loadResult = currentProjectModelLoadResult
    const viewer = options.getViewer()

    return (nodeKey: string): AssemblyTransform | null => {
      if (!loadResult || !viewer) {
        return null
      }

      for (const binding of loadResult.sceneBindings) {
        if (binding.nodeKey !== nodeKey) {
          continue
        }

        const sceneObject = viewer.getSceneObjectById?.(binding.objectId) ?? null
        if (!sceneObject) {
          continue
        }

        sceneObject.updateWorldMatrix(true, false)
        return assemblyTransformFromMatrix(sceneObject.matrixWorld)
      }

      return null
    }
  }

  /**
   * 整项目切换成功后收口旧项目运行态。
   *
   * 设计说明：整项目切换是项目加载边界，旧 root 及其子 tab、hierarchy、导入结果、pending loading
   * 都不应继续暴露给预览或编辑态；否则用户会在新模型下点到旧子装配 tab，或让旧请求的 loading
   * 状态污染当前项目。该函数只在 `applyProjectModelLoadResult` 成功返回后执行，因此失败时旧状态仍保留。
   *
   * @param rootNodeKey 新项目根节点 key。
   */
  function clearProjectModelRuntimeStateForRootSwitch(rootNodeKey: string): void {
    const retainedResult = loadedResultByTabId.get(rootNodeKey) ?? null
    const retainedHierarchy = glbHierarchyByTabId.get(rootNodeKey) ?? null
    const retainedTab = partTabsStore.tabs.find((tab) => tab.id === rootNodeKey) ?? null

    loadedResultByTabId.clear()
    if (retainedResult) {
      loadedResultByTabId.set(rootNodeKey, retainedResult)
    }

    glbHierarchyByTabId.clear()
    if (retainedHierarchy) {
      glbHierarchyByTabId.set(rootNodeKey, retainedHierarchy)
    }

    partTabsStore.tabs = retainedTab
      ? [{ ...retainedTab, loading: false, scopeObjectId: null }]
      : []
    partTabsStore.activeTabId = retainedTab ? rootNodeKey : null
    mountedGlbTabId.value = retainedResult ? rootNodeKey : null
    pendingPartTabId.value = null
    childTabLoadingCoordinator.reset()
    clearImportedModelInteractionState()
    abortCurrentInsertionHandle()
  }

  /**
   * 清空可继续复用的导入模型缓存内容。
   *
   * 设计说明：根 Viewer 重建后 loader 实例仍会继续服务新文档，因此不能调用会把缓存管理器标记为
   * disposed 的 `disposeCaches()`；这里只清空各缓存内容，保留缓存对象本身供后续加载复用。
   */
  function clearReusableImportedModelCaches(): void {
    caches.partAssetCache.clear()
    caches.assemblyModelCache.clear()
    caches.assemblyManifestCache.clear()
    caches.parsedGlbCache.clear()
    caches.glbResourceCache.clear()
    caches.importedBrepRuntimeCache.clear()
  }

  /**
   * 标记完整文档 runtime 已提交后的 web 侧提交失败。
   *
   * 设计说明：full-document Worker runtime 一旦 commit 就不能回滚到旧文档。若随后场景、tab、
   * Mate 文档或设计树投影提交失败，继续允许编辑会扩大新旧状态分裂；这里复用 CadSessionStore
   * 的 reload-required 通道，让 Editor 既有结构写守卫阻止后续写操作，并提示用户重新加载。
   *
   * @param error 触发半提交失败的异常。
   */
  function markFullDocumentRuntimeCommitRequiresReload(error: unknown): void {
    const errorDetail = resolveProjectModelLoadFailureErrorDetail(error)
    cadSessionStore.markRequiresReload(`CAD 文档运行时已切换，但模型界面提交失败：${errorDetail}`)
    options.showError('模型切换失败，当前项目需要重新加载后才能继续编辑')
  }

  /**
   * 标记局部子树 runtime 已提交后的 web 侧提交失败。
   *
   * 设计说明：局部子树 Worker runtime commit 后，Mate、BRep 与拓扑索引已经指向新子树。
   * 若随后设计树、tab 或缓存提交失败，不能继续做普通局部回滚，否则会把可见模型与 Worker
   * 几何状态再次拆开；这里进入 reload-required，让后续写操作停止，并提示用户重新加载整文档。
   *
   * @param error 触发半提交失败的异常。
   * @param targetNodeKey 已提交的局部子树节点 key，用于排查具体污染边界。
   */
  function markScopedSubtreeRuntimeCommitRequiresReload(
    error: unknown,
    targetNodeKey: string | null,
  ): void {
    const errorDetail = resolveProjectModelLoadFailureErrorDetail(error)
    const targetDetail = targetNodeKey ? `targetNodeKey=${targetNodeKey}；` : ''
    cadSessionStore.markRequiresReload(`CAD 局部子树运行时已切换，但模型界面提交失败：${targetDetail}${errorDetail}`)
    options.showError('局部模型切换失败，当前项目需要重新加载后才能继续编辑')
  }

  /**
   * 清理当前根 CAD 文档的全部 loader 运行态。
   *
   * 设计说明：该函数只服务“完整根文档边界”切换，包括根项目切换、提交后重载与 Viewer 重建；
   * 局部子树替换不能调用它，否则会破坏未受影响组件的 tab、hierarchy 和 Worker ownership。
   *
   * @param errorContext 资源释放失败时写入日志的上下文。
   * @param input.disposeMode 导入模型结果的释放策略；根重建保留缓存管理器，最终 dispose 保持 full dispose。
   * @param input.preserveActiveProjectRootSnapshotSwitch 是否保留当前根快照切换请求。
   */
  function clearRootDocumentRuntimeState(
    errorContext: string,
    input: {
      readonly disposeMode?: 'committed-background' | 'full'
      readonly preserveActiveProjectRootSnapshotSwitch?: boolean
    } = {},
  ): void {
    cancelScheduledSceneContentFocus()
    clearImportedModelInteractionState()
    if (!input.preserveActiveProjectRootSnapshotSwitch) {
      abortActiveProjectRootSnapshotSwitch()
    }
    abortActiveProjectRootRuntimeSwitchInBackground(errorContext)
    abortCurrentInsertionHandle()
    requestRegistry.abortAllCurrent()

    const scene = options.getScene()
    if (scene) {
      detachImportedAssemblyFromScene(scene)
    }

    const activeWorkAssemblySnapshot = completeWorkAssemblySession.getActiveSnapshot()
    const snapshotOwnedResults = activeWorkAssemblySnapshot
      ? new Set<ImportedModelResult>([
        activeWorkAssemblySnapshot.importedResult,
        ...activeWorkAssemblySnapshot.packageResults.map((item) => item.result),
      ])
      : null
    // Viewer/Worker 根运行时被清空后，旧快照不能再作为可编辑 WorkAssembly 恢复。
    completeWorkAssemblySession.invalidate()

    const cachedResults = collectUniqueImportedModelResults([
      ...loadedResultByTabId.values(),
      ...collectProjectModelPackageImportedResults(),
    ]).filter((result) => !snapshotOwnedResults?.has(result))
    for (const cachedResult of cachedResults) {
      if (input.disposeMode === 'full') {
        try {
          disposeImportedModel(cachedResult)
        } catch (error) {
          logger.error(`[GlbLoader] ${errorContext} dispose imported model failed`, error)
        }
        continue
      }
      if (isPuppyPackageImportedModelResult(cachedResult)) {
        continue
      }
      disposeCommittedBackgroundImportedModelResult(cachedResult, errorContext)
    }
    if (input.disposeMode !== 'full') {
      disposePuppyPackageImportedModelResults(cachedResults)
    }

    loadedResultByTabId.clear()
    reportedExternalReferenceWarningByTabId.clear()
    glbHierarchyByTabId.clear()
    projectModelPackageResultByNodeKey.clear()
    currentProjectModelLoadResult = null
    currentProjectModelContextIdentity = null
    currentRootDocumentType = undefined
    projectModelFileBytesByIdentityKey.clear()
    assemblyDraftDocumentStore.clear()
    openProjectSessionInitRequestId += 1
    latestInsertionTreeRefreshRequestId += 1
    clearLoadableOpenProjectRootNode({ blockStoreFallback: true })
    mountedGlbTabId.value = null
    pendingPartTabId.value = null
    childTabLoadingCoordinator.reset()
    glbFeatureTree.clearTree()
    clearReusableImportedModelCaches()
    partTabsStore.reset()
  }

  /**
   * 为根 Viewer 重建释放旧根文档运行态。
   *
   * 设计说明：Editor 仍会继续使用同一个 loader 实例加载新根文档，因此这里只清根文档状态，
   * 不设置 `isDisposed`，也不重置 cadSessionStore。
   */
  function resetForRootViewerRebuild(input: RootViewerRebuildResetOptions = {}): void {
    clearRootDocumentRuntimeState('root viewer rebuild', {
      preserveActiveProjectRootSnapshotSwitch: input.preserveActiveProjectRootSnapshotSwitch,
    })
  }

  /**
   * 中止当前仍在执行的整项目历史快照切换。
   *
   * 设计说明：历史版本列表允许快速切换。新切换开始时，旧切换不仅要在结果落地阶段失效，
   * 还要尽早中止版本文件 fetch，避免无用的大模型请求继续占用带宽和内存。
   */
  function abortActiveProjectRootSnapshotSwitch(): void {
    if (!activeProjectRootSnapshotAbortController) {
      return
    }
    activeProjectRootSnapshotAbortController.abort()
    activeProjectRootSnapshotAbortController = null
    // 历史根切换可能已经打开 staged Worker runtime 并卡在文件读取；
    // 抢占请求必须立即关闭它，不能等被 abort 的 fetch/解析链路自然返回。
    abortActiveProjectRootRuntimeSwitchInBackground('project-root snapshot switch abort')
  }

  /**
   * 为一次新的整项目历史快照切换创建取消控制器。
   *
   * @returns 本次切换专属的取消控制器。
   */
  function createProjectRootSnapshotAbortController(): AbortController {
    abortActiveProjectRootSnapshotSwitch()
    const abortController = new AbortController()
    activeProjectRootSnapshotAbortController = abortController
    return abortController
  }

  /**
   * 解析历史提交对应的根 CAD 节点快照。
   *
   * 设计说明：整项目切换的唯一入口只消费根节点快照，不直接消费旧文件清单。
   * 这样预览态切换和“继续编辑”可以共享同一条模型落地链路，外层只决定成功后进入哪种模式。
   *
   * @param input 整项目切换输入。
   * @returns 可交给 project-model-loading service 的根 CAD 节点。
   */
  async function resolveProjectRootSnapshotNode(
    input: ProjectModelRootSnapshotSwitchInput,
    resolveOptions: ProjectModelRootSnapshotResolveOptions = {},
  ): Promise<CadNode> {
    const signal = resolveOptions.signal
    // 版本切换的取消信号必须覆盖 OpenProject 初始化阶段，避免旧切换继续占用网络与解析资源。
    assertProjectModelLoadSignalActive(signal)
    const openProjectInput = resolveProjectRootSnapshotOpenProjectInput(
      input.snapshot,
      {
        repositoryId: cadSessionStore.currentRepositoryId,
        branchId: cadSessionStore.currentBranchId,
      },
    )

    const sessionInitRequestId = beginOpenProjectSessionInit()
    if (signal) {
      await cadSessionStore.initSession(openProjectInput, { signal })
    } else {
      await cadSessionStore.initSession(openProjectInput)
    }
    assertProjectModelLoadSignalActive(signal)
    assertOpenProjectSessionInitCurrent(sessionInitRequestId)
    assertProjectRootSnapshotSwitchCurrent(input.snapshot, input.context)
    if (cadSessionStore.error) {
      throw new Error(`[GlbLoader] OpenProject 切换历史版本失败：${cadSessionStore.error}`)
    }
    const rootNode = cadSessionStore.openProjectRootNode
    if (!rootNode) {
      throw new Error(`[GlbLoader] OpenProject 未返回根节点，无法切换历史版本：commitId=${input.snapshot.commitId}`)
    }
    if (openProjectInput.commitId && rootNode.commitId !== openProjectInput.commitId) {
      throw new Error(`[GlbLoader] OpenProject 返回提交与目标不一致：expected=${openProjectInput.commitId}, actual=${rootNode.commitId}`)
    }

    clearLoadableOpenProjectRootNode({ blockStoreFallback: true })
    const nextRootNode = await resolveCurrentLoadableProjectRootNode({
      isCurrent: () => sessionInitRequestId === openProjectSessionInitRequestId
        && !signal?.aborted
        && (input.context?.isCurrent() ?? true),
    })
    assertProjectModelLoadSignalActive(signal)
    if (!nextRootNode) {
      throw new Error('[GlbLoader] 整项目历史版本切换未返回 OpenProject root')
    }

    return nextRootNode
  }
  /**
   * 整体切换项目根模型快照。
   *
   * 设计说明：
   * 1. 预览态和“预览后继续编辑”都调用这个函数，避免模型、设计树和草稿 store 分散切换；
   * 2. `beforeCommit` 中才写入装配草稿，确保模型下载/解析失败不会污染当前可见项目；
   * 3. 该操作属于项目加载边界，不是草图、特征或装配命令，因此不写入 Undo/Redo 历史。
   *
   * @param input 整项目根快照切换输入。
   */
  async function switchProjectModelRootSnapshot(
    input: ProjectModelRootSnapshotSwitchInput,
  ): Promise<void> {
    const { snapshot, context } = input
    assertProjectRootSnapshotSwitchCurrent(snapshot, context)
    const abortController = createProjectRootSnapshotAbortController()
    const shouldRefreshDesignTreeAfterApply = input.replaceDesignTreeAfterApply === true
    let runtimeSwitchSession: ActiveProjectRootRuntimeSwitchSession | null = null
    let nextProjectModelContextIdentity: ProjectModelContextIdentity | null = null

    try {
      const nextRootNode = await resolveProjectRootSnapshotNode(input, {
        signal: abortController.signal,
      })
      assertProjectRootSnapshotSwitchCurrent(snapshot, context)
      assertProjectModelLoadSignalActive(abortController.signal)

      const nextRootLoadUnit = mapProjectRootSnapshotToLoadUnit(nextRootNode)
      const nextDraftDocumentStore = new AssemblyDraftDocumentStore()
      const supportsCompleteWorkAssembly = completeWorkAssemblySession.supportsRoot({
        root: nextRootLoadUnit,
        archiveFile: loadableOpenProjectArchiveFile,
      })
      const buildPreparedRoot = async (
        workAssemblyBuildContext?: PuppyCADWeb.CompleteWorkAssemblyBuildContext,
      ): Promise<PuppyCADWeb.CompleteWorkAssemblyPublishInput> => {
        const preparedLoad = await prepareProjectModelBackendLoad({
          planInput: { kind: 'root-load', root: nextRootLoadUnit },
          reader: createAuthorizedVersionFileReader({
            getAuthorizationHeader: resolveVersionFileAuthorizationHeader,
            refreshAuthorizationHeader: refreshVersionFileAuthorizationHeader,
          }),
          fileCache: createProjectModelFileByteCacheAdapter(),
          nodeReloadAdapter: options.projectRootNodeReloadAdapter,
          backendBaseUrl: apiConfig.baseUrl,
          signal: abortController.signal,
        })
        const preparedResult = preparedLoad.loadResult
        assertProjectRootSnapshotSwitchCurrent(snapshot, context)
        assertProjectModelLoadSignalActive(abortController.signal)

        await input.beforeRuntimeSwitch?.()
        assertProjectRootSnapshotSwitchCurrent(snapshot, context)
        assertProjectModelLoadSignalActive(abortController.signal)

        nextProjectModelContextIdentity = createProjectRootContextIdentity(preparedResult.root)
        runtimeSwitchSession = await beginProjectRootDocumentRuntimeSwitch(nextProjectModelContextIdentity)
        assertProjectRootSnapshotSwitchCurrent(snapshot, context)
        assertProjectModelLoadSignalActive(abortController.signal)

        await applyProjectModelLoadResult(preparedResult, {
          loadingText: input.loadingText ?? `正在加载历史版本：${snapshot.title}`,
          loadingOwner: input.loadingOwner,
          preserveLoadingAfterApply: input.preserveLoadingAfterApply,
          deferFeatureTreeRefresh: shouldRefreshDesignTreeAfterApply,
          signal: abortController.signal,
          brepRegistrar: getProjectRootRuntimeSwitchHandle(runtimeSwitchSession).brepRegistrar,
          preparedFileReadResults: preparedLoad.fileReadResults,
          preparedCompleteGlbPackageSnapshotsByNodeKey:
            preparedLoad.completeGlbPackageSnapshotsByNodeKey,
          workAssemblyRegistryOwner: Boolean(workAssemblyBuildContext),
          workAssemblyBuildContext,
          beforeCommit: () => {
            assertProjectRootSnapshotSwitchCurrent(snapshot, context)
            assertProjectModelLoadSignalActive(abortController.signal)
            initializeAssemblyDraftDocumentStoreFromRoot({
              root: preparedResult.root,
              loadResult: preparedResult,
              store: nextDraftDocumentStore,
            })
          },
          beforeModelCommit: async () => {
            if (!runtimeSwitchSession) {
              throw new Error('项目根快照切换缺少 runtime switch 会话，无法提交 Worker 文档运行时')
            }
            await commitProjectRootRuntimeSwitchSession(runtimeSwitchSession)
          },
        })
        const committedResult = currentProjectModelLoadResult
        const importedResult = loadedResultByTabId.get(preparedResult.root.nodeKey)
        if (!committedResult || !importedResult) {
          throw new Error(`项目根快照提交后缺少 WorkAssembly 交付物：rootNodeKey=${preparedResult.root.nodeKey}`)
        }
        return {
          root: committedResult.root,
          archiveFile: loadableOpenProjectArchiveFile,
          projectModelResult: committedResult,
          packageResults: collectProjectModelPackageResults(),
          importedResult,
        }
      }

      const delivery = supportsCompleteWorkAssembly
        ? (await completeWorkAssemblySession.resolveRoot(
            { root: nextRootLoadUnit, archiveFile: loadableOpenProjectArchiveFile },
            async (buildContext) => buildPreparedRoot(buildContext),
          )).snapshot
        : await buildPreparedRoot()
      const loadResult = delivery.projectModelResult
      if (supportsCompleteWorkAssembly && !nextProjectModelContextIdentity) {
        nextProjectModelContextIdentity = createProjectRootContextIdentity(loadResult.root)
        restoreActiveWorkAssemblySnapshot(
          loadResult.root,
          loadResult.root.nodeKey,
          options.getScene()!,
          nextProjectModelContextIdentity,
        )
        initializeAssemblyDraftDocumentStoreFromRoot({
          root: loadResult.root,
          loadResult,
          store: nextDraftDocumentStore,
        })
      }
      const nextRootNodeKey = loadResult.root.nodeKey

      // WorkAssembly 交付可能经过异步等待，退役请求不得清理新切换已经发布的标签和缓存。
      assertProjectRootSnapshotSwitchCurrent(snapshot, context)
      assertProjectModelLoadSignalActive(abortController.signal)
      assemblyDraftDocumentStore.clear()
      const nextDraftRecord = nextDraftDocumentStore.get(nextRootNodeKey)
      if (nextDraftRecord) {
        assemblyDraftDocumentStore.set(nextDraftRecord)
      }
      clearProjectModelRuntimeStateForRootSwitch(nextRootNodeKey)
      if (shouldRefreshDesignTreeAfterApply) {
        // 等特征恢复完成后直接构建最终树；merge 路径自身已原子替换旧节点表，无须先清一次树。
        await pendingPuppyFeatureRestore
        assertProjectRootSnapshotSwitchCurrent(snapshot, context)
        assertProjectModelLoadSignalActive(abortController.signal)
        await featureTreeStore.refreshTree(nextRootNodeKey)
      }
      // 刷新也可能跨越下一次根切换，旧请求不得在最后回写过期文档身份。
      assertProjectRootSnapshotSwitchCurrent(snapshot, context)
      assertProjectModelLoadSignalActive(abortController.signal)
      currentProjectModelContextIdentity = nextProjectModelContextIdentity
    } catch (error) {
      await tryAbortProjectRootRuntimeSwitchSession(runtimeSwitchSession, 'project-root snapshot switch catch')
      throw error
    } finally {
      if (activeProjectRootSnapshotAbortController === abortController) {
        activeProjectRootSnapshotAbortController = null
      }
    }
  }

  /**
   * 根据分支节点列表中的子节点引用定位当前项目模型一级子节点。
   *
   * @param reference 分支节点列表编码出来的子节点身份。
   * @returns 命中的一级模型节点；无法唯一定位时返回 null。
   */
  function resolveCurrentProjectModelFirstLevelNodeByReference(reference: {
    readonly repositoryId: string
    readonly instanceUuid?: string
    readonly adoptedCommitId?: string | null
    readonly nodeKey?: string
  }): ModelAssetNode | null {
    const root = currentProjectModelLoadResult?.root
    if (!root) {
      return null
    }

    const nodeKey = reference.nodeKey?.trim()
    if (nodeKey) {
      const exactNodeKey = root.children.find((child) => child.nodeKey === nodeKey)
      if (exactNodeKey) {
        return exactNodeKey
      }
    }

    const instanceUuid = reference.instanceUuid?.trim()
    if (instanceUuid) {
      const exactInstance = root.children.find((child) =>
        child.repositoryId === reference.repositoryId && child.instanceUuid === instanceUuid)
      if (exactInstance) {
        return exactInstance
      }
    }

    const candidates = root.children.filter((child) => child.repositoryId === reference.repositoryId)
    if (candidates.length === 1) {
      return candidates[0]
    }

    const adoptedCommitId = reference.adoptedCommitId?.trim()
    if (adoptedCommitId) {
      return candidates.find((child) => child.commitId === adoptedCommitId) ?? null
    }

    return null
  }

  /**
   * 读取当前项目根装配引用识别所需的运行态上下文。
   *
   * @returns 当前加载结果、工作装配文档和 root hierarchy；项目模型未加载时返回 null。
   */
  function resolveCurrentAssemblyBranchNodeReferenceContext(): {
    readonly loadResult: ProjectModelLoadResult
    readonly assemblyDocument: AssemblyDocument
    readonly hierarchy: GlbHierarchyNode | null
  } | null {
    const loadResult = currentProjectModelLoadResult
    const rootNodeKey = loadResult?.root.nodeKey
    if (!loadResult || !rootNodeKey) {
      return null
    }

    const assemblyDocument = assemblyDraftDocumentStore.get(rootNodeKey)?.workingAssemblyDocument
      ?? loadResult.assemblyDocumentsByNodeKey.get(rootNodeKey)
      ?? null
    if (!assemblyDocument) {
      return null
    }

    return {
      loadResult,
      assemblyDocument,
      hierarchy: glbHierarchyByTabId.get(rootNodeKey)
        ?? (partTabsStore.activeTabId ? glbHierarchyByTabId.get(partTabsStore.activeTabId) : undefined)
        ?? null,
    }
  }

  /**
   * 同时按 canonical nodeKey 与工作装配文档 nodeKey 查询运行时绑定。
   *
   * complete 归档物化后，两套 key 属于不同命名空间；加载 finalization 已建立反向索引，
   * 此处只消费该权威关系，禁止再次通过显示名称或 instance-id 猜测。
   *
   * @param runtimeState 当前项目模型运行时状态。
   * @param nodeKey canonical 或工作装配文档节点 key。
   * @returns 唯一运行时绑定；未建立关系时返回 null。
   */
  function resolveProjectModelRuntimeBinding(
    runtimeState: PuppyCAD.ProjectModelRuntimeState,
    nodeKey: string,
  ): PuppyCAD.ProjectModelRuntimeBinding | null {
    return runtimeState.bindingsByNodeKey.get(nodeKey)
      ?? runtimeState.bindingsByAssemblyDocumentNodeKey?.get(nodeKey)
      ?? null
  }

  /**
   * 根据分支节点列表中的子节点引用识别当前装配 direct child。
   *
   * @param reference 分支节点列表编码出来的子节点身份。
   * @returns 命中的装配节点引用目标；无法定位时返回 null。
   */
  function resolveCurrentProjectModelFirstLevelTargetByReference(reference: {
    readonly repositoryId: string
    readonly instanceUuid?: string
    readonly adoptedCommitId?: string | null
    readonly nodeKey?: string
  }): AssemblyBranchNodeReferenceTarget | null {
    const context = resolveCurrentAssemblyBranchNodeReferenceContext()
    if (!context) {
      return null
    }

    return resolveAssemblyBranchNodeReferenceTarget({
      root: context.loadResult.root,
      assemblyDocument: context.assemblyDocument,
      hierarchy: context.hierarchy,
      resolveRuntimeBindingByNodeKey: nodeKey => (
        resolveProjectModelRuntimeBinding(context.loadResult.runtimeState, nodeKey)
      ),
      rootNodeKey: context.loadResult.root.nodeKey,
      repositoryPuppyPackageFileId: resolvePuppyAssemblyPackageRootFile(context.loadResult.root)?.fileId,
      reference,
    })
  }

  /**
   * 根据分支节点列表中的子节点引用定位 root `.puppy` 内部的运行期 hover 目标。
   *
   * 设计说明：
   * 提交后重新打开装配体时，根节点通常命中单个 `.puppy` 包，OpenProject root.children
   * 不再承载一级子节点；但 `_assembly.json` 工作文档和 `.puppy` hierarchy 都保留稳定
   * assemblyNodeId。该入口把这两份权威数据接起来，让左侧分支节点 hover 仍能命中组件包围盒。
   *
   * @param reference 分支节点列表编码出来的子节点身份。
   * @returns 可交给 Viewer 高亮的运行期目标；无法唯一定位时返回 null。
   */
  function resolveCurrentProjectModelFirstLevelHoverTargetByReference(reference: {
    readonly repositoryId: string
    readonly instanceUuid?: string
    readonly adoptedCommitId?: string | null
    readonly nodeKey?: string
  }): AssemblyBranchNodeHoverTarget | null {
    const context = resolveCurrentAssemblyBranchNodeReferenceContext()
    if (!context) {
      return null
    }

    return resolveAssemblyBranchNodeRuntimeHoverTarget({
      root: context.loadResult.root,
      assemblyDocument: context.assemblyDocument,
      hierarchy: context.hierarchy,
      resolveRuntimeBindingByNodeKey: nodeKey => (
        resolveProjectModelRuntimeBinding(context.loadResult.runtimeState, nodeKey)
      ),
      rootNodeKey: context.loadResult.root.nodeKey,
      reference,
    })
  }

  /**
   * 从项目根节点包加载结果中读取 `.puppy/assembly.json` 的根配合文档。
   *
   * 设计说明：
   * 该字段保留给非项目模型的旧恢复入口；项目模型会额外通过 `resolveProjectModelPuppyMateDocuments`
   * 收集 root 与子装配包的 Mate 文档，并由 cad-core 的 scoped 恢复保证子装配不会污染外层装配。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 根包携带的配合文档；不存在时返回 undefined。
   */
  function resolvePuppyMateDocumentForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): AssemblyMateDocumentJSON | undefined {
    return packageResults.find((packageResult) =>
      packageResult.nodeKey === root.nodeKey && Boolean(packageResult.puppyMateDocument),
    )?.puppyMateDocument
  }

  //------ 项目模型 puppy Mate/基准文档重映射 开始------

  /**
   * 创建项目模型 `.puppy` Mate 文档恢复单元。
   *
   * @param root 当前项目模型根节点。
   * @param packageResult 单个节点包加载结果。
   * @returns 可恢复的 Mate 文档单元；非装配包返回 null。
   */
  function createProjectModelPuppyMateDocument(
    root: ModelAssetNode,
    packageResult: LoadedProjectModelPackageResult,
  ): ProjectModelPuppyMateDocument | null {
    const document = packageResult.puppyMateDocument
    if (!document) {
      return null
    }

    const restoreAsRootDocument = packageResult.nodeKey === root.nodeKey
    const aliasByNodeId = createCadCoreProjectModelPuppyMateNodeAliasByNodeId({
      document,
      hierarchy: packageResult.result.hierarchy,
      targetRootAssemblyId: packageResult.nodeKey,
      scopedTargetRootAssemblyId: restoreAsRootDocument ? undefined : packageResult.nodeKey,
    })
    const remappedDocument = remapPuppyAssemblyMateDocumentNodeReferences({
      mates: document,
      aliasByNodeId,
    })
    return {
      assemblyNodeId: packageResult.nodeKey,
      document: remappedDocument,
      restoreAsRootDocument,
      scope: restoreAsRootDocument ? undefined : 'subtree',
    }
  }

  /**
   * 从项目根节点包加载结果中读取并重映射 `.puppy` 基准扩展。
   *
   * 设计说明：
   * 1. 文档级基准属于当前 Viewer 根文档，只在完整项目根加载时恢复 root 包数据；
   * 2. 包内 `assemblyNodeId` 可能仍是离线包内部 ID，恢复前必须映射到 OpenProject 的 nodeKey；
   * 3. 子装配包的基准不能在这里整表恢复，否则局部包会覆盖父装配当前文档的基准列表。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 已按项目 nodeKey 重映射后的基准扩展；根包不是 `.puppy` 时返回 undefined。
   */
  //------ 草图离线包定义持久化 开始------
  /**
   * 从项目根节点包加载结果中读取 `.puppy` 草图扩展。
   *
   * 设计说明：
   * 1. 草图属于当前 Viewer 根文档，只在完整项目根加载时恢复 root 包数据；
   * 2. 草图记录内部使用持久 ID 引用，不涉及装配节点 ID，无需 nodeKey 重映射；
   * 3. 子装配包的草图不在这里恢复，避免局部包覆盖父文档草图。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 根包携带的草图扩展；根包不是 `.puppy` 或无草图时返回 undefined。
   */
  function resolvePuppySketchExtensionForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): PuppySketchExtension | undefined {
    const packageResult = packageResults.find((item) =>
      item.nodeKey === root.nodeKey && Boolean(item.puppySketchExtension),
    )
    return packageResult?.puppySketchExtension
  }

  /**
   * 从项目根节点包加载结果中读取转换实体引用扩展。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 根包携带的引用扩展；无引用时返回 undefined。
   */
  function resolvePuppySketchEntityReferenceExtensionForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): PuppySketchEntityReferenceExtension | undefined {
    return packageResults.find((item) => (
      item.nodeKey === root.nodeKey && Boolean(item.puppySketchEntityReferenceExtension)
    ))?.puppySketchEntityReferenceExtension
  }
  //------ 草图离线包定义持久化 结束------

  //------ 特征离线包定义持久化 开始------
  /**
   * 从项目根节点包加载结果中读取 `.puppy` 特征扩展。
   *
   * 设计说明：特征时间线属于当前 Viewer 根文档，只在完整项目根加载时恢复 root 包数据；
   * 快照内草图引用为持久 ID，与装配节点 ID 无关，无需 nodeKey 重映射。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 根包携带的特征扩展；根包不是 `.puppy` 或无特征时返回 undefined。
   */
  function resolvePuppyFeatureExtensionForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): PuppyFeatureExtension | undefined {
    const packageResult = packageResults.find((item) =>
      item.nodeKey === root.nodeKey && Boolean(item.puppyFeatureExtension),
    )
    return packageResult?.puppyFeatureExtension
  }
  //------ 特征离线包定义持久化 结束------

  /**
   * 从项目根节点包加载结果中读取 `.puppy` 剖面视图扩展。
   *
   * 剖面视图属于 Viewer 根文档显示状态，只允许完整根包决定恢复结果。返回 `undefined`
   * 表示本次结果不具备根视图权威性；返回 `null` 表示权威根包没有剖面定义，需要清空旧状态。
   *
   * @param root 当前项目根资产节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @returns 根包剖面扩展、显式空状态或非权威状态。
   */
  function resolvePuppySectionViewExtensionForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): PuppySectionViewExtension | null | undefined {
    const packageResult = packageResults.find((item) =>
      item.nodeKey === root.nodeKey && item.puppySectionViewExtension !== undefined,
    )
    return packageResult?.puppySectionViewExtension
  }

  function resolvePuppyDatumExtensionForProjectModelRoot(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
  ): PuppyDatumExtension | undefined {
    const packageResult = packageResults.find((item) =>
      item.nodeKey === root.nodeKey && Boolean(item.puppyDatumExtension),
    )
    if (!packageResult?.puppyDatumExtension) {
      return undefined
    }

    const aliasByNodeId = createCadCoreProjectModelPuppyNodeAliasByNodeId({
      documentAssemblyId: packageResult.puppyMateDocument?.assemblyId,
      hierarchy: packageResult.result.hierarchy,
      targetRootAssemblyId: packageResult.nodeKey,
    })
    return remapPuppyDatumExtensionNodeReferences({
      datumExtension: packageResult.puppyDatumExtension,
      aliasByNodeId,
    })
  }
  //------ 项目模型 puppy Mate/基准文档重映射 结束------

  /**
   * 解析本次项目模型加载涉及的全部 `.puppy` Mate 文档。
   *
   * 设计说明：
   * root `.puppy` 代表当前 Viewer 的根装配文档；子装配 `.puppy` 代表该子装配自身文件内的
   * Mate，切换子节点版本后也必须恢复，否则设计树只能看到空的“配合”文件夹。
   *
   * @param root 当前项目模型根节点。
   * @param packageResults 本次项目模型加载完成的节点包结果。
   * @param cleanedRootMateDocument 调用方已经清理过旧子树 Mate 引用的根文档；存在时覆盖 root 包内 Mate 文档。
   * @returns 按根文档优先、子装配随后恢复的 Mate 文档单元。
   */
  function resolveProjectModelPuppyMateDocuments(
    root: ModelAssetNode,
    packageResults: readonly LoadedProjectModelPackageResult[],
    cleanedRootMateDocument?: AssemblyMateDocumentJSON,
  ): readonly ProjectModelPuppyMateDocument[] {
    const mateDocuments = packageResults
      .map((packageResult) => createProjectModelPuppyMateDocument(root, packageResult))
      .filter((document): document is ProjectModelPuppyMateDocument => document !== null)
      .sort((left, right) => Number(right.restoreAsRootDocument) - Number(left.restoreAsRootDocument))

    if (!cleanedRootMateDocument) {
      return mateDocuments
    }

    return [
      {
        assemblyNodeId: root.nodeKey,
        document: cleanedRootMateDocument,
        restoreAsRootDocument: true,
      },
      ...mateDocuments.filter((mateDocument) => !mateDocument.restoreAsRootDocument),
    ]
  }

  /**
   * 将项目模型 `.puppy` Mate 文档恢复到当前 Viewer。
   *
   * 设计说明：
   * root 文档代表整个 Viewer Mate 文档，子装配文档只能按装配范围替换，避免局部版本切换清掉父装配配合。
   *
   * @param mateDocuments 已经按项目模型 nodeKey 重映射后的 Mate 文档恢复单元。
   * @param restoreMode 恢复模式；局部切换时根文档不得重放切换前的全量组件位姿。
   * @param replacedAssemblyNodeId 局部替换目标；新版本无自身 Mate 时用于清空旧目标 scope。
   */
  function restoreProjectModelPuppyMateDocuments(
    mateDocuments: readonly ProjectModelPuppyMateDocument[],
    restoreMode: 'full-document' | 'scoped-subtree' = 'full-document',
    writableComponentInstanceIds?: readonly string[],
    replacedAssemblyNodeId?: string,
  ): void {
    const scopedReplacementAssemblyNodeId = restoreMode === 'scoped-subtree'
      ? replacedAssemblyNodeId?.trim() ?? ''
      : ''
    const mustClearReplacedScope = Boolean(scopedReplacementAssemblyNodeId)
      && !mateDocuments.some((mateDocument) => (
        !mateDocument.restoreAsRootDocument
        && mateDocument.assemblyNodeId === scopedReplacementAssemblyNodeId
      ))
    if (mateDocuments.length === 0 && !mustClearReplacedScope) {
      return
    }

    const controller = options.getViewer()?.assemblyMateController
    if (mustClearReplacedScope) {
      /**
       * 先清空整个替换目标，再恢复目标内部实际存在的子装配文档。
       * 顺序不能反过来，否则 target 的 subtree 覆盖会再次删掉刚恢复的后代 Mate。
       */
      controller?.fromJSONByAssembly?.(
        scopedReplacementAssemblyNodeId,
        createAssemblyMateDocument({ assemblyId: scopedReplacementAssemblyNodeId }),
        {
          scope: 'subtree',
          restoreReferences: true,
          notifyDocumentChanged: mateDocuments.length === 0,
          preserveCurrentComponentStates: true,
        },
      )
    }
    mateDocuments.forEach((mateDocument, index) => {
      const notifyDocumentChanged = index === mateDocuments.length - 1
      if (mateDocument.restoreAsRootDocument) {
        controller?.fromJSON(mateDocument.document, {
          restoreReferences: true,
          notifyDocumentChanged,
          ...(restoreMode === 'scoped-subtree'
            ? { preserveCurrentComponentStates: true }
            : {}),
        })
        return
      }

      controller?.fromJSONByAssembly?.(mateDocument.assemblyNodeId, mateDocument.document, {
        scope: mateDocument.scope,
        restoreReferences: true,
        notifyDocumentChanged,
        ...(restoreMode === 'scoped-subtree'
          ? { preserveCurrentComponentStates: true }
          : {}),
      })
    })
    solveRestoredCommittedPuppyMates(
      restoreMode === 'scoped-subtree' ? writableComponentInstanceIds : undefined,
    )
  }

  /**
   * 在 Mate 文档恢复完成后立即收敛已提交配合。
   *
   * 设计说明：
   * 1. `.puppy` 文档恢复只保证 Mate 定义、引用和组件快照回到控制器；
   * 2. 子装配版本切换后的新几何可能尚未满足这些 Mate，必须交给 cad-core 的恢复后求解入口处理；
   * 3. web-client 不拼装求解器输入，也不伪造移动命令，保持配合求解语义集中在 cad-core。
   */
  function solveRestoredCommittedPuppyMates(
    writableComponentInstanceIds?: readonly string[],
  ): void {
    options.getViewer()?.assemblyMateController?.solveCommittedMatesAfterDocumentRestore?.({
      updateDocumentStatus: true,
      ...(writableComponentInstanceIds
        ? { writableComponentInstanceIds }
        : {}),
    })
  }

  /**
   * 将加载结果携带的 `.puppy` 配合文档恢复到当前 Viewer。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppyMateDocument(loadedModel: LoadedImportedModel): void {
    if (loadedModel.displayType !== 'assembly') {
      return
    }

    const controller = options.getViewer()?.assemblyMateController
    const projectModelMateDocuments = loadedModel.projectModelPuppyMateDocuments ?? []
    if (projectModelMateDocuments.length > 0) {
      restoreProjectModelPuppyMateDocuments(projectModelMateDocuments)
      return
    }

    if (!loadedModel.puppyMateDocument) {
      return
    }

    controller?.fromJSON(loadedModel.puppyMateDocument, {
      restoreReferences: true,
      notifyDocumentChanged: true,
    })
    solveRestoredCommittedPuppyMates()
  }

  //------ 离线包基准扩展 开始------
  /**
   * 将加载结果携带的 `.puppy` 基准扩展恢复到当前 Viewer。
   *
   * 设计说明：
   * 1. `puppyDatumExtension` 只在 `.puppy` 包加载链路上设置，普通 GLB tab 切换不能清空当前建模基准；
   * 2. 空扩展对象表示“当前 `.puppy` 文档没有用户基准”，仍需要覆盖旧文档残留；
   * 3. 恢复必须发生在设计树刷新前，否则基准节点投影会读取到旧状态。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppyDatumExtension(loadedModel: LoadedImportedModel): void {
    if (!loadedModel.puppyDatumExtension) {
      return
    }
    options.getViewer()?.restorePuppyDatumExtension?.(loadedModel.puppyDatumExtension)
  }
  //------ 离线包基准扩展 结束------

  //------ 草图离线包定义持久化 开始------
  /**
   * 将加载结果携带的 `.puppy` 草图扩展恢复到当前 Viewer。
   *
   * 设计说明：
   * 1. `puppySketchExtension` 只在 `.puppy` 包加载链路上设置，普通 GLB tab 切换不触发恢复；
   * 2. 恢复必须发生在场景挂载后，由 SketchGroupManager 统一接管索引与显示态；
   * 3. 同 ID 草图已存在时 restoreGroup 幂等跳过，避免 tab 反复切换产生重复草图。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppySketchExtension(loadedModel: LoadedImportedModel): void {
    if (!loadedModel.puppySketchExtension) {
      return
    }
    options.getViewer()?.restorePuppySketchExtension?.(loadedModel.puppySketchExtension)
  }

  /**
   * 将加载结果携带的转换实体引用 Record 恢复到当前 Viewer。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppySketchEntityReferenceExtension(
    loadedModel: LoadedImportedModel,
  ): void {
    if (
      !loadedModel.puppySketchEntityReferenceExtension
      && !isPuppyPackageImportedModelResult(loadedModel.result)
      && !loadedModel.puppyDatumExtension
      && !loadedModel.puppySketchExtension
      && !loadedModel.puppyFeatureExtension
    ) {
      return
    }
    options.getViewer()?.restorePuppySketchEntityReferenceExtension?.(
      loadedModel.puppySketchEntityReferenceExtension,
    )
  }
  //------ 草图离线包定义持久化 结束------

  //------ 特征离线包定义持久化 开始------
  /**
   * 将加载结果携带的 `.puppy` 特征扩展恢复到当前 Viewer。
   *
   * 设计说明：
   * 1. 必须在草图恢复之后调用：特征快照内草图引用为持久 ID，恢复门面需要
   *    已恢复草图的“持久 ID → 新运行时 uuid”映射完成重挂；
   * 2. 本阶段恢复特征时间线主状态（设计树/退回控制棒/吸收关系），结果实体
   *    仍来自离线包 BRep 基线。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   * @param documentType 当前项目根文档的权威类型。
   */
  function restoreLoadedPuppyFeatureExtension(
    loadedModel: LoadedImportedModel,
    documentType: PuppyPackageDocumentType | undefined,
  ): Promise<void> {
    return options.getViewer()?.restorePuppyFeatureExtension?.(
      loadedModel.puppyFeatureExtension,
      documentType,
    ) ?? Promise.resolve()
  }
  //------ 特征离线包定义持久化 结束------

  //------ 线性零部件阵列离线包定义持久化 开始------
  /**
   * 在基础项目模型挂载后根据 `.puppy` 定义重新求解线性零部件阵列。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppyLinearComponentPatterns(loadedModel: LoadedImportedModel): void {
    if (!loadedModel.puppyLinearComponentPatternDocument) {
      return
    }
    const viewer = options.getViewer()
    // 项目组合会接管包内组件；在恢复前由 SDK 将旧包 UUID 绑定到最终 occurrence，UI 不解析对象身份。
    const document = loadedModel.result.kind === 'composed-assembly'
      ? new ProjectModelLinearComponentPatternComponentRootRebinder().rebindRootDocument(
        loadedModel.puppyLinearComponentPatternDocument,
        loadedModel.result,
      )
      : loadedModel.puppyLinearComponentPatternDocument
    viewer?.restorePuppyLinearComponentPatternDocument?.(
      document,
    )
    syncLinearComponentPatternFeatureTree(
      viewer?.linearComponentPatternManager?.getCommittedRecords() ?? [],
      {
        tabId: mountedGlbTabId.value,
        reason: LinearComponentPatternFeatureTreeSyncReason.HistoryRestore,
        deferRefresh: true,
      },
    )
  }
  //------ 线性零部件阵列离线包定义持久化 结束------

  /**
   * 在模型、拓扑与阵列恢复稳定后恢复根文档剖面视图。
   *
   * `undefined` 代表局部子树事务或非 `.puppy` 结果，不得触碰当前根视图；`null` 则是
   * 权威根包的显式空状态，必须交给 Viewer 清除上一文档残留的剖面定义。
   *
   * @param loadedModel 即将提交到当前 tab 的模型加载结果。
   */
  function restoreLoadedPuppySectionViewSnapshot(loadedModel: LoadedImportedModel): void {
    if (loadedModel.puppySectionViewExtension === undefined) {
      return
    }
    options.getViewer()?.restorePuppySectionViewSnapshot?.(
      loadedModel.puppySectionViewExtension?.snapshot ?? null,
    )
  }

  //------ 显示模型下拉 开始------
  /**
   * 在导入模型完成挂载后同步显示模型模式。
   *
   * 设计说明：
   * 1. imported assembly 的显示边会创建拓扑 pick host，不能在 three.js `childadded` 派发中抢跑；
   * 2. 该入口只由导入模型加载/切换完成路径调用，确保整棵模型与拓扑仓库已经稳定；
   * 3. 统一从 Viewer 门面同步，避免页面层直接操作 cad-core 内部渲染控制器。
   */
  function syncModelDisplayModeAfterImportedModelMounted(): void {
    const viewer = options.getViewer()
    if (!viewer?.syncModelDisplayMode) {
      return
    }

    viewer.syncModelDisplayMode()
    // 显示模式可能替换导入实体材质；实例组必须按新的材质投影键原子重建。
    viewer.rebuildImportedModelInstancing?.()
    viewer.requestImmediateRender?.()
  }

  /**
   * 通过 cad-core Viewer 门面挂载导入结果并自动启动实例化投影。
   *
   * @param result 已完成加载与语义收口的导入模型结果。
   * @param scene 当前 Viewer 主场景。
   */
  function mountImportedModelRuntime(result: ImportedModelResult, scene: THREE.Scene): void {
    const viewer = options.getViewer()
    // 标签切换必须先恢复旧 source Mesh，避免旧投影在 detached 场景中继续持有资源。
    viewer?.deactivateImportedModelInstancing?.()
    mountImportedModelToScene(result, scene)
    if (result.kind === 'composed-assembly') {
      viewer?.activateImportedModelInstancing?.(result)
    }
  }
  //------ 显示模型下拉 结束------

  /**
   * 失效当前 Viewer 的配合组件变换索引。
   *
   * 设计说明：局部子树替换会在不重新载入配合文档的情况下替换 Object3D 实例，配合运行时缓存的
   * component -> transform root 映射必须在 Worker 与场景都提交成功后失效，下一次求解才会绑定新对象。
   */
  function invalidateAssemblyMateComponentTransformRootIndex(): void {
    options.getViewer()?.assemblyMateController?.invalidateComponentTransformRootIndex?.()
  }

  /**
   * 将导入模型切换结果提交到场景与设计树。
   */
  function commitPartTabSwitch(
    tabId: string,
    loadedModel: LoadedImportedModel,
    scene: THREE.Scene,
    switchOptions: CommitPartTabSwitchOptions = {},
  ): void {
    const loadedDocumentType = loadedModel.displayType === 'part'
      || loadedModel.displayType === 'assembly'
      ? loadedModel.displayType
      : undefined
    const nextRootDocumentType = switchOptions.rootDocumentType
      ?? currentRootDocumentType
      ?? loadedDocumentType
    const previousTabId = mountedGlbTabId.value
    if (previousTabId) {
      detachImportedAssemblyFromScene(scene)
    }

    mountImportedModelRuntime(loadedModel.result, scene)
    mountedGlbTabId.value = tabId
    partTabsStore.switchTab(tabId)
    loadedResultByTabId.set(tabId, loadedModel.result)
    reportExternalReferenceLoadWarnings(tabId, loadedModel.result)

    const resolvedTabType = resolveImportedModelTabType(loadedModel.displayType)
    partTabsStore.updateTabType(tabId, resolvedTabType, resolvedTabType)

    if (loadedModel.result.kind === 'composed-assembly' && 'manifest' in loadedModel.result) {
      const partTabTypes = collectResolvedPartTabTypesFromAssembly(
        loadedModel.result as never,
        glbFileIdByReferenceKeyMap.value,
      )
      for (const { tabId: otherTabId, type } of partTabTypes) {
        if (otherTabId !== tabId) {
          partTabsStore.updateTabType(otherTabId, type, type)
        }
      }
    }

    syncPartTabsWithImportedModelCaches()

    const hierarchy = resolveImportedModelHierarchy(loadedModel.result)
    glbHierarchyByTabId.set(tabId, hierarchy)
    restoreLoadedPuppyMateDocument(loadedModel)
    restoreLoadedPuppyDatumExtension(loadedModel)
    //------ 草图离线包定义持久化 开始------
    restoreLoadedPuppySketchExtension(loadedModel)
    restoreLoadedPuppySketchEntityReferenceExtension(loadedModel)
    //------ 草图离线包定义持久化 结束------
    //------ 特征离线包定义持久化 开始------
    /**
     * 特征恢复必须晚于草图恢复（快照内草图引用按持久 ID 映射回新运行时 uuid），
     * 且 Worker 重放会推进历史版本：设计树刷新必须排在重放落地之后，
     * 否则并发刷新请求会以 history-seq-stale 作废。
     */
    /** 普通单模型首次打开没有显式 root 提交参数，此时首个已提交标签就是根文档。 */
    currentRootDocumentType = nextRootDocumentType
    const featureRestorePromise = restoreLoadedPuppyFeatureExtension(
      loadedModel,
      currentRootDocumentType,
    )
    // 延迟刷新路径不在此处消费该 promise，必须记录下来供替换设计树前等待重放落地。
    pendingPuppyFeatureRestore = featureRestorePromise.catch(() => undefined)
    //------ 特征离线包定义持久化 结束------
    restoreLoadedPuppyLinearComponentPatterns(loadedModel)
    restoreLoadedPuppySectionViewSnapshot(loadedModel)

    if (!switchOptions.deferFeatureTreeRefresh) {
      void featureRestorePromise
        .then(() => featureTreeStore.refreshTree(tabId))
        .catch((error) => {
          logger.error('[GlbLoader] refreshTree on commitPartTabSwitch failed', error)
        })
    }

    focusViewerOnSceneContent()
    syncModelDisplayModeAfterImportedModelMounted()
  }

  /**
   * 回滚局部项目模型替换已经完成的 web 场景与缓存切换。
   *
   * 设计说明：scoped Worker commit 是不可回滚边界。为了避免 Worker commit 失败后
   * web-client 已经显示新子树，这里只恢复局部替换前的可见模型引用和项目模型缓存。
   *
   * @param input 回滚所需的旧状态快照。
   */
  function rollbackScopedProjectModelTabSwitchWebCommit(input: {
    readonly tabId: string
    readonly scene: THREE.Scene
    readonly previousMountedGlbTabId: string | null
    readonly previousActiveTabId: string | null
    readonly previousLoadedResult: ImportedModelResult | undefined
    readonly previousHierarchy: GlbHierarchyNode | undefined
    readonly previousProjectModelLoadResult: ProjectModelLoadResult | null
    readonly previousPackageResultsByNodeKey: ReadonlyMap<string, readonly LoadedProjectModelPackageResult[]>
  }): void {
    try {
      detachImportedAssemblyFromScene(input.scene)
      if (input.previousLoadedResult) {
        mountImportedModelRuntime(input.previousLoadedResult, input.scene)
      }
    } catch (rollbackError) {
      logger.error('[GlbLoader] rollback scoped project model scene failed', rollbackError)
    }

    mountedGlbTabId.value = input.previousMountedGlbTabId
    if (input.previousActiveTabId) {
      partTabsStore.switchTab(input.previousActiveTabId)
    }
    if (input.previousLoadedResult) {
      loadedResultByTabId.set(input.tabId, input.previousLoadedResult)
    } else {
      loadedResultByTabId.delete(input.tabId)
    }
    if (input.previousHierarchy) {
      glbHierarchyByTabId.set(input.tabId, input.previousHierarchy)
    } else {
      glbHierarchyByTabId.delete(input.tabId)
    }
    currentProjectModelLoadResult = input.previousProjectModelLoadResult
    projectModelPackageResultByNodeKey.clear()
    input.previousPackageResultsByNodeKey.forEach((packageResults, nodeKey) => {
      projectModelPackageResultByNodeKey.set(nodeKey, [...packageResults])
    })
    // commitPartTabSwitch 已可能刷新过新树；回滚后必须再用旧 hierarchy 覆盖一次 UI 投影。
    void featureTreeStore.refreshTree(input.tabId).catch((refreshError) => {
      logger.error('[GlbLoader] refresh scoped project model rollback tree failed', refreshError)
    })
  }

  /**
   * 清理离线包导入前的 imported-model 会话状态。
   *
   * 设计说明：
   * 1. 离线包导入是“替换当前可见 CAD 文档”，不能把旧零件或旧装配体 tab 留在底部标签栏；
   * 2. 这里不调用 loader 的 `dispose()`，因为导入后还要继续使用同一个 loader 实例；
   * 3. 缓存实例本身保留，内部内容清空，避免旧装配体模板、旧 BRep sidecar 或旧 tab 影响新包。
   */
  function clearImportedModelSessionForOfflinePackageImport(): void {
    clearImportedModelInteractionState()
    abortCurrentInsertionHandle()
    requestRegistry.abortAllCurrent()

    const scene = options.getScene()
    if (scene) {
      detachImportedAssemblyFromScene(scene)
    }

    for (const cachedResult of collectUniqueImportedModelResults(loadedResultByTabId.values())) {
      if (isPuppyPackageImportedModelResult(cachedResult)) {
        continue
      }
      disposeCommittedBackgroundImportedModelResult(
        cachedResult,
        'offline-package-import clear previous session',
      )
    }
    disposePuppyPackageImportedModelResults(loadedResultByTabId.values())

    loadedResultByTabId.clear()
    reportedExternalReferenceWarningByTabId.clear()
    glbHierarchyByTabId.clear()
    currentProjectModelContextIdentity = null
    currentRootDocumentType = undefined
    mountedGlbTabId.value = null
    pendingPartTabId.value = null
    childTabLoadingCoordinator.reset()
    glbFeatureTree.clearTree()
    featureTreeStore.isTreeLoading = false

    caches.partAssetCache.clear()
    caches.assemblyModelCache.clear()
    caches.assemblyManifestCache.clear()
    caches.parsedGlbCache.clear()
    caches.glbResourceCache.clear()
    caches.importedBrepRuntimeCache.clear()
    partTabsStore.reset()
  }

  /**
   * 将 `.puppy` 离线包导入结果挂载到 imported-model tab 与设计树链路。
   *
   * @param input 挂载输入。
   * @returns 实际创建的 tab 描述；返回前已完成当前 tab 的设计树刷新。
   */
  async function mountOfflinePuppyPackage(input: OfflinePuppyPackageMountInput): Promise<ImportedModelTabDescriptor> {
    const scene = options.getScene()
    if (!scene) {
      throw new Error('当前 Viewer 场景未初始化，无法挂载离线包')
    }

    const fallbackLabel = input.tabLabel?.trim()
      || input.result.partName
      || input.result.fileInfo.fileId
    clearImportedModelSessionForOfflinePackageImport()
    const tabSet = createOfflinePuppyTabDescriptors({
      result: input.result,
      documentType: input.documentType,
      tabTemplates: input.tabTemplates,
      baseTabId: input.tabId ?? input.result.fileInfo.fileId,
      fallbackLabel,
      isTabIdReserved: (tabId) => partTabsStore.tabs.some((tab) => tab.id === tabId),
    })

    mountImportedModelRuntime(input.result, scene)
    tabSet.descriptors.forEach((descriptor) => {
      const templateNodeId = tabSet.templateNodeIdByTabId[descriptor.id]
      const tabResult = templateNodeId
        ? input.tabModelResultsByNodeId?.[templateNodeId] ?? input.result
        : input.result
      partTabsStore.addTab(descriptor)
      loadedResultByTabId.set(descriptor.id, tabResult)
      glbHierarchyByTabId.set(descriptor.id, resolveImportedModelHierarchy(tabResult))
    })
    mountedGlbTabId.value = tabSet.rootDescriptor.id
    partTabsStore.switchTab(tabSet.rootDescriptor.id)

    const viewer = options.getViewer()
    currentRootDocumentType = input.documentType
    viewer?.restorePuppyDatumExtension?.(input.datumExtension ?? createEmptyPuppyDatumExtension())
    //------ 草图离线包定义持久化 开始------
    // 草图恢复必须在场景挂载后执行；扩展缺失表示该文档没有草图，保持空操作。
    viewer?.restorePuppySketchExtension?.(input.sketchExtension)
    // Record 必须在草图实体就位后、FeatureTimeline 重建前恢复。
    viewer?.restorePuppySketchEntityReferenceExtension?.(
      input.sketchEntityReferenceExtension,
    )
    //------ 草图离线包定义持久化 结束------
    //------ 特征离线包定义持久化 开始------
    // 特征恢复必须晚于草图恢复，且必须等待 Worker 重放落地后再刷新设计树（防 history-seq-stale）。
    await viewer?.restorePuppyFeatureExtension?.(input.featureExtension, input.documentType)
    //------ 特征离线包定义持久化 结束------
    // 基础模型与拓扑已经挂载完成，此时按定义求解阵列，随后设计树刷新才能读取新生成的 Group。
    viewer?.restorePuppyLinearComponentPatternDocument?.(
      input.linearComponentPatternExtension ?? createEmptyPuppyLinearComponentPatternDocument(),
    )
    viewer?.restorePuppySectionViewSnapshot?.(input.sectionViewExtension?.snapshot)
    syncLinearComponentPatternFeatureTree(
      viewer?.linearComponentPatternManager?.getCommittedRecords() ?? [],
      {
        tabId: tabSet.rootDescriptor.id,
        reason: LinearComponentPatternFeatureTreeSyncReason.HistoryRestore,
        deferRefresh: true,
      },
    )
    await featureTreeStore.refreshTree(tabSet.rootDescriptor.id)

    focusViewerOnSceneContent()
    syncModelDisplayModeAfterImportedModelMounted()
    return tabSet.rootDescriptor
  }

  /**
   * 命中 live state 时的 tab 切换快速路径。
   */
  function restoreTabFromCache(targetTabId: string, scene: THREE.Scene): boolean {
    const cachedResult = loadedResultByTabId.get(targetTabId)
    if (!cachedResult) {
      return false
    }

    const previousTabId = mountedGlbTabId.value
    if (previousTabId && previousTabId !== targetTabId) {
      detachImportedAssemblyFromScene(scene)
    }
    mountImportedModelRuntime(cachedResult, scene)
    mountedGlbTabId.value = targetTabId
    partTabsStore.switchTab(targetTabId)
    reportExternalReferenceLoadWarnings(targetTabId, cachedResult)

    void featureTreeStore.refreshTree(targetTabId).catch((error) => {
      logger.error('[GlbLoader] refreshTree on restoreTabFromCache failed', error)
    })

    focusViewerOnSceneContent()
    syncModelDisplayModeAfterImportedModelMounted()
    return true
  }

  /**
   * 只清理由指定插入树请求拥有的 loading 状态。
   *
   * 局部子树投影可能在旧完整刷新尚未返回时成为最新请求；该入口同时供两条路径使用，
   * 避免旧请求因为检测到“已有更新 owner”而永久保留 spinner。
   *
   * @param tabId 插入结构所属标签。
   * @param requestId 本次插入树请求编号。
   */
  function clearInsertionTreeRefreshLoadingState(tabId: string, requestId: number): void {
    if (!shouldPreserveNewerInsertionRefreshSpinner(tabId, requestId)) {
      partTabsStore.setTabLoading(tabId, false)
    }
    if (!isCurrentInsertionTreeRefreshOwner(tabId, requestId)) {
      return
    }
    featureTreeStore.isTreeLoading = false
    if (options.resetLoading) {
      options.resetLoading(tabId)
    } else {
      options.hideLoading(tabId)
    }
  }

  /**
   * 插入零部件成功后优先提交局部树投影，必要时回退权威完整刷新。
   *
   * @param tabId 插入结构所属标签。
   * @param insertedObjectId 新增组件的场景对象 ID。
   * @param parentHierarchy 插入前的稳定父 hierarchy。
   * @param insertedHierarchy 已提交主状态的新增 hierarchy 子树。
   */
  function syncFeatureTreeAfterInsertion(
    tabId: string,
    insertedObjectId?: string,
    parentHierarchy?: GlbHierarchyNode,
    insertedHierarchy?: GlbHierarchyNode,
  ): void {
    latestInsertionTreeRefreshRequestId += 1
    const requestId = latestInsertionTreeRefreshRequestId

    /**
     * 插入提交已经持有旧稳定父节点和完整新增 hierarchy，直接复用运行时子树投影器。
     * 只有当前树尚未建立父节点或更新属于后台标签时，才进入下面的完整刷新兜底。
     */
    if (
      parentHierarchy
      && insertedHierarchy
      && featureTreeStore.activeTreeTabId === tabId
      && typeof featureTreeStore.applyGlbRuntimeSubtreeUpdate === 'function'
    ) {
      const subtreeUpdate = glbFeatureTree.buildRuntimeSubtreeUpdate(
        tabId,
        parentHierarchy,
        [],
        [insertedHierarchy],
        glbFileIdByReferenceKeyMap.value,
      )
      const runtimeSubtreeApplied = featureTreeStore.applyGlbRuntimeSubtreeUpdate(
        tabId,
        subtreeUpdate,
      )
      if (runtimeSubtreeApplied) {
        if (insertedObjectId) {
          /** 局部投影已同步建立 binding，可沿用正式选择入口立即定位新增节点。 */
          const insertedTreeKey = glbFeatureTree.getTreeKeyByObjectId(insertedObjectId)
          if (insertedTreeKey) {
            void Promise.resolve()
              .then(() => isCurrentInsertionTreeRefreshOwner(tabId, requestId)
                ? featureTreeStore.handleFeatureSelect?.([insertedTreeKey])
                : undefined)
              .catch((error) => {
                logger.error('[GlbLoader] select inserted runtime subtree node failed', error)
              })
          }
        }
        /** 当前局部请求已成为 owner，必须接管并清除可能由旧完整刷新留下的 loading。 */
        clearInsertionTreeRefreshLoadingState(tabId, requestId)
        return
      }
    }

    featureTreeStore.isTreeLoading = true
    partTabsStore.setTabLoading(tabId, true)
    options.showLoading('正在更新设计树...', tabId)

    void featureTreeStore.refreshTree(tabId)
      .then(async () => {
        /**
         * 插入完成后自动选中新插入节点。
         *
         * 设计说明：
         * 1. 大装配树中新节点通常被追加到末尾，若不主动定位，用户会误判为“树没刷新”；
         * 2. 这里等待权威 refresh 完成后，再通过最新 binding 索引反查 tree key，
         *    保证选中的一定是当前树里的最新节点；
         * 3. 仍复用既有 `handleFeatureSelect` 语义入口，避免 loader 直接散写
         *    `selectedKeys` / `selectedObject`，保持选择链路单一出口。
         */
        if (insertedObjectId && isCurrentInsertionTreeRefreshOwner(tabId, requestId)) {
          const insertedTreeKey = glbFeatureTree.getTreeKeyByObjectId(insertedObjectId)
          if (insertedTreeKey) {
            await featureTreeStore.handleFeatureSelect?.([insertedTreeKey])
          }
        }
      })
      .catch((error) => {
        logger.error('[GlbLoader] refreshTree after insertion failed', error)
        if (isCurrentInsertionTreeRefreshOwner(tabId, requestId)) {
          options.showError('设计树刷新失败，请重试')
        }
      })
      .finally(() => clearInsertionTreeRefreshLoadingState(tabId, requestId))
  }

  /**
   * 记录一个由 loader 托管的插入 request handle。
   *
   * @param handle request registry 返回的插入请求句柄。
   */
  function trackInsertionRequestHandle(handle: ImportedModelRequestHandle): void {
    currentInsertionHandle = handle
    insertionRequestHandleById.set(handle.requestId, handle)
  }

  /**
   * 从 loader 托管集合中移除插入 request handle。
   *
   * @param handle 需要移除的插入请求句柄。
   */
  function untrackInsertionRequestHandle(handle: ImportedModelRequestHandle): void {
    insertionRequestHandleById.delete(handle.requestId)
    if (currentInsertionHandle === handle) {
      currentInsertionHandle = null
    }
  }

  /**
   * 主动 abort 当前所有 insertion handle（若有）。
   * 用于 tab-switch / dispose / cancelInsertion 等需要剥夺 insertion 提交资格的场景。
   */
  function abortCurrentInsertionHandle(): void {
    const handles = Array.from(insertionRequestHandleById.values())
    insertionRequestHandleById.clear()
    preparedInsertionHandleById.clear()
    currentInsertionHandle = null
    for (const handle of handles) {
      handle.abort()
    }
  }

  //------ 装配体引用复制 开始------
  /**
   * 将 Object3D 局部矩阵转成 manifest 使用的矩阵元组。
   *
   * @param object 场景对象。
   * @returns 16 元素列主序矩阵。
   */
  function createMatrixTupleFromObject(object: THREE.Object3D): Matrix4Tuple {
    object.updateMatrix()
    const elements = object.matrix.elements
    return [
      elements[0], elements[1], elements[2], elements[3],
      elements[4], elements[5], elements[6], elements[7],
      elements[8], elements[9], elements[10], elements[11],
      elements[12], elements[13], elements[14], elements[15],
    ]
  }

  /**
   * 将组件 transform 快照应用到 Object3D。
   *
   * @param object 目标对象。
   * @param transform 组件 transform 快照。
   */
  function applyComponentTransformSnapshot(
    object: THREE.Object3D,
    transform: ComponentTransformSnapshot,
  ): void {
    object.position.set(transform.position[0], transform.position[1], transform.position[2])
    object.quaternion.set(
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    )
    object.scale.set(transform.scale[0], transform.scale[1], transform.scale[2])
    object.updateMatrix()
    object.updateWorldMatrix(true, true)
  }

  //------ 装配体引用复制 开始------
  /**
   * 规范化引用复制预览根的初始平移。
   *
   * 设计说明：
   * 引用复制的放置位置由新 occurrence 根节点承载，不能把源 occurrence 的平移继续保留在
   * 克隆根上。否则导出时会出现“复制根 transform + 子 display 源偏移”双重位姿，
   * 重载后复制体会回到源位置或从场景中看起来丢失。
   *
   * @param object 待放置的复制体根对象。
   */
  function normalizeAssemblyReferenceCopyPreviewRoot(object: THREE.Object3D): void {
    object.position.set(0, 0, 0)
    object.updateMatrix()
    object.updateWorldMatrix(true, true)
  }
  //------ 装配体引用复制 结束------

  /**
   * 重映射可选 Object3D ID。
   *
   * @param objectId 源对象 ID。
   * @param uuidMap UUID 映射。
   * @returns 新对象 ID。
   */
  function remapAssemblyReferenceCopyObjectId(
    objectId: string | null | undefined,
    uuidMap: Map<string, string>,
  ): string | null | undefined {
    if (objectId === null || objectId === undefined) {
      return objectId
    }
    return uuidMap.get(objectId) ?? objectId
  }

  /**
   * 重映射高亮目标中的对象 ID。
   *
   * @param targets 高亮目标。
   * @param uuidMap UUID 映射。
   * @returns 重映射后的高亮目标。
   */
  function remapAssemblyReferenceCopyHighlightTargets(
    targets: GlbHierarchyNodeMeta['highlightTargets'],
    uuidMap: Map<string, string>,
  ): GlbHierarchyNodeMeta['highlightTargets'] {
    return targets?.map((target) => ({
      ...target,
      objectId: uuidMap.get(target.objectId) ?? target.objectId,
      primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
    }))
  }

  /**
   * 克隆并重映射 hierarchy meta。
   *
   * @param meta 源 meta。
   * @param uuidMap UUID 映射。
   * @param rootSelectionObjectId 根选择对象 ID。
   * @param isRoot 是否为复制根节点。
   * @param rootDisplayName 复制根节点显示名称。
   * @returns 新 meta。
   */
  function cloneAssemblyReferenceCopyHierarchyMeta(
    meta: GlbHierarchyNodeMeta | undefined,
    uuidMap: Map<string, string>,
    rootSelectionObjectId: string,
    isRoot: boolean,
    rootDisplayName: string,
  ): GlbHierarchyNodeMeta | undefined {
    if (!meta) {
      return undefined
    }

    return {
      ...meta,
      materialNames: [...(meta.materialNames ?? [])],
      bodyEntries: meta.bodyEntries ? meta.bodyEntries.map((entry) => ({ ...entry })) : undefined,
      bodyPrimitiveIndicesById: meta.bodyPrimitiveIndicesById
        ? Object.fromEntries(
          Object.entries(meta.bodyPrimitiveIndicesById).map(([bodyId, primitiveIndices]) => [
            bodyId,
            [...primitiveIndices],
          ]),
        )
        : undefined,
      bodyHighlightTargetsById: meta.bodyHighlightTargetsById
        ? Object.fromEntries(
          Object.entries(meta.bodyHighlightTargetsById).map(([bodyId, targets]) => [
            bodyId,
            remapAssemblyReferenceCopyHighlightTargets(targets, uuidMap) ?? [],
          ]),
        )
        : undefined,
      highlightTargets: remapAssemblyReferenceCopyHighlightTargets(meta.highlightTargets, uuidMap),
      selectionObjectId: isRoot
        ? rootSelectionObjectId
        : remapAssemblyReferenceCopyObjectId(meta.selectionObjectId, uuidMap) ?? meta.selectionObjectId,
      // 设计树显示外部引用零部件时会优先读取 externalAssemblyName，复制根必须同步改名。
      externalAssemblyName: isRoot ? rootDisplayName : meta.externalAssemblyName,
      isFixed: isRoot ? false : meta.isFixed,
    }
  }

  /**
   * 克隆并重建复制体 hierarchy。
   *
   * @param input 重建输入。
   * @returns 新 hierarchy 节点。
   */
  function createAssemblyReferenceCopyHierarchyNode(input: {
    sourceNode: GlbHierarchyNode
    parentObjectId: string | null
    parentPath: string
    displayName: string
    uuidMap: Map<string, string>
    isRoot: boolean
    rootObjectId: string
  }): GlbHierarchyNode {
    const objectId = input.isRoot
      ? input.rootObjectId
      : input.uuidMap.get(input.sourceNode.objectId) ?? input.sourceNode.objectId
    const nodeName = input.isRoot ? input.displayName : input.sourceNode.name
    const path = input.parentPath ? `${input.parentPath}/${nodeName}` : nodeName
    const children = input.sourceNode.children.map((child) => (
      createAssemblyReferenceCopyHierarchyNode({
        sourceNode: child,
        parentObjectId: objectId,
        parentPath: path,
        displayName: child.name,
        uuidMap: input.uuidMap,
        isRoot: false,
        rootObjectId: input.rootObjectId,
      })
    ))

    return {
      ...input.sourceNode,
      objectId,
      parentObjectId: input.parentObjectId,
      path,
      name: nodeName,
      depth: path ? path.split('/').length - 1 : 0,
      meta: cloneAssemblyReferenceCopyHierarchyMeta(
        input.sourceNode.meta,
        input.uuidMap,
        input.rootObjectId,
        input.isRoot,
        input.displayName,
      ),
      children,
    }
  }

  /**
   * 解析当前装配体复制上下文。
   *
   * @returns 复制上下文；当前不在装配体时返回 null。
   */
  function resolveAssemblyReferenceCopyContext(): {
    tabId: string
    hierarchy: GlbHierarchyNode
    result: Extract<ImportedModelResult, { kind: 'composed-assembly' }>
    parentNodeId: string
  } | null {
    const tabId = mountedGlbTabId.value
    if (!tabId) {
      return null
    }

    const currentTab = partTabsStore.tabs.find((tab) => tab.id === tabId)
    const result = loadedResultByTabId.get(tabId)
    const hierarchy = glbHierarchyByTabId.get(tabId)
    if (currentTab?.type !== 'assembly' || result?.kind !== 'composed-assembly' || !hierarchy) {
      return null
    }

    return {
      tabId,
      hierarchy,
      result,
      parentNodeId: result.manifest.nodesById[tabId] ? tabId : result.manifest.rootNodeId,
    }
  }

  /**
   * 解析源组件的装配节点 ID。
   *
   * @param sourceObject 源对象。
   * @returns 装配节点 ID。
   */
  function resolveAssemblyReferenceCopySourceNodeId(sourceObject: THREE.Object3D): string {
    const existingReferenceCopyMetadata = AssemblyReferenceCopyMetadataService
      .readMetadataFromSelfOrAncestors(sourceObject)
    const candidateIds = [
      existingReferenceCopyMetadata?.sourceNodeId,
      readComponentInstanceAssemblyNodeId(sourceObject, {
        resolveSelectionProxyObjectId: ImportedModelObjectMetadataService.resolveSelectionProxyObjectId,
        resolveHierarchySelectionObjectId: ImportedModelObjectMetadataService.resolveHierarchySelectionObjectId,
        resolveAssemblyNodeId: ImportedModelObjectMetadataService.resolveAssemblyNodeId,
      }),
      ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(sourceObject),
      sourceObject.uuid,
    ]
    return candidateIds.find((candidateId) => typeof candidateId === 'string' && candidateId.trim().length > 0)
      ?.trim()
      ?? sourceObject.uuid
  }

  /**
   * 解析本次复制直接选中的 occurrence ID。
   *
   * 设计说明：
   * `sourceNodeId` 为了兼容旧保存格式会优先指向原始来源；而 A_1 再复制出 A_2 时，
   * `copiedFromOccurrenceId` 必须记录 A_1，才能表达真实复制链。
   *
   * @param sourceObject 源对象。
   * @returns 直接来源 occurrence ID。
   */
  function resolveAssemblyReferenceCopyDirectSourceOccurrenceId(sourceObject: THREE.Object3D): string {
    const candidateIds = [
      readComponentInstanceAssemblyNodeId(sourceObject, {
        resolveSelectionProxyObjectId: ImportedModelObjectMetadataService.resolveSelectionProxyObjectId,
        resolveHierarchySelectionObjectId: ImportedModelObjectMetadataService.resolveHierarchySelectionObjectId,
        resolveAssemblyNodeId: ImportedModelObjectMetadataService.resolveAssemblyNodeId,
      }),
      ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(sourceObject),
      sourceObject.uuid,
    ]
    return candidateIds.find((candidateId) => typeof candidateId === 'string' && candidateId.trim().length > 0)
      ?.trim()
      ?? sourceObject.uuid
  }

  /**
   * 解析引用复制稳定定义 ID。
   *
   * @param sourcePartFileUuid 源零件或子装配 UUID。
   * @param sourceNodeId 源装配节点 ID。
   * @returns 稳定定义 ID。
   */
  function resolveAssemblyReferenceCopyDefinitionId(
    sourcePartFileUuid: string | undefined,
    sourceNodeId: string,
  ): string {
    return sourcePartFileUuid?.trim() || sourceNodeId
  }

  /**
   * 创建引用复制族元数据。
   *
   * @param input 元数据输入。
   * @returns 源对象与复制体应写入的 lineage 元数据。
   */
  function createAssemblyReferenceCopyLineage(input: {
    sourceObject: THREE.Object3D
    sourceNodeId: string
    directSourceOccurrenceId: string
    sourcePartFileUuid?: string
  }): {
    sourceLineage: AssemblyReferenceCopyLineageMetadata
    copiedLineage: AssemblyReferenceCopyLineageMetadata
  } {
    const existingLineage = AssemblyReferenceCopyLineageMetadataService
      .readMetadataFromSelfOrAncestors(input.sourceObject)
    const existingReferenceCopy = AssemblyReferenceCopyMetadataService
      .readMetadataFromSelfOrAncestors(input.sourceObject)
    const definitionId = existingLineage?.definitionId
      ?? existingReferenceCopy?.definitionId
      ?? resolveAssemblyReferenceCopyDefinitionId(input.sourcePartFileUuid, input.sourceNodeId)
    const originOccurrenceId = existingLineage?.originOccurrenceId
      ?? existingReferenceCopy?.originOccurrenceId
      ?? input.directSourceOccurrenceId
    const referenceFamilyId = existingLineage?.referenceFamilyId
      ?? existingReferenceCopy?.referenceFamilyId
      ?? `reference-family:${definitionId}:${originOccurrenceId}`
    const sourceLineage: AssemblyReferenceCopyLineageMetadata = existingLineage ?? {
      referenceFamilyId,
      definitionId,
      originOccurrenceId,
      role: AssemblyReferenceCopyLineageRole.OriginOccurrence,
    }
    const copiedLineage: AssemblyReferenceCopyLineageMetadata = {
      referenceFamilyId,
      definitionId,
      originOccurrenceId,
      role: AssemblyReferenceCopyLineageRole.ReferenceOccurrence,
      copiedFromOccurrenceId: input.directSourceOccurrenceId,
    }

    return {
      sourceLineage,
      copiedLineage,
    }
  }

  /**
   * 解析源组件引用的文件 UUID。
   *
   * @param sourceNodeId 源装配节点 ID。
   * @param sourceObject 源对象。
   * @param result 当前装配结果。
   * @param sourceHierarchy 源 hierarchy 节点。
   * @returns 源文件 UUID。
   */
  function resolveAssemblyReferenceCopySourcePartFileUuid(
    sourceNodeId: string,
    sourceObject: THREE.Object3D,
    result: Extract<ImportedModelResult, { kind: 'composed-assembly' }>,
    sourceHierarchy: GlbHierarchyNode,
  ): string | undefined {
    const existingReferenceCopyMetadata = AssemblyReferenceCopyMetadataService
      .readMetadataFromSelfOrAncestors(sourceObject)
    return existingReferenceCopyMetadata?.sourcePartFileUuid
      ?? result.manifest.nodesById[sourceNodeId]?.partFileUuid
      ?? ImportedModelObjectMetadataService.resolvePartFileUuidFromSelfOrAncestors(sourceObject)
      ?? sourceHierarchy.meta?.externalFileUuid
  }

  /**
   * 解析装配体引用复制的兜底拓扑源。
   *
   * 设计说明：
   * 复制体优先镜像源实例已有 topology runtime；但源实例没有激活仓库、或复制链来自历史恢复时，
   * 仍需要从装配缓存的零件/子装配资产中拿到拓扑源，保证复制体悬停后可以创建自己的点线面代理。
   *
   * @param result 当前装配加载结果。
   * @param handle 引用复制句柄。
   * @returns 可用于激活复制体拓扑仓库的资产；不存在时返回 undefined。
   */
  function resolveAssemblyReferenceCopyFallbackTopologySource(
    result: Extract<ImportedModelResult, { kind: 'composed-assembly' }>,
    handle: AssemblyReferenceCopyPreparationHandle,
  ): ImportedTopologySource | undefined {
    const candidateIds = [
      handle.sourcePartFileUuid,
      handle.definitionId,
    ].filter((candidateId): candidateId is string => Boolean(candidateId?.trim()))

    for (const candidateId of candidateIds) {
      const partAsset = result.partAssetsByUuid[candidateId]
      if (partAsset) {
        return partAsset
      }

      const assemblyTopologySource = result.assemblyAssetsByUuid[candidateId]?.localTopologySource
      if (assemblyTopologySource) {
        return assemblyTopologySource
      }
    }

    return undefined
  }

  /**
   * 创建装配体引用复制预览句柄。
   *
   * @param sourceObjectId 显式源对象 ID。
   * @param forcedCopiedNodeId 历史 redo 使用的固定复制节点 ID。
   * @param forcedDisplayName 历史 redo 使用的固定复制显示名称。
   * @returns 预览句柄；当前状态不满足复制条件时返回 null。
   */
  function prepareAssemblyReferenceCopy(
    sourceObjectId?: string | null,
    forcedCopiedNodeId?: string,
    forcedDisplayName?: string,
  ): AssemblyReferenceCopyPreparationHandle | null {
    logger.info('[GlbLoader] 装配体引用复制：开始准备复制', {
      sourceObjectId: sourceObjectId ?? null,
      forcedCopiedNodeId: forcedCopiedNodeId ?? null,
      forcedDisplayName: forcedDisplayName ?? null,
      mountedGlbTabId: mountedGlbTabId.value,
      activePartTabId: partTabsStore.activeTabId,
    })
    const context = resolveAssemblyReferenceCopyContext()
    if (!context) {
      logger.warn('[GlbLoader] prepareAssemblyReferenceCopy: current tab is not assembly', {
        mountedGlbTabId: mountedGlbTabId.value,
        activePartTabId: partTabsStore.activeTabId,
        activeTabType: partTabsStore.activeTab?.type ?? null,
      })
      return null
    }

    // 快捷键、设计树和历史恢复共用 SDK 的拓扑归属解析。
    const sourceObject = options.getViewer()?.resolveAssemblyReferenceCopySourceObject(sourceObjectId)
    if (!sourceObject) {
      logger.warn('[GlbLoader] prepareAssemblyReferenceCopy: source object not found', {
        sourceObjectId: sourceObjectId ?? null,
        selectedObjectIds: options.getViewer()?.getSelectedObjectIds?.() ?? [],
      })
      return null
    }

    const sourceNodeId = resolveAssemblyReferenceCopySourceNodeId(sourceObject)
    const directSourceOccurrenceId = resolveAssemblyReferenceCopyDirectSourceOccurrenceId(sourceObject)
    const sourceHierarchy = findHierarchyNodeByObjectId(context.hierarchy, sourceNodeId)
      ?? findHierarchyNodeByObjectId(context.hierarchy, sourceObject.uuid)
    if (!sourceHierarchy) {
      logger.warn('[GlbLoader] prepareAssemblyReferenceCopy: source hierarchy not found', {
        sourceNodeId,
        sourceObjectId: sourceObject.uuid,
        sourceObjectName: sourceObject.name,
        hierarchyRootObjectId: context.hierarchy.objectId,
      })
      return null
    }

    const cloneResult = cloneWithSharedGeometryRuntimeMap(sourceObject, {
      listSourceChildren: object => options.getViewer()!.listImportedModelSourceHierarchyChildren(object),
    })
    const clonedObject = cloneResult.object
    if (forcedCopiedNodeId?.trim()) {
      clonedObject.uuid = forcedCopiedNodeId.trim()
      cloneResult.clonedObjectIdBySourceObjectId.set(sourceObject.uuid, clonedObject.uuid)
    }
    normalizeAssemblyReferenceCopyPreviewRoot(clonedObject)
    const uuidMap = cloneResult.clonedObjectIdBySourceObjectId
    AssemblyReferenceCopyMetadataService.clearMetadataInTree(clonedObject)
    AssemblyReferenceCopyLineageMetadataService.clearMetadataInTree(clonedObject)

    const sourcePartFileUuid = resolveAssemblyReferenceCopySourcePartFileUuid(
      sourceNodeId,
      sourceObject,
      context.result,
      sourceHierarchy,
    )
    const lineage = createAssemblyReferenceCopyLineage({
      sourceObject,
      sourceNodeId,
      directSourceOccurrenceId,
      ...(sourcePartFileUuid ? { sourcePartFileUuid } : {}),
    })
    const displayName = forcedDisplayName?.trim() || AssemblyReferenceCopyNameService.resolveNextDisplayName({
      parentHierarchy: context.hierarchy,
      sourceHierarchy,
      sourceLineage: lineage.sourceLineage,
      fallbackSourceName: sourceObject.name?.trim() || '复制零部件',
      defaultBaseName: '复制零部件',
    })
    // 场景选择摘要和部分旧链路仍会读取 Object3D.name，复制根必须与设计树显示名一致。
    clonedObject.name = displayName
    const copiedHierarchy = createAssemblyReferenceCopyHierarchyNode({
      sourceNode: sourceHierarchy,
      parentObjectId: context.hierarchy.objectId,
      parentPath: context.hierarchy.path,
      displayName,
      uuidMap,
      isRoot: true,
      rootObjectId: clonedObject.uuid,
    })

    logger.info('[GlbLoader] 装配体引用复制：复制预览句柄准备完成', {
      tabId: context.tabId,
      parentNodeId: context.parentNodeId,
      sourceNodeId,
      directSourceOccurrenceId,
      sourceObjectId: sourceObject.uuid,
      copiedNodeId: clonedObject.uuid,
      sourcePartFileUuid: sourcePartFileUuid ?? null,
      referenceFamilyId: lineage.copiedLineage.referenceFamilyId,
      definitionId: lineage.copiedLineage.definitionId,
      displayName,
    })

    return {
      tabId: context.tabId,
      parentNodeId: context.parentNodeId,
      copiedNodeId: clonedObject.uuid,
      sourceNodeId,
      directSourceOccurrenceId,
      sourceComponentInstanceId: sourceObject.uuid,
      sourceObject,
      ...(sourcePartFileUuid ? { sourcePartFileUuid } : {}),
      referenceFamilyId: lineage.copiedLineage.referenceFamilyId,
      definitionId: lineage.copiedLineage.definitionId,
      originOccurrenceId: lineage.copiedLineage.originOccurrenceId,
      sourceLineage: lineage.sourceLineage,
      copiedLineage: lineage.copiedLineage,
      displayName,
      previewObject: clonedObject,
      sourceHierarchy,
      copiedHierarchy,
      clonedObjectBySourceObject: cloneResult.clonedObjectBySourceObject,
      clonedObjectIdBySourceObjectId: cloneResult.clonedObjectIdBySourceObjectId,
    }
  }

  /**
   * 将引用复制节点写入 manifest。
   *
   * @param input 写入输入。
   */
  function upsertAssemblyReferenceCopyManifestNode(input: {
    result: Extract<ImportedModelResult, { kind: 'composed-assembly' }>
    handle: AssemblyReferenceCopyPreparationHandle
    sceneObject: THREE.Object3D
  }): void {
    const sourceNode = input.result.manifest.nodesById[input.handle.sourceNodeId]
    input.result.manifest.nodesById[input.handle.copiedNodeId] = {
      ...(sourceNode ?? {
        nodeId: input.handle.sourceNodeId,
        parentNodeId: input.handle.parentNodeId,
        name: input.handle.displayName,
        semanticKind: input.handle.copiedHierarchy.meta?.semanticKind === 'assembly' ? 'assembly' : 'part',
        transform: createMatrixTupleFromObject(input.sceneObject),
      }),
      nodeId: input.handle.copiedNodeId,
      parentNodeId: input.handle.parentNodeId,
      name: input.handle.displayName,
      transform: createMatrixTupleFromObject(input.sceneObject),
      ...(input.handle.sourcePartFileUuid ? { partFileUuid: input.handle.sourcePartFileUuid } : {}),
    }
    const childIds = input.result.manifest.childIdsByNodeId[input.handle.parentNodeId] ?? []
    if (!childIds.includes(input.handle.copiedNodeId)) {
      input.result.manifest.childIdsByNodeId[input.handle.parentNodeId] = [
        ...childIds,
        input.handle.copiedNodeId,
      ]
    }
    input.result.manifest.childIdsByNodeId[input.handle.copiedNodeId] = []
  }

  /**
   * 创建引用复制历史快照。
   *
   * @param input 快照输入。
   * @returns 装配组件历史快照。
   */
  function createAssemblyReferenceCopyHistorySnapshot(input: {
    tabId: string
    handle: AssemblyReferenceCopyPreparationHandle
    sceneObject: THREE.Object3D
  }): AssemblyComponentHistorySnapshot {
    const componentSnapshot = createComponentInstanceSnapshotFromObject({
      object: input.sceneObject as unknown as WebGpuObject3D,
      assemblyId: input.tabId,
      componentInstanceId: input.handle.copiedNodeId,
      sourceNodeId: input.handle.copiedNodeId,
      ...(input.handle.sourcePartFileUuid ? { sourcePartFileUuid: input.handle.sourcePartFileUuid } : {}),
      parentComponentInstanceId: input.handle.parentNodeId,
      suppressed: false,
    })
    return {
      ...convertComponentInstanceSnapshotToAssemblyHistorySnapshot(componentSnapshot),
      referenceSourceNodeId: input.handle.sourceNodeId,
      referenceSourceComponentInstanceId: input.handle.sourceComponentInstanceId,
      displayName: input.handle.displayName,
    }
  }

  /**
   * 提交装配体引用复制。
   *
   * @param handle 复制预览句柄。
   * @param position 鼠标确认位置。
   * @param transformSnapshot 历史 redo 使用的完整 transform。
   * @returns 提交结果。
   */
  function commitAssemblyReferenceCopy(
    handle: AssemblyReferenceCopyPreparationHandle,
    position: THREE.Vector3,
    transformSnapshot?: ComponentTransformSnapshot,
  ): AssemblyReferenceCopyCommitResult | null {
    const context = resolveAssemblyReferenceCopyContext()
    if (!context || context.tabId !== handle.tabId) {
      logger.warn('[GlbLoader] commitAssemblyReferenceCopy: assembly context changed')
      return null
    }

    const scene = options.getScene()
    const viewer = options.getViewer()
    const assemblyGroup = scene?.getObjectByName('imported-assembly') ?? null
    if (!scene || !viewer || !assemblyGroup) {
      logger.warn('[GlbLoader] commitAssemblyReferenceCopy: scene or viewer not ready')
      return null
    }

    const sceneObject = handle.previewObject
    if (transformSnapshot) {
      applyComponentTransformSnapshot(sceneObject, transformSnapshot)
    } else {
      sceneObject.position.copy(position)
      sceneObject.updateMatrix()
      sceneObject.updateWorldMatrix(true, true)
    }

    viewer.addObject(sceneObject, assemblyGroup)
    upsertAssemblyReferenceCopyManifestNode({
      result: context.result,
      handle,
      sceneObject,
    })
    AssemblyReferenceCopyLineageMetadataService.writeMetadata(handle.sourceObject, handle.sourceLineage)
    AssemblyReferenceCopyLineageMetadataService.writeMetadata(sceneObject, handle.copiedLineage)

    const referenceCopyMetadata = {
      kind: AssemblyReferenceCopyKind.ReferenceOccurrence,
      sourceNodeId: handle.sourceNodeId,
      ...(handle.sourcePartFileUuid ? { sourcePartFileUuid: handle.sourcePartFileUuid } : {}),
      sourceComponentInstanceId: handle.sourceComponentInstanceId,
      parentNodeId: handle.parentNodeId,
      referenceFamilyId: handle.referenceFamilyId,
      definitionId: handle.definitionId,
      originOccurrenceId: handle.originOccurrenceId,
      copiedFromOccurrenceId: handle.directSourceOccurrenceId,
    }
    AssemblyReferenceCopyMetadataService.writeMetadata(sceneObject, referenceCopyMetadata)

    const insertedSelectionRebindResult = rebindInsertedImportedModelAssemblySelectionMetadata({
      root: sceneObject,
      sourceHierarchy: handle.sourceHierarchy,
      insertedHierarchy: handle.copiedHierarchy,
      selectionIndex: context.result.selectionIndex,
      sceneBindings: context.result.sceneBindings,
      sceneFileInfo: context.result.fileInfo,
      insertedPartUuid: handle.sourcePartFileUuid,
    })
    const clonedSelectionRebindResult = rebindClonedImportedModelAssemblySelectionMetadata({
      root: sceneObject,
      clonedRootObjectId: handle.copiedNodeId,
      clonedObjectIdBySourceObjectId: handle.clonedObjectIdBySourceObjectId,
      selectionIndex: context.result.selectionIndex,
      sceneBindings: context.result.sceneBindings,
      sceneFileInfo: context.result.fileInfo,
      insertedPartUuid: handle.sourcePartFileUuid,
    })
    const fallbackTopologySource = resolveAssemblyReferenceCopyFallbackTopologySource(context.result, handle)
    const topologyRuntimeCloneResult = cloneImportedModelTopologyRuntime({
      sourceRoot: handle.sourceObject,
      clonedRoot: sceneObject,
      listSourceChildren: object => viewer.listImportedModelSourceHierarchyChildren(object),
      ...(fallbackTopologySource ? { fallbackTopologySource } : {}),
      resolveClonedObject: (sourceObject) => handle.clonedObjectBySourceObject.get(sourceObject) ?? null,
    })
    AssemblyComponentFixationService.writeSceneObjectFixationState(
      sceneObject,
      AssemblyComponentFixationState.Floating,
    )

    // addObject 时尚无副本拓扑仓库；必须在注册完成后派生带边线上色和拾取宿主。
    viewer.syncModelDisplayMode()
    viewer.requestRender()

    const nextHierarchy: GlbHierarchyNode = {
      ...context.hierarchy,
      children: [...context.hierarchy.children, handle.copiedHierarchy],
    }
    glbHierarchyByTabId.set(context.tabId, nextHierarchy)
    context.result.hierarchy = nextHierarchy
    syncFeatureTreeAfterInsertion(
      context.tabId,
      handle.copiedHierarchy.objectId,
      context.hierarchy,
      handle.copiedHierarchy,
    )

    logger.info('[GlbLoader] assembly reference copy committed', {
      sourceNodeId: handle.sourceNodeId,
      directSourceOccurrenceId: handle.directSourceOccurrenceId,
      copiedNodeId: handle.copiedNodeId,
      referenceFamilyId: handle.referenceFamilyId,
      definitionId: handle.definitionId,
      insertedReboundObjectCount: insertedSelectionRebindResult.reboundObjectCount,
      clonedReboundObjectCount: clonedSelectionRebindResult.reboundObjectCount,
      topologyRuntimeRegistered: topologyRuntimeCloneResult.registered,
      topologyVisibleMeshCount: topologyRuntimeCloneResult.visibleMeshCount,
      topologyBodyRecordCount: topologyRuntimeCloneResult.bodyRecordCount,
      fallbackTopologySourceAvailable: Boolean(fallbackTopologySource),
    })

    return {
      componentInstanceId: handle.copiedNodeId,
      historySnapshot: createAssemblyReferenceCopyHistorySnapshot({
        tabId: context.tabId,
        handle,
        sceneObject,
      }),
    }
  }

  /**
   * 从 hierarchy 中删除指定节点。
   *
   * @param node 当前节点。
   * @param objectId 待删除对象 ID。
   * @returns 删除后的节点与是否发生变化。
   */
  function removeHierarchyNodeByObjectId(
    node: GlbHierarchyNode,
    objectId: string,
  ): { node: GlbHierarchyNode | null; changed: boolean } {
    if (node.objectId === objectId || node.meta?.selectionObjectId === objectId) {
      return { node: null, changed: true }
    }

    let changed = false
    const children = node.children.flatMap((child) => {
      const result = removeHierarchyNodeByObjectId(child, objectId)
      changed = changed || result.changed
      return result.node ? [result.node] : []
    })

    return changed
      ? { node: { ...node, children }, changed: true }
      : { node, changed: false }
  }

  /**
   * 移除引用复制组件实例。
   *
   * @param componentInstanceId 组件实例 ID。
   */
  function removeAssemblyReferenceCopy(componentInstanceId: string): void {
    const snapshot: AssemblyComponentHistorySnapshot = {
      componentInstanceId,
      assemblyId: mountedGlbTabId.value ?? '',
      sourceNodeId: componentInstanceId,
      transform: {
        position: [0, 0, 0],
        quaternion: [0, 0, 0, 1],
        scale: [1, 1, 1],
      },
      fixationState: 'floating',
    }
    const sceneObject = resolveAssemblyHistorySceneObject(options.getViewer(), snapshot)
    const referenceCopyMetadata = AssemblyReferenceCopyMetadataService
      .readMetadataFromSelfOrAncestors(sceneObject)
    if (!sceneObject || !referenceCopyMetadata) {
      logger.warn('[GlbLoader] removeAssemblyReferenceCopy: target is not reference copy', componentInstanceId)
      return
    }

    const resolvedTarget = resolveAssemblyHistoryTarget(loadedResultByTabId, snapshot)
    const tabId = resolvedTarget?.tabId ?? mountedGlbTabId.value
    const result = resolvedTarget?.result ?? (tabId ? loadedResultByTabId.get(tabId) : null)
    const cachedHierarchy = tabId ? glbHierarchyByTabId.get(tabId) : null
    disposeImportedModelTopologyHosts(sceneObject)
    sceneObject.traverse((object) => {
      if (result?.kind === 'composed-assembly') {
        delete result.selectionIndex.byObject3DUuid[object.uuid]
        delete result.sceneBindings.byObject3DUuid[object.uuid]
      }
    })
    sceneObject.removeFromParent()

    if (result?.kind === 'composed-assembly') {
      delete result.manifest.nodesById[componentInstanceId]
      delete result.manifest.childIdsByNodeId[componentInstanceId]
      Object.keys(result.manifest.childIdsByNodeId).forEach((parentNodeId) => {
        result.manifest.childIdsByNodeId[parentNodeId] = result.manifest.childIdsByNodeId[parentNodeId]
          .filter((childId) => childId !== componentInstanceId)
      })
      delete result.sceneBindings.byAssemblyNodeId[componentInstanceId]
    }

    if (tabId && cachedHierarchy) {
      const removedHierarchy = findHierarchyNodeByObjectId(
        cachedHierarchy,
        componentInstanceId,
      )
      const nextHierarchyResult = removeHierarchyNodeByObjectId(cachedHierarchy, componentInstanceId)
      if (nextHierarchyResult.node) {
        glbHierarchyByTabId.set(tabId, nextHierarchyResult.node)
        if (result?.kind === 'composed-assembly') {
          result.hierarchy = nextHierarchyResult.node
        }
      }
      let runtimeSubtreeApplied = false
      if (
        removedHierarchy
        && nextHierarchyResult.node
        && featureTreeStore.activeTreeTabId === tabId
        && typeof featureTreeStore.applyGlbRuntimeSubtreeUpdate === 'function'
      ) {
        /** 引用复制撤销同样只删除稳定装配根下的目标子树，保留全部兄弟节点引用。 */
        const subtreeUpdate = glbFeatureTree.buildRuntimeSubtreeUpdate(
          tabId,
          cachedHierarchy,
          [removedHierarchy],
          [],
          glbFileIdByReferenceKeyMap.value,
        )
        runtimeSubtreeApplied = featureTreeStore.applyGlbRuntimeSubtreeUpdate(
          tabId,
          subtreeUpdate,
        )
      }
      if (!runtimeSubtreeApplied) {
        void featureTreeStore.refreshTree(tabId).catch((error) => {
          logger.error('[GlbLoader] removeAssemblyReferenceCopy: refresh tree failed', error)
        })
      }
    }
  }

  /**
   * 根据历史快照恢复引用复制组件实例。
   *
   * @param snapshot 历史快照。
   * @returns 恢复出的 Object3D。
   */
  function restoreAssemblyReferenceCopyFromHistorySnapshot(
    snapshot: AssemblyComponentHistorySnapshot,
  ): THREE.Object3D | null {
    const existingObject = resolveAssemblyHistorySceneObject(options.getViewer(), snapshot)
    if (existingObject) {
      return existingObject
    }

    if (!snapshot.referenceSourceComponentInstanceId && !snapshot.referenceSourceNodeId) {
      return null
    }

    const sourceObjectId = snapshot.referenceSourceComponentInstanceId
      ?? snapshot.referenceSourceNodeId
      ?? ''
    const handle = prepareAssemblyReferenceCopy(
      sourceObjectId,
      snapshot.componentInstanceId,
      snapshot.displayName,
    )
    if (!handle) {
      return null
    }
    const result = commitAssemblyReferenceCopy(
      handle,
      new THREE.Vector3(
        snapshot.transform.position[0],
        snapshot.transform.position[1],
        snapshot.transform.position[2],
      ),
      snapshot.transform,
    )
    return result ? handle.previewObject : null
  }
  //------ 装配体引用复制 结束------

  /**
   * 切换到底部标签栏指定的导入模型。
   */
  async function handlePartTabSelect(tabId: string): Promise<void> {
    const projectModelRoot = resolveCurrentProjectModelRoot()
    if (projectModelRoot?.nodeKey === tabId) {
      // OpenProject 根 tab 的复用判断必须基于 commit/package/session identity，
      // 不能走普通 GLB tab 的 mounted/cache 快速路径，否则会把旧 Worker runtime 混入新文档。
      await loadProjectRootTab(projectModelRoot)
      return
    }

    if (mountedGlbTabId.value === tabId && displayActivePartTabId.value === tabId) {
      return
    }

    const scene = options.getScene()
    if (!scene) {
      logger.error('[GlbLoader] Scene is not ready, cannot switch tab')
      return
    }

    clearImportedModelInteractionState()

    // tab 切换剥夺任何 insertion 的提交资格（spec: "tab 切换或再次插入时，旧请求失去提交资格"）。
    abortCurrentInsertionHandle()

    if (loadedResultByTabId.has(tabId)) {
      // cache-hit 快速路径。协作式抢占：同 scope 下其它 preload / tab-switch 的
      // transaction 不会被这次 acquire abort，它们可以继续跑完并 commit 到共享 cache。
      const restoreHandle = requestRegistry.acquire('tab-switch', {
        scope: TAB_REQUEST_SCOPE,
        cooperative: true,
      })
      let restored = false
      try {
        restored = restoreTabFromCache(tabId, scene)
      } catch (error) {
        logger.error('[GlbLoader] restoreTabFromCache failed, falling back to reload', error)
        loadedResultByTabId.delete(tabId)
        glbHierarchyByTabId.delete(tabId)
        restoreHandle.abort()
        // 不再 fall through 前做任何清理 —— 走 slow path 会重新设置 overlay；
        // 但若有被抢占的 pendingPartTabId === tabId，避免被旧状态污染。
      }
      if (restored) {
        // restore 成功：本 handle 即刻成为当前 current owner。以下 cleanup 必须即便抛异常
        // 也不影响最终 overlay 清零 —— 用 try/finally 把 `resetLoading(tabId)` 当作
        // 无条件兜底步骤执行。resetLoading 是宿主侧的**权威清零**接口（无视 owner-guard），
        // 保证任何 owner=被抢占 tab 的 overlay 都会归零。
        try {
          restoreHandle.transaction.commit()
          restoreHandle.release()
          // Reclaim singleton（overlay / isTreeLoading）+ 本 tab 的 per-tab spinner。
          // 关键：**只碰本 tabId 自己的 per-tab 状态**。
          //
          // 协作式抢占下，其它 tab 的 loading=true 状态都是合法 in-flight 信号：
          //   - 被抢占的慢路径 handlePartTabSelect(B) 仍在跑，B 的 spinner 是合法的；
          //     它最终走 `!isCurrent()` 分支时会自己清 `setTabLoading(B, false)`。
          //   - 独立的 preloadTabModel(C) 仍在飞，C 的 spinner 是合法的；preload 的
          //     finally 块会清 `setTabLoading(C, false)`。
          //   - child-resolve refcount 驱动的子 tab spinner 也是合法 in-flight 信号，
          //     onChildResolveComplete 会清零。
          // 历史遗毒：以前这里有一条"兜底"循环遍历所有 tabs 把 loading=true 都清掉，
          // 结果切到已加载 tab 的瞬间其它并发中的 tab 的 spinner 全部消失（regression）。
          //
          // pendingPartTabId 单值存储，只 reset 由**本 cache-hit 路径**写入的那份；
          // 若当前值是被抢占慢路径写的 otherTab，让那条慢路径的 cleanup 自己去清。
          if (pendingPartTabId.value === tabId) {
            pendingPartTabId.value = null
          }
          try {
            partTabsStore.setTabLoading(tabId, false)
          } catch (error) {
            logger.error('[GlbLoader] clear current tab spinner failed', error)
          }
          featureTreeStore.isTreeLoading = false
        } finally {
          // 权威清零 overlay。优先使用宿主提供的 `resetLoading`（单步原子：把 owner 置为
          // 本 tabId 再无条件清零 isModelLoading/text/owner）。向后兼容：宿主没实现时退回
          // 两段式 `showLoading('', tabId) + hideLoading(tabId)`。
          if (options.resetLoading) {
            options.resetLoading(tabId)
          } else {
            options.showLoading('', tabId)
            options.hideLoading(tabId)
          }
        }
        return
      }
    }

    // 慢路径：协作式抢占同 scope 下其它 handle —— 旧 handle 失去 isCurrent() 但
    // transaction 继续跑，commit 到共享 cache。本 handle 负责前台挂载。
    const handle = requestRegistry.acquire('tab-switch', {
      scope: TAB_REQUEST_SCOPE,
      cooperative: true,
    })

    pendingPartTabId.value = tabId
    const tabLabel = partTabsStore.tabs.find((item) => item.id === tabId)?.label ?? tabId
    options.showLoading(`正在切换到模型：${tabLabel}`, tabId)
    partTabsStore.setTabLoading(tabId, true)
    featureTreeStore.isTreeLoading = true

    // 局部帮手：在 "自己仍 current" 且需要退出当前请求的分支统一 abort + hide + 清掉 pending。
    // 被抢占的分支绝对不进这里：那时 isCurrent() 已经为 false，前台归属已属于新 handle，
    // 任何 hideLoading / pending 清理都会污染新请求。
    const abortAndClearOwnedLoading = (): void => {
      if (!handle.isCurrent()) {
        return
      }
      handle.abort()
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(tabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(tabId)
    }

    try {
      const file = cadSessionStore.getGlbFiles().find((item: FileInfo) => item.fileId === tabId)
      if (!file) {
        logger.error(`[GlbLoader] Target file not found: ${tabId}`)
        abortAndClearOwnedLoading()
        return
      }

      const loadedModel = await loadImportedModelWithHandle(handle, file)

      // 协作式抢占 → 三分支决策（await 返回之后）：
      //   (a) handle.transaction.isAborted：被显式 abort（非协作抢占 / dispose / tab 切换剥夺
      //       insertion 资格等非 tab-switch 路径）。dispose result、清本 tab per-tab 状态、返回。
      //   (b) !handle.isCurrent() && !isAborted：协作式抢占（典型：快速再次 tab-switch）。
      //       commit transaction 把成果写入共享 cache，只清理未挂载实例（前台归属已给新 handle，
      //       不挂载 scene，且 cache 已接管共享资源），清本 tab per-tab 状态、返回。
      //   (c) handle.isCurrent() && !isAborted：仍是当前 owner，正常成功路径。
      if (handle.transaction.isAborted) {
        if (loadedModel?.result) {
          try {
            disposeImportedModel(loadedModel.result)
          } catch (error) {
            logger.error('[GlbLoader] dispose aborted tab-switch result failed', error)
          }
        }
        partTabsStore.setTabLoading(tabId, false)
        if (pendingPartTabId.value === tabId) {
          pendingPartTabId.value = null
        }
        return
      }

      if (!handle.isCurrent()) {
        // 协作被抢占：把解析成果 commit 到共享 cache，供新 handle / 将来切回时命中。
        if (loadedModel) {
          commitAndDisposeBackgroundImportedModelResult(handle, loadedModel, 'cooperative-preempt')
        } else {
          // loader 返回 null（abort-error）但 transaction 未显式 abort：兜底。
          handle.abort()
        }
        partTabsStore.setTabLoading(tabId, false)
        if (pendingPartTabId.value === tabId) {
          pendingPartTabId.value = null
        }
        return
      }

      if (!loadedModel) {
        options.showError('模型加载失败，请重试')
        abortAndClearOwnedLoading()
        return
      }

      // 仍 current：commit → 前台挂载
      handle.transaction.commit()
      commitPartTabSwitch(tabId, loadedModel, scene)
      if (pendingPartTabId.value === tabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(tabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(tabId)
      handle.release()
      logger.info(`[GlbLoader] switch complete: ${loadedModel.result.partName}`)
    } catch (error) {
      if (isImportedModelLoadAbortedError(error) || handle.transaction.isAborted || !handle.isCurrent()) {
        // 被抢占 / 取消 / abort-error：清理本 tabId 自己的 per-tab 状态。
        // singleton（featureTreeStore.isTreeLoading / hideLoading）属于新 current handle，
        // 不能在此触碰。
        partTabsStore.setTabLoading(tabId, false)
        if (pendingPartTabId.value === tabId) {
          pendingPartTabId.value = null
        }
        if (handle.isCurrent()) {
          handle.abort()
        }
        return
      }
      logger.error(`[GlbLoader] Switch failed: ${tabId}`, error)
      options.showError('模型加载失败，请重试')
      abortAndClearOwnedLoading()
    }
  }

  /**
   * 释放历史版本预览成功换入新模型后被替换的旧缓存结果。
   *
   * @param previousResults 预览重载开始前 loader 持有的缓存结果快照。
   * @param retainedResult 成功换入后仍由 loader 持有的新结果。
   */
  function disposeVersionPreviewReplacedResults(
    previousResults: readonly ImportedModelResult[],
    retainedResult: ImportedModelResult,
  ): void {
    for (const cachedResult of previousResults) {
      if (cachedResult === retainedResult) {
        continue
      }
      disposeCommittedBackgroundImportedModelResult(
        cachedResult,
        'reloadCurrentProjectForVersionPreview replaced result',
      )
    }
  }

  /**
   * 历史版本预览 swap 进入 scene/store 变更前必须完成的纯准备结果。
   *
   * 这些字段全部由新加载结果推导，不改写 scene、标签 store 或 loader cache；
   * 因此任一解析抛错时，旧模型与旧 hierarchy/cache 仍处在完整事务边界外。
   */
  type VersionPreviewPreparedSwap = {
    /** 新模型的权威 hierarchy。 */
    hierarchy: GlbHierarchyNode
    /** 新模型对应的顶层 tab 类型。 */
    resolvedTabType: ReturnType<typeof resolveImportedModelTabType>
    /** 新装配体 manifest 可推导出的子 tab 类型。 */
    resolvedPartTabTypes: ReturnType<typeof collectResolvedPartTabTypesFromAssembly>
  }

  /**
   * 为历史版本预览 swap 预解析所有可能抛错的纯数据。
   *
   * 设计说明：
   * 1. 该函数禁止改写 scene、store、cache，也禁止提交 request transaction；
   * 2. hierarchy、tab 类型、子 tab 类型都在这里一次性解析，保证后续 swap 阶段只做提交；
   * 3. 如果未来新增 preview swap 所需的纯准备步骤，也必须先放在这里。
   *
   * @param loadedModel 已成功加载但尚未提交到前台的模型。
   * @returns 可用于事务式 swap 的预解析数据。
   */
  function prepareVersionPreviewSwap(loadedModel: LoadedImportedModel): VersionPreviewPreparedSwap {
    const hierarchy = resolveImportedModelHierarchy(loadedModel.result)
    const resolvedTabType = resolveImportedModelTabType(loadedModel.displayType)
    const resolvedPartTabTypes = loadedModel.result.kind === 'composed-assembly' && 'manifest' in loadedModel.result
      ? collectResolvedPartTabTypesFromAssembly(
        loadedModel.result as never,
        glbFileIdByReferenceKeyMap.value,
      )
      : []

    return {
      hierarchy,
      resolvedTabType,
      resolvedPartTabTypes,
    }
  }

  /**
   * 将 loader 的结果与 hierarchy cache 恢复为 preview swap 前的快照。
   *
   * @param loadedEntries swap 前的 loaded result cache 快照。
   * @param hierarchyEntries swap 前的 hierarchy cache 快照。
   */
  function restoreVersionPreviewCacheSnapshot(
    loadedEntries: Array<[string, ImportedModelResult]>,
    hierarchyEntries: Array<[string, GlbHierarchyNode]>,
  ): void {
    loadedResultByTabId.clear()
    for (const [tabId, result] of loadedEntries) {
      loadedResultByTabId.set(tabId, result)
    }
    glbHierarchyByTabId.clear()
    for (const [tabId, hierarchy] of hierarchyEntries) {
      glbHierarchyByTabId.set(tabId, hierarchy)
    }
  }

  /**
   * 恢复历史版本预览 swap 前的标签页快照。
   *
   * @param previousTabs swap 前的标签数组快照。
   * @param previousActiveTabId swap 前激活标签 ID。
   */
  function restoreVersionPreviewPartTabsSnapshot(
    previousTabs: typeof partTabsStore.tabs,
    previousActiveTabId: string | null,
  ): void {
    partTabsStore.tabs = previousTabs.map((tab) => ({ ...tab }))
    partTabsStore.activeTabId = previousActiveTabId
  }

  /**
   * 历史版本预览 swap 失败后尽力恢复旧场景。
   *
   * 设计说明：
   * 1. 恢复只使用 swap 前仍由 cache 持有的旧 result；
   * 2. 旧 result 重新挂回后绝不能在外层 catch 中 dispose；
   * 3. 如果恢复自身也失败，只记录日志并保留已恢复的 cache/store 快照，避免继续破坏旧状态。
   *
   * @param scene 当前 three.js 场景。
   * @param previousResult swap 前可见 tab 对应的旧模型结果。
   */
  function restoreVersionPreviewSceneSnapshot(
    scene: THREE.Scene,
    previousResult: ImportedModelResult | null,
  ): void {
    if (!previousResult) {
      return
    }
    try {
      mountImportedModelRuntime(previousResult, scene)
      syncModelDisplayModeAfterImportedModelMounted()
    } catch (error) {
      logger.error('[GlbLoader] reloadCurrentProjectForVersionPreview restore old scene failed', error)
    }
  }

  /**
   * 成功换入历史预览模型后收口缓存，只保留当前目标 tab 的权威结果与 hierarchy。
   *
   * @param tabId 历史预览目标标签 ID。
   * @param result 已成功挂载的新导入结果。
   * @param hierarchy 已成功生成并写入设计树的 hierarchy。
   */
  function retainVersionPreviewTargetCache(
    tabId: string,
    result: ImportedModelResult,
    hierarchy: GlbHierarchyNode,
  ): void {
    loadedResultByTabId.clear()
    loadedResultByTabId.set(tabId, result)
    glbHierarchyByTabId.clear()
    glbHierarchyByTabId.set(tabId, hierarchy)
  }

  /**
   * 执行历史版本预览专用的事务式 scene/cache swap。
   *
   * 事务边界：
   * - 调用本函数前必须已经完成 `prepareVersionPreviewSwap` 的所有纯解析；
   * - 本函数第一步提交加载 transaction，随后才允许 detach/mount 或写入 loader cache；
   * - 旧缓存和旧模型只在本函数完整成功返回后由调用方释放。
   *
   * @param tabId 历史预览目标标签 ID。
   * @param loadedModel 已完成纯准备的新模型。
   * @param prepared 纯准备阶段产出的 hierarchy 与 tab 类型。
   * @param scene 当前 three.js 场景。
   * @param handle 当前 preview reload 请求句柄。
   * @param onTransactionCommitted transaction 成功提交后的同步回调。
   */
  async function commitVersionPreviewPreparedSwap(
    tabId: string,
    loadedModel: LoadedImportedModel,
    prepared: VersionPreviewPreparedSwap,
    scene: THREE.Scene,
    handle: ImportedModelRequestHandle,
    isCurrentRequest: () => boolean,
    onTransactionCommitted: () => void,
  ): Promise<void> {
    const previousMountedTabId = mountedGlbTabId.value
    const previousActiveTabId = partTabsStore.activeTabId
    const previousTabs = partTabsStore.tabs.map((tab) => ({ ...tab }))
    const previousLoadedEntries = Array.from(loadedResultByTabId.entries())
    const previousHierarchyEntries = Array.from(glbHierarchyByTabId.entries())
    const previousWarnings = Array.from(reportedExternalReferenceWarningByTabId.entries())
    const previousResult = previousMountedTabId
      ? loadedResultByTabId.get(previousMountedTabId) ?? null
      : null

    /**
     * 校验当前 swap 是否仍然持有提交资格。
     *
     * @param stage 当前校验所处阶段，便于日志定位是哪一个异步边界失效。
     */
    const assertCurrentSwapOwner = (stage: string): void => {
      if (handle.transaction.isAborted || !handle.isCurrent() || !isCurrentRequest()) {
        throw new Error(`[GlbLoader] 历史版本预览 swap 已失效：stage=${stage}, tabId=${tabId}`)
      }
    }

    assertCurrentSwapOwner('before-transaction-commit')
    handle.transaction.commit()
    onTransactionCommitted()

    try {
      /**
       * 事务提交完成后才进入 scene/store 变更区。
       * 这里显式 detach 旧 scene，再 mount 新 result；`mountImportedModelToScene`
       * 内部对 legacy/composed 仍有自己的防御式 detach，重复 detach 是幂等的。
       */
      if (previousMountedTabId) {
        detachImportedAssemblyFromScene(scene)
      }
      mountImportedModelRuntime(loadedModel.result, scene)

      mountedGlbTabId.value = tabId
      partTabsStore.switchTab(tabId)
      loadedResultByTabId.set(tabId, loadedModel.result)
      reportExternalReferenceLoadWarnings(tabId, loadedModel.result)

      partTabsStore.updateTabType(tabId, prepared.resolvedTabType, prepared.resolvedTabType)
      for (const { tabId: otherTabId, type } of prepared.resolvedPartTabTypes) {
        if (otherTabId !== tabId) {
          partTabsStore.updateTabType(otherTabId, type, type)
        }
      }

      syncPartTabsWithImportedModelCaches()
      glbHierarchyByTabId.set(tabId, prepared.hierarchy)

      await featureTreeStore.refreshTree(tabId)
      assertCurrentSwapOwner('after-feature-tree-refresh')

      focusViewerOnSceneContent()
      retainVersionPreviewTargetCache(tabId, loadedModel.result, prepared.hierarchy)
      syncModelDisplayModeAfterImportedModelMounted()
      handle.release()
    } catch (error) {
      /**
       * mount 之后的任何异常都必须回滚到 preview reload 前的可见状态。
       * 这里不 dispose 旧 result，也不清空旧 cache；外层只会释放未接管的新 result。
       */
      restoreVersionPreviewCacheSnapshot(previousLoadedEntries, previousHierarchyEntries)
      restoreVersionPreviewPartTabsSnapshot(previousTabs, previousActiveTabId)
      reportedExternalReferenceWarningByTabId.clear()
      for (const [warningTabId, warningKey] of previousWarnings) {
        reportedExternalReferenceWarningByTabId.set(warningTabId, warningKey)
      }
      mountedGlbTabId.value = previousMountedTabId
      try {
        detachImportedAssemblyFromScene(scene)
      } catch (detachError) {
        logger.error('[GlbLoader] reloadCurrentProjectForVersionPreview rollback detach failed', detachError)
      }
      restoreVersionPreviewSceneSnapshot(scene, previousResult)
      throw error
    }
  }

  /**
   * 为历史版本预览强制重载当前项目模型。
   *
   * mock 阶段历史快照仍复用当前项目文件，但历史版本切换必须重新经过模型加载、挂载和设计树刷新链路，
   * 不能只更新版本面板文本。这里使用 preview reload 专用慢路径：新模型 load/验证成功前不 detach、
   * 不 dispose 旧模型、不清空缓存；只有成功后才通过 `commitPartTabSwitch` 完成 swap。这样加载失败时
   * 旧模型仍保留在场景与缓存中，用户不会因为一次历史预览失败丢失当前可见模型。
   *
   * @param label 历史提交的显示名称，用于错误日志定位。
   * @param context 版本切换请求上下文，用于在真正替换场景前阻断陈旧请求。
   * @returns 当前项目模型重新挂载完成后的 Promise。
   */
  async function reloadCurrentProjectForVersionPreview(
    snapshot: ProjectCommitSnapshot,
    context?: { isCurrent: () => boolean },
  ): Promise<void> {
    const label = snapshot.title
    const activeTabId = partTabsStore.activeTabId
    const modelLoadInput = resolveVersionPreviewModelLoadInput(
      snapshot.modelSource,
      resolveCurrentModelLoadFiles(),
      cadSessionStore.getGlbFiles(),
      cadSessionStore.sessionId,
      resolveImportedModelCacheVersion(),
      activeTabId,
    )
    const targetFile = modelLoadInput?.targetFile ?? null

    if (!targetFile) {
      throw new Error(`[GlbLoader] 无可加载模型，无法切换历史版本预览：${label}`)
    }

    const scene = options.getScene()
    if (!scene) {
      throw new Error(`[GlbLoader] 场景未就绪，无法切换历史版本预览：${label}`)
    }

    clearImportedModelInteractionState()
    abortCurrentInsertionHandle()
    const targetTabId = targetFile.fileId
    const previousPendingTabId = pendingPartTabId.value
    const previousCachedResults = collectUniqueImportedModelResults(loadedResultByTabId.values())
    const handle = requestRegistry.acquire('tab-switch', {
      scope: TAB_REQUEST_SCOPE,
    })

    pendingPartTabId.value = targetTabId
    partTabsStore.setTabLoading(targetTabId, true)
    featureTreeStore.isTreeLoading = true
    options.showLoading(`正在加载历史版本：${label}`, targetTabId)

    const restorePreviewReloadLoading = (): void => {
      if (pendingPartTabId.value === targetTabId) {
        pendingPartTabId.value = previousPendingTabId
      }
      partTabsStore.setTabLoading(targetTabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(targetTabId)
    }

    const isCurrentVersionPreviewRequest = (): boolean => {
      return !context || context.isCurrent()
    }

    let loadedModel: LoadedImportedModel | null = null
    let hasCommittedTransaction = false
    let hasCommittedSwap = false

    try {
      loadedModel = await loadImportedModelWithHandle(
        handle,
        targetFile,
        modelLoadInput ?? undefined,
      )
      if (!loadedModel || handle.transaction.isAborted || !handle.isCurrent()) {
        throw new Error(
          `[GlbLoader] 历史版本预览重载失败：label=${label}, targetTabId=${targetTabId}`,
        )
      }
      if (context && !context.isCurrent()) {
        throw new Error(
          `[GlbLoader] 历史版本预览重载请求已失效：label=${label}, targetTabId=${targetTabId}`,
        )
      }

      const preparedSwap = prepareVersionPreviewSwap(loadedModel)
      if (context && !context.isCurrent()) {
        throw new Error(
          `[GlbLoader] 历史版本预览重载请求已失效：label=${label}, targetTabId=${targetTabId}`,
        )
      }
      await commitVersionPreviewPreparedSwap(
        targetTabId,
        loadedModel,
        preparedSwap,
        scene,
        handle,
        isCurrentVersionPreviewRequest,
        () => {
          hasCommittedTransaction = true
        },
      )
      hasCommittedSwap = true

      disposeVersionPreviewReplacedResults(previousCachedResults, loadedModel.result)

      if (!isCurrentVersionPreviewRequest()) {
        return
      }

      if (pendingPartTabId.value === targetTabId) {
        pendingPartTabId.value = null
      }
      partTabsStore.setTabLoading(targetTabId, false)
      featureTreeStore.isTreeLoading = false
      options.hideLoading(targetTabId)
    } catch (error) {
      if (!hasCommittedSwap && loadedModel?.result) {
        try {
          if (hasCommittedTransaction) {
            disposeCommittedBackgroundImportedModelResult(
              loadedModel.result,
              'reloadCurrentProjectForVersionPreview',
            )
          } else {
            disposeImportedModel(loadedModel.result)
          }
        } catch (disposeError) {
          logger.error('[GlbLoader] reloadCurrentProjectForVersionPreview dispose failed result failed', disposeError)
        }
      }
      try {
        handle.abort()
      } catch (abortError) {
        logger.error('[GlbLoader] reloadCurrentProjectForVersionPreview abort failed', abortError)
      }
      if (isCurrentVersionPreviewRequest()) {
        restorePreviewReloadLoading()
      }
      if (isImportedModelLoadAbortedError(error)) {
        throw new Error(
          `[GlbLoader] 历史版本预览重载失败：label=${label}, targetTabId=${targetTabId}`,
        )
      }
      logger.error('[GlbLoader] reloadCurrentProjectForVersionPreview failed', error)
      throw new Error(
        `[GlbLoader] 历史版本预览重载失败：label=${label}, targetTabId=${targetTabId}`,
      )
    }
  }

  /**
   * 放置确认后将已加载的模型插入到当前装配体。
   */
  function insertImportedModel(
    file: FileInfo,
    loadedModel: LoadedImportedModel,
    sceneObject: THREE.Object3D,
    position: THREE.Vector3,
    fixationMode: InsertComponentFixationMode,
  ): boolean {
    const tabId = mountedGlbTabId.value
    if (!tabId) {
      logger.warn('[GlbLoader] insertImportedModel: no mounted tab')
      return false
    }

    const currentTab = partTabsStore.tabs.find((tab) => tab.id === tabId)
    if (currentTab?.type !== 'assembly') {
      logger.warn('[GlbLoader] insertImportedModel: current tab is not an assembly')
      return false
    }

    const currentResult = loadedResultByTabId.get(tabId)
    if (!currentResult) {
      logger.warn('[GlbLoader] insertImportedModel: no cached result for current tab', { tabId })
      return false
    }
    if (currentResult.kind !== 'composed-assembly') {
      logger.warn('[GlbLoader] insertImportedModel: hard-guard rejected non-assembly result', {
        tabId,
        expectedKind: 'composed-assembly',
        actualKind: currentResult.kind,
        planDisplayType: loadedModel.displayType,
      })
      return false
    }

    const scene = options.getScene()
    if (!scene) {
      logger.warn('[GlbLoader] insertImportedModel: scene not ready')
      return false
    }

    const assemblyGroup = scene.getObjectByName('imported-assembly')
    if (!assemblyGroup) {
      logger.warn('[GlbLoader] insertImportedModel: imported-assembly group not found')
      return false
    }

    const cachedHierarchy = glbHierarchyByTabId.get(tabId)
    if (!cachedHierarchy) {
      logger.warn('[GlbLoader] insertImportedModel: no cached hierarchy')
      return false
    }

    const viewer = options.getViewer()
    if (!viewer) {
      logger.warn('[GlbLoader] insertImportedModel: viewer not ready')
      return false
    }

    sceneObject.position.copy(position)
    viewer.addObject(sceneObject, assemblyGroup)

    const originalChildHierarchy = resolveImportedModelHierarchy(loadedModel.result)
    const baseName =
      originalChildHierarchy.name?.trim() || extractFileName(file.fileId)
    const dedupedName = resolveUniqueChildName(cachedHierarchy, baseName)
    const fixationState = AssemblyComponentFixationService.resolveInsertionFixationState({
      mode: fixationMode,
      hasExistingComponent: AssemblyComponentFixationService.hasExistingComponent(cachedHierarchy),
    })

    const insertedChildHierarchy = AssemblyComponentFixationService.buildInsertedComponentHierarchyNode({
      sourceNode: originalChildHierarchy,
      displayName: dedupedName,
      fixationState,
      parentObjectId: cachedHierarchy.objectId,
      parentPath: cachedHierarchy.path,
    })

    const selectionRebindResult = rebindInsertedImportedModelAssemblySelectionMetadata({
      root: sceneObject,
      sourceHierarchy: originalChildHierarchy,
      insertedHierarchy: insertedChildHierarchy,
      selectionIndex: currentResult.selectionIndex,
      sceneBindings: currentResult.sceneBindings,
      sceneFileInfo: currentResult.fileInfo,
      insertedPartUuid: loadedModel.result.fileInfo.uuid,
    })
    AssemblyComponentFixationService.writeSceneObjectFixationState(sceneObject, fixationState)
    const nextHierarchy: GlbHierarchyNode = {
      ...cachedHierarchy,
      children: [...cachedHierarchy.children, insertedChildHierarchy],
    }
    if (nextHierarchy.meta) {
      const currentSemanticKind = nextHierarchy.meta.semanticKind
      if (currentSemanticKind == null || currentSemanticKind === 'unknown') {
        nextHierarchy.meta = {
          ...nextHierarchy.meta,
          semanticKind: 'assembly',
        }
      }
    }
    glbHierarchyByTabId.set(tabId, nextHierarchy)
    currentResult.hierarchy = nextHierarchy
    syncFeatureTreeAfterInsertion(
      tabId,
      insertedChildHierarchy.objectId,
      cachedHierarchy,
      insertedChildHierarchy,
    )
    //------ 显示模型下拉 开始------
    // 插入零部件绕过整模型切换链路，必须在插入挂载完成后显式同步显示模式，确保带边线上色可立即创建拓扑显示边。
    syncModelDisplayModeAfterImportedModelMounted()
    //------ 显示模型下拉 结束------

    logger.info(`[GlbLoader] inserted component: ${file.fileId} as "${dedupedName}"`, {
      reboundObjectCount: selectionRebindResult.reboundObjectCount,
      selectionBindingCount: selectionRebindResult.selectionBindingCount,
      sceneBindingCount: selectionRebindResult.sceneBindingCount,
    })
    return true
  }

  /**
   * 插入流程第一阶段：下载并解析插入模型，返回句柄供调用方持有。
   *
   * 取消协议：
   *   - 每次调用通过 registry 取得独占的 insertion handle（独立 scope）；
   *   - tab-switch 或再次 prepareInsertion 主动 abort 旧 handle → 其 transaction.isAborted；
   *   - await 回来时如 handle 不再 current，dispose result 并返回 null。
   */
  async function prepareInsertionWithScope(
    descriptor: InsertCadSourceDescriptor,
    prepareOptions: {
      scope: string
      preemptExistingInsertion: boolean
      showErrorOnFailure: boolean
    },
  ): Promise<InsertPreparationHandle | null> {
    if (prepareOptions.preemptExistingInsertion) {
      abortCurrentInsertionHandle()
    }
    const handle = requestRegistry.acquire('prepare-insertion', { scope: prepareOptions.scope })
    trackInsertionRequestHandle(handle)

    let loaded: LoadedImportedModel | null = null
    try {
      const brepRegistrar = resolveBRepRegistrar()
      loaded = await loadInsertedCadModel({
        sessionId: cadSessionStore.sessionId,
        brepRegistrar,
        descriptor,
      })
    } catch (error) {
      logger.error('[GlbLoader] prepareInsertion: loadInsertedCadModel threw', error)
      const errorMessage = error instanceof Error && error.message.trim()
        ? error.message
        : '插入组件失败，请重试'
      if (prepareOptions.showErrorOnFailure) {
        options.showError(errorMessage)
      }
      handle.abort()
      untrackInsertionRequestHandle(handle)
      return null
    }

    if (
      insertionRequestHandleById.get(handle.requestId) !== handle
      || !handle.isCurrent()
      || handle.transaction.isAborted
    ) {
      // 被抢占或主动取消：释放 result。
      if (loaded?.result) {
        try {
          disposeImportedModel(loaded.result)
        } catch (error) {
          logger.error(
            '[GlbLoader] prepareInsertion: disposeImportedModel on preempt failed',
            error,
          )
        }
      }
      untrackInsertionRequestHandle(handle)
      preparedInsertionHandleById.delete(handle.requestId)
      return null
    }

    if (!loaded) {
      if (prepareOptions.showErrorOnFailure) {
        options.showError('插入组件失败，请重试')
      }
      handle.abort()
      untrackInsertionRequestHandle(handle)
      return null
    }

    const file = {
      fileId: descriptor.selectedFileId,
      uuid: `insert:${descriptor.projectRootPath}`,
      kind: descriptor.cadType,
      displayName: descriptor.displayName,
    } as unknown as FileInfo

    const preparationHandle = {
      loaded,
      requestId: handle.requestId,
      file,
      fixationMode: descriptor.fixationMode ?? DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
    }
    preparedInsertionHandleById.set(handle.requestId, preparationHandle)
    return preparationHandle
  }

  async function prepareInsertion(
    descriptor: InsertCadSourceDescriptor,
  ): Promise<InsertPreparationHandle | null> {
    // 单项插入保持旧语义：新准备开始前抢占并取消任何旧插入句柄。
    return prepareInsertionWithScope(descriptor, {
      scope: INSERTION_REQUEST_SCOPE,
      preemptExistingInsertion: true,
      showErrorOnFailure: true,
    })
  }

  /**
   * 队列插入流程的准备入口。
   *
   * @param descriptor 插入来源描述。
   * @param queueOptions 队列加载提示选项。
   * @returns 可提交或丢弃的插入准备句柄；加载失败或被取消时返回 null。
   */
  async function prepareQueuedInsertion(
    descriptor: InsertCadSourceDescriptor,
    queueOptions: PrepareQueuedInsertionOptions = {},
  ): Promise<InsertPreparationHandle | null> {
    queuedInsertionScopeSeq += 1
    return prepareInsertionWithScope(descriptor, {
      scope: `${INSERTION_REQUEST_SCOPE}:queued:${queuedInsertionScopeSeq}`,
      preemptExistingInsertion: false,
      showErrorOnFailure: queueOptions.showErrorOnFailure ?? true,
    })
  }

  /**
   * 插入流程第二阶段：将准备好的模型同步地挂载到当前装配体。
   *
   * ownership 校验基于 loader 是否仍托管该 requestId。
   * 失配（被 tab-switch / 再次 prepare / discard 抢占）时 dispose + 返回 false。
   */
  function commitInsertion(
    handle: InsertPreparationHandle,
    worldPosition: THREE.Vector3,
  ): boolean {
    const currentHandle = insertionRequestHandleById.get(handle.requestId)
    if (
      !currentHandle
      || preparedInsertionHandleById.get(handle.requestId) !== handle
      || !currentHandle.isCurrent()
      || currentHandle.transaction.isAborted
    ) {
      try {
        disposeImportedModel(handle.loaded.result)
      } catch (error) {
        logger.error(
          '[GlbLoader] commitInsertion: disposeImportedModel on ownership mismatch failed',
          error,
        )
      }
      return false
    }

    const scene = options.getScene()
    if (!scene) {
      logger.warn('[GlbLoader] commitInsertion: scene not ready, discarding loaded result')
      try {
        disposeImportedModel(handle.loaded.result)
      } catch (error) {
        logger.error(
          '[GlbLoader] commitInsertion: disposeImportedModel on scene-not-ready failed',
          error,
        )
      }
      currentHandle.abort()
      untrackInsertionRequestHandle(currentHandle)
      preparedInsertionHandleById.delete(handle.requestId)
      return false
    }

    const sceneObject = resolveSceneObject(handle.loaded.result)
    const inserted = insertImportedModel(
      handle.file,
      handle.loaded,
      sceneObject,
      worldPosition,
      handle.fixationMode,
    )
    if (!inserted) {
      try {
        disposeImportedModel(handle.loaded.result)
      } catch (error) {
        logger.error(
          '[GlbLoader] commitInsertion: disposeImportedModel on guard reject failed',
          error,
        )
      }
      currentHandle.abort()
      untrackInsertionRequestHandle(currentHandle)
      preparedInsertionHandleById.delete(handle.requestId)
      return false
    }
    // 成功路径：释放 ownership（handle 已完成其使命；transaction 本身由 insert-cad-loader
    // 内部的一次性 transaction 管理）。
    currentHandle.release()
    untrackInsertionRequestHandle(currentHandle)
    preparedInsertionHandleById.delete(handle.requestId)
    return true
  }

  /**
   * 用户取消放置后释放已准备好的句柄。
   * 通过 currentInsertionHandle.abort() 使任何仍在途的 prepareInsertion
   * 在 await 返回后自动丢弃结果，并立即 dispose 句柄所持有的已加载结果。
   */
  function discardPreparedInsertion(handle: InsertPreparationHandle): void {
    // 如果 handle 仍是当前 owner，abort 以剥夺 commit 资格。
    const currentHandle = insertionRequestHandleById.get(handle.requestId)
    if (currentHandle) {
      currentHandle.abort()
      untrackInsertionRequestHandle(currentHandle)
    }
    preparedInsertionHandleById.delete(handle.requestId)
    try {
      disposeImportedModel(handle.loaded.result)
    } catch (error) {
      logger.error('[GlbLoader] discardPreparedInsertion: dispose failed', error)
    }
  }

  /**
   * 取消当前在途的插入请求。
   * 通过 current handle 的 abort() 触发 transaction abort + ownership 释放。
   * 没有在途 insertion 时为 no-op（不再依赖 seq 语义）。
   */
  function cancelInsertion(): void {
    abortCurrentInsertionHandle()
  }

  /**
   * 在父节点的现有子节点中寻找一个不冲突的名称。
   */
  function resolveUniqueChildName(
    parent: GlbHierarchyNode,
    base: string,
  ): string {
    const existing = new Set<string>()
    for (const child of parent.children) {
      const name = child.name?.trim()
      if (name) existing.add(name)
    }
    if (!existing.has(base)) return base
    let suffix = 1
    while (existing.has(`${base}(${suffix})`)) {
      suffix += 1
    }
    return `${base}(${suffix})`
  }

  /**
   * 设置装配体零部件固定/浮动状态。
   *
   * 设计说明：
   * 1. 设计树右键菜单只传入目标对象与目标状态，不直接写 hierarchy 或 Three.js 运行时元数据；
   * 2. hierarchy 是可持久化的权威状态，场景对象 metadata 是运行时镜像，两者必须在这里成对更新；
   * 3. 更新完成后仅投影实际变化节点的展示字段，前缀仍复用完整构树规则。
   *
   * @param input 固定/浮动状态切换输入。
   */
  async function setAssemblyComponentFixationState(input: {
    /** 目标 hierarchy objectId 或 selectionObjectId。 */
    objectId: string
    /** 目标场景对象 ID；缺省时复用 objectId。 */
    sceneObjectId?: string
    /** 目标固定/浮动状态。 */
    state: AssemblyComponentFixationState
    /** 触发操作的设计树 key，用于失败时保留调用上下文。 */
    treeKey: string
  }): Promise<void> {
    const tabId = mountedGlbTabId.value
    if (!tabId) {
      logger.warn('[GlbLoader] setAssemblyComponentFixationState: no mounted tab', input)
      return
    }

    const cachedHierarchy = glbHierarchyByTabId.get(tabId)
    if (!cachedHierarchy) {
      logger.warn('[GlbLoader] setAssemblyComponentFixationState: no cached hierarchy', input)
      return
    }

    const viewer = options.getViewer()
    const sceneObject = viewer?.getSceneObjectById?.(input.sceneObjectId ?? input.objectId)
      ?? (input.sceneObjectId ? viewer?.getSceneObjectById?.(input.objectId) : null)
      ?? null
    if (!sceneObject) {
      logger.warn('[GlbLoader] setAssemblyComponentFixationState: no scene object', input)
      return
    }

    const result = AssemblyComponentFixationService.projectHierarchyNodeFixationState(
      cachedHierarchy,
      input.objectId,
      input.state,
    )
    if (!result.matched) {
      logger.warn('[GlbLoader] setAssemblyComponentFixationState: hierarchy node not found', input)
      return
    }

    const historyRecord = applyAssemblyFixationStateWithHistory({
      object: sceneObject as unknown as WebGpuObject3D | null,
      fixationState: input.state,
      assemblyId: tabId,
      componentInstanceId: input.objectId,
    })
    await viewer?.moveComponentController?.commitAssemblyTransformHistoryRecord?.(historyRecord)

    glbHierarchyByTabId.set(tabId, result.hierarchy)
    const currentResult = loadedResultByTabId.get(tabId)
    if (currentResult?.kind === 'composed-assembly') {
      currentResult.hierarchy = result.hierarchy
    }
    // 固定状态与历史已经提交；展示投影与普通重绘共享下一宿主帧。
    viewer?.requestRender()

    syncAssemblyHierarchyPresentation(tabId, result.changedNodes)
  }

  /**
   * 将 SDK 返回的实际层级变化同步到 GLB 绑定和激活树，纯位姿不进入 UI 投影。
   * @param tabId 所属标签。
   * @param changedNodes 固定字段实际变化的节点；不含仅引用更新的祖先。
   */
  function syncAssemblyHierarchyPresentation(tabId: string, changedNodes: readonly GlbHierarchyNode[]): void {
    if (changedNodes.length === 0) return
    const updates = glbFeatureTree.updateHierarchyNodePresentations(tabId, changedNodes)
    featureTreeStore.applyGlbNodePresentationUpdates(tabId, updates)
  }

  /**
   * 将装配组件历史快照同步回导入层主状态投影。
   *
   * 设计说明：
   * undo/redo 与普通提交都会先恢复或更新场景对象位姿，再调用该入口同步 hierarchy/currentResult。
   * `workingAssemblyDocument` 只承担协同节点记录职责，移动位姿不得在这里写回工作草稿。
   *
   * @param snapshot 装配组件历史快照。
   */
  function applyAssemblyComponentHistorySnapshot(
    snapshot: AssemblyComponentHistorySnapshot,
  ): void {
    const resolvedTarget = resolveAssemblyHistoryTarget(loadedResultByTabId, snapshot)
    const cachedHierarchy = resolvedTarget
      ? glbHierarchyByTabId.get(resolvedTarget.tabId) ?? resolvedTarget.result.hierarchy
      : glbHierarchyByTabId.get(snapshot.assemblyId)
    if (!cachedHierarchy) {
      logger.warn('[GlbLoader] applyAssemblyComponentHistorySnapshot: no cached hierarchy', snapshot)
      return
    }

    const fixationState = snapshot.fixationState === 'fixed'
      ? AssemblyComponentFixationState.Fixed
      : AssemblyComponentFixationState.Floating
    const sceneObject = resolveAssemblyHistorySceneObject(options.getViewer(), snapshot)
    const hierarchyObjectIds = [
      sceneObject?.uuid,
      snapshot.componentInstanceId,
      snapshot.sourceNodeId,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    let result: PuppyCAD.AssemblyHierarchyFixationProjection = {
      hierarchy: cachedHierarchy, matched: false, changedNodes: [],
    }
    for (const objectId of hierarchyObjectIds) {
      result = AssemblyComponentFixationService.projectHierarchyNodeFixationState(
        cachedHierarchy,
        objectId,
        fixationState,
      )
      if (result.matched) {
        break
      }
    }

    if (!result.matched) {
      logger.warn('[GlbLoader] applyAssemblyComponentHistorySnapshot: hierarchy node not found', snapshot)
    }

    const targetTabId = resolvedTarget?.tabId ?? snapshot.assemblyId
    glbHierarchyByTabId.set(targetTabId, result.hierarchy)
    const currentResult = resolvedTarget?.result ?? loadedResultByTabId.get(snapshot.assemblyId)
    if (currentResult?.kind === 'composed-assembly') {
      currentResult.hierarchy = result.hierarchy
      applyAssemblyTransformToManifestNode(currentResult, snapshot, sceneObject)
    }

    // manifest 位姿仍完整写回；UI 只消费固定状态差异，不能按每个联动组件重建整树。
    syncAssemblyHierarchyPresentation(targetTabId, result.changedNodes)
  }

  /**
   * 清理导入模型加载器的全部运行时状态。
   */
  function dispose(): void {
    isDisposed = true
    unsubscribeAssemblyModelCache?.()
    unsubscribePartAssetCache?.()
    unsubscribeAssemblyModelCache = null
    unsubscribePartAssetCache = null
    featureTreeStore.unregisterGlbTreeRefreshProvider()
    featureTreeStore.isTreeLoading = false
    clearRootDocumentRuntimeState('editor dispose', { disposeMode: 'full' })
    try {
      importedModelRevisionSession.dispose()
    } catch (error) {
      logger.error('[GlbLoader] dispose imported model revision session failed', error)
    }
    completeWorkAssemblySession.dispose()
    disposeCaches()
    cadSessionStore.reset()
  }

  //------ 线性零部件阵列特征树同步 开始------

  /**
   * 线性阵列运行时节点在 GlbHierarchyNodeMeta 中的标识键。
   *
   * 设计说明：
   * 使用 runtimeWrapper 标记区分运行时生成的虚拟节点与从 GLB 文件解析的实际节点，
   * 保证每次重新同步时可精确过滤、替换旧节点，不影响其他子树。
   */
  enum LinearPatternRuntimeTreeMarker {
    GroupNodeType = 'LinearPatternRuntimeGroup',
    PartNodeType = 'LinearPatternRuntimePart',
  }

  //------ 线性零部件阵列撤销重做设计树同步 开始------
  /**
   * 线性零部件阵列设计树同步选项。
   */
  interface LinearComponentPatternFeatureTreeSyncOptions {
    /** 显式指定要写入运行时阵列节点的标签 ID。 */
    readonly tabId?: string | null
    /** 当前同步原因，用于选择刷新语义。 */
    readonly reason?: LinearComponentPatternFeatureTreeSyncReason
    /** 调用方是否会在当前加载事务末尾统一刷新设计树。 */
    readonly deferRefresh?: boolean
  }

  /**
   * 解析线性零部件阵列设计树同步目标标签。
   *
   * 设计说明：
   * 1. Undo/Redo 恢复时设计树 active owner 可能尚未稳定，必须允许调用方显式指定 tab；
   * 2. 常规提交仍回退到当前设计树归属，保持原有交互不变；
   * 3. 所有目标 tab 解析集中在这里，避免不同同步入口各自猜测标签来源。
   *
   * @param options 同步选项。
   * @returns 目标标签 ID；无法解析时返回 null。
   */
  function resolveLinearComponentPatternFeatureTreeSyncTabId(
    options: LinearComponentPatternFeatureTreeSyncOptions,
  ): string | null {
    const explicitTabId = options.tabId?.trim() ?? ''
    if (explicitTabId.length > 0) {
      return explicitTabId
    }

    return resolveCurrentFeatureTreeTabId()
  }
  //------ 线性零部件阵列撤销重做设计树同步 结束------

  //------ 线性零部件阵列运行时树节点构建 开始------
  /**
   * 递归查找指定对象 ID 对应的 hierarchy 节点。
   *
   * 设计说明：
   * 1. 线性阵列提交记录只持有来源零部件对象 ID；
   * 2. 运行时树同步需要先回查来源节点，才能复用其 nodeType 与 meta；
   * 3. 这里保持纯 DFS 查询，避免把来源节点索引额外持久化到 loader 运行时状态中。
   *
   * @param root 当前查找起点。
   * @param objectId 目标对象 ID。
   * @returns 命中的 hierarchy 节点；未命中时返回 null。
   */
  function findHierarchyNodeByObjectId(
    root: GlbHierarchyNode,
    objectId: string,
  ): GlbHierarchyNode | null {
    if (root.objectId === objectId || root.meta?.selectionObjectId === objectId) {
      return root
    }

    for (const child of root.children) {
      const matchedNode = findHierarchyNodeByObjectId(child, objectId)
      if (matchedNode) {
        return matchedNode
      }
    }

    return null
  }

  /**
   * 克隆来源零部件元数据，并把选择锚点改写到阵列实例。
   *
   * 设计说明：
   * 1. 设计树下级节点（系统节点、实体、材质、固定/浮动菜单）都依赖 hierarchy meta 自动推导；
   * 2. 阵列实例必须复用来源零件的导入语义，但选择锚点必须落到新的阵列实例对象 ID；
   * 3. 这里做显式字段克隆，避免把来源节点的可变数组直接共享给运行时阵列节点。
   *
   * @param sourceMeta 来源零部件元数据。
   * @param patternedObjectId 阵列实例对象 ID。
   * @returns 可安全写入运行时节点的元数据副本。
   */
  function cloneLinearPatternSourceMeta(
    sourceMeta: GlbHierarchyNodeMeta | undefined,
    patternedObjectId: string,
    sourceObjectId: string,
  ): GlbHierarchyNodeMeta {
    const bodyEntries = Array.isArray(sourceMeta?.bodyEntries)
      ? sourceMeta.bodyEntries.map((entry) => ({ ...entry }))
      : undefined
    const bodyPrimitiveIndicesById = sourceMeta?.bodyPrimitiveIndicesById
      ? Object.fromEntries(
        Object.entries(sourceMeta.bodyPrimitiveIndicesById).map(([bodyId, primitiveIndices]) => [bodyId, [...primitiveIndices]]),
      )
      : undefined
    const bodyHighlightTargetsById = sourceMeta?.bodyHighlightTargetsById
      ? Object.fromEntries(
        Object.entries(sourceMeta.bodyHighlightTargetsById).map(([bodyId, highlightTargets]) => [bodyId, [...highlightTargets]]),
      )
      : undefined

    return {
      semanticKind: sourceMeta?.semanticKind ?? 'part',
      hasGeometry: sourceMeta?.hasGeometry ?? true,
      hasDirectGeometry: sourceMeta?.hasDirectGeometry ?? true,
      pmi: sourceMeta?.pmi,
      bodyCount: sourceMeta?.bodyCount,
      bodyEntries,
      bodyPrimitiveIndicesById,
      bodyHighlightTargetsById,
      materialNames: [...(sourceMeta?.materialNames ?? [])],
      materialName: sourceMeta?.materialName,
      isFixed: sourceMeta?.isFixed,
      instanceIndex: sourceMeta?.instanceIndex,
      configurationName: sourceMeta?.configurationName,
      displayStateName: sourceMeta?.displayStateName,
      externalFileUuid: sourceMeta?.externalFileUuid,
      externalAssemblyName: sourceMeta?.externalAssemblyName,
      selectionObjectId: patternedObjectId,
      runtimeWrapper: true,
      persistenceMode: GlbHierarchyPersistenceMode.RuntimeDerived,
      //------ 线性阵列来源对象粉色高亮 开始------
      /**
       * 阵列复制体节点必须保留源对象 ID，供分组节点在特征树选中时回溯并粉色高亮源零部件。
       * 该字段不参与复制体自身选择锚点，避免真实 selectedObjects 混入源对象。
       */
      linearComponentPatternSourceObjectId: sourceObjectId,
      //------ 线性阵列来源对象粉色高亮 结束------
      highlightTargets: sourceMeta?.highlightTargets ? [...sourceMeta.highlightTargets] : undefined,
    }
  }

  //------ 线性阵列来源对象粉色高亮 开始------
  /**
   * 汇总线性阵列记录中的源零部件对象 ID。
   *
   * 设计说明：
   * 1. 同一个源对象可能生成多个复制体，分组节点只需要记录一次；
   * 2. 过滤空字符串，避免失效数据进入设计树交互元信息；
   * 3. 保持顺序稳定，方便测试和日志比对。
   *
   * @param record 线性阵列提交记录。
   * @returns 去重后的源对象 ID 列表。
   */
  function collectLinearPatternSourceObjectIds(
    record: LinearComponentPatternCommittedRecord,
  ): string[] {
    const sourceObjectIdSet = new Set<string>()

    record.partRecords.forEach((partRecord) => {
      const sourceObjectId = partRecord.sourceObjectId.trim()
      if (sourceObjectId.length > 0) {
        sourceObjectIdSet.add(sourceObjectId)
      }
    })

    return [...sourceObjectIdSet]
  }
  //------ 线性阵列来源对象粉色高亮 结束------

  /**
   * 构建单个线性阵列零部件的运行时 hierarchy 节点。
   *
   * 设计说明：
   * 1. 该函数是线性阵列零件设计树的唯一生成入口，专门负责把来源零件映射为阵列实例；
   * 2. 通过复用来源零件的 nodeType 与 meta，让现有 glb-feature-manager-tree 自动补齐系统节点、实体与材质节点；
   * 3. 当来源节点丢失时，回退到最小 part 语义节点并输出日志，避免一次阵列同步直接中断整棵树刷新。
   *
   * @param record 阵列记录。
   * @param patternedPart 阵列零部件记录。
   * @param index 当前零部件序号。
   * @param cachedHierarchy 当前 tab 的权威 hierarchy。
   * @returns 线性阵列零部件对应的运行时 hierarchy 节点。
   */
  function buildLinearPatternPartRuntimeNode(
    record: LinearComponentPatternCommittedRecord,
    patternedPart: LinearComponentPatternCommittedRecord['partRecords'][number],
    index: number,
    cachedHierarchy: GlbHierarchyNode,
  ): GlbHierarchyNode {
    const sourceNode = findHierarchyNodeByObjectId(cachedHierarchy, patternedPart.sourceObjectId)
    const fallbackName = patternedPart.displayName || `零部件${index + 1}`

    if (!sourceNode) {
      logger.warn(
        '[GlbLoader] buildLinearPatternPartRuntimeNode: source hierarchy node not found',
        patternedPart.sourceObjectId,
      )
      return {
        objectId: patternedPart.objectId,
        parentObjectId: record.groupObjectId,
        path: `${cachedHierarchy.path}/${record.groupObjectId}/${patternedPart.objectId}`,
        name: fallbackName,
        nodeType: LinearPatternRuntimeTreeMarker.PartNodeType,
        selectable: true,
        depth: cachedHierarchy.depth + 2,
        meta: {
          semanticKind: 'part',
          hasGeometry: true,
          hasDirectGeometry: true,
          materialNames: [],
          selectionObjectId: patternedPart.objectId,
          runtimeWrapper: true,
          persistenceMode: GlbHierarchyPersistenceMode.RuntimeDerived,
          linearComponentPatternSourceObjectId: patternedPart.sourceObjectId,
        },
        children: [],
      }
    }

    return {
      objectId: patternedPart.objectId,
      parentObjectId: record.groupObjectId,
      path: `${cachedHierarchy.path}/${record.groupObjectId}/${patternedPart.objectId}`,
      name: fallbackName,
      nodeType: sourceNode.nodeType,
      selectable: sourceNode.selectable,
      depth: cachedHierarchy.depth + 2,
      meta: cloneLinearPatternSourceMeta(sourceNode.meta, patternedPart.objectId, patternedPart.sourceObjectId),
      /**
       * 只复用来源零件的语义元数据，不复制来源 hierarchy 子节点引用。
       * 现有树构建器会基于 meta 自动生成系统节点、实体与材质节点，
       * 这样既能得到与来源一致的设计树表达，又能避免旧 objectId 被带入阵列实例。
       */
      children: [],
    }
  }

  //------ 线性零部件阵列运行时树节点构建 结束------

  /**
   * 同步线性零部件阵列特征树节点。
   *
   * 设计说明：
   * 1. 每次提交阵列或 Undo/Redo 恢复后调用，从管理器读取当前所有已提交记录；
   * 2. 先从根层级 children 中过滤掉所有旧的线性阵列运行时节点；
   * 3. 再为每条记录追加新的 GlbHierarchyNode；
   * 4. 更新 glbHierarchyByTabId 与 loadedResultByTabId，并按同步原因触发设计树刷新。
   *
   * @param committedRecords 管理器当前持有的全部已提交阵列记录。
   * @param options 同步选项。
   * @returns 同步是否成功（tabId 或 hierarchy 未就绪则返回 false）。
   */
  function syncLinearComponentPatternFeatureTree(
    committedRecords: readonly LinearComponentPatternCommittedRecord[],
    options: LinearComponentPatternFeatureTreeSyncOptions = {},
  ): boolean {
    // 历史恢复必须优先使用显式 tab，避免 redo 时把运行时阵列节点写入旧树归属。
    const tabId = resolveLinearComponentPatternFeatureTreeSyncTabId(options)
    if (!tabId) {
      logger.warn('[GlbLoader] syncLinearComponentPatternFeatureTree: no active tabId')
      return false
    }

    const cachedHierarchy = glbHierarchyByTabId.get(tabId)
    if (!cachedHierarchy) {
      logger.warn('[GlbLoader] syncLinearComponentPatternFeatureTree: no cached hierarchy for tabId', tabId)
      return false
    }

    // 保存旧运行时节点，供当前激活树执行稳定父节点下的局部替换。
    const removedPatternNodes = cachedHierarchy.children.filter(
      (child) => child.nodeType === LinearPatternRuntimeTreeMarker.GroupNodeType,
    )
    // 过滤掉所有已有的线性阵列运行时虚拟节点，避免重复追加
    const filteredChildren = cachedHierarchy.children.filter(
      (child) => child.nodeType !== LinearPatternRuntimeTreeMarker.GroupNodeType,
    )

    // 为每条已提交记录构建 GlbHierarchyNode 虚拟节点
    const patternNodes: GlbHierarchyNode[] = committedRecords.map((record) => {
      const partChildren: GlbHierarchyNode[] = record.partRecords.map((part, index) => (
        buildLinearPatternPartRuntimeNode(record, part, index, cachedHierarchy)
      ))
      const sourceObjectIds = collectLinearPatternSourceObjectIds(record)

      return {
        objectId: record.groupObjectId,
        parentObjectId: cachedHierarchy.objectId,
        path: `${cachedHierarchy.path}/${record.groupObjectId}`,
        name: record.displayName,
        nodeType: LinearPatternRuntimeTreeMarker.GroupNodeType,
        selectable: false,
        depth: cachedHierarchy.depth + 1,
        meta: {
          semanticKind: 'unknown',
          hasGeometry: false,
          hasDirectGeometry: false,
          materialNames: [],
          runtimeWrapper: true,
          persistenceMode: GlbHierarchyPersistenceMode.RuntimeDerived,
          linearComponentPatternSourceObjectIds: sourceObjectIds,
        },
        children: partChildren,
      }
    })

    // 构建更新后的根层级节点
    const nextHierarchy: GlbHierarchyNode = {
      ...cachedHierarchy,
      children: [...filteredChildren, ...patternNodes],
    }

    // 更新权威缓存
    glbHierarchyByTabId.set(tabId, nextHierarchy)
    const currentResult = loadedResultByTabId.get(tabId)
    if (currentResult?.kind === 'composed-assembly') {
      currentResult.hierarchy = nextHierarchy
    }

    //------ 线性零部件阵列撤销重做设计树同步 开始------
    const syncReason = options.reason ?? LinearComponentPatternFeatureTreeSyncReason.Commit
    let runtimeSubtreeApplied = false
    if (options.deferRefresh) {
      // 离线包或项目模型加载会在全部文档扩展恢复后统一刷新，避免并发刷新读到半恢复状态。
    } else if (featureTreeStore.activeTreeTabId === tabId) {
      /**
       * 当前活动树已经有稳定装配根时，只替换其直接阵列子树。
       * Hook 与 Store 共用同一份片段更新，因此不会重复请求 Worker、重新运行 GLB provider，
       * 也不会打断无关节点的展开、选择与滚动状态。
       */
      const subtreeUpdate = glbFeatureTree.buildRuntimeSubtreeUpdate(
        tabId,
        cachedHierarchy,
        removedPatternNodes,
        patternNodes,
        glbFileIdByReferenceKeyMap.value,
      )
      runtimeSubtreeApplied = featureTreeStore.applyGlbRuntimeSubtreeUpdate(tabId, subtreeUpdate)
    }

    if (!options.deferRefresh
      && !runtimeSubtreeApplied
      && syncReason === LinearComponentPatternFeatureTreeSyncReason.HistoryRestore) {
      // 历史恢复只需要刷新当前 tab 的权威树投影，不进入插入流程的 loading 与自动选中语义。
      void featureTreeStore.refreshTree(tabId).catch((error) => {
        logger.error('[GlbLoader] refreshTree after linear pattern history restore failed', error)
      })
    } else if (!options.deferRefresh && !runtimeSubtreeApplied) {
      // 线性阵列提交后只刷新设计树，不复用“插入后自动选中新节点”语义，
      // 否则会触发程序化选择，将新阵列对象写入 interactionState 的 selectedObjects。
      // 当前父节点不存在时仍保留原有 loading 与错误处理路径，保证冷启动结构完整。
      syncFeatureTreeAfterInsertion(tabId)
    }
    //------ 线性零部件阵列撤销重做设计树同步 结束------

    return true
  }

  //------ 线性零部件阵列特征树同步 结束------

  return {
    cadSessionStore,
    partTabsStore,
    glbFeatureTree,
    mountedGlbTabId,
    pendingPartTabId,
    displayActivePartTabId,
    glbHierarchyByTabId,
    initSessionAndLoadFiles,
    preloadDefaultTabModel,
    loadDefaultTab,
    handlePartTabSelect,
    prepareProjectModelBackendLoadUnit,
    createSwitchFirstLevelNodeVersionLoadUnit,
    createAppendMissingFirstLevelNodeVersionLoadUnit,
    applyProjectModelLoadResult,
    getCurrentProjectModelLoadResult,
    resolveFeatureDocumentScope,
    deleteProjectModelNodeLocally,
    applyAssemblyStructureHistorySnapshotLocally,
    createProjectModelWorldTransformReader,
    resolveCurrentProjectModelFirstLevelNodeByReference,
    resolveCurrentProjectModelFirstLevelTargetByReference,
    resolveCurrentProjectModelFirstLevelHoverTargetByReference,
    assemblyDraftDocumentStore,
    resetForRootViewerRebuild,
    switchProjectModelRootSnapshot,
    reloadCurrentProjectForVersionPreview,
    mountOfflinePuppyPackage,
    insertImportedModel,
    prepareInsertion,
    prepareQueuedInsertion,
    commitInsertion,
    discardPreparedInsertion,
    cancelInsertion,
    prepareAssemblyReferenceCopy,
    commitAssemblyReferenceCopy,
    removeAssemblyReferenceCopy,
    restoreAssemblyReferenceCopyFromHistorySnapshot,
    setAssemblyComponentFixationState,
    applyAssemblyComponentHistorySnapshot,
    syncLinearComponentPatternFeatureTree,
    createCurrentPuppyExportAssemblySemanticSource,
    createCurrentStepExportSource,
    dispose,
  }
}
